import { BAD_REQUEST, NON_AUTHORITATIVE_INFORMATION, INTERNAL_SERVER_ERROR, NOT_FOUND } from 'http-status-codes';
import { IndexedFormula, Fetcher, Serializer, NamedNode, uri as uri$1, parse } from 'rdflib';
import { NQuadsParser } from 'n-quads-parser';

(function (Object) {
  typeof globalThis !== 'object' && (
    this ?
      get() :
      (Object.defineProperty(Object.prototype, '_T_', {
        configurable: true,
        get: get
      }), _T_)
  );
  function get() {
    this.globalThis = this;
    delete Object.prototype._T_;
  }
}(Object));

var TermType;
(function (TermType) {
    TermType["NamedNode"] = "NamedNode";
    TermType["BlankNode"] = "BlankNode";
    TermType["Literal"] = "Literal";
})(TermType || (TermType = {}));
var QuadPosition;
(function (QuadPosition) {
    QuadPosition[QuadPosition["subject"] = 0] = "subject";
    QuadPosition[QuadPosition["predicate"] = 1] = "predicate";
    QuadPosition[QuadPosition["object"] = 2] = "object";
    QuadPosition[QuadPosition["graph"] = 3] = "graph";
})(QuadPosition || (QuadPosition = {}));
var Feature;
(function (Feature) {
    /** Whether the factory supports termType:Collection terms */
    Feature["collections"] = "COLLECTIONS";
    /** Whether the factory supports termType:DefaultGraph terms */
    Feature["defaultGraphType"] = "DEFAULT_GRAPH_TYPE";
    /** Whether the factory supports equals on produced instances */
    Feature["equalsMethod"] = "EQUALS_METHOD";
    /**
     * Whether the factory is an {IdentityFactory}, so it allows reverse lookups to be done with
     * {fromId}.
     */
    Feature["identity"] = "IDENTITY";
    /** Whether the factory supports mapping ids back to instances */
    Feature["nodeLookup"] = "NODE_LOOKUP";
    /** Whether the factory supports termType:Variable terms */
    Feature["variableType"] = "VARIABLE_TYPE";
})(Feature || (Feature = {}));

var datatypes = {
    boolean: "http://www.w3.org/2001/XMLSchema#boolean",
    dateTime: "http://www.w3.org/2001/XMLSchema#dateTime",
    double: "http://www.w3.org/2001/XMLSchema#double",
    decimal: "http://www.w3.org/2001/XMLSchema#decimal",
    integer: "http://www.w3.org/2001/XMLSchema#integer",
    langString: "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString",
    string: "http://www.w3.org/2001/XMLSchema#string"
};
function equals(a, b) {
    if (a === b) {
        return true;
    }
    if (!a || !b) {
        return a === b;
    }
    if (Object.prototype.hasOwnProperty.call(a, 'equals')) {
        return a.equals(b);
    }
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!(Array.isArray(a) && Array.isArray(b))) {
            return false;
        }
        return equals(a[0], b[0])
            && equals(a[1], b[1])
            && equals(a[2], b[2])
            && equals(a[3], b[3]);
    }
    if (PlainFactory.isQuad(a) || PlainFactory.isQuad(b)) {
        if (!PlainFactory.isQuad(a) || !PlainFactory.isQuad(b)) {
            return false;
        }
        return equals(a.subject, b.subject)
            && equals(a.predicate, b.predicate)
            && equals(a.object, b.object)
            && equals(a.graph, b.graph);
    }
    switch (a.termType) {
        case 'Literal': {
            return a.termType === b.termType
                && a.value === b.value
                && b.datatype === b.datatype
                && b.language === b.language;
        }
        default:
            if (!a.termType || !b.termType) {
                return false;
            }
            return a.termType === b.termType
                && a.value === b.value;
    }
}
var RDFBase = {
    equals: function (other) {
        return equals(this, other);
    },
    /* rdflib compat */
    toCanonical: function () {
        return this;
    }
};
/**
 * Plain JS/functional implementation of the RDF/JS: Data model specification, limited to a strict
 * rdf subset (no Variable, no Literal as predicate, etc.).
 */
var PlainFactory = /** @class */ (function () {
    function PlainFactory(opts) {
        var _a;
        if (opts === void 0) { opts = {}; }
        this.bnIndex = opts.bnIndex || 0;
        this.supports = opts.supports || (_a = {},
            _a[Feature.collections] = false,
            _a[Feature.defaultGraphType] = false,
            _a[Feature.equalsMethod] = false,
            _a[Feature.identity] = false,
            _a[Feature.nodeLookup] = false,
            _a[Feature.variableType] = false,
            _a);
    }
    /**
     * Checks if the object {obj} is a Quad.
     */
    PlainFactory.isQuad = function (obj) {
        if (Array.isArray(obj) || !obj) {
            return false;
        }
        return !!(Object.prototype.hasOwnProperty.call(obj, "subject")
            && Object.prototype.hasOwnProperty.call(obj, "predicate")
            && Object.prototype.hasOwnProperty.call(obj, "object"));
    };
    PlainFactory.prototype.namedNode = function (value) {
        var term = Object.create(RDFBase);
        term.termType = TermType.NamedNode;
        term.value = value;
        return term;
    };
    PlainFactory.prototype.blankNode = function (value) {
        var term = Object.create(RDFBase);
        term.termType = TermType.BlankNode;
        term.value = value || "b" + ++this.bnIndex;
        return term;
    };
    PlainFactory.prototype.literal = function (value, languageOrDatatype) {
        if (typeof value !== "string") {
            return this.parseLiteral(value);
        }
        var term = Object.create(RDFBase);
        term.termType = TermType.Literal;
        // Perf: Always set all properties to prevent the creation of unnecessary HiddenClass objects.
        term.language = typeof languageOrDatatype === "string" ? languageOrDatatype : undefined;
        term.datatype = typeof languageOrDatatype === "string"
            ? this.namedNode(datatypes.langString)
            : languageOrDatatype || this.namedNode(datatypes.string);
        term.value = value;
        return term;
    };
    PlainFactory.prototype.defaultGraph = function () {
        return this.namedNode("");
    };
    /**
     * Create an RDF statement in object form.
     * @param subject The subject of the statement
     * @param predicate The predicate of the statement
     * @param object The object of the statement
     * @param graph The graph of the statement
     */
    PlainFactory.prototype.quad = function (subject, predicate, object, graph) {
        return {
            subject: subject,
            predicate: predicate,
            object: object,
            graph: graph || this.defaultGraph()
        };
    };
    PlainFactory.prototype.isQuad = function (obj) {
        return PlainFactory.isQuad(obj);
    };
    /**
     * * Returns an RDF statement in array form.
     * @param subject The subject of the statement
     * @param predicate The predicate of the statement
     * @param object The object of the statement
     * @param graph The graph of the statement
     */
    PlainFactory.prototype.quadruple = function (subject, predicate, object, graph) {
        return [
            subject,
            predicate,
            object,
            graph || this.defaultGraph(),
        ];
    };
    PlainFactory.prototype.fromTerm = function (original) {
        if (original.termType === "Literal") {
            return this.literal(original.value, original.language || original.datatype);
        }
        return {
            termType: original.termType,
            value: original.value
        };
    };
    PlainFactory.prototype.fromQuad = function (original) {
        return {
            subject: original.subject,
            predicate: original.predicate,
            object: original.object,
            graph: original.graph || this.defaultGraph()
        };
    };
    PlainFactory.prototype.fromQdr = function (original) {
        return {
            subject: original[0],
            predicate: original[1],
            object: original[2],
            graph: original[3] || this.defaultGraph()
        };
    };
    PlainFactory.prototype.qdrFromQuad = function (original) {
        return [
            original.subject,
            original.predicate,
            original.object,
            original.graph || this.defaultGraph(),
        ];
    };
    PlainFactory.prototype.qdrFromQdr = function (original) {
        return [
            original[0],
            original[1],
            original[2],
            original[3] || this.defaultGraph(),
        ];
    };
    /**
     * Compare if two RDF objects are the same.
     *
     * Should work with non-standard rdf libraries as well (e.g. supporting Variable and Collection).
     */
    PlainFactory.prototype.equals = function (a, b) {
        return equals(a, b);
    };
    PlainFactory.prototype.id = function (obj) {
        return this.toNQ(obj);
    };
    PlainFactory.prototype.toNQ = function (term) {
        if (Array.isArray(term)) {
            var graph = term[3] === this.defaultGraph() ? '' : (this.toNQ(term[3]) + ' ');
            return this.toNQ(term[0]) + " " + this.toNQ(term[1]) + " " + this.toNQ(term[2]) + " " + graph + ".";
        }
        else if (this.isQuad(term)) {
            return this.quadToNQ(term);
        }
        return this.termToNQ(term);
    };
    PlainFactory.prototype.parseLiteral = function (value) {
        var literalValue = undefined;
        var datatype = undefined;
        if (typeof value === "number" || value instanceof Number) {
            // From rdflib; https://github.com/linkeddata/rdflib.js/blob/master/src/literal.js#L104
            if (value.toString().indexOf('e') < 0 && Math.ceil(value) <= Number.MAX_SAFE_INTEGER) {
                datatype = Number.isInteger(value) ? datatypes.integer : datatypes.decimal;
            }
            else {
                datatype = datatypes.double;
            }
            literalValue = value.toString();
        }
        else if (typeof value === "boolean" || value instanceof Boolean) {
            literalValue = value.toString();
            datatype = datatypes.boolean;
        }
        else if (typeof value === "bigint") {
            datatype = datatypes.integer;
            literalValue = value.toString();
        }
        else if (typeof value === "object") {
            if (value instanceof Date) {
                if (isNaN(value.getTime())) {
                    throw new Error('Invalid date given');
                }
                literalValue = value.toISOString();
                datatype = datatypes.dateTime;
            }
        }
        if (typeof datatype !== "undefined") {
            return this.literal(literalValue, this.namedNode(datatype));
        }
        throw new Error("Can't parse value '" + value + "'");
    };
    PlainFactory.prototype.termToNQ = function (term) {
        switch (term.termType) {
            case TermType.BlankNode:
                return "_:" + term.value;
            case TermType.NamedNode:
                return "<" + term.value + ">";
            case TermType.Literal:
                if (term.datatype) {
                    return "\"" + term.value + "\"^^" + this.termToNQ(term.datatype);
                }
                return term.value + "@" + term.language;
            default:
                throw new Error("Nonstandard termtype '" + term.termType + "' given");
        }
    };
    PlainFactory.prototype.quadrupleToNQ = function (quad) {
        var graph = !quad[QuadPosition.graph] || quad[QuadPosition.graph] === this.defaultGraph()
            ? ''
            : (this.toNQ(quad[QuadPosition.graph]) + ' ');
        return this.toNQ(quad[QuadPosition.subject]) + " " + this.toNQ(quad[QuadPosition.predicate]) + " " + this.toNQ(quad[QuadPosition.object]) + " " + graph + ".\n";
    };
    PlainFactory.prototype.quadToNQ = function (quad) {
        return this.quadrupleToNQ([
            quad.subject,
            quad.predicate,
            quad.object,
            quad.graph || this.defaultGraph(),
        ]);
    };
    return PlainFactory;
}());
/**
 * Plain JS/functional implementation of the RDF/JS: Data model specification, limited to a strict
 * rdf subset (no Variable, no Literal as predicate, etc.).
 */
var DefaultFactory = new PlainFactory();

function isTerm(obj) {
    return obj && Object.prototype.hasOwnProperty.call(obj, "termType");
}
function isBlankNode(obj) {
    return isTerm(obj) && obj.termType === TermType.BlankNode;
}
var TermTypes = [
    TermType.NamedNode.toString(),
    TermType.BlankNode.toString(),
    TermType.Literal.toString()
];
var NodeTypes = [TermType.NamedNode.toString(), TermType.BlankNode.toString()];
function isQuad(obj) {
    return obj && Object.prototype.hasOwnProperty.call(obj, "subject");
}

var globalFactory;
if (typeof Symbol !== "undefined") {
    var rdfFactory_1 = Symbol["for"]('rdfFactory');
    globalFactory = globalThis[rdfFactory_1];
}
else {
    var rdfFactory_2 = 'rdfFactory';
    globalFactory = globalThis[rdfFactory_2];
}
var createNS = function (ns) {
    return function (term) {
        return globalFactory.namedNode("" + ns + term);
    };
};
var proxy;
if (typeof Proxy !== "undefined") {
    proxy = new Proxy(globalFactory || {}, {
        ownKeys: function () {
            return globalFactory && Object.keys(globalFactory) || [];
        },
        getOwnPropertyDescriptor: function (_, k) {
            return Object.getOwnPropertyDescriptor(globalFactory, k);
        },
        set: function (_, property, value) {
            globalFactory[property] = value;
            return true;
        },
        get: function (_, property) {
            return globalFactory[property];
        }
    });
}
else {
    proxy = [
        'namedNode',
        'blankNode',
        'literal',
        'defaultGraph',
        'quad',
        'quadruple',
        'fromTerm',
        'fromQuad',
        'fromQdr',
        'qdrFromQuad',
        'qdrFromQdr',
        'equals',
        'id',
        'toNQ',
        'fromId',
        'termToNQ',
        'quadrupleToNQ',
        'quadToNQ',
    ].reduce(function (acc, key) {
        acc[key] = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return globalFactory[key].apply(globalFactory, args);
        };
        return acc;
    }, {});
}
var rdfFactory = proxy;

/** Function to create arbitrary terms within the 'Resource Description Framework' ontology */
const ns = createNS("http://www.w3.org/1999/02/22-rdf-syntax-ns#");
/** The datatype of RDF literals storing fragments of HTML content */
const HTML = ns("HTML");
/** The datatype of language-tagged string values */
const langString = ns("langString");
/** The class of plain (i.e. untyped) literal values, as used in RIF and OWL 2 */
const PlainLiteral = ns("PlainLiteral");
/** The class of RDF properties. */
const Property = ns("Property");
/** The class of RDF statements. */
const Statement = ns("Statement");
/** The class of unordered containers. */
const Bag = ns("Bag");
/** The class of ordered containers. */
const Seq = ns("Seq");
/** The class of containers of alternatives. */
const Alt = ns("Alt");
/** The class of RDF Lists. */
const List = ns("List");
/** The empty list, with no items in it. If the rest of a list is nil then the list has no more items in it. */
const nil = ns("nil");
/** The datatype of XML literal values. */
const XMLLiteral = ns("XMLLiteral");
/** The subject is an instance of a class. */
const type = ns("type");
/** The subject of the subject RDF statement. */
const subject = ns("subject");
/** The predicate of the subject RDF statement. */
const predicate = ns("predicate");
/** The object of the subject RDF statement. */
const object = ns("object");
/** Idiomatic property used for structured values. */
const value = ns("value");
/** The first item in the subject RDF list. */
const first = ns("first");
/** The rest of the subject RDF list after the first item. */
const rest = ns("rest");
var rdf = {
    /** Function to create arbitrary terms within the 'Resource Description Framework' ontology */
    ns,
    /** The datatype of RDF literals storing fragments of HTML content */
    HTML,
    /** The datatype of language-tagged string values */
    langString,
    /** The class of plain (i.e. untyped) literal values, as used in RIF and OWL 2 */
    PlainLiteral,
    /** The class of RDF properties. */
    Property,
    /** The class of RDF statements. */
    Statement,
    /** The class of unordered containers. */
    Bag,
    /** The class of ordered containers. */
    Seq,
    /** The class of containers of alternatives. */
    Alt,
    /** The class of RDF Lists. */
    List,
    /** The empty list, with no items in it. If the rest of a list is nil then the list has no more items in it. */
    nil,
    /** The datatype of XML literal values. */
    XMLLiteral,
    /** The subject is an instance of a class. */
    type,
    /** The subject of the subject RDF statement. */
    subject,
    /** The predicate of the subject RDF statement. */
    predicate,
    /** The object of the subject RDF statement. */
    object,
    /** Idiomatic property used for structured values. */
    value,
    /** The first item in the subject RDF list. */
    first,
    /** The rest of the subject RDF list after the first item. */
    rest
};

/** Function to create arbitrary terms within the 'Schema.org' ontology */
const ns$1 = createNS("http://schema.org/");
/** A radio channel that uses AM. */
const AMRadioChannel = ns$1("AMRadioChannel");
/** Reference documentation for application programming interfaces (APIs). */
const APIReference = ns$1("APIReference");
/** Web page type: About page. */
const AboutPage = ns$1("AboutPage");
/** The act of committing to/adopting an object.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/RejectAction">RejectAction</a>: The antonym of AcceptAction.</li>
 </ul>
 */
const AcceptAction = ns$1("AcceptAction");
/** An accommodation is a place that can accommodate human beings, e.g. a hotel room, a camping pitch, or a meeting room. Many accommodations are for overnight stays, but this is not a mandatory requirement.
 For more specific types of accommodations not defined in schema.org, one can use additionalType with external vocabularies.
 <br /><br />
 See also the <a href="/docs/hotels.html">dedicated document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const Accommodation = ns$1("Accommodation");
/** Accountancy business.<br/><br/>

 As a <a class="localLink" href="http://schema.org/LocalBusiness">LocalBusiness</a> it can be described as a <a class="localLink" href="http://schema.org/provider">provider</a> of one or more <a class="localLink" href="http://schema.org/Service">Service</a>(s). */
const AccountingService = ns$1("AccountingService");
/** The act of accomplishing something via previous efforts. It is an instantaneous action rather than an ongoing process. */
const AchieveAction = ns$1("AchieveAction");
/** An action performed by a direct agent and indirect participants upon a direct object. Optionally happens at a location with the help of an inanimate instrument. The execution of the action may produce a result. Specific action sub-type documentation specifies the exact expectation of each argument/role.<br/><br/>

 See also <a href="http://blog.schema.org/2014/04/announcing-schemaorg-actions.html">blog post</a> and <a href="http://schema.org/docs/actions.html">Actions overview document</a>. */
const Action = ns$1("Action");
/** A set of requirements that a must be fulfilled in order to perform an Action. */
const ActionAccessSpecification = ns$1("ActionAccessSpecification");
/** The status of an Action. */
const ActionStatusType = ns$1("ActionStatusType");
/** The act of starting or activating a device or application (e.g. starting a timer or turning on a flashlight). */
const ActivateAction = ns$1("ActivateAction");
/** The act of editing by adding an object to a collection. */
const AddAction = ns$1("AddAction");
/** A geographical region, typically under the jurisdiction of a particular government. */
const AdministrativeArea = ns$1("AdministrativeArea");
/** An adult entertainment establishment. */
const AdultEntertainment = ns$1("AdultEntertainment");
/** When a single product is associated with multiple offers (for example, the same pair of shoes is offered by different merchants), then AggregateOffer can be used. */
const AggregateOffer = ns$1("AggregateOffer");
/** The average rating based on multiple ratings or reviews. */
const AggregateRating = ns$1("AggregateRating");
/** The act of expressing a consistency of opinion with the object. An agent agrees to/about an object (a proposition, topic or theme) with participants. */
const AgreeAction = ns$1("AgreeAction");
/** An organization that provides flights for passengers. */
const Airline = ns$1("Airline");
/** An airport. */
const Airport = ns$1("Airport");
/** An intangible item that describes an alignment between a learning resource and a node in an educational framework. */
const AlignmentObject = ns$1("AlignmentObject");
/** The act of organizing tasks/objects/events by associating resources to it. */
const AllocateAction = ns$1("AllocateAction");
/** An amusement park. */
const AmusementPark = ns$1("AmusementPark");
/** Animal shelter. */
const AnimalShelter = ns$1("AnimalShelter");
/** An answer offered to a question; perhaps correct, perhaps opinionated or wrong. */
const Answer = ns$1("Answer");
/** An apartment (in American English) or flat (in British English) is a self-contained housing unit (a type of residential real estate) that occupies only part of a building (Source: Wikipedia, the free encyclopedia, see <a href="http://en.wikipedia.org/wiki/Apartment">http://en.wikipedia.org/wiki/Apartment</a>). */
const Apartment = ns$1("Apartment");
/** Residence type: Apartment complex. */
const ApartmentComplex = ns$1("ApartmentComplex");
/** The act of inserting at the end if an ordered collection. */
const AppendAction = ns$1("AppendAction");
/** The act of registering to an organization/service without the guarantee to receive it.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/RegisterAction">RegisterAction</a>: Unlike RegisterAction, ApplyAction has no guarantees that the application will be accepted.</li>
 </ul>
 */
const ApplyAction = ns$1("ApplyAction");
/** Aquarium. */
const Aquarium = ns$1("Aquarium");
/** The act of arriving at a place. An agent arrives at a destination from a fromLocation, optionally with participants. */
const ArriveAction = ns$1("ArriveAction");
/** An art gallery. */
const ArtGallery = ns$1("ArtGallery");
/** An article, such as a news article or piece of investigative report. Newspapers and magazines have articles of many different types and this is intended to cover them all.<br/><br/>

 See also <a href="http://blog.schema.org/2014/09/schemaorg-support-for-bibliographic_2.html">blog post</a>. */
const Article = ns$1("Article");
/** The act of posing a question / favor to someone.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/ReplyAction">ReplyAction</a>: Appears generally as a response to AskAction.</li>
 </ul>
 */
const AskAction = ns$1("AskAction");
/** The act of forming one's opinion, reaction or sentiment. */
const AssessAction = ns$1("AssessAction");
/** The act of allocating an action/event/task to some destination (someone or something). */
const AssignAction = ns$1("AssignAction");
/** Professional service: Attorney. <br/><br/>

 This type is deprecated - <a class="localLink" href="http://schema.org/LegalService">LegalService</a> is more inclusive and less ambiguous. */
const Attorney = ns$1("Attorney");
/** Intended audience for an item, i.e. the group for whom the item was created. */
const Audience = ns$1("Audience");
/** An audio file. */
const AudioObject = ns$1("AudioObject");
/** The act of granting permission to an object. */
const AuthorizeAction = ns$1("AuthorizeAction");
/** Auto body shop. */
const AutoBodyShop = ns$1("AutoBodyShop");
/** An car dealership. */
const AutoDealer = ns$1("AutoDealer");
/** An auto parts store. */
const AutoPartsStore = ns$1("AutoPartsStore");
/** A car rental business. */
const AutoRental = ns$1("AutoRental");
/** Car repair business. */
const AutoRepair = ns$1("AutoRepair");
/** A car wash business. */
const AutoWash = ns$1("AutoWash");
/** ATM/cash machine. */
const AutomatedTeller = ns$1("AutomatedTeller");
/** Car repair, sales, or parts. */
const AutomotiveBusiness = ns$1("AutomotiveBusiness");
/** A bakery. */
const Bakery = ns$1("Bakery");
/** A product or service offered by a bank whereby one may deposit, withdraw or transfer money and in some cases be paid interest. */
const BankAccount = ns$1("BankAccount");
/** Bank or credit union. */
const BankOrCreditUnion = ns$1("BankOrCreditUnion");
/** A bar or pub. */
const BarOrPub = ns$1("BarOrPub");
/** An image of a visual machine-readable code such as a barcode or QR code. */
const Barcode = ns$1("Barcode");
/** Beach. */
const Beach = ns$1("Beach");
/** Beauty salon. */
const BeautySalon = ns$1("BeautySalon");
/** Bed and breakfast.
 <br /><br />
 See also the <a href="/docs/hotels.html">dedicated document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const BedAndBreakfast = ns$1("BedAndBreakfast");
/** An entity holding detailed information about the available bed types, e.g. the quantity of twin beds for a hotel room. For the single case of just one bed of a certain type, you can use bed directly with a text. See also <a class="localLink" href="http://schema.org/BedType">BedType</a> (under development). */
const BedDetails = ns$1("BedDetails");
/** A type of bed. This is used for indicating the bed or beds available in an accommodation. */
const BedType = ns$1("BedType");
/** The act of forming a personal connection with someone (object) mutually/bidirectionally/symmetrically.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/FollowAction">FollowAction</a>: Unlike FollowAction, BefriendAction implies that the connection is reciprocal.</li>
 </ul>
 */
const BefriendAction = ns$1("BefriendAction");
/** A bike store. */
const BikeStore = ns$1("BikeStore");
/** A blog. */
const Blog = ns$1("Blog");
/** A blog post. */
const BlogPosting = ns$1("BlogPosting");
/** A type of boarding policy used by an airline. */
const BoardingPolicyType = ns$1("BoardingPolicyType");
/** A body of water, such as a sea, ocean, or lake. */
const BodyOfWater = ns$1("BodyOfWater");
/** A book. */
const Book = ns$1("Book");
/** The publication format of the book. */
const BookFormatType = ns$1("BookFormatType");
/** A series of books. Included books can be indicated with the hasPart property. */
const BookSeries = ns$1("BookSeries");
/** A bookstore. */
const BookStore = ns$1("BookStore");
/** An agent bookmarks/flags/labels/tags/marks an object. */
const BookmarkAction = ns$1("BookmarkAction");
/** Boolean: True or False. */
const Boolean$1 = ns$1("Boolean");
/** The act of obtaining an object under an agreement to return it at a later date. Reciprocal of LendAction.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/LendAction">LendAction</a>: Reciprocal of BorrowAction.</li>
 </ul>
 */
const BorrowAction = ns$1("BorrowAction");
/** A bowling alley. */
const BowlingAlley = ns$1("BowlingAlley");
/** A brand is a name used by an organization or business person for labeling a product, product group, or similar. */
const Brand = ns$1("Brand");
/** A BreadcrumbList is an ItemList consisting of a chain of linked Web pages, typically described using at least their URL and their name, and typically ending with the current page.<br/><br/>

 The <a class="localLink" href="http://schema.org/position">position</a> property is used to reconstruct the order of the items in a BreadcrumbList The convention is that a breadcrumb list has an <a class="localLink" href="http://schema.org/itemListOrder">itemListOrder</a> of <a class="localLink" href="http://schema.org/ItemListOrderAscending">ItemListOrderAscending</a> (lower values listed first), and that the first items in this list correspond to the "top" or beginning of the breadcrumb trail, e.g. with a site or section homepage. The specific values of 'position' are not assigned meaning for a BreadcrumbList, but they should be integers, e.g. beginning with '1' for the first item in the list. */
const BreadcrumbList = ns$1("BreadcrumbList");
/** Brewery. */
const Brewery = ns$1("Brewery");
/** A bridge. */
const Bridge = ns$1("Bridge");
/** A unique instance of a BroadcastService on a CableOrSatelliteService lineup. */
const BroadcastChannel = ns$1("BroadcastChannel");
/** An over the air or online broadcast event. */
const BroadcastEvent = ns$1("BroadcastEvent");
/** The frequency in MHz and the modulation used for a particular BroadcastService. */
const BroadcastFrequencySpecification = ns$1("BroadcastFrequencySpecification");
/** A delivery service through which content is provided via broadcast over the air or online. */
const BroadcastService = ns$1("BroadcastService");
/** A Buddhist temple. */
const BuddhistTemple = ns$1("BuddhistTemple");
/** A reservation for bus travel. <br/><br/>

 Note: This type is for information about actual reservations, e.g. in confirmation emails or HTML pages with individual confirmations of reservations. For offers of tickets, use <a class="localLink" href="http://schema.org/Offer">Offer</a>. */
const BusReservation = ns$1("BusReservation");
/** A bus station. */
const BusStation = ns$1("BusStation");
/** A bus stop. */
const BusStop = ns$1("BusStop");
/** A trip on a commercial bus line. */
const BusTrip = ns$1("BusTrip");
/** A set of characteristics belonging to businesses, e.g. who compose an item's target audience. */
const BusinessAudience = ns$1("BusinessAudience");
/** A business entity type is a conceptual entity representing the legal form, the size, the main line of business, the position in the value chain, or any combination thereof, of an organization or business person.<br/><br/>

 Commonly used values:<br/><br/>

 <ul>
 <li>http://purl.org/goodrelations/v1#Business</li>
 <li>http://purl.org/goodrelations/v1#Enduser</li>
 <li>http://purl.org/goodrelations/v1#PublicInstitution</li>
 <li>http://purl.org/goodrelations/v1#Reseller</li>
 </ul>
 */
const BusinessEntityType = ns$1("BusinessEntityType");
/** Event type: Business event. */
const BusinessEvent = ns$1("BusinessEvent");
/** The business function specifies the type of activity or access (i.e., the bundle of rights) offered by the organization or business person through the offer. Typical are sell, rental or lease, maintenance or repair, manufacture / produce, recycle / dispose, engineering / construction, or installation. Proprietary specifications of access rights are also instances of this class.<br/><br/>

 Commonly used values:<br/><br/>

 <ul>
 <li>http://purl.org/goodrelations/v1#ConstructionInstallation</li>
 <li>http://purl.org/goodrelations/v1#Dispose</li>
 <li>http://purl.org/goodrelations/v1#LeaseOut</li>
 <li>http://purl.org/goodrelations/v1#Maintain</li>
 <li>http://purl.org/goodrelations/v1#ProvideService</li>
 <li>http://purl.org/goodrelations/v1#Repair</li>
 <li>http://purl.org/goodrelations/v1#Sell</li>
 <li>http://purl.org/goodrelations/v1#Buy</li>
 </ul>
 */
const BusinessFunction = ns$1("BusinessFunction");
/** The act of giving money to a seller in exchange for goods or services rendered. An agent buys an object, product, or service from a seller for a price. Reciprocal of SellAction. */
const BuyAction = ns$1("BuyAction");
/** A service which provides access to media programming like TV or radio. Access may be via cable or satellite. */
const CableOrSatelliteService = ns$1("CableOrSatelliteService");
/** A cafe or coffee shop. */
const CafeOrCoffeeShop = ns$1("CafeOrCoffeeShop");
/** A camping site, campsite, or <a class="localLink" href="http://schema.org/Campground">Campground</a> is a place used for overnight stay in the outdoors, typically containing individual <a class="localLink" href="http://schema.org/CampingPitch">CampingPitch</a> locations. <br/><br/>

 In British English a campsite is an area, usually divided into a number of pitches, where people can camp overnight using tents or camper vans or caravans; this British English use of the word is synonymous with the American English expression campground. In American English the term campsite generally means an area where an individual, family, group, or military unit can pitch a tent or park a camper; a campground may contain many campsites (Source: Wikipedia see <a href="https://en.wikipedia.org/wiki/Campsite">https://en.wikipedia.org/wiki/Campsite</a>).<br/><br/>

 See also the dedicated <a href="/docs/hotels.html">document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const Campground = ns$1("Campground");
/** A <a class="localLink" href="http://schema.org/CampingPitch">CampingPitch</a> is an individual place for overnight stay in the outdoors, typically being part of a larger camping site, or <a class="localLink" href="http://schema.org/Campground">Campground</a>.<br/><br/>

 In British English a campsite, or campground, is an area, usually divided into a number of pitches, where people can camp overnight using tents or camper vans or caravans; this British English use of the word is synonymous with the American English expression campground. In American English the term campsite generally means an area where an individual, family, group, or military unit can pitch a tent or park a camper; a campground may contain many campsites.
 (Source: Wikipedia see <a href="https://en.wikipedia.org/wiki/Campsite">https://en.wikipedia.org/wiki/Campsite</a>).<br/><br/>

 See also the dedicated <a href="/docs/hotels.html">document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const CampingPitch = ns$1("CampingPitch");
/** A canal, like the Panama Canal. */
const Canal = ns$1("Canal");
/** The act of asserting that a future event/action is no longer going to happen.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/ConfirmAction">ConfirmAction</a>: The antonym of CancelAction.</li>
 </ul>
 */
const CancelAction = ns$1("CancelAction");
/** A car is a wheeled, self-powered motor vehicle used for transportation. */
const Car = ns$1("Car");
/** A casino. */
const Casino = ns$1("Casino");
/** A Catholic church. */
const CatholicChurch = ns$1("CatholicChurch");
/** A graveyard. */
const Cemetery = ns$1("Cemetery");
/** An agent inspects, determines, investigates, inquires, or examines an object's accuracy, quality, condition, or state. */
const CheckAction = ns$1("CheckAction");
/** The act of an agent communicating (service provider, social media, etc) their arrival by registering/confirming for a previously reserved service (e.g. flight check in) or at a place (e.g. hotel), possibly resulting in a result (boarding pass, etc).<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/CheckOutAction">CheckOutAction</a>: The antonym of CheckInAction.</li>
 <li><a class="localLink" href="http://schema.org/ArriveAction">ArriveAction</a>: Unlike ArriveAction, CheckInAction implies that the agent is informing/confirming the start of a previously reserved service.</li>
 <li><a class="localLink" href="http://schema.org/ConfirmAction">ConfirmAction</a>: Unlike ConfirmAction, CheckInAction implies that the agent is informing/confirming the <em>start</em> of a previously reserved service rather than its validity/existence.</li>
 </ul>
 */
const CheckInAction = ns$1("CheckInAction");
/** The act of an agent communicating (service provider, social media, etc) their departure of a previously reserved service (e.g. flight check in) or place (e.g. hotel).<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/CheckInAction">CheckInAction</a>: The antonym of CheckOutAction.</li>
 <li><a class="localLink" href="http://schema.org/DepartAction">DepartAction</a>: Unlike DepartAction, CheckOutAction implies that the agent is informing/confirming the end of a previously reserved service.</li>
 <li><a class="localLink" href="http://schema.org/CancelAction">CancelAction</a>: Unlike CancelAction, CheckOutAction implies that the agent is informing/confirming the end of a previously reserved service.</li>
 </ul>
 */
const CheckOutAction = ns$1("CheckOutAction");
/** Web page type: Checkout page. */
const CheckoutPage = ns$1("CheckoutPage");
/** A Childcare center. */
const ChildCare = ns$1("ChildCare");
/** Event type: Children's event. */
const ChildrensEvent = ns$1("ChildrensEvent");
/** The act of expressing a preference from a set of options or a large or unbounded set of choices/options. */
const ChooseAction = ns$1("ChooseAction");
/** A church. */
const Church = ns$1("Church");
/** A city or town. */
const City = ns$1("City");
/** A city hall. */
const CityHall = ns$1("CityHall");
/** A public structure, such as a town hall or concert hall. */
const CivicStructure = ns$1("CivicStructure");
/** A fact-checking review of claims made (or reported) in some creative work (referenced via itemReviewed). */
const ClaimReview = ns$1("ClaimReview");
/** A short TV or radio program or a segment/part of a program. */
const Clip = ns$1("Clip");
/** A clothing store. */
const ClothingStore = ns$1("ClothingStore");
/** Computer programming source code. Example: Full (compile ready) solutions, code snippet samples, scripts, templates. */
const Code = ns$1("Code");
/** Web page type: Collection page. */
const CollectionPage = ns$1("CollectionPage");
/** A college, university, or other third-level educational institution. */
const CollegeOrUniversity = ns$1("CollegeOrUniversity");
/** A comedy club. */
const ComedyClub = ns$1("ComedyClub");
/** Event type: Comedy event. */
const ComedyEvent = ns$1("ComedyEvent");
/** A comment on an item - for example, a comment on a blog post. The comment's content is expressed via the <a class="localLink" href="http://schema.org/text">text</a> property, and its topic via <a class="localLink" href="http://schema.org/about">about</a>, properties shared with all CreativeWorks. */
const Comment = ns$1("Comment");
/** The act of generating a comment about a subject. */
const CommentAction = ns$1("CommentAction");
/** The act of conveying information to another person via a communication medium (instrument) such as speech, email, or telephone conversation. */
const CommunicateAction = ns$1("CommunicateAction");
/** A compound price specification is one that bundles multiple prices that all apply in combination for different dimensions of consumption. Use the name property of the attached unit price specification for indicating the dimension of a price component (e.g. "electricity" or "final cleaning"). */
const CompoundPriceSpecification = ns$1("CompoundPriceSpecification");
/** This type covers computer programming languages such as Scheme and Lisp, as well as other language-like computer representations. Natural languages are best represented with the <a class="localLink" href="http://schema.org/Language">Language</a> type. */
const ComputerLanguage = ns$1("ComputerLanguage");
/** A computer store. */
const ComputerStore = ns$1("ComputerStore");
/** The act of notifying someone that a future event/action is going to happen as expected.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/CancelAction">CancelAction</a>: The antonym of ConfirmAction.</li>
 </ul>
 */
const ConfirmAction = ns$1("ConfirmAction");
/** The act of ingesting information/resources/food. */
const ConsumeAction = ns$1("ConsumeAction");
/** Web page type: Contact page. */
const ContactPage = ns$1("ContactPage");
/** A contact point&#x2014;for example, a Customer Complaints department. */
const ContactPoint = ns$1("ContactPoint");
/** Enumerated options related to a ContactPoint. */
const ContactPointOption = ns$1("ContactPointOption");
/** One of the continents (for example, Europe or Africa). */
const Continent = ns$1("Continent");
/** An agent controls a device or application. */
const ControlAction = ns$1("ControlAction");
/** A convenience store. */
const ConvenienceStore = ns$1("ConvenienceStore");
/** One or more messages between organizations or people on a particular topic. Individual messages can be linked to the conversation with isPartOf or hasPart properties. */
const Conversation = ns$1("Conversation");
/** The act of producing/preparing food. */
const CookAction = ns$1("CookAction");
/** Organization: A business corporation. */
const Corporation = ns$1("Corporation");
/** A country. */
const Country = ns$1("Country");
/** A description of an educational course which may be offered as distinct instances at which take place at different times or take place at different locations, or be offered through different media or modes of study. An educational course is a sequence of one or more educational events and/or creative works which aims to build knowledge, competence or ability of learners. */
const Course = ns$1("Course");
/** An instance of a <a class="localLink" href="http://schema.org/Course">Course</a> which is distinct from other instances because it is offered at a different time or location or through different media or modes of study or to a specific section of students. */
const CourseInstance = ns$1("CourseInstance");
/** A courthouse. */
const Courthouse = ns$1("Courthouse");
/** The act of deliberately creating/producing/generating/building a result out of the agent. */
const CreateAction = ns$1("CreateAction");
/** The most generic kind of creative work, including books, movies, photographs, software programs, etc. */
const CreativeWork = ns$1("CreativeWork");
/** A media season e.g. tv, radio, video game etc. */
const CreativeWorkSeason = ns$1("CreativeWorkSeason");
/** A CreativeWorkSeries in schema.org is a group of related items, typically but not necessarily of the same kind. CreativeWorkSeries are usually organized into some order, often chronological. Unlike <a class="localLink" href="http://schema.org/ItemList">ItemList</a> which is a general purpose data structure for lists of things, the emphasis with CreativeWorkSeries is on published materials (written e.g. books and periodicals, or media such as tv, radio and games).<br/><br/>

 Specific subtypes are available for describing <a class="localLink" href="http://schema.org/TVSeries">TVSeries</a>, <a class="localLink" href="http://schema.org/RadioSeries">RadioSeries</a>, <a class="localLink" href="http://schema.org/MovieSeries">MovieSeries</a>, <a class="localLink" href="http://schema.org/BookSeries">BookSeries</a>, <a class="localLink" href="http://schema.org/Periodical">Periodical</a> and <a class="localLink" href="http://schema.org/VideoGameSeries">VideoGameSeries</a>. In each case, the <a class="localLink" href="http://schema.org/hasPart">hasPart</a> / <a class="localLink" href="http://schema.org/isPartOf">isPartOf</a> properties can be used to relate the CreativeWorkSeries to its parts. The general CreativeWorkSeries type serves largely just to organize these more specific and practical subtypes.<br/><br/>

 It is common for properties applicable to an item from the series to be usefully applied to the containing group. Schema.org attempts to anticipate some of these cases, but publishers should be free to apply properties of the series parts to the series as a whole wherever they seem appropriate. */
const CreativeWorkSeries = ns$1("CreativeWorkSeries");
/** A card payment method of a particular brand or name.  Used to mark up a particular payment method and/or the financial product/service that supplies the card account.<br/><br/>

 Commonly used values:<br/><br/>

 <ul>
 <li>http://purl.org/goodrelations/v1#AmericanExpress</li>
 <li>http://purl.org/goodrelations/v1#DinersClub</li>
 <li>http://purl.org/goodrelations/v1#Discover</li>
 <li>http://purl.org/goodrelations/v1#JCB</li>
 <li>http://purl.org/goodrelations/v1#MasterCard</li>
 <li>http://purl.org/goodrelations/v1#VISA</li>
 </ul>
 */
const CreditCard = ns$1("CreditCard");
/** A crematorium. */
const Crematorium = ns$1("Crematorium");
/** A service to convert funds from one currency to another currency. */
const CurrencyConversionService = ns$1("CurrencyConversionService");
/** Event type: A social dance. */
const DanceEvent = ns$1("DanceEvent");
/** A dance group&#x2014;for example, the Alvin Ailey Dance Theater or Riverdance. */
const DanceGroup = ns$1("DanceGroup");
/** A collection of datasets. */
const DataCatalog = ns$1("DataCatalog");
/** A dataset in downloadable form. */
const DataDownload = ns$1("DataDownload");
/** A single feed providing structured information about one or more entities or topics. */
const DataFeed = ns$1("DataFeed");
/** A single item within a larger data feed. */
const DataFeedItem = ns$1("DataFeedItem");
/** The basic data types such as Integers, Strings, etc. */
const DataType = ns$1("DataType");
/** A body of structured information describing some topic(s) of interest. */
const Dataset = ns$1("Dataset");
/** A date value in <a href="http://en.wikipedia.org/wiki/ISO_8601">ISO 8601 date format</a>. */
const Date$1 = ns$1("Date");
/** A combination of date and time of day in the form [-]CCYY-MM-DDThh:mm:ss[Z|(+|-)hh:mm] (see Chapter 5.4 of ISO 8601). */
const DateTime = ns$1("DateTime");
/** A DatedMoneySpecification represents monetary values with optional start and end dates. For example, this could represent an employee's salary over a specific period of time. <strong>Note:</strong> This type has been superseded by <a class="localLink" href="http://schema.org/MonetaryAmount">MonetaryAmount</a> use of that type is recommended */
const DatedMoneySpecification = ns$1("DatedMoneySpecification");
/** The day of the week, e.g. used to specify to which day the opening hours of an OpeningHoursSpecification refer.<br/><br/>

 Originally, URLs from <a href="http://purl.org/goodrelations/v1">GoodRelations</a> were used (for <a class="localLink" href="http://schema.org/Monday">Monday</a>, <a class="localLink" href="http://schema.org/Tuesday">Tuesday</a>, <a class="localLink" href="http://schema.org/Wednesday">Wednesday</a>, <a class="localLink" href="http://schema.org/Thursday">Thursday</a>, <a class="localLink" href="http://schema.org/Friday">Friday</a>, <a class="localLink" href="http://schema.org/Saturday">Saturday</a>, <a class="localLink" href="http://schema.org/Sunday">Sunday</a> plus a special entry for <a class="localLink" href="http://schema.org/PublicHolidays">PublicHolidays</a>); these have now been integrated directly into schema.org. */
const DayOfWeek = ns$1("DayOfWeek");
/** A day spa. */
const DaySpa = ns$1("DaySpa");
/** The act of stopping or deactivating a device or application (e.g. stopping a timer or turning off a flashlight). */
const DeactivateAction = ns$1("DeactivateAction");
/** A defence establishment, such as an army or navy base. */
const DefenceEstablishment = ns$1("DefenceEstablishment");
/** The act of editing a recipient by removing one of its objects. */
const DeleteAction = ns$1("DeleteAction");
/** The price for the delivery of an offer using a particular delivery method. */
const DeliveryChargeSpecification = ns$1("DeliveryChargeSpecification");
/** An event involving the delivery of an item. */
const DeliveryEvent = ns$1("DeliveryEvent");
/** A delivery method is a standardized procedure for transferring the product or service to the destination of fulfillment chosen by the customer. Delivery methods are characterized by the means of transportation used, and by the organization or group that is the contracting party for the sending organization or person.<br/><br/>

 Commonly used values:<br/><br/>

 <ul>
 <li>http://purl.org/goodrelations/v1#DeliveryModeDirectDownload</li>
 <li>http://purl.org/goodrelations/v1#DeliveryModeFreight</li>
 <li>http://purl.org/goodrelations/v1#DeliveryModeMail</li>
 <li>http://purl.org/goodrelations/v1#DeliveryModeOwnFleet</li>
 <li>http://purl.org/goodrelations/v1#DeliveryModePickUp</li>
 <li>http://purl.org/goodrelations/v1#DHL</li>
 <li>http://purl.org/goodrelations/v1#FederalExpress</li>
 <li>http://purl.org/goodrelations/v1#UPS</li>
 </ul>
 */
const DeliveryMethod = ns$1("DeliveryMethod");
/** A demand entity represents the public, not necessarily binding, not necessarily exclusive, announcement by an organization or person to seek a certain type of goods or services. For describing demand using this type, the very same properties used for Offer apply. */
const Demand = ns$1("Demand");
/** A dentist. */
const Dentist = ns$1("Dentist");
/** The act of  departing from a place. An agent departs from an fromLocation for a destination, optionally with participants. */
const DepartAction = ns$1("DepartAction");
/** A department store. */
const DepartmentStore = ns$1("DepartmentStore");
/** A type of Bank Account with a main purpose of depositing funds to gain interest or other benefits. */
const DepositAccount = ns$1("DepositAccount");
/** An electronic file or document. */
const DigitalDocument = ns$1("DigitalDocument");
/** A permission for a particular person or group to access a particular file. */
const DigitalDocumentPermission = ns$1("DigitalDocumentPermission");
/** A type of permission which can be granted for accessing a digital document. */
const DigitalDocumentPermissionType = ns$1("DigitalDocumentPermissionType");
/** The act of expressing a difference of opinion with the object. An agent disagrees to/about an object (a proposition, topic or theme) with participants. */
const DisagreeAction = ns$1("DisagreeAction");
/** The act of discovering/finding an object. */
const DiscoverAction = ns$1("DiscoverAction");
/** A posting to a discussion forum. */
const DiscussionForumPosting = ns$1("DiscussionForumPosting");
/** The act of expressing a negative sentiment about the object. An agent dislikes an object (a proposition, topic or theme) with participants. */
const DislikeAction = ns$1("DislikeAction");
/** Properties that take Distances as values are of the form '&lt;Number&gt; &lt;Length unit of measure&gt;'. E.g., '7 ft'. */
const Distance = ns$1("Distance");
/** A distillery. */
const Distillery = ns$1("Distillery");
/** The act of providing goods, services, or money without compensation, often for philanthropic reasons. */
const DonateAction = ns$1("DonateAction");
/** The act of downloading an object. */
const DownloadAction = ns$1("DownloadAction");
/** The act of producing a visual/graphical representation of an object, typically with a pen/pencil and paper as instruments. */
const DrawAction = ns$1("DrawAction");
/** The act of swallowing liquids. */
const DrinkAction = ns$1("DrinkAction");
/** A value indicating which roadwheels will receive torque. */
const DriveWheelConfigurationValue = ns$1("DriveWheelConfigurationValue");
/** A dry-cleaning business. */
const DryCleaningOrLaundry = ns$1("DryCleaningOrLaundry");
/** Quantity: Duration (use <a href="http://en.wikipedia.org/wiki/ISO_8601">ISO 8601 duration format</a>). */
const Duration = ns$1("Duration");
/** The act of swallowing solid objects. */
const EatAction = ns$1("EatAction");
/** Event type: Education event. */
const EducationEvent = ns$1("EducationEvent");
/** An EducationalAudience. */
const EducationalAudience = ns$1("EducationalAudience");
/** An educational organization. */
const EducationalOrganization = ns$1("EducationalOrganization");
/** An electrician. */
const Electrician = ns$1("Electrician");
/** An electronics store. */
const ElectronicsStore = ns$1("ElectronicsStore");
/** An elementary school. */
const ElementarySchool = ns$1("ElementarySchool");
/** An email message. */
const EmailMessage = ns$1("EmailMessage");
/** An embassy. */
const Embassy = ns$1("Embassy");
/** An emergency service, such as a fire station or ER. */
const EmergencyService = ns$1("EmergencyService");
/** A subclass of OrganizationRole used to describe employee relationships. */
const EmployeeRole = ns$1("EmployeeRole");
/** An aggregate rating of an Organization related to its role as an employer. */
const EmployerAggregateRating = ns$1("EmployerAggregateRating");
/** An employment agency. */
const EmploymentAgency = ns$1("EmploymentAgency");
/** An agent approves/certifies/likes/supports/sanction an object. */
const EndorseAction = ns$1("EndorseAction");
/** An EndorsementRating is a rating that expresses some level of endorsement, for example inclusion in a "critic's pick" blog, a
 "Like" or "+1" on a social network. It can be considered the <a class="localLink" href="http://schema.org/result">result</a> of an <a class="localLink" href="http://schema.org/EndorseAction">EndorseAction</a> in which the <a class="localLink" href="http://schema.org/object">object</a> of the action is rated positively by
 some <a class="localLink" href="http://schema.org/agent">agent</a>. As is common elsewhere in schema.org, it is sometimes more useful to describe the results of such an action without explicitly describing the <a class="localLink" href="http://schema.org/Action">Action</a>.<br/><br/>

 An <a class="localLink" href="http://schema.org/EndorsementRating">EndorsementRating</a> may be part of a numeric scale or organized system, but this is not required: having an explicit type for indicating a positive,
 endorsement rating is particularly useful in the absence of numeric scales as it helps consumers understand that the rating is broadly positive. */
const EndorsementRating = ns$1("EndorsementRating");
/** Properties that take Energy as values are of the form '&lt;Number&gt; &lt;Energy unit of measure&gt;'. */
const Energy = ns$1("Energy");
/** Information about the engine of the vehicle. A vehicle can have multiple engines represented by multiple engine specification entities. */
const EngineSpecification = ns$1("EngineSpecification");
/** A business providing entertainment. */
const EntertainmentBusiness = ns$1("EntertainmentBusiness");
/** An entry point, within some Web-based protocol. */
const EntryPoint = ns$1("EntryPoint");
/** Lists or enumerations—for example, a list of cuisines or music genres, etc. */
const Enumeration = ns$1("Enumeration");
/** A media episode (e.g. TV, radio, video game) which can be part of a series or season. */
const Episode = ns$1("Episode");
/** An event happening at a certain time and location, such as a concert, lecture, or festival. Ticketing information may be added via the <a class="localLink" href="http://schema.org/offers">offers</a> property. Repeated events may be structured as separate Event objects. */
const Event = ns$1("Event");
/** A reservation for an event like a concert, sporting event, or lecture.<br/><br/>

 Note: This type is for information about actual reservations, e.g. in confirmation emails or HTML pages with individual confirmations of reservations. For offers of tickets, use <a class="localLink" href="http://schema.org/Offer">Offer</a>. */
const EventReservation = ns$1("EventReservation");
/** EventStatusType is an enumeration type whose instances represent several states that an Event may be in. */
const EventStatusType = ns$1("EventStatusType");
/** An event venue. */
const EventVenue = ns$1("EventVenue");
/** The act of participating in exertive activity for the purposes of improving health and fitness. */
const ExerciseAction = ns$1("ExerciseAction");
/** A gym. */
const ExerciseGym = ns$1("ExerciseGym");
/** Event type: Exhibition event, e.g. at a museum, library, archive, tradeshow, ... */
const ExhibitionEvent = ns$1("ExhibitionEvent");
/** A <a class="localLink" href="http://schema.org/FAQPage">FAQPage</a> is a <a class="localLink" href="http://schema.org/WebPage">WebPage</a> presenting one or more "<a href="https://en.wikipedia.org/wiki/FAQ">Frequently asked questions</a>" (see also <a class="localLink" href="http://schema.org/QAPage">QAPage</a>). */
const FAQPage = ns$1("FAQPage");
/** A radio channel that uses FM. */
const FMRadioChannel = ns$1("FMRadioChannel");
/** A fast-food restaurant. */
const FastFoodRestaurant = ns$1("FastFoodRestaurant");
/** Event type: Festival. */
const Festival = ns$1("Festival");
/** The act of capturing sound and moving images on film, video, or digitally. */
const FilmAction = ns$1("FilmAction");
/** A product provided to consumers and businesses by financial institutions such as banks, insurance companies, brokerage firms, consumer finance companies, and investment companies which comprise the financial services industry. */
const FinancialProduct = ns$1("FinancialProduct");
/** Financial services business. */
const FinancialService = ns$1("FinancialService");
/** The act of finding an object.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/SearchAction">SearchAction</a>: FindAction is generally lead by a SearchAction, but not necessarily.</li>
 </ul>
 */
const FindAction = ns$1("FindAction");
/** A fire station. With firemen. */
const FireStation = ns$1("FireStation");
/** An airline flight. */
const Flight = ns$1("Flight");
/** A reservation for air travel.<br/><br/>

 Note: This type is for information about actual reservations, e.g. in confirmation emails or HTML pages with individual confirmations of reservations. For offers of tickets, use <a class="localLink" href="http://schema.org/Offer">Offer</a>. */
const FlightReservation = ns$1("FlightReservation");
/** Data type: Floating number. */
const Float = ns$1("Float");
/** A florist. */
const Florist = ns$1("Florist");
/** The act of forming a personal connection with someone/something (object) unidirectionally/asymmetrically to get updates polled from.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/BefriendAction">BefriendAction</a>: Unlike BefriendAction, FollowAction implies that the connection is <em>not</em> necessarily reciprocal.</li>
 <li><a class="localLink" href="http://schema.org/SubscribeAction">SubscribeAction</a>: Unlike SubscribeAction, FollowAction implies that the follower acts as an active agent constantly/actively polling for updates.</li>
 <li><a class="localLink" href="http://schema.org/RegisterAction">RegisterAction</a>: Unlike RegisterAction, FollowAction implies that the agent is interested in continuing receiving updates from the object.</li>
 <li><a class="localLink" href="http://schema.org/JoinAction">JoinAction</a>: Unlike JoinAction, FollowAction implies that the agent is interested in getting updates from the object.</li>
 <li><a class="localLink" href="http://schema.org/TrackAction">TrackAction</a>: Unlike TrackAction, FollowAction refers to the polling of updates of all aspects of animate objects rather than the location of inanimate objects (e.g. you track a package, but you don't follow it).</li>
 </ul>
 */
const FollowAction = ns$1("FollowAction");
/** A food-related business. */
const FoodEstablishment = ns$1("FoodEstablishment");
/** A reservation to dine at a food-related business.<br/><br/>

 Note: This type is for information about actual reservations, e.g. in confirmation emails or HTML pages with individual confirmations of reservations. */
const FoodEstablishmentReservation = ns$1("FoodEstablishmentReservation");
/** Event type: Food event. */
const FoodEvent = ns$1("FoodEvent");
/** A food service, like breakfast, lunch, or dinner. */
const FoodService = ns$1("FoodService");
/** A furniture store. */
const FurnitureStore = ns$1("FurnitureStore");
/** The Game type represents things which are games. These are typically rule-governed recreational activities, e.g. role-playing games in which players assume the role of characters in a fictional setting. */
const Game = ns$1("Game");
/** Indicates whether this game is multi-player, co-op or single-player. */
const GamePlayMode = ns$1("GamePlayMode");
/** Server that provides game interaction in a multiplayer game. */
const GameServer = ns$1("GameServer");
/** Status of a game server. */
const GameServerStatus = ns$1("GameServerStatus");
/** A garden store. */
const GardenStore = ns$1("GardenStore");
/** A gas station. */
const GasStation = ns$1("GasStation");
/** Residence type: Gated community. */
const GatedResidenceCommunity = ns$1("GatedResidenceCommunity");
/** An enumeration of genders. */
const GenderType = ns$1("GenderType");
/** A general contractor. */
const GeneralContractor = ns$1("GeneralContractor");
/** A GeoCircle is a GeoShape representing a circular geographic area. As it is a GeoShape
 it provides the simple textual property 'circle', but also allows the combination of postalCode alongside geoRadius.
 The center of the circle can be indicated via the 'geoMidpoint' property, or more approximately using 'address', 'postalCode'. */
const GeoCircle = ns$1("GeoCircle");
/** The geographic coordinates of a place or event. */
const GeoCoordinates = ns$1("GeoCoordinates");
/** The geographic shape of a place. A GeoShape can be described using several properties whose values are based on latitude/longitude pairs. Either whitespace or commas can be used to separate latitude and longitude; whitespace should be used when writing a list of several such points. */
const GeoShape = ns$1("GeoShape");
/** The act of transferring ownership of an object to a destination. Reciprocal of TakeAction.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/TakeAction">TakeAction</a>: Reciprocal of GiveAction.</li>
 <li><a class="localLink" href="http://schema.org/SendAction">SendAction</a>: Unlike SendAction, GiveAction implies that ownership is being transferred (e.g. I may send my laptop to you, but that doesn't mean I'm giving it to you).</li>
 </ul>
 */
const GiveAction = ns$1("GiveAction");
/** A golf course. */
const GolfCourse = ns$1("GolfCourse");
/** A government building. */
const GovernmentBuilding = ns$1("GovernmentBuilding");
/** A government office&#x2014;for example, an IRS or DMV office. */
const GovernmentOffice = ns$1("GovernmentOffice");
/** A governmental organization or agency. */
const GovernmentOrganization = ns$1("GovernmentOrganization");
/** A permit issued by a government agency. */
const GovernmentPermit = ns$1("GovernmentPermit");
/** A service provided by a government organization, e.g. food stamps, veterans benefits, etc. */
const GovernmentService = ns$1("GovernmentService");
/** A grocery store. */
const GroceryStore = ns$1("GroceryStore");
/** A business that provide Heating, Ventilation and Air Conditioning services. */
const HVACBusiness = ns$1("HVACBusiness");
/** A hair salon. */
const HairSalon = ns$1("HairSalon");
/** A hardware store. */
const HardwareStore = ns$1("HardwareStore");
/** Health and beauty. */
const HealthAndBeautyBusiness = ns$1("HealthAndBeautyBusiness");
/** A health club. */
const HealthClub = ns$1("HealthClub");
/** A high school. */
const HighSchool = ns$1("HighSchool");
/** A Hindu temple. */
const HinduTemple = ns$1("HinduTemple");
/** A store that sells materials useful or necessary for various hobbies. */
const HobbyShop = ns$1("HobbyShop");
/** A construction business.<br/><br/>

 A HomeAndConstructionBusiness is a <a class="localLink" href="http://schema.org/LocalBusiness">LocalBusiness</a> that provides services around homes and buildings.<br/><br/>

 As a <a class="localLink" href="http://schema.org/LocalBusiness">LocalBusiness</a> it can be described as a <a class="localLink" href="http://schema.org/provider">provider</a> of one or more <a class="localLink" href="http://schema.org/Service">Service</a>(s). */
const HomeAndConstructionBusiness = ns$1("HomeAndConstructionBusiness");
/** A home goods store. */
const HomeGoodsStore = ns$1("HomeGoodsStore");
/** A hospital. */
const Hospital = ns$1("Hospital");
/** A hostel - cheap accommodation, often in shared dormitories.
 <br /><br />
 See also the <a href="/docs/hotels.html">dedicated document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const Hostel = ns$1("Hostel");
/** A hotel is an establishment that provides lodging paid on a short-term basis (Source: Wikipedia, the free encyclopedia, see http://en.wikipedia.org/wiki/Hotel).
 <br /><br />
 See also the <a href="/docs/hotels.html">dedicated document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const Hotel = ns$1("Hotel");
/** A hotel room is a single room in a hotel.
 <br /><br />
 See also the <a href="/docs/hotels.html">dedicated document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const HotelRoom = ns$1("HotelRoom");
/** A house is a building or structure that has the ability to be occupied for habitation by humans or other creatures (Source: Wikipedia, the free encyclopedia, see <a href="http://en.wikipedia.org/wiki/House">http://en.wikipedia.org/wiki/House</a>). */
const House = ns$1("House");
/** A house painting service. */
const HousePainter = ns$1("HousePainter");
/** Instructions that explain how to achieve a result by performing a sequence of steps. */
const HowTo = ns$1("HowTo");
/** A direction indicating a single action to do in the instructions for how to achieve a result. */
const HowToDirection = ns$1("HowToDirection");
/** An item used as either a tool or supply when performing the instructions for how to to achieve a result. */
const HowToItem = ns$1("HowToItem");
/** A sub-grouping of steps in the instructions for how to achieve a result (e.g. steps for making a pie crust within a pie recipe). */
const HowToSection = ns$1("HowToSection");
/** A step in the instructions for how to achieve a result. It is an ordered list with HowToDirection and/or HowToTip items. */
const HowToStep = ns$1("HowToStep");
/** A supply consumed when performing the instructions for how to achieve a result. */
const HowToSupply = ns$1("HowToSupply");
/** An explanation in the instructions for how to achieve a result. It provides supplementary information about a technique, supply, author's preference, etc. It can explain what could be done, or what should not be done, but doesn't specify what should be done (see HowToDirection). */
const HowToTip = ns$1("HowToTip");
/** A tool used (but not consumed) when performing instructions for how to achieve a result. */
const HowToTool = ns$1("HowToTool");
/** An ice cream shop. */
const IceCreamShop = ns$1("IceCreamShop");
/** The act of intentionally disregarding the object. An agent ignores an object. */
const IgnoreAction = ns$1("IgnoreAction");
/** Web page type: Image gallery page. */
const ImageGallery = ns$1("ImageGallery");
/** An image file. */
const ImageObject = ns$1("ImageObject");
/** A single, identifiable product instance (e.g. a laptop with a particular serial number). */
const IndividualProduct = ns$1("IndividualProduct");
/** The act of notifying someone of information pertinent to them, with no expectation of a response. */
const InformAction = ns$1("InformAction");
/** The act of adding at a specific location in an ordered collection. */
const InsertAction = ns$1("InsertAction");
/** The act of installing an application. */
const InstallAction = ns$1("InstallAction");
/** An Insurance agency. */
const InsuranceAgency = ns$1("InsuranceAgency");
/** A utility class that serves as the umbrella for a number of 'intangible' things such as quantities, structured values, etc. */
const Intangible = ns$1("Intangible");
/** Data type: Integer. */
const Integer = ns$1("Integer");
/** The act of interacting with another person or organization. */
const InteractAction = ns$1("InteractAction");
/** A summary of how users have interacted with this CreativeWork. In most cases, authors will use a subtype to specify the specific type of interaction. */
const InteractionCounter = ns$1("InteractionCounter");
/** An internet cafe. */
const InternetCafe = ns$1("InternetCafe");
/** A type of financial product that typically requires the client to transfer funds to a financial service in return for potential beneficial financial return. */
const InvestmentOrDeposit = ns$1("InvestmentOrDeposit");
/** The act of asking someone to attend an event. Reciprocal of RsvpAction. */
const InviteAction = ns$1("InviteAction");
/** A statement of the money due for goods or services; a bill. */
const Invoice = ns$1("Invoice");
/** A list of possible product availability options. */
const ItemAvailability = ns$1("ItemAvailability");
/** A list of items of any sort&#x2014;for example, Top 10 Movies About Weathermen, or Top 100 Party Songs. Not to be confused with HTML lists, which are often used only for formatting. */
const ItemList = ns$1("ItemList");
/** Enumerated for values for itemListOrder for indicating how an ordered ItemList is organized. */
const ItemListOrderType = ns$1("ItemListOrderType");
/** A page devoted to a single item, such as a particular product or hotel. */
const ItemPage = ns$1("ItemPage");
/** A jewelry store. */
const JewelryStore = ns$1("JewelryStore");
/** A listing that describes a job opening in a certain organization. */
const JobPosting = ns$1("JobPosting");
/** An agent joins an event/group with participants/friends at a location.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/RegisterAction">RegisterAction</a>: Unlike RegisterAction, JoinAction refers to joining a group/team of people.</li>
 <li><a class="localLink" href="http://schema.org/SubscribeAction">SubscribeAction</a>: Unlike SubscribeAction, JoinAction does not imply that you'll be receiving updates.</li>
 <li><a class="localLink" href="http://schema.org/FollowAction">FollowAction</a>: Unlike FollowAction, JoinAction does not imply that you'll be polling for updates.</li>
 </ul>
 */
const JoinAction = ns$1("JoinAction");
/** A lake (for example, Lake Pontrachain). */
const LakeBodyOfWater = ns$1("LakeBodyOfWater");
/** A landform or physical feature.  Landform elements include mountains, plains, lakes, rivers, seascape and oceanic waterbody interface features such as bays, peninsulas, seas and so forth, including sub-aqueous terrain features such as submersed mountain ranges, volcanoes, and the great ocean basins. */
const Landform = ns$1("Landform");
/** An historical landmark or building. */
const LandmarksOrHistoricalBuildings = ns$1("LandmarksOrHistoricalBuildings");
/** Natural languages such as Spanish, Tamil, Hindi, English, etc. Formal language code tags expressed in <a href="https://en.wikipedia.org/wiki/IETF_language_tag">BCP 47</a> can be used via the <a class="localLink" href="http://schema.org/alternateName">alternateName</a> property. The Language type previously also covered programming languages such as Scheme and Lisp, which are now best represented using <a class="localLink" href="http://schema.org/ComputerLanguage">ComputerLanguage</a>. */
const Language = ns$1("Language");
/** An agent leaves an event / group with participants/friends at a location.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/JoinAction">JoinAction</a>: The antonym of LeaveAction.</li>
 <li><a class="localLink" href="http://schema.org/UnRegisterAction">UnRegisterAction</a>: Unlike UnRegisterAction, LeaveAction implies leaving a group/team of people rather than a service.</li>
 </ul>
 */
const LeaveAction = ns$1("LeaveAction");
/** A LegalService is a business that provides legally-oriented services, advice and representation, e.g. law firms.<br/><br/>

 As a <a class="localLink" href="http://schema.org/LocalBusiness">LocalBusiness</a> it can be described as a <a class="localLink" href="http://schema.org/provider">provider</a> of one or more <a class="localLink" href="http://schema.org/Service">Service</a>(s). */
const LegalService = ns$1("LegalService");
/** A legislative building&#x2014;for example, the state capitol. */
const LegislativeBuilding = ns$1("LegislativeBuilding");
/** The act of providing an object under an agreement that it will be returned at a later date. Reciprocal of BorrowAction.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/BorrowAction">BorrowAction</a>: Reciprocal of LendAction.</li>
 </ul>
 */
const LendAction = ns$1("LendAction");
/** A library. */
const Library = ns$1("Library");
/** The act of expressing a positive sentiment about the object. An agent likes an object (a proposition, topic or theme) with participants. */
const LikeAction = ns$1("LikeAction");
/** A shop that sells alcoholic drinks such as wine, beer, whisky and other spirits. */
const LiquorStore = ns$1("LiquorStore");
/** An list item, e.g. a step in a checklist or how-to description. */
const ListItem = ns$1("ListItem");
/** The act of consuming audio content. */
const ListenAction = ns$1("ListenAction");
/** Event type: Literary event. */
const LiteraryEvent = ns$1("LiteraryEvent");
/** A blog post intended to provide a rolling textual coverage of an ongoing event through continuous updates. */
const LiveBlogPosting = ns$1("LiveBlogPosting");
/** A financial product for the loaning of an amount of money under agreed terms and charges. */
const LoanOrCredit = ns$1("LoanOrCredit");
/** A particular physical business or branch of an organization. Examples of LocalBusiness include a restaurant, a particular branch of a restaurant chain, a branch of a bank, a medical practice, a club, a bowling alley, etc. */
const LocalBusiness = ns$1("LocalBusiness");
/** Specifies a location feature by providing a structured value representing a feature of an accommodation as a property-value pair of varying degrees of formality. */
const LocationFeatureSpecification = ns$1("LocationFeatureSpecification");
/** A DeliveryMethod in which an item is made available via locker. */
const LockerDelivery = ns$1("LockerDelivery");
/** A locksmith. */
const Locksmith = ns$1("Locksmith");
/** A lodging business, such as a motel, hotel, or inn. */
const LodgingBusiness = ns$1("LodgingBusiness");
/** A reservation for lodging at a hotel, motel, inn, etc.<br/><br/>

 Note: This type is for information about actual reservations, e.g. in confirmation emails or HTML pages with individual confirmations of reservations. */
const LodgingReservation = ns$1("LodgingReservation");
/** The act of being defeated in a competitive activity. */
const LoseAction = ns$1("LoseAction");
/** A map. */
const Map$1 = ns$1("Map");
/** An enumeration of several kinds of Map. */
const MapCategoryType = ns$1("MapCategoryType");
/** The act of marrying a person. */
const MarryAction = ns$1("MarryAction");
/** Properties that take Mass as values are of the form '&lt;Number&gt; &lt;Mass unit of measure&gt;'. E.g., '7 kg'. */
const Mass = ns$1("Mass");
/** A media object, such as an image, video, or audio object embedded in a web page or a downloadable dataset i.e. DataDownload. Note that a creative work may have many media objects associated with it on the same web page. For example, a page about a single song (MusicRecording) may have a music video (VideoObject), and a high and low bandwidth audio stream (2 AudioObject's). */
const MediaObject = ns$1("MediaObject");
/** A subscription which allows a user to access media including audio, video, books, etc. */
const MediaSubscription = ns$1("MediaSubscription");
/** A medical organization (physical or not), such as hospital, institution or clinic. */
const MedicalOrganization = ns$1("MedicalOrganization");
/** A meeting room, conference room, or conference hall is a room provided for singular events such as business conferences and meetings (Source: Wikipedia, the free encyclopedia, see <a href="http://en.wikipedia.org/wiki/Conference_hall">http://en.wikipedia.org/wiki/Conference_hall</a>).
 <br /><br />
 See also the <a href="/docs/hotels.html">dedicated document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const MeetingRoom = ns$1("MeetingRoom");
/** A men's clothing store. */
const MensClothingStore = ns$1("MensClothingStore");
/** A structured representation of food or drink items available from a FoodEstablishment. */
const Menu = ns$1("Menu");
/** A food or drink item listed in a menu or menu section. */
const MenuItem = ns$1("MenuItem");
/** A sub-grouping of food or drink items in a menu. E.g. courses (such as 'Dinner', 'Breakfast', etc.), specific type of dishes (such as 'Meat', 'Vegan', 'Drinks', etc.), or some other classification made by the menu provider. */
const MenuSection = ns$1("MenuSection");
/** A single message from a sender to one or more organizations or people. */
const Message = ns$1("Message");
/** A middle school (typically for children aged around 11-14, although this varies somewhat). */
const MiddleSchool = ns$1("MiddleSchool");
/** A software application designed specifically to work well on a mobile device such as a telephone. */
const MobileApplication = ns$1("MobileApplication");
/** A store that sells mobile phones and related accessories. */
const MobilePhoneStore = ns$1("MobilePhoneStore");
/** A monetary value or range. This type can be used to describe an amount of money such as $50 USD, or a range as in describing a bank account being suitable for a balance between £1,000 and £1,000,000 GBP, or the value of a salary, etc. It is recommended to use <a class="localLink" href="http://schema.org/PriceSpecification">PriceSpecification</a> Types to describe the price of an Offer, Invoice, etc. */
const MonetaryAmount = ns$1("MonetaryAmount");
/** A statistical distribution of monetary amounts. */
const MonetaryAmountDistribution = ns$1("MonetaryAmountDistribution");
/** A mosque. */
const Mosque = ns$1("Mosque");
/** A motel.
 <br /><br />
 See also the <a href="/docs/hotels.html">dedicated document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const Motel = ns$1("Motel");
/** A motorcycle dealer. */
const MotorcycleDealer = ns$1("MotorcycleDealer");
/** A motorcycle repair shop. */
const MotorcycleRepair = ns$1("MotorcycleRepair");
/** A mountain, like Mount Whitney or Mount Everest. */
const Mountain = ns$1("Mountain");
/** The act of an agent relocating to a place.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/TransferAction">TransferAction</a>: Unlike TransferAction, the subject of the move is a living Person or Organization rather than an inanimate object.</li>
 </ul>
 */
const MoveAction = ns$1("MoveAction");
/** A movie. */
const Movie = ns$1("Movie");
/** A short segment/part of a movie. */
const MovieClip = ns$1("MovieClip");
/** A movie rental store. */
const MovieRentalStore = ns$1("MovieRentalStore");
/** A series of movies. Included movies can be indicated with the hasPart property. */
const MovieSeries = ns$1("MovieSeries");
/** A movie theater. */
const MovieTheater = ns$1("MovieTheater");
/** A moving company. */
const MovingCompany = ns$1("MovingCompany");
/** A museum. */
const Museum = ns$1("Museum");
/** A collection of music tracks. */
const MusicAlbum = ns$1("MusicAlbum");
/** Classification of the album by it's type of content: soundtrack, live album, studio album, etc. */
const MusicAlbumProductionType = ns$1("MusicAlbumProductionType");
/** The kind of release which this album is: single, EP or album. */
const MusicAlbumReleaseType = ns$1("MusicAlbumReleaseType");
/** A musical composition. */
const MusicComposition = ns$1("MusicComposition");
/** Event type: Music event. */
const MusicEvent = ns$1("MusicEvent");
/** A musical group, such as a band, an orchestra, or a choir. Can also be a solo musician. */
const MusicGroup = ns$1("MusicGroup");
/** A collection of music tracks in playlist form. */
const MusicPlaylist = ns$1("MusicPlaylist");
/** A music recording (track), usually a single song. */
const MusicRecording = ns$1("MusicRecording");
/** A MusicRelease is a specific release of a music album. */
const MusicRelease = ns$1("MusicRelease");
/** Format of this release (the type of recording media used, ie. compact disc, digital media, LP, etc.). */
const MusicReleaseFormatType = ns$1("MusicReleaseFormatType");
/** A music store. */
const MusicStore = ns$1("MusicStore");
/** A music venue. */
const MusicVenue = ns$1("MusicVenue");
/** A music video file. */
const MusicVideoObject = ns$1("MusicVideoObject");
/** Organization: Non-governmental Organization. */
const NGO = ns$1("NGO");
/** A nail salon. */
const NailSalon = ns$1("NailSalon");
/** A NewsArticle is an article whose content reports news, or provides background context and supporting materials for understanding the news.<br/><br/>

 A more detailed overview of <a href="/docs/news.html">schema.org News markup</a> is also available. */
const NewsArticle = ns$1("NewsArticle");
/** A nightclub or discotheque. */
const NightClub = ns$1("NightClub");
/** A notary. */
const Notary = ns$1("Notary");
/** A file containing a note, primarily for the author. */
const NoteDigitalDocument = ns$1("NoteDigitalDocument");
/** Data type: Number.<br/><br/>

 Usage guidelines:<br/><br/>

 <ul>
 <li>Use values from 0123456789 (Unicode 'DIGIT ZERO' (U+0030) to 'DIGIT NINE' (U+0039)) rather than superficially similiar Unicode symbols.</li>
 <li>Use '.' (Unicode 'FULL STOP' (U+002E)) rather than ',' to indicate a decimal point. Avoid using these symbols as a readability separator.</li>
 </ul>
 */
const Number$1 = ns$1("Number");
/** Nutritional information about the recipe. */
const NutritionInformation = ns$1("NutritionInformation");
/** A profession, may involve prolonged training and/or a formal qualification. */
const Occupation = ns$1("Occupation");
/** An ocean (for example, the Pacific). */
const OceanBodyOfWater = ns$1("OceanBodyOfWater");
/** An offer to transfer some rights to an item or to provide a service — for example, an offer to sell tickets to an event, to rent the DVD of a movie, to stream a TV show over the internet, to repair a motorcycle, or to loan a book.<br/><br/>

 For <a href="http://www.gs1.org/barcodes/technical/idkeys/gtin">GTIN</a>-related fields, see <a href="http://www.gs1.org/barcodes/support/check_digit_calculator">Check Digit calculator</a> and <a href="http://www.gs1us.org/resources/standards/gtin-validation-guide">validation guide</a> from <a href="http://www.gs1.org/">GS1</a>. */
const Offer = ns$1("Offer");
/** An OfferCatalog is an ItemList that contains related Offers and/or further OfferCatalogs that are offeredBy the same provider. */
const OfferCatalog = ns$1("OfferCatalog");
/** A list of possible conditions for the item. */
const OfferItemCondition = ns$1("OfferItemCondition");
/** An office equipment store. */
const OfficeEquipmentStore = ns$1("OfficeEquipmentStore");
/** A publication event e.g. catch-up TV or radio podcast, during which a program is available on-demand. */
const OnDemandEvent = ns$1("OnDemandEvent");
/** A structured value providing information about the opening hours of a place or a certain service inside a place.<br/><br/>

 The place is <strong>open</strong> if the <a class="localLink" href="http://schema.org/opens">opens</a> property is specified, and <strong>closed</strong> otherwise.<br/><br/>

 If the value for the <a class="localLink" href="http://schema.org/closes">closes</a> property is less than the value for the <a class="localLink" href="http://schema.org/opens">opens</a> property then the hour range is assumed to span over the next day. */
const OpeningHoursSpecification = ns$1("OpeningHoursSpecification");
/** An order is a confirmation of a transaction (a receipt), which can contain multiple line items, each represented by an Offer that has been accepted by the customer. */
const Order = ns$1("Order");
/** An agent orders an object/product/service to be delivered/sent. */
const OrderAction = ns$1("OrderAction");
/** An order item is a line of an order. It includes the quantity and shipping details of a bought offer. */
const OrderItem = ns$1("OrderItem");
/** Enumerated status values for Order. */
const OrderStatus = ns$1("OrderStatus");
/** An organization such as a school, NGO, corporation, club, etc. */
const Organization = ns$1("Organization");
/** A subclass of Role used to describe roles within organizations. */
const OrganizationRole = ns$1("OrganizationRole");
/** The act of manipulating/administering/supervising/controlling one or more objects. */
const OrganizeAction = ns$1("OrganizeAction");
/** An outlet store. */
const OutletStore = ns$1("OutletStore");
/** A structured value providing information about when a certain organization or person owned a certain product. */
const OwnershipInfo = ns$1("OwnershipInfo");
/** The act of producing a painting, typically with paint and canvas as instruments. */
const PaintAction = ns$1("PaintAction");
/** A painting. */
const Painting = ns$1("Painting");
/** The delivery of a parcel either via the postal service or a commercial service. */
const ParcelDelivery = ns$1("ParcelDelivery");
/** A private parcel service as the delivery mode available for a certain offer.<br/><br/>

 Commonly used values:<br/><br/>

 <ul>
 <li>http://purl.org/goodrelations/v1#DHL</li>
 <li>http://purl.org/goodrelations/v1#FederalExpress</li>
 <li>http://purl.org/goodrelations/v1#UPS</li>
 </ul>
 */
const ParcelService = ns$1("ParcelService");
/** A set of characteristics describing parents, who can be interested in viewing some content. */
const ParentAudience = ns$1("ParentAudience");
/** A park. */
const Park = ns$1("Park");
/** A parking lot or other parking facility. */
const ParkingFacility = ns$1("ParkingFacility");
/** A shop that will buy, or lend money against the security of, personal possessions. */
const PawnShop = ns$1("PawnShop");
/** An agent pays a price to a participant. */
const PayAction = ns$1("PayAction");
/** A payment method using a credit, debit, store or other card to associate the payment with an account. */
const PaymentCard = ns$1("PaymentCard");
/** The costs of settling the payment using a particular payment method. */
const PaymentChargeSpecification = ns$1("PaymentChargeSpecification");
/** A payment method is a standardized procedure for transferring the monetary amount for a purchase. Payment methods are characterized by the legal and technical structures used, and by the organization or group carrying out the transaction.<br/><br/>

 Commonly used values:<br/><br/>

 <ul>
 <li>http://purl.org/goodrelations/v1#ByBankTransferInAdvance</li>
 <li>http://purl.org/goodrelations/v1#ByInvoice</li>
 <li>http://purl.org/goodrelations/v1#Cash</li>
 <li>http://purl.org/goodrelations/v1#CheckInAdvance</li>
 <li>http://purl.org/goodrelations/v1#COD</li>
 <li>http://purl.org/goodrelations/v1#DirectDebit</li>
 <li>http://purl.org/goodrelations/v1#GoogleCheckout</li>
 <li>http://purl.org/goodrelations/v1#PayPal</li>
 <li>http://purl.org/goodrelations/v1#PaySwarm</li>
 </ul>
 */
const PaymentMethod = ns$1("PaymentMethod");
/** A Service to transfer funds from a person or organization to a beneficiary person or organization. */
const PaymentService = ns$1("PaymentService");
/** A specific payment status. For example, PaymentDue, PaymentComplete, etc. */
const PaymentStatusType = ns$1("PaymentStatusType");
/** A set of characteristics belonging to people, e.g. who compose an item's target audience. */
const PeopleAudience = ns$1("PeopleAudience");
/** The act of participating in performance arts. */
const PerformAction = ns$1("PerformAction");
/** A PerformanceRole is a Role that some entity places with regard to a theatrical performance, e.g. in a Movie, TVSeries etc. */
const PerformanceRole = ns$1("PerformanceRole");
/** A theater or other performing art center. */
const PerformingArtsTheater = ns$1("PerformingArtsTheater");
/** A performance group, such as a band, an orchestra, or a circus. */
const PerformingGroup = ns$1("PerformingGroup");
/** A publication in any medium issued in successive parts bearing numerical or chronological designations and intended, such as a magazine, scholarly journal, or newspaper to continue indefinitely.<br/><br/>

 See also <a href="http://blog.schema.org/2014/09/schemaorg-support-for-bibliographic_2.html">blog post</a>. */
const Periodical = ns$1("Periodical");
/** A permit issued by an organization, e.g. a parking pass. */
const Permit = ns$1("Permit");
/** A person (alive, dead, undead, or fictional). */
const Person = ns$1("Person");
/** A pet store. */
const PetStore = ns$1("PetStore");
/** A pharmacy or drugstore. */
const Pharmacy = ns$1("Pharmacy");
/** A photograph. */
const Photograph = ns$1("Photograph");
/** The act of capturing still images of objects using a camera. */
const PhotographAction = ns$1("PhotographAction");
/** A doctor's office. */
const Physician = ns$1("Physician");
/** Entities that have a somewhat fixed, physical extension. */
const Place = ns$1("Place");
/** Place of worship, such as a church, synagogue, or mosque. */
const PlaceOfWorship = ns$1("PlaceOfWorship");
/** The act of planning the execution of an event/task/action/reservation/plan to a future date. */
const PlanAction = ns$1("PlanAction");
/** The act of playing/exercising/training/performing for enjoyment, leisure, recreation, Competition or exercise.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/ListenAction">ListenAction</a>: Unlike ListenAction (which is under ConsumeAction), PlayAction refers to performing for an audience or at an event, rather than consuming music.</li>
 <li><a class="localLink" href="http://schema.org/WatchAction">WatchAction</a>: Unlike WatchAction (which is under ConsumeAction), PlayAction refers to showing/displaying for an audience or at an event, rather than consuming visual content.</li>
 </ul>
 */
const PlayAction = ns$1("PlayAction");
/** A playground. */
const Playground = ns$1("Playground");
/** A plumbing service. */
const Plumber = ns$1("Plumber");
/** A police station. */
const PoliceStation = ns$1("PoliceStation");
/** A pond. */
const Pond = ns$1("Pond");
/** A post office. */
const PostOffice = ns$1("PostOffice");
/** The mailing address. */
const PostalAddress = ns$1("PostalAddress");
/** An agent orders a (not yet released) object/product/service to be delivered/sent. */
const PreOrderAction = ns$1("PreOrderAction");
/** The act of inserting at the beginning if an ordered collection. */
const PrependAction = ns$1("PrependAction");
/** A preschool. */
const Preschool = ns$1("Preschool");
/** A file containing slides or used for a presentation. */
const PresentationDigitalDocument = ns$1("PresentationDigitalDocument");
/** A structured value representing a price or price range. Typically, only the subclasses of this type are used for markup. It is recommended to use <a class="localLink" href="http://schema.org/MonetaryAmount">MonetaryAmount</a> to describe independent amounts of money such as a salary, credit card limits, etc. */
const PriceSpecification = ns$1("PriceSpecification");
/** Any offered product or service. For example: a pair of shoes; a concert ticket; the rental of a car; a haircut; or an episode of a TV show streamed online. */
const Product = ns$1("Product");
/** A datasheet or vendor specification of a product (in the sense of a prototypical description). */
const ProductModel = ns$1("ProductModel");
/** Original definition: "provider of professional services."<br/><br/>

 The general <a class="localLink" href="http://schema.org/ProfessionalService">ProfessionalService</a> type for local businesses was deprecated due to confusion with <a class="localLink" href="http://schema.org/Service">Service</a>. For reference, the types that it included were: <a class="localLink" href="http://schema.org/Dentist">Dentist</a>,
 <a class="localLink" href="http://schema.org/AccountingService">AccountingService</a>, <a class="localLink" href="http://schema.org/Attorney">Attorney</a>, <a class="localLink" href="http://schema.org/Notary">Notary</a>, as well as types for several kinds of <a class="localLink" href="http://schema.org/HomeAndConstructionBusiness">HomeAndConstructionBusiness</a>: <a class="localLink" href="http://schema.org/Electrician">Electrician</a>, <a class="localLink" href="http://schema.org/GeneralContractor">GeneralContractor</a>,
 <a class="localLink" href="http://schema.org/HousePainter">HousePainter</a>, <a class="localLink" href="http://schema.org/Locksmith">Locksmith</a>, <a class="localLink" href="http://schema.org/Plumber">Plumber</a>, <a class="localLink" href="http://schema.org/RoofingContractor">RoofingContractor</a>. <a class="localLink" href="http://schema.org/LegalService">LegalService</a> was introduced as a more inclusive supertype of <a class="localLink" href="http://schema.org/Attorney">Attorney</a>. */
const ProfessionalService = ns$1("ProfessionalService");
/** Web page type: Profile page. */
const ProfilePage = ns$1("ProfilePage");
/** Used to describe membership in a loyalty programs (e.g. "StarAliance"), traveler clubs (e.g. "AAA"), purchase clubs ("Safeway Club"), etc. */
const ProgramMembership = ns$1("ProgramMembership");
/** A property-value pair, e.g. representing a feature of a product or place. Use the 'name' property for the name of the property. If there is an additional human-readable version of the value, put that into the 'description' property.<br/><br/>

 Always use specific schema.org properties when a) they exist and b) you can populate them. Using PropertyValue as a substitute will typically not trigger the same effect as using the original, specific property. */
const PropertyValue = ns$1("PropertyValue");
/** A Property value specification. */
const PropertyValueSpecification = ns$1("PropertyValueSpecification");
/** A public swimming pool. */
const PublicSwimmingPool = ns$1("PublicSwimmingPool");
/** A PublicationEvent corresponds indifferently to the event of publication for a CreativeWork of any type e.g. a broadcast event, an on-demand event, a book/journal publication via a variety of delivery media. */
const PublicationEvent = ns$1("PublicationEvent");
/** A part of a successively published publication such as a periodical or publication volume, often numbered, usually containing a grouping of works such as articles.<br/><br/>

 See also <a href="http://blog.schema.org/2014/09/schemaorg-support-for-bibliographic_2.html">blog post</a>. */
const PublicationIssue = ns$1("PublicationIssue");
/** A part of a successively published publication such as a periodical or multi-volume work, often numbered. It may represent a time span, such as a year.<br/><br/>

 See also <a href="http://blog.schema.org/2014/09/schemaorg-support-for-bibliographic_2.html">blog post</a>. */
const PublicationVolume = ns$1("PublicationVolume");
/** A QAPage is a WebPage focussed on a specific Question and its Answer(s), e.g. in a question answering site or documenting Frequently Asked Questions (FAQs). */
const QAPage = ns$1("QAPage");
/** A predefined value for a product characteristic, e.g. the power cord plug type 'US' or the garment sizes 'S', 'M', 'L', and 'XL'. */
const QualitativeValue = ns$1("QualitativeValue");
/** A point value or interval for product characteristics and other purposes. */
const QuantitativeValue = ns$1("QuantitativeValue");
/** A statistical distribution of values. */
const QuantitativeValueDistribution = ns$1("QuantitativeValueDistribution");
/** Quantities such as distance, time, mass, weight, etc. Particular instances of say Mass are entities like '3 Kg' or '4 milligrams'. */
const Quantity = ns$1("Quantity");
/** A specific question - e.g. from a user seeking answers online, or collected in a Frequently Asked Questions (FAQ) document. */
const Question = ns$1("Question");
/** An agent quotes/estimates/appraises an object/product/service with a price at a location/store. */
const QuoteAction = ns$1("QuoteAction");
/** A place offering space for "Recreational Vehicles", Caravans, mobile homes and the like. */
const RVPark = ns$1("RVPark");
/** A unique instance of a radio BroadcastService on a CableOrSatelliteService lineup. */
const RadioChannel = ns$1("RadioChannel");
/** A short radio program or a segment/part of a radio program. */
const RadioClip = ns$1("RadioClip");
/** A radio episode which can be part of a series or season. */
const RadioEpisode = ns$1("RadioEpisode");
/** Season dedicated to radio broadcast and associated online delivery. */
const RadioSeason = ns$1("RadioSeason");
/** CreativeWorkSeries dedicated to radio broadcast and associated online delivery. */
const RadioSeries = ns$1("RadioSeries");
/** A radio station. */
const RadioStation = ns$1("RadioStation");
/** A rating is an evaluation on a numeric scale, such as 1 to 5 stars. */
const Rating = ns$1("Rating");
/** The act of responding instinctively and emotionally to an object, expressing a sentiment. */
const ReactAction = ns$1("ReactAction");
/** The act of consuming written content. */
const ReadAction = ns$1("ReadAction");
/** A real-estate agent. */
const RealEstateAgent = ns$1("RealEstateAgent");
/** The act of physically/electronically taking delivery of an object thathas been transferred from an origin to a destination. Reciprocal of SendAction.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/SendAction">SendAction</a>: The reciprocal of ReceiveAction.</li>
 <li><a class="localLink" href="http://schema.org/TakeAction">TakeAction</a>: Unlike TakeAction, ReceiveAction does not imply that the ownership has been transfered (e.g. I can receive a package, but it does not mean the package is now mine).</li>
 </ul>
 */
const ReceiveAction = ns$1("ReceiveAction");
/** A recipe. For dietary restrictions covered by the recipe, a few common restrictions are enumerated via <a class="localLink" href="http://schema.org/suitableForDiet">suitableForDiet</a>. The <a class="localLink" href="http://schema.org/keywords">keywords</a> property can also be used to add more detail. */
const Recipe = ns$1("Recipe");
/** A recycling center. */
const RecyclingCenter = ns$1("RecyclingCenter");
/** The act of registering to be a user of a service, product or web page.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/JoinAction">JoinAction</a>: Unlike JoinAction, RegisterAction implies you are registering to be a user of a service, <em>not</em> a group/team of people.</li>
 <li>[FollowAction]]: Unlike FollowAction, RegisterAction doesn't imply that the agent is expecting to poll for updates from the object.</li>
 <li><a class="localLink" href="http://schema.org/SubscribeAction">SubscribeAction</a>: Unlike SubscribeAction, RegisterAction doesn't imply that the agent is expecting updates from the object.</li>
 </ul>
 */
const RegisterAction = ns$1("RegisterAction");
/** The act of rejecting to/adopting an object.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/AcceptAction">AcceptAction</a>: The antonym of RejectAction.</li>
 </ul>
 */
const RejectAction = ns$1("RejectAction");
/** The act of giving money in return for temporary use, but not ownership, of an object such as a vehicle or property. For example, an agent rents a property from a landlord in exchange for a periodic payment. */
const RentAction = ns$1("RentAction");
/** A reservation for a rental car.<br/><br/>

 Note: This type is for information about actual reservations, e.g. in confirmation emails or HTML pages with individual confirmations of reservations. */
const RentalCarReservation = ns$1("RentalCarReservation");
/** The act of editing a recipient by replacing an old object with a new object. */
const ReplaceAction = ns$1("ReplaceAction");
/** The act of responding to a question/message asked/sent by the object. Related to <a class="localLink" href="http://schema.org/AskAction">AskAction</a><br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/AskAction">AskAction</a>: Appears generally as an origin of a ReplyAction.</li>
 </ul>
 */
const ReplyAction = ns$1("ReplyAction");
/** A Report generated by governmental or non-governmental organization. */
const Report = ns$1("Report");
/** Describes a reservation for travel, dining or an event. Some reservations require tickets. <br/><br/>

 Note: This type is for information about actual reservations, e.g. in confirmation emails or HTML pages with individual confirmations of reservations. For offers of tickets, restaurant reservations, flights, or rental cars, use <a class="localLink" href="http://schema.org/Offer">Offer</a>. */
const Reservation = ns$1("Reservation");
/** A group of multiple reservations with common values for all sub-reservations. */
const ReservationPackage = ns$1("ReservationPackage");
/** Enumerated status values for Reservation. */
const ReservationStatusType = ns$1("ReservationStatusType");
/** Reserving a concrete object.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/ScheduleAction">ScheduleAction</a></a>: Unlike ScheduleAction, ReserveAction reserves concrete objects (e.g. a table, a hotel) towards a time slot / spatial allocation.</li>
 </ul>
 */
const ReserveAction = ns$1("ReserveAction");
/** A reservoir of water, typically an artificially created lake, like the Lake Kariba reservoir. */
const Reservoir = ns$1("Reservoir");
/** The place where a person lives. */
const Residence = ns$1("Residence");
/** A resort is a place used for relaxation or recreation, attracting visitors for holidays or vacations. Resorts are places, towns or sometimes commercial establishment operated by a single company (Source: Wikipedia, the free encyclopedia, see <a href="http://en.wikipedia.org/wiki/Resort">http://en.wikipedia.org/wiki/Resort</a>).
 <br /><br />
 See also the <a href="/docs/hotels.html">dedicated document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const Resort = ns$1("Resort");
/** A restaurant. */
const Restaurant = ns$1("Restaurant");
/** A diet restricted to certain foods or preparations for cultural, religious, health or lifestyle reasons. */
const RestrictedDiet = ns$1("RestrictedDiet");
/** The act of resuming a device or application which was formerly paused (e.g. resume music playback or resume a timer). */
const ResumeAction = ns$1("ResumeAction");
/** The act of returning to the origin that which was previously received (concrete objects) or taken (ownership). */
const ReturnAction = ns$1("ReturnAction");
/** A review of an item - for example, of a restaurant, movie, or store. */
const Review = ns$1("Review");
/** The act of producing a balanced opinion about the object for an audience. An agent reviews an object with participants resulting in a review. */
const ReviewAction = ns$1("ReviewAction");
/** A river (for example, the broad majestic Shannon). */
const RiverBodyOfWater = ns$1("RiverBodyOfWater");
/** Represents additional information about a relationship or property. For example a Role can be used to say that a 'member' role linking some SportsTeam to a player occurred during a particular time period. Or that a Person's 'actor' role in a Movie was for some particular characterName. Such properties can be attached to a Role entity, which is then associated with the main entities using ordinary properties like 'member' or 'actor'.<br/><br/>

 See also <a href="http://blog.schema.org/2014/06/introducing-role.html">blog post</a>. */
const Role = ns$1("Role");
/** A roofing contractor. */
const RoofingContractor = ns$1("RoofingContractor");
/** A room is a distinguishable space within a structure, usually separated from other spaces by interior walls. (Source: Wikipedia, the free encyclopedia, see <a href="http://en.wikipedia.org/wiki/Room">http://en.wikipedia.org/wiki/Room</a>).
 <br /><br />
 See also the <a href="/docs/hotels.html">dedicated document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const Room = ns$1("Room");
/** The act of notifying an event organizer as to whether you expect to attend the event. */
const RsvpAction = ns$1("RsvpAction");
/** RsvpResponseType is an enumeration type whose instances represent responding to an RSVP request. */
const RsvpResponseType = ns$1("RsvpResponseType");
/** Event type: Sales event. */
const SaleEvent = ns$1("SaleEvent");
/** Scheduling future actions, events, or tasks.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/ReserveAction">ReserveAction</a>: Unlike ReserveAction, ScheduleAction allocates future actions (e.g. an event, a task, etc) towards a time slot / spatial allocation.</li>
 </ul>
 */
const ScheduleAction = ns$1("ScheduleAction");
/** A scholarly article. */
const ScholarlyArticle = ns$1("ScholarlyArticle");
/** A school. */
const School = ns$1("School");
/** A screening of a movie or other video. */
const ScreeningEvent = ns$1("ScreeningEvent");
/** A piece of sculpture. */
const Sculpture = ns$1("Sculpture");
/** A sea (for example, the Caspian sea). */
const SeaBodyOfWater = ns$1("SeaBodyOfWater");
/** The act of searching for an object.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/FindAction">FindAction</a>: SearchAction generally leads to a FindAction, but not necessarily.</li>
 </ul>
 */
const SearchAction = ns$1("SearchAction");
/** Web page type: Search results page. */
const SearchResultsPage = ns$1("SearchResultsPage");
/** A media season e.g. tv, radio, video game etc. */
const Season = ns$1("Season");
/** Used to describe a seat, such as a reserved seat in an event reservation. */
const Seat = ns$1("Seat");
/** A self-storage facility. */
const SelfStorage = ns$1("SelfStorage");
/** The act of taking money from a buyer in exchange for goods or services rendered. An agent sells an object, product, or service to a buyer for a price. Reciprocal of BuyAction. */
const SellAction = ns$1("SellAction");
/** The act of physically/electronically dispatching an object for transfer from an origin to a destination.Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/ReceiveAction">ReceiveAction</a>: The reciprocal of SendAction.</li>
 <li><a class="localLink" href="http://schema.org/GiveAction">GiveAction</a>: Unlike GiveAction, SendAction does not imply the transfer of ownership (e.g. I can send you my laptop, but I'm not necessarily giving it to you).</li>
 </ul>
 */
const SendAction = ns$1("SendAction");
/** A Series in schema.org is a group of related items, typically but not necessarily of the same kind. See also <a class="localLink" href="http://schema.org/CreativeWorkSeries">CreativeWorkSeries</a>, <a class="localLink" href="http://schema.org/EventSeries">EventSeries</a>. */
const Series = ns$1("Series");
/** A service provided by an organization, e.g. delivery service, print services, etc. */
const Service = ns$1("Service");
/** A means for accessing a service, e.g. a government office location, web site, or phone number. */
const ServiceChannel = ns$1("ServiceChannel");
/** The act of distributing content to people for their amusement or edification. */
const ShareAction = ns$1("ShareAction");
/** A shoe store. */
const ShoeStore = ns$1("ShoeStore");
/** A shopping center or mall. */
const ShoppingCenter = ns$1("ShoppingCenter");
/** Residence type: Single-family home. */
const SingleFamilyResidence = ns$1("SingleFamilyResidence");
/** A navigation element of the page. */
const SiteNavigationElement = ns$1("SiteNavigationElement");
/** A ski resort. */
const SkiResort = ns$1("SkiResort");
/** Event type: Social event. */
const SocialEvent = ns$1("SocialEvent");
/** A post to a social media platform, including blog posts, tweets, Facebook posts, etc. */
const SocialMediaPosting = ns$1("SocialMediaPosting");
/** A software application. */
const SoftwareApplication = ns$1("SoftwareApplication");
/** Computer programming source code. Example: Full (compile ready) solutions, code snippet samples, scripts, templates. */
const SoftwareSourceCode = ns$1("SoftwareSourceCode");
/** A placeholder for multiple similar products of the same kind. */
const SomeProducts = ns$1("SomeProducts");
/** A SpeakableSpecification indicates (typically via <a class="localLink" href="http://schema.org/xpath">xpath</a> or <a class="localLink" href="http://schema.org/cssSelector">cssSelector</a>) sections of a document that are highlighted as particularly <a class="localLink" href="http://schema.org/speakable">speakable</a>. Instances of this type are expected to be used primarily as values of the <a class="localLink" href="http://schema.org/speakable">speakable</a> property. */
const SpeakableSpecification = ns$1("SpeakableSpecification");
/** Any branch of a field in which people typically develop specific expertise, usually after significant study, time, and effort. */
const Specialty = ns$1("Specialty");
/** A sporting goods store. */
const SportingGoodsStore = ns$1("SportingGoodsStore");
/** A sports location, such as a playing field. */
const SportsActivityLocation = ns$1("SportsActivityLocation");
/** A sports club. */
const SportsClub = ns$1("SportsClub");
/** Event type: Sports event. */
const SportsEvent = ns$1("SportsEvent");
/** Represents the collection of all sports organizations, including sports teams, governing bodies, and sports associations. */
const SportsOrganization = ns$1("SportsOrganization");
/** Organization: Sports team. */
const SportsTeam = ns$1("SportsTeam");
/** A spreadsheet file. */
const SpreadsheetDigitalDocument = ns$1("SpreadsheetDigitalDocument");
/** A stadium. */
const StadiumOrArena = ns$1("StadiumOrArena");
/** A state or province of a country. */
const State = ns$1("State");
/** A value indicating a steering position. */
const SteeringPositionValue = ns$1("SteeringPositionValue");
/** A retail good store. */
const Store = ns$1("Store");
/** Structured values are used when the value of a property has a more complex structure than simply being a textual value or a reference to another thing. */
const StructuredValue = ns$1("StructuredValue");
/** The act of forming a personal connection with someone/something (object) unidirectionally/asymmetrically to get updates pushed to.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/FollowAction">FollowAction</a>: Unlike FollowAction, SubscribeAction implies that the subscriber acts as a passive agent being constantly/actively pushed for updates.</li>
 <li><a class="localLink" href="http://schema.org/RegisterAction">RegisterAction</a>: Unlike RegisterAction, SubscribeAction implies that the agent is interested in continuing receiving updates from the object.</li>
 <li><a class="localLink" href="http://schema.org/JoinAction">JoinAction</a>: Unlike JoinAction, SubscribeAction implies that the agent is interested in continuing receiving updates from the object.</li>
 </ul>
 */
const SubscribeAction = ns$1("SubscribeAction");
/** A subway station. */
const SubwayStation = ns$1("SubwayStation");
/** A suite in a hotel or other public accommodation, denotes a class of luxury accommodations, the key feature of which is multiple rooms (Source: Wikipedia, the free encyclopedia, see <a href="http://en.wikipedia.org/wiki/Suite_(hotel)">http://en.wikipedia.org/wiki/Suite_(hotel)</a>).
 <br /><br />
 See also the <a href="/docs/hotels.html">dedicated document on the use of schema.org for marking up hotels and other forms of accommodations</a>. */
const Suite = ns$1("Suite");
/** The act of momentarily pausing a device or application (e.g. pause music playback or pause a timer). */
const SuspendAction = ns$1("SuspendAction");
/** A synagogue. */
const Synagogue = ns$1("Synagogue");
/** A short TV program or a segment/part of a TV program. */
const TVClip = ns$1("TVClip");
/** A TV episode which can be part of a series or season. */
const TVEpisode = ns$1("TVEpisode");
/** Season dedicated to TV broadcast and associated online delivery. */
const TVSeason = ns$1("TVSeason");
/** CreativeWorkSeries dedicated to TV broadcast and associated online delivery. */
const TVSeries = ns$1("TVSeries");
/** A table on a Web page. */
const Table = ns$1("Table");
/** The act of gaining ownership of an object from an origin. Reciprocal of GiveAction.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/GiveAction">GiveAction</a>: The reciprocal of TakeAction.</li>
 <li><a class="localLink" href="http://schema.org/ReceiveAction">ReceiveAction</a>: Unlike ReceiveAction, TakeAction implies that ownership has been transfered.</li>
 </ul>
 */
const TakeAction = ns$1("TakeAction");
/** A tattoo parlor. */
const TattooParlor = ns$1("TattooParlor");
/** A taxi. */
const Taxi = ns$1("Taxi");
/** A reservation for a taxi.<br/><br/>

 Note: This type is for information about actual reservations, e.g. in confirmation emails or HTML pages with individual confirmations of reservations. For offers of tickets, use <a class="localLink" href="http://schema.org/Offer">Offer</a>. */
const TaxiReservation = ns$1("TaxiReservation");
/** A service for a vehicle for hire with a driver for local travel. Fares are usually calculated based on distance traveled. */
const TaxiService = ns$1("TaxiService");
/** A taxi stand. */
const TaxiStand = ns$1("TaxiStand");
/** A technical article - Example: How-to (task) topics, step-by-step, procedural troubleshooting, specifications, etc. */
const TechArticle = ns$1("TechArticle");
/** A unique instance of a television BroadcastService on a CableOrSatelliteService lineup. */
const TelevisionChannel = ns$1("TelevisionChannel");
/** A television station. */
const TelevisionStation = ns$1("TelevisionStation");
/** A tennis complex. */
const TennisComplex = ns$1("TennisComplex");
/** Data type: Text. */
const Text = ns$1("Text");
/** A file composed primarily of text. */
const TextDigitalDocument = ns$1("TextDigitalDocument");
/** Event type: Theater performance. */
const TheaterEvent = ns$1("TheaterEvent");
/** A theater group or company, for example, the Royal Shakespeare Company or Druid Theatre. */
const TheaterGroup = ns$1("TheaterGroup");
/** The most generic type of item. */
const Thing = ns$1("Thing");
/** Used to describe a ticket to an event, a flight, a bus ride, etc. */
const Ticket = ns$1("Ticket");
/** The act of reaching a draw in a competitive activity. */
const TieAction = ns$1("TieAction");
/** A point in time recurring on multiple days in the form hh:mm:ss[Z|(+|-)hh:mm] (see <a href="http://www.w3.org/TR/xmlschema-2/#time">XML schema for details</a>). */
const Time = ns$1("Time");
/** The act of giving money voluntarily to a beneficiary in recognition of services rendered. */
const TipAction = ns$1("TipAction");
/** A tire shop. */
const TireShop = ns$1("TireShop");
/** A tourist attraction.  In principle any Thing can be a <a class="localLink" href="http://schema.org/TouristAttraction">TouristAttraction</a>, from a <a class="localLink" href="http://schema.org/Mountain">Mountain</a> and <a class="localLink" href="http://schema.org/LandmarksOrHistoricalBuildings">LandmarksOrHistoricalBuildings</a> to a <a class="localLink" href="http://schema.org/LocalBusiness">LocalBusiness</a>.  This Type can be used on its own to describe a general <a class="localLink" href="http://schema.org/TouristAttraction">TouristAttraction</a>, or be used as an <a class="localLink" href="http://schema.org/additionalType">additionalType</a> to add tourist attraction properties to any other type.  (See examples below) */
const TouristAttraction = ns$1("TouristAttraction");
/** A tourist information center. */
const TouristInformationCenter = ns$1("TouristInformationCenter");
/** A toy store. */
const ToyStore = ns$1("ToyStore");
/** An agent tracks an object for updates.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/FollowAction">FollowAction</a>: Unlike FollowAction, TrackAction refers to the interest on the location of innanimates objects.</li>
 <li><a class="localLink" href="http://schema.org/SubscribeAction">SubscribeAction</a>: Unlike SubscribeAction, TrackAction refers to  the interest on the location of innanimate objects.</li>
 </ul>
 */
const TrackAction = ns$1("TrackAction");
/** The act of participating in an exchange of goods and services for monetary compensation. An agent trades an object, product or service with a participant in exchange for a one time or periodic payment. */
const TradeAction = ns$1("TradeAction");
/** A reservation for train travel.<br/><br/>

 Note: This type is for information about actual reservations, e.g. in confirmation emails or HTML pages with individual confirmations of reservations. For offers of tickets, use <a class="localLink" href="http://schema.org/Offer">Offer</a>. */
const TrainReservation = ns$1("TrainReservation");
/** A train station. */
const TrainStation = ns$1("TrainStation");
/** A trip on a commercial train line. */
const TrainTrip = ns$1("TrainTrip");
/** The act of transferring/moving (abstract or concrete) animate or inanimate objects from one place to another. */
const TransferAction = ns$1("TransferAction");
/** The act of traveling from an fromLocation to a destination by a specified mode of transport, optionally with participants. */
const TravelAction = ns$1("TravelAction");
/** A travel agency. */
const TravelAgency = ns$1("TravelAgency");
/** A trip or journey. An itinerary of visits to one or more places. */
const Trip = ns$1("Trip");
/** A structured value indicating the quantity, unit of measurement, and business function of goods included in a bundle offer. */
const TypeAndQuantityNode = ns$1("TypeAndQuantityNode");
/** Data type: URL. */
const URL$1 = ns$1("URL");
/** The act of un-registering from a service.<br/><br/>

 Related actions:<br/><br/>

 <ul>
 <li><a class="localLink" href="http://schema.org/RegisterAction">RegisterAction</a>: antonym of UnRegisterAction.</li>
 <li><a class="localLink" href="http://schema.org/LeaveAction">LeaveAction</a>: Unlike LeaveAction, UnRegisterAction implies that you are unregistering from a service you werer previously registered, rather than leaving a team/group of people.</li>
 </ul>
 */
const UnRegisterAction = ns$1("UnRegisterAction");
/** The price asked for a given offer by the respective organization or person. */
const UnitPriceSpecification = ns$1("UnitPriceSpecification");
/** The act of managing by changing/editing the state of the object. */
const UpdateAction = ns$1("UpdateAction");
/** The act of applying an object to its intended purpose. */
const UseAction = ns$1("UseAction");
/** UserInteraction and its subtypes is an old way of talking about users interacting with pages. It is generally better to use <a class="localLink" href="http://schema.org/Action">Action</a>-based vocabulary, alongside types such as <a class="localLink" href="http://schema.org/Comment">Comment</a>. */
const UserBlocks = ns$1("UserBlocks");
/** UserInteraction and its subtypes is an old way of talking about users interacting with pages. It is generally better to use <a class="localLink" href="http://schema.org/Action">Action</a>-based vocabulary, alongside types such as <a class="localLink" href="http://schema.org/Comment">Comment</a>. */
const UserCheckins = ns$1("UserCheckins");
/** UserInteraction and its subtypes is an old way of talking about users interacting with pages. It is generally better to use <a class="localLink" href="http://schema.org/Action">Action</a>-based vocabulary, alongside types such as <a class="localLink" href="http://schema.org/Comment">Comment</a>. */
const UserComments = ns$1("UserComments");
/** UserInteraction and its subtypes is an old way of talking about users interacting with pages. It is generally better to use <a class="localLink" href="http://schema.org/Action">Action</a>-based vocabulary, alongside types such as <a class="localLink" href="http://schema.org/Comment">Comment</a>. */
const UserDownloads = ns$1("UserDownloads");
/** UserInteraction and its subtypes is an old way of talking about users interacting with pages. It is generally better to use <a class="localLink" href="http://schema.org/Action">Action</a>-based vocabulary, alongside types such as <a class="localLink" href="http://schema.org/Comment">Comment</a>. */
const UserInteraction = ns$1("UserInteraction");
/** UserInteraction and its subtypes is an old way of talking about users interacting with pages. It is generally better to use <a class="localLink" href="http://schema.org/Action">Action</a>-based vocabulary, alongside types such as <a class="localLink" href="http://schema.org/Comment">Comment</a>. */
const UserLikes = ns$1("UserLikes");
/** UserInteraction and its subtypes is an old way of talking about users interacting with pages. It is generally better to use <a class="localLink" href="http://schema.org/Action">Action</a>-based vocabulary, alongside types such as <a class="localLink" href="http://schema.org/Comment">Comment</a>. */
const UserPageVisits = ns$1("UserPageVisits");
/** UserInteraction and its subtypes is an old way of talking about users interacting with pages. It is generally better to use <a class="localLink" href="http://schema.org/Action">Action</a>-based vocabulary, alongside types such as <a class="localLink" href="http://schema.org/Comment">Comment</a>. */
const UserPlays = ns$1("UserPlays");
/** UserInteraction and its subtypes is an old way of talking about users interacting with pages. It is generally better to use <a class="localLink" href="http://schema.org/Action">Action</a>-based vocabulary, alongside types such as <a class="localLink" href="http://schema.org/Comment">Comment</a>. */
const UserPlusOnes = ns$1("UserPlusOnes");
/** UserInteraction and its subtypes is an old way of talking about users interacting with pages. It is generally better to use <a class="localLink" href="http://schema.org/Action">Action</a>-based vocabulary, alongside types such as <a class="localLink" href="http://schema.org/Comment">Comment</a>. */
const UserTweets = ns$1("UserTweets");
/** A vehicle is a device that is designed or used to transport people or cargo over land, water, air, or through space. */
const Vehicle = ns$1("Vehicle");
/** Web page type: Video gallery page. */
const VideoGallery = ns$1("VideoGallery");
/** A video game is an electronic game that involves human interaction with a user interface to generate visual feedback on a video device. */
const VideoGame = ns$1("VideoGame");
/** A short segment/part of a video game. */
const VideoGameClip = ns$1("VideoGameClip");
/** A video game series. */
const VideoGameSeries = ns$1("VideoGameSeries");
/** A video file. */
const VideoObject = ns$1("VideoObject");
/** The act of consuming static visual content. */
const ViewAction = ns$1("ViewAction");
/** Event type: Visual arts event. */
const VisualArtsEvent = ns$1("VisualArtsEvent");
/** A work of art that is primarily visual in character. */
const VisualArtwork = ns$1("VisualArtwork");
/** A volcano, like Fuji san. */
const Volcano = ns$1("Volcano");
/** The act of expressing a preference from a fixed/finite/structured set of choices/options. */
const VoteAction = ns$1("VoteAction");
/** An advertising section of the page. */
const WPAdBlock = ns$1("WPAdBlock");
/** The footer section of the page. */
const WPFooter = ns$1("WPFooter");
/** The header section of the page. */
const WPHeader = ns$1("WPHeader");
/** A sidebar section of the page. */
const WPSideBar = ns$1("WPSideBar");
/** The act of expressing a desire about the object. An agent wants an object. */
const WantAction = ns$1("WantAction");
/** A structured value representing the duration and scope of services that will be provided to a customer free of charge in case of a defect or malfunction of a product. */
const WarrantyPromise = ns$1("WarrantyPromise");
/** A range of of services that will be provided to a customer free of charge in case of a defect or malfunction of a product.<br/><br/>

 Commonly used values:<br/><br/>

 <ul>
 <li>http://purl.org/goodrelations/v1#Labor-BringIn</li>
 <li>http://purl.org/goodrelations/v1#PartsAndLabor-BringIn</li>
 <li>http://purl.org/goodrelations/v1#PartsAndLabor-PickUp</li>
 </ul>
 */
const WarrantyScope = ns$1("WarrantyScope");
/** The act of consuming dynamic/moving visual content. */
const WatchAction = ns$1("WatchAction");
/** A waterfall, like Niagara. */
const Waterfall = ns$1("Waterfall");
/** The act of dressing oneself in clothing. */
const WearAction = ns$1("WearAction");
/** Web applications. */
const WebApplication = ns$1("WebApplication");
/** A web page. Every web page is implicitly assumed to be declared to be of type WebPage, so the various properties about that webpage, such as <code>breadcrumb</code> may be used. We recommend explicit declaration if these properties are specified, but if they are found outside of an itemscope, they will be assumed to be about the page. */
const WebPage = ns$1("WebPage");
/** A web page element, like a table or an image. */
const WebPageElement = ns$1("WebPageElement");
/** A WebSite is a set of related web pages and other items typically served from a single web domain and accessible via URLs. */
const WebSite = ns$1("WebSite");
/** A wholesale store. */
const WholesaleStore = ns$1("WholesaleStore");
/** The act of achieving victory in a competitive activity. */
const WinAction = ns$1("WinAction");
/** A winery. */
const Winery = ns$1("Winery");
/** A Workers Union (also known as a Labor Union, Labour Union, or Trade Union) is an organization that promotes the interests of its worker members by collectively bargaining with management, organizing, and political lobbying. */
const WorkersUnion = ns$1("WorkersUnion");
/** The act of authoring written creative content. */
const WriteAction = ns$1("WriteAction");
/** A zoo. */
const Zoo = ns$1("Zoo");
/** An in-progress action (e.g, while watching the movie, or driving to a location). */
const ActiveActionStatus = ns$1("ActiveActionStatus");
/** AlbumRelease. */
const AlbumRelease = ns$1("AlbumRelease");
/** All-wheel Drive is a transmission layout where the engine drives all four wheels. */
const AllWheelDriveConfiguration = ns$1("AllWheelDriveConfiguration");
/** Book format: Audiobook. This is an enumerated value for use with the bookFormat property. There is also a type 'Audiobook' in the bib extension which includes Audiobook specific properties. */
const AudiobookFormat = ns$1("AudiobookFormat");
/** BroadcastRelease. */
const BroadcastRelease = ns$1("BroadcastRelease");
/** CDFormat. */
const CDFormat = ns$1("CDFormat");
/** CassetteFormat. */
const CassetteFormat = ns$1("CassetteFormat");
/** Play mode: CoOp. Co-operative games, where you play on the same team with friends. */
const CoOp = ns$1("CoOp");
/** Permission to add comments to the document. */
const CommentPermission = ns$1("CommentPermission");
/** CompilationAlbum. */
const CompilationAlbum = ns$1("CompilationAlbum");
/** An action that has already taken place. */
const CompletedActionStatus = ns$1("CompletedActionStatus");
/** DJMixAlbum. */
const DJMixAlbum = ns$1("DJMixAlbum");
/** DVDFormat. */
const DVDFormat = ns$1("DVDFormat");
/** Indicates that the item is damaged. */
const DamagedCondition = ns$1("DamagedCondition");
/** DemoAlbum. */
const DemoAlbum = ns$1("DemoAlbum");
/** A diet appropriate for people with diabetes. */
const DiabeticDiet = ns$1("DiabeticDiet");
/** DigitalAudioTapeFormat. */
const DigitalAudioTapeFormat = ns$1("DigitalAudioTapeFormat");
/** DigitalFormat. */
const DigitalFormat = ns$1("DigitalFormat");
/** Indicates that the item has been discontinued. */
const Discontinued = ns$1("Discontinued");
/** Book format: Ebook. */
const EBook = ns$1("EBook");
/** EPRelease. */
const EPRelease = ns$1("EPRelease");
/** The event has been cancelled. If the event has multiple startDate values, all are assumed to be cancelled. Either startDate or previousStartDate may be used to specify the event's cancelled date(s). */
const EventCancelled = ns$1("EventCancelled");
/** The event has been postponed and no new date has been set. The event's previousStartDate should be set. */
const EventPostponed = ns$1("EventPostponed");
/** The event has been rescheduled. The event's previousStartDate should be set to the old date and the startDate should be set to the event's new date. (If the event has been rescheduled multiple times, the previousStartDate property may be repeated). */
const EventRescheduled = ns$1("EventRescheduled");
/** The event is taking place or has taken place on the startDate as scheduled. Use of this value is optional, as it is assumed by default. */
const EventScheduled = ns$1("EventScheduled");
/** An action that failed to complete. The action's error property and the HTTP return code contain more information about the failure. */
const FailedActionStatus = ns$1("FailedActionStatus");
/** The boolean value false. */
const False = ns$1("False");
/** The female gender. */
const Female = ns$1("Female");
/** Four-wheel drive is a transmission layout where the engine primarily drives two wheels with a part-time four-wheel drive capability. */
const FourWheelDriveConfiguration = ns$1("FourWheelDriveConfiguration");
/** The day of the week between Thursday and Saturday. */
const Friday = ns$1("Friday");
/** Front-wheel drive is a transmission layout where the engine drives the front wheels. */
const FrontWheelDriveConfiguration = ns$1("FrontWheelDriveConfiguration");
/** A diet exclusive of gluten. */
const GlutenFreeDiet = ns$1("GlutenFreeDiet");
/** The airline boards by groups based on check-in time, priority, etc. */
const GroupBoardingPolicy = ns$1("GroupBoardingPolicy");
/** A diet conforming to Islamic dietary practices. */
const HalalDiet = ns$1("HalalDiet");
/** Book format: Hardcover. */
const Hardcover = ns$1("Hardcover");
/** Uses devices to support users with hearing impairments. */
const HearingImpairedSupported = ns$1("HearingImpairedSupported");
/** A diet conforming to Hindu dietary practices, in particular, beef-free. */
const HinduDiet = ns$1("HinduDiet");
/** Indicates that the item is in stock. */
const InStock = ns$1("InStock");
/** Indicates that the item is available only at physical locations. */
const InStoreOnly = ns$1("InStoreOnly");
/** An ItemList ordered with lower values listed first. */
const ItemListOrderAscending = ns$1("ItemListOrderAscending");
/** An ItemList ordered with higher values listed first. */
const ItemListOrderDescending = ns$1("ItemListOrderDescending");
/** An ItemList ordered with no explicit order. */
const ItemListUnordered = ns$1("ItemListUnordered");
/** A diet conforming to Jewish dietary practices. */
const KosherDiet = ns$1("KosherDiet");
/** LaserDiscFormat. */
const LaserDiscFormat = ns$1("LaserDiscFormat");
/** The steering position is on the left side of the vehicle (viewed from the main direction of driving). */
const LeftHandDriving = ns$1("LeftHandDriving");
/** Indicates that the item has limited availability. */
const LimitedAvailability = ns$1("LimitedAvailability");
/** LiveAlbum. */
const LiveAlbum = ns$1("LiveAlbum");
/** A diet focused on reduced calorie intake. */
const LowCalorieDiet = ns$1("LowCalorieDiet");
/** A diet focused on reduced fat and cholesterol intake. */
const LowFatDiet = ns$1("LowFatDiet");
/** A diet appropriate for people with lactose intolerance. */
const LowLactoseDiet = ns$1("LowLactoseDiet");
/** A diet focused on reduced sodium intake. */
const LowSaltDiet = ns$1("LowSaltDiet");
/** The male gender. */
const Male = ns$1("Male");
/** MixtapeAlbum. */
const MixtapeAlbum = ns$1("MixtapeAlbum");
/** The day of the week between Sunday and Tuesday. */
const Monday = ns$1("Monday");
/** Play mode: MultiPlayer. Requiring or allowing multiple human players to play simultaneously. */
const MultiPlayer = ns$1("MultiPlayer");
/** Indicates that the item is new. */
const NewCondition = ns$1("NewCondition");
/** Game server status: OfflinePermanently. Server is offline and not available. */
const OfflinePermanently = ns$1("OfflinePermanently");
/** Game server status: OfflineTemporarily. Server is offline now but it can be online soon. */
const OfflineTemporarily = ns$1("OfflineTemporarily");
/** A DeliveryMethod in which an item is collected on site, e.g. in a store or at a box office. */
const OnSitePickup = ns$1("OnSitePickup");
/** Game server status: Online. Server is available. */
const Online = ns$1("Online");
/** Game server status: OnlineFull. Server is online but unavailable. The maximum number of players has reached. */
const OnlineFull = ns$1("OnlineFull");
/** Indicates that the item is available only online. */
const OnlineOnly = ns$1("OnlineOnly");
/** OrderStatus representing cancellation of an order. */
const OrderCancelled = ns$1("OrderCancelled");
/** OrderStatus representing successful delivery of an order. */
const OrderDelivered = ns$1("OrderDelivered");
/** OrderStatus representing that an order is in transit. */
const OrderInTransit = ns$1("OrderInTransit");
/** OrderStatus representing that payment is due on an order. */
const OrderPaymentDue = ns$1("OrderPaymentDue");
/** OrderStatus representing availability of an order for pickup. */
const OrderPickupAvailable = ns$1("OrderPickupAvailable");
/** OrderStatus representing that there is a problem with the order. */
const OrderProblem = ns$1("OrderProblem");
/** OrderStatus representing that an order is being processed. */
const OrderProcessing = ns$1("OrderProcessing");
/** OrderStatus representing that an order has been returned. */
const OrderReturned = ns$1("OrderReturned");
/** Indicates that the item is out of stock. */
const OutOfStock = ns$1("OutOfStock");
/** Book format: Paperback. */
const Paperback = ns$1("Paperback");
/** A parking map. */
const ParkingMap = ns$1("ParkingMap");
/** An automatic payment system is in place and will be used. */
const PaymentAutomaticallyApplied = ns$1("PaymentAutomaticallyApplied");
/** The payment has been received and processed. */
const PaymentComplete = ns$1("PaymentComplete");
/** The payee received the payment, but it was declined for some reason. */
const PaymentDeclined = ns$1("PaymentDeclined");
/** The payment is due, but still within an acceptable time to be received. */
const PaymentDue = ns$1("PaymentDue");
/** The payment is due and considered late. */
const PaymentPastDue = ns$1("PaymentPastDue");
/** A description of an action that is supported. */
const PotentialActionStatus = ns$1("PotentialActionStatus");
/** Indicates that the item is available for pre-order, but will be delivered when generally available. */
const PreOrder = ns$1("PreOrder");
/** Indicates that the item is available for ordering and delivery before general availability. */
const PreSale = ns$1("PreSale");
/** This stands for any day that is a public holiday; it is a placeholder for all official public holidays in some particular location. While not technically a "day of the week", it can be used with <a class="localLink" href="http://schema.org/OpeningHoursSpecification">OpeningHoursSpecification</a>. In the context of an opening hours specification it can be used to indicate opening hours on public holidays, overriding general opening hours for the day of the week on which a public holiday occurs. */
const PublicHolidays = ns$1("PublicHolidays");
/** Permission to read or view the document. */
const ReadPermission = ns$1("ReadPermission");
/** Real-wheel drive is a transmission layout where the engine drives the rear wheels. */
const RearWheelDriveConfiguration = ns$1("RearWheelDriveConfiguration");
/** Indicates that the item is refurbished. */
const RefurbishedCondition = ns$1("RefurbishedCondition");
/** RemixAlbum. */
const RemixAlbum = ns$1("RemixAlbum");
/** Researchers. */
const Researcher = ns$1("Researcher");
/** The status for a previously confirmed reservation that is now cancelled. */
const ReservationCancelled = ns$1("ReservationCancelled");
/** The status of a confirmed reservation. */
const ReservationConfirmed = ns$1("ReservationConfirmed");
/** The status of a reservation on hold pending an update like credit card number or flight changes. */
const ReservationHold = ns$1("ReservationHold");
/** The status of a reservation when a request has been sent, but not confirmed. */
const ReservationPending = ns$1("ReservationPending");
/** The steering position is on the right side of the vehicle (viewed from the main direction of driving). */
const RightHandDriving = ns$1("RightHandDriving");
/** The invitee may or may not attend. */
const RsvpResponseMaybe = ns$1("RsvpResponseMaybe");
/** The invitee will not attend. */
const RsvpResponseNo = ns$1("RsvpResponseNo");
/** The invitee will attend. */
const RsvpResponseYes = ns$1("RsvpResponseYes");
/** The day of the week between Friday and Sunday. */
const Saturday = ns$1("Saturday");
/** A seating map. */
const SeatingMap = ns$1("SeatingMap");
/** Play mode: SinglePlayer. Which is played by a lone player. */
const SinglePlayer = ns$1("SinglePlayer");
/** SingleRelease. */
const SingleRelease = ns$1("SingleRelease");
/** Indicates that the item has sold out. */
const SoldOut = ns$1("SoldOut");
/** SoundtrackAlbum. */
const SoundtrackAlbum = ns$1("SoundtrackAlbum");
/** SpokenWordAlbum. */
const SpokenWordAlbum = ns$1("SpokenWordAlbum");
/** StudioAlbum. */
const StudioAlbum = ns$1("StudioAlbum");
/** The day of the week between Saturday and Monday. */
const Sunday = ns$1("Sunday");
/** The day of the week between Wednesday and Friday. */
const Thursday = ns$1("Thursday");
/** The associated telephone number is toll free. */
const TollFree = ns$1("TollFree");
/** A transit map. */
const TransitMap = ns$1("TransitMap");
/** The boolean value true. */
const True = ns$1("True");
/** The day of the week between Monday and Wednesday. */
const Tuesday = ns$1("Tuesday");
/** Indicates that the item is used. */
const UsedCondition = ns$1("UsedCondition");
/** A diet exclusive of all animal products. */
const VeganDiet = ns$1("VeganDiet");
/** A diet exclusive of animal meat. */
const VegetarianDiet = ns$1("VegetarianDiet");
/** A venue map (e.g. for malls, auditoriums, museums, etc.). */
const VenueMap = ns$1("VenueMap");
/** VinylFormat. */
const VinylFormat = ns$1("VinylFormat");
/** The day of the week between Tuesday and Thursday. */
const Wednesday = ns$1("Wednesday");
/** Permission to write or edit the document. */
const WritePermission = ns$1("WritePermission");
/** The airline boards by zones of the plane. */
const ZoneBoardingPolicy = ns$1("ZoneBoardingPolicy");
/** The answer(s) that has been accepted as best, typically on a Question/Answer site. Sites vary in their selection mechanisms, e.g. drawing on community opinion and/or the view of the Question author. */
const acceptedAnswer = ns$1("acceptedAnswer");
/** The offer(s) -- e.g., product, quantity and price combinations -- included in the order. */
const acceptedOffer = ns$1("acceptedOffer");
/** The payment method(s) accepted by seller for this offer. */
const acceptedPaymentMethod = ns$1("acceptedPaymentMethod");
/** Indicates whether a FoodEstablishment accepts reservations. Values can be Boolean, an URL at which reservations can be made or (for backwards compatibility) the strings <code>Yes</code> or <code>No</code>. */
const acceptsReservations = ns$1("acceptsReservations");
/** Password, PIN, or access code needed for delivery (e.g. from a locker). */
const accessCode = ns$1("accessCode");
/** The human sensory perceptual system or cognitive faculty through which a person may process or perceive information. Expected values include: auditory, tactile, textual, visual, colorDependent, chartOnVisual, chemOnVisual, diagramOnVisual, mathOnVisual, musicOnVisual, textOnVisual. */
const accessMode = ns$1("accessMode");
/** A list of single or combined accessModes that are sufficient to understand all the intellectual content of a resource. Expected values include:  auditory, tactile, textual, visual. */
const accessModeSufficient = ns$1("accessModeSufficient");
/** Indicates that the resource is compatible with the referenced accessibility API (<a href="http://www.w3.org/wiki/WebSchemas/Accessibility">WebSchemas wiki lists possible values</a>). */
const accessibilityAPI = ns$1("accessibilityAPI");
/** Identifies input methods that are sufficient to fully control the described resource (<a href="http://www.w3.org/wiki/WebSchemas/Accessibility">WebSchemas wiki lists possible values</a>). */
const accessibilityControl = ns$1("accessibilityControl");
/** Content features of the resource, such as accessible media, alternatives and supported enhancements for accessibility (<a href="http://www.w3.org/wiki/WebSchemas/Accessibility">WebSchemas wiki lists possible values</a>). */
const accessibilityFeature = ns$1("accessibilityFeature");
/** A characteristic of the described resource that is physiologically dangerous to some users. Related to WCAG 2.0 guideline 2.3 (<a href="http://www.w3.org/wiki/WebSchemas/Accessibility">WebSchemas wiki lists possible values</a>). */
const accessibilityHazard = ns$1("accessibilityHazard");
/** A human-readable summary of specific accessibility features or deficiencies, consistent with the other accessibility metadata but expressing subtleties such as "short descriptions are present but long descriptions will be needed for non-visual users" or "short descriptions are present and no long descriptions are needed." */
const accessibilitySummary = ns$1("accessibilitySummary");
/** The identifier for the account the payment will be applied to. */
const accountId = ns$1("accountId");
/** Specifies the Person that is legally accountable for the CreativeWork. */
const accountablePerson = ns$1("accountablePerson");
/** The organization or person from which the product was acquired. */
const acquiredFrom = ns$1("acquiredFrom");
/** A set of requirements that a must be fulfilled in order to perform an Action. If more than one value is specied, fulfilling one set of requirements will allow the Action to be performed. */
const actionAccessibilityRequirement = ns$1("actionAccessibilityRequirement");
/** The high level platform(s) where the Action can be performed for the given URL. To specify a specific application or operating system instance, use actionApplication. */
const actionPlatform = ns$1("actionPlatform");
/** Indicates the current disposition of the Action. */
const actionStatus = ns$1("actionStatus");
/** An actor, e.g. in tv, radio, movie, video games etc. Actors can be associated with individual items or with a series, episode, clip. */
const actors = ns$1("actors");
/** An additional offer that can only be obtained in combination with the first base offer (e.g. supplements and extensions that are available for a surcharge). */
const addOn = ns$1("addOn");
/** An additional name for a Person, can be used for a middle name. */
const additionalName = ns$1("additionalName");
/** If responding yes, the number of guests who will attend in addition to the invitee. */
const additionalNumberOfGuests = ns$1("additionalNumberOfGuests");
/** A property-value pair representing an additional characteristics of the entitity, e.g. a product feature or another characteristic for which there is no matching property in schema.org.<br/><br/>

 Note: Publishers should be aware that applications designed to use specific schema.org properties (e.g. http://schema.org/width, http://schema.org/color, http://schema.org/gtin13, ...) will typically expect such data to be provided using those properties, rather than using the generic property/value mechanism. */
const additionalProperty = ns$1("additionalProperty");
/** An additional type for the item, typically used for adding more specific types from external vocabularies in microdata syntax. This is a relationship between something and a class that the thing is in. In RDFa syntax, it is better to use the native RDFa syntax - the 'typeof' attribute - for multiple types. Schema.org tools may have only weaker understanding of extra types, in particular those defined externally. */
const additionalType = ns$1("additionalType");
/** Physical address of the item. */
const address = ns$1("address");
/** The country. For example, USA. You can also provide the two-letter <a href="http://en.wikipedia.org/wiki/ISO_3166-1">ISO 3166-1 alpha-2 country code</a>. */
const addressCountry = ns$1("addressCountry");
/** The locality in which the street address is, and which is in the region. For example, Mountain View. */
const addressLocality = ns$1("addressLocality");
/** The region in which the locality is, and which is in the country. For example, California or another appropriate first-level <a href="https://en.wikipedia.org/wiki/List_of_administrative_divisions_by_country">Administrative division</a> */
const addressRegion = ns$1("addressRegion");
/** The amount of time that is required between accepting the offer and the actual usage of the resource or service. */
const advanceBookingRequirement = ns$1("advanceBookingRequirement");
/** An organization that this person is affiliated with. For example, a school/university, a club, or a team. */
const affiliation = ns$1("affiliation");
/** A media object representing the circumstances after performing this direction. */
const afterMedia = ns$1("afterMedia");
/** The direct performer or driver of the action (animate or inanimate). e.g. <em>John</em> wrote a book. */
const agent = ns$1("agent");
/** The overall rating, based on a collection of reviews or ratings, of the item. */
const aggregateRating = ns$1("aggregateRating");
/** The kind of aircraft (e.g., "Boeing 747"). */
const aircraft = ns$1("aircraft");
/** Classification of the album by it's type of content: soundtrack, live album, studio album, etc. */
const albumProductionType = ns$1("albumProductionType");
/** The kind of release which this album is: single, EP or album. */
const albumReleaseType = ns$1("albumReleaseType");
/** A collection of music albums. */
const albums = ns$1("albums");
/** A category of alignment between the learning resource and the framework node. Recommended values include: 'assesses', 'teaches', 'requires', 'textComplexity', 'readingLevel', 'educationalSubject', and 'educationalLevel'. */
const alignmentType = ns$1("alignmentType");
/** An alias for the item. */
const alternateName = ns$1("alternateName");
/** A secondary title of the CreativeWork. */
const alternativeHeadline = ns$1("alternativeHeadline");
/** An amenity feature (e.g. a characteristic or service) of the Accommodation. This generic property does not make a statement about whether the feature is included in an offer for the main accommodation or available at extra costs. */
const amenityFeature = ns$1("amenityFeature");
/** The amount of money. */
const amount = ns$1("amount");
/** The quantity of the goods included in the offer. */
const amountOfThisGood = ns$1("amountOfThisGood");
/** The annual rate that is charged for borrowing (or made by investing), expressed as a single percentage number that represents the actual yearly cost of funds over the term of a loan. This includes any fees or additional costs associated with the transaction. */
const annualPercentageRate = ns$1("annualPercentageRate");
/** The number of answers this question has received. */
const answerCount = ns$1("answerCount");
/** An application that can complete the request. */
const application = ns$1("application");
/** Type of software application, e.g. 'Game, Multimedia'. */
const applicationCategory = ns$1("applicationCategory");
/** Subcategory of the application, e.g. 'Arcade Game'. */
const applicationSubCategory = ns$1("applicationSubCategory");
/** The name of the application suite to which the application belongs (e.g. Excel belongs to Office). */
const applicationSuite = ns$1("applicationSuite");
/** The delivery method(s) to which the delivery charge or payment charge specification applies. */
const appliesToDeliveryMethod = ns$1("appliesToDeliveryMethod");
/** The payment method(s) to which the payment charge specification applies. */
const appliesToPaymentMethod = ns$1("appliesToPaymentMethod");
/** The area within which users can expect to reach the broadcast service. */
const area = ns$1("area");
/** The airport where the flight terminates. */
const arrivalAirport = ns$1("arrivalAirport");
/** The stop or station from which the bus arrives. */
const arrivalBusStop = ns$1("arrivalBusStop");
/** Identifier of the flight's arrival gate. */
const arrivalGate = ns$1("arrivalGate");
/** The platform where the train arrives. */
const arrivalPlatform = ns$1("arrivalPlatform");
/** The station where the train trip ends. */
const arrivalStation = ns$1("arrivalStation");
/** Identifier of the flight's arrival terminal. */
const arrivalTerminal = ns$1("arrivalTerminal");
/** The expected arrival time. */
const arrivalTime = ns$1("arrivalTime");
/** The number of copies when multiple copies of a piece of artwork are produced - e.g. for a limited edition of 20 prints, 'artEdition' refers to the total number of copies (in this example "20"). */
const artEdition = ns$1("artEdition");
/** The material used. (e.g. Oil, Watercolour, Acrylic, Linoprint, Marble, Cyanotype, Digital, Lithograph, DryPoint, Intaglio, Pastel, Woodcut, Pencil, Mixed Media, etc.) */
const artMedium = ns$1("artMedium");
/** e.g. Painting, Drawing, Sculpture, Print, Photograph, Assemblage, Collage, etc. */
const artform = ns$1("artform");
/** The actual body of the article. */
const articleBody = ns$1("articleBody");
/** Articles may belong to one or more 'sections' in a magazine or newspaper, such as Sports, Lifestyle, etc. */
const articleSection = ns$1("articleSection");
/** Library file name e.g., mscorlib.dll, system.web.dll. */
const assembly = ns$1("assembly");
/** Associated product/technology version. e.g., .NET Framework 4.5. */
const assemblyVersion = ns$1("assemblyVersion");
/** A NewsArticle associated with the Media Object. */
const associatedArticle = ns$1("associatedArticle");
/** A media object that encodes this CreativeWork. This property is a synonym for encoding. */
const associatedMedia = ns$1("associatedMedia");
/** A person that acts as performing member of a sports team; a player as opposed to a coach. */
const athlete = ns$1("athlete");
/** A person attending the event. */
const attendees = ns$1("attendees");
/** The target group associated with a given audience (e.g. veterans, car owners, musicians, etc.). */
const audienceType = ns$1("audienceType");
/** An embedded audio object. */
const audio = ns$1("audio");
/** The Organization responsible for authenticating the user's subscription. For example, many media apps require a cable/satellite provider to authenticate your subscription before playing media. */
const authenticator = ns$1("authenticator");
/** The author of this content or rating. Please note that author is special in that HTML 5 provides a special mechanism for indicating authorship via the rel tag. That is equivalent to this and may be used interchangeably. */
const author = ns$1("author");
/** The availability of this item&#x2014;for example In stock, Out of stock, Pre-order, etc. */
const availability = ns$1("availability");
/** The end of the availability of the product or service included in the offer. */
const availabilityEnds = ns$1("availabilityEnds");
/** The beginning of the availability of the product or service included in the offer. */
const availabilityStarts = ns$1("availabilityStarts");
/** The place(s) from which the offer can be obtained (e.g. store locations). */
const availableAtOrFrom = ns$1("availableAtOrFrom");
/** A means of accessing the service (e.g. a phone bank, a web site, a location, etc.). */
const availableChannel = ns$1("availableChannel");
/** The delivery method(s) available for this offer. */
const availableDeliveryMethod = ns$1("availableDeliveryMethod");
/** When the item is available for pickup from the store, locker, etc. */
const availableFrom = ns$1("availableFrom");
/** A language someone may use with or at the item, service or place. Please use one of the language codes from the <a href="http://tools.ietf.org/html/bcp47">IETF BCP 47 standard</a>. See also <a class="localLink" href="http://schema.org/inLanguage">inLanguage</a> */
const availableLanguage = ns$1("availableLanguage");
/** After this date, the item will no longer be available for pickup. */
const availableThrough = ns$1("availableThrough");
/** Awards won by or for this item. */
const awards = ns$1("awards");
/** The away team in a sports event. */
const awayTeam = ns$1("awayTeam");
/** The base salary of the job or of an employee in an EmployeeRole. */
const baseSalary = ns$1("baseSalary");
/** A sub property of recipient. The recipient blind copied on a message. */
const bccRecipient = ns$1("bccRecipient");
/** The type of bed or beds included in the accommodation. For the single case of just one bed of a certain type, you use bed directly with a text.
 If you want to indicate the quantity of a certain kind of bed, use an instance of BedDetails. For more detailed information, use the amenityFeature property. */
const bed = ns$1("bed");
/** A media object representing the circumstances before performing this direction. */
const beforeMedia = ns$1("beforeMedia");
/** Description of benefits associated with the job. */
const benefits = ns$1("benefits");
/** The highest value allowed in this rating system. If bestRating is omitted, 5 is assumed. */
const bestRating = ns$1("bestRating");
/** The billing address for the order. */
const billingAddress = ns$1("billingAddress");
/** This property specifies the minimal quantity and rounding increment that will be the basis for the billing. The unit of measurement is specified by the unitCode property. */
const billingIncrement = ns$1("billingIncrement");
/** The time interval used to compute the invoice. */
const billingPeriod = ns$1("billingPeriod");
/** Date of birth. */
const birthDate = ns$1("birthDate");
/** The place where the person was born. */
const birthPlace = ns$1("birthPlace");
/** The bitrate of the media object. */
const bitrate = ns$1("bitrate");
/** The postings that are part of this blog. */
const blogPosts = ns$1("blogPosts");
/** The airline-specific indicator of boarding order / preference. */
const boardingGroup = ns$1("boardingGroup");
/** The type of boarding policy used by the airline (e.g. zone-based or group-based). */
const boardingPolicy = ns$1("boardingPolicy");
/** The edition of the book. */
const bookEdition = ns$1("bookEdition");
/** The format of the book. */
const bookFormat = ns$1("bookFormat");
/** 'bookingAgent' is an out-dated term indicating a 'broker' that serves as a booking agent. */
const bookingAgent = ns$1("bookingAgent");
/** The date and time the reservation was booked. */
const bookingTime = ns$1("bookingTime");
/** A sub property of participant. The person that borrows the object being lent. */
const borrower = ns$1("borrower");
/** A box is the area enclosed by the rectangle formed by two points. The first point is the lower corner, the second point is the upper corner. A box is expressed as two points separated by a space character. */
const box = ns$1("box");
/** A short textual code (also called "store code") that uniquely identifies a place of business. The code is typically assigned by the parentOrganization and used in structured URLs.<br/><br/>

 For example, in the URL http://www.starbucks.co.uk/store-locator/etc/detail/3047 the code "3047" is a branchCode for a particular branch. */
const branchCode = ns$1("branchCode");
/** The larger organization that this local business is a branch of, if any. Not to be confused with (anatomical)<a class="localLink" href="http://schema.org/branch">branch</a>. */
const branchOf = ns$1("branchOf");
/** The brand(s) associated with a product or service, or the brand(s) maintained by an organization or business person. */
const brand = ns$1("brand");
/** A set of links that can help a user understand and navigate a website hierarchy. */
const breadcrumb = ns$1("breadcrumb");
/** The media network(s) whose content is broadcast on this station. */
const broadcastAffiliateOf = ns$1("broadcastAffiliateOf");
/** The unique address by which the BroadcastService can be identified in a provider lineup. In US, this is typically a number. */
const broadcastChannelId = ns$1("broadcastChannelId");
/** The name displayed in the channel guide. For many US affiliates, it is the network name. */
const broadcastDisplayName = ns$1("broadcastDisplayName");
/** The frequency used for over-the-air broadcasts. Numeric values or simple ranges e.g. 87-99. In addition a shortcut idiom is supported for frequences of AM and FM radio channels, e.g. "87 FM". */
const broadcastFrequency = ns$1("broadcastFrequency");
/** The frequency in MHz for a particular broadcast. */
const broadcastFrequencyValue = ns$1("broadcastFrequencyValue");
/** The event being broadcast such as a sporting event or awards ceremony. */
const broadcastOfEvent = ns$1("broadcastOfEvent");
/** The type of service required to have access to the channel (e.g. Standard or Premium). */
const broadcastServiceTier = ns$1("broadcastServiceTier");
/** The timezone in <a href="http://en.wikipedia.org/wiki/ISO_8601">ISO 8601 format</a> for which the service bases its broadcasts */
const broadcastTimezone = ns$1("broadcastTimezone");
/** The organization owning or operating the broadcast service. */
const broadcaster = ns$1("broadcaster");
/** Specifies browser requirements in human-readable text. For example, 'requires HTML5 support'. */
const browserRequirements = ns$1("browserRequirements");
/** The name of the bus (e.g. Bolt Express). */
const busName = ns$1("busName");
/** The unique identifier for the bus. */
const busNumber = ns$1("busNumber");
/** The business function (e.g. sell, lease, repair, dispose) of the offer or component of a bundle (TypeAndQuantityNode). The default is http://purl.org/goodrelations/v1#Sell. */
const businessFunction = ns$1("businessFunction");
/** A sub property of participant. The participant/person/organization that bought the object. */
const buyer = ns$1("buyer");
/** The artist that performed this album or recording. */
const byArtist = ns$1("byArtist");
/** The number of calories. */
const calories = ns$1("calories");
/** A sub property of object. The candidate subject of this action. */
const candidate = ns$1("candidate");
/** The caption for this object. For downloadable machine formats (closed caption, subtitles etc.) use MediaObject and indicate the <a class="localLink" href="http://schema.org/encodingFormat">encodingFormat</a>. */
const caption = ns$1("caption");
/** The number of grams of carbohydrates. */
const carbohydrateContent = ns$1("carbohydrateContent");
/** The available volume for cargo or luggage. For automobiles, this is usually the trunk volume.<br/><br/>

 Typical unit code(s): LTR for liters, FTQ for cubic foot/feet<br/><br/>

 Note: You can use <a class="localLink" href="http://schema.org/minValue">minValue</a> and <a class="localLink" href="http://schema.org/maxValue">maxValue</a> to indicate ranges. */
const cargoVolume = ns$1("cargoVolume");
/** 'carrier' is an out-dated term indicating the 'provider' for parcel delivery and flights. */
const carrier = ns$1("carrier");
/** Specifies specific carrier(s) requirements for the application (e.g. an application may only work on a specific carrier network). */
const carrierRequirements = ns$1("carrierRequirements");
/** A data catalog which contains this dataset. */
const catalog = ns$1("catalog");
/** The catalog number for the release. */
const catalogNumber = ns$1("catalogNumber");
/** A category for the item. Greater signs or slashes can be used to informally indicate a category hierarchy. */
const category = ns$1("category");
/** A sub property of recipient. The recipient copied on a message. */
const ccRecipient = ns$1("ccRecipient");
/** Fictional person connected with a creative work. */
const character = ns$1("character");
/** A piece of data that represents a particular aspect of a fictional character (skill, power, character points, advantage, disadvantage). */
const characterAttribute = ns$1("characterAttribute");
/** The name of a character played in some acting or performing role, i.e. in a PerformanceRole. */
const characterName = ns$1("characterName");
/** Cheat codes to the game. */
const cheatCode = ns$1("cheatCode");
/** The earliest someone may check into a lodging establishment. */
const checkinTime = ns$1("checkinTime");
/** The latest someone may check out of a lodging establishment. */
const checkoutTime = ns$1("checkoutTime");
/** Maximal age of the child. */
const childMaxAge = ns$1("childMaxAge");
/** Minimal age of the child. */
const childMinAge = ns$1("childMinAge");
/** A child of the person. */
const children = ns$1("children");
/** The number of milligrams of cholesterol. */
const cholesterolContent = ns$1("cholesterolContent");
/** A circle is the circular region of a specified radius centered at a specified latitude and longitude. A circle is expressed as a pair followed by a radius in meters. */
const circle = ns$1("circle");
/** A citation or reference to another creative work, such as another publication, web page, scholarly article, etc. */
const citation = ns$1("citation");
/** A short summary of the specific claims reviewed in a ClaimReview. */
const claimReviewed = ns$1("claimReviewed");
/** Position of the clip within an ordered group of clips. */
const clipNumber = ns$1("clipNumber");
/** The closing hour of the place or service on the given day(s) of the week. */
const closes = ns$1("closes");
/** A person that acts in a coaching role for a sports team. */
const coach = ns$1("coach");
/** Link to the repository where the un-compiled, human readable code and related code is located (SVN, github, CodePlex). */
const codeRepository = ns$1("codeRepository");
/** A colleague of the person. */
const colleagues = ns$1("colleagues");
/** A sub property of object. The collection target of the action. */
const collection = ns$1("collection");
/** The color of the product. */
const color = ns$1("color");
/** Comments, typically from users. */
const comment = ns$1("comment");
/** The number of comments this CreativeWork (e.g. Article, Question or Answer) has received. This is most applicable to works published in Web sites with commenting system; additional comments may exist elsewhere. */
const commentCount = ns$1("commentCount");
/** The text of the UserComment. */
const commentText = ns$1("commentText");
/** The time at which the UserComment was made. */
const commentTime = ns$1("commentTime");
/** The person or organization who wrote a composition, or who is the composer of a work performed at some event. */
const composer = ns$1("composer");
/** A number that confirms the given order or payment has been received. */
const confirmationNumber = ns$1("confirmationNumber");
/** An option available on this contact point (e.g. a toll-free number or support for hearing-impaired callers). */
const contactOption = ns$1("contactOption");
/** A contact point for a person or organization. */
const contactPoints = ns$1("contactPoints");
/** A person or organization can have different contact points, for different purposes. For example, a sales contact point, a PR contact point and so on. This property is used to specify the kind of contact point. */
const contactType = ns$1("contactType");
/** The basic containment relation between a place and one that contains it. */
const containedIn = ns$1("containedIn");
/** Official rating of a piece of content&#x2014;for example,'MPAA PG-13'. */
const contentRating = ns$1("contentRating");
/** File size in (mega/kilo) bytes. */
const contentSize = ns$1("contentSize");
/** The supported content type(s) for an EntryPoint response. */
const contentType = ns$1("contentType");
/** Actual bytes of the media object, for example the image file or video file. */
const contentUrl = ns$1("contentUrl");
/** A secondary contributor to the CreativeWork or Event. */
const contributor = ns$1("contributor");
/** The time it takes to actually cook the dish, in <a href="http://en.wikipedia.org/wiki/ISO_8601">ISO 8601 duration format</a>. */
const cookTime = ns$1("cookTime");
/** The method of cooking, such as Frying, Steaming, ... */
const cookingMethod = ns$1("cookingMethod");
/** The party holding the legal copyright to the CreativeWork. */
const copyrightHolder = ns$1("copyrightHolder");
/** The year during which the claimed copyright for the CreativeWork was first asserted. */
const copyrightYear = ns$1("copyrightYear");
/** Countries for which the application is not supported. You can also provide the two-letter ISO 3166-1 alpha-2 country code. */
const countriesNotSupported = ns$1("countriesNotSupported");
/** Countries for which the application is supported. You can also provide the two-letter ISO 3166-1 alpha-2 country code. */
const countriesSupported = ns$1("countriesSupported");
/** The country of the principal offices of the production company or individual responsible for the movie or program. */
const countryOfOrigin = ns$1("countryOfOrigin");
/** A sub property of location. The course where this action was taken. */
const course = ns$1("course");
/** The identifier for the <a class="localLink" href="http://schema.org/Course">Course</a> used by the course <a class="localLink" href="http://schema.org/provider">provider</a> (e.g. CS101 or 6.001). */
const courseCode = ns$1("courseCode");
/** The medium or means of delivery of the course instance or the mode of study, either as a text label (e.g. "online", "onsite" or "blended"; "synchronous" or "asynchronous"; "full-time" or "part-time") or as a URL reference to a term from a controlled vocabulary (e.g. https://ceds.ed.gov/element/001311#Asynchronous ). */
const courseMode = ns$1("courseMode");
/** Requirements for taking the Course. May be completion of another <a class="localLink" href="http://schema.org/Course">Course</a> or a textual description like "permission of instructor". Requirements may be a pre-requisite competency, referenced using <a class="localLink" href="http://schema.org/AlignmentObject">AlignmentObject</a>. */
const coursePrerequisites = ns$1("coursePrerequisites");
/** The time when the live blog will stop covering the Event. Note that coverage may continue after the Event concludes. */
const coverageEndTime = ns$1("coverageEndTime");
/** The time when the live blog will begin covering the Event. Note that coverage may begin before the Event's start time. The LiveBlogPosting may also be created before coverage begins. */
const coverageStartTime = ns$1("coverageStartTime");
/** The creator/author of this CreativeWork. This is the same as the Author property for CreativeWork. */
const creator = ns$1("creator");
/** The group the release is credited to if different than the byArtist. For example, Red and Blue is credited to "Stefani Germanotta Band", but by Lady Gaga. */
const creditedTo = ns$1("creditedTo");
/** A CSS selector, e.g. of a <a class="localLink" href="http://schema.org/SpeakableSpecification">SpeakableSpecification</a> or <a class="localLink" href="http://schema.org/WebPageElement">WebPageElement</a>. In the latter case, multiple matches within a page can constitute a single conceptual "Web page element". */
const cssSelector = ns$1("cssSelector");
/** The currency accepted.<br/><br/>

 Use standard formats: <a href="http://en.wikipedia.org/wiki/ISO_4217">ISO 4217 currency format</a> e.g. "USD"; <a href="https://en.wikipedia.org/wiki/List_of_cryptocurrencies">Ticker symbol</a> for cryptocurrencies e.g. "BTC"; well known names for <a href="https://en.wikipedia.org/wiki/Local_exchange_trading_system">Local Exchange Tradings Systems</a> (LETS) and other currency types e.g. "Ithaca HOUR". */
const currenciesAccepted = ns$1("currenciesAccepted");
/** The currency in which the monetary amount is expressed.<br/><br/>

 Use standard formats: <a href="http://en.wikipedia.org/wiki/ISO_4217">ISO 4217 currency format</a> e.g. "USD"; <a href="https://en.wikipedia.org/wiki/List_of_cryptocurrencies">Ticker symbol</a> for cryptocurrencies e.g. "BTC"; well known names for <a href="https://en.wikipedia.org/wiki/Local_exchange_trading_system">Local Exchange Tradings Systems</a> (LETS) and other currency types e.g. "Ithaca HOUR". */
const currency = ns$1("currency");
/** Party placing the order or paying the invoice. */
const customer = ns$1("customer");
/** An item within in a data feed. Data feeds may have many elements. */
const dataFeedElement = ns$1("dataFeedElement");
/** The range of temporal applicability of a dataset, e.g. for a 2011 census dataset, the year 2011 (in ISO 8601 time interval format). */
const datasetTimeInterval = ns$1("datasetTimeInterval");
/** The date on which the CreativeWork was created or the item was added to a DataFeed. */
const dateCreated = ns$1("dateCreated");
/** The datetime the item was removed from the DataFeed. */
const dateDeleted = ns$1("dateDeleted");
/** The date the ticket was issued. */
const dateIssued = ns$1("dateIssued");
/** The date on which the CreativeWork was most recently modified or when the item's entry was modified within a DataFeed. */
const dateModified = ns$1("dateModified");
/** Publication date for the job posting. */
const datePosted = ns$1("datePosted");
/** Date of first broadcast/publication. */
const datePublished = ns$1("datePublished");
/** The date/time at which the message has been read by the recipient if a single recipient exists. */
const dateRead = ns$1("dateRead");
/** The date/time the message was received if a single recipient exists. */
const dateReceived = ns$1("dateReceived");
/** The date/time at which the message was sent. */
const dateSent = ns$1("dateSent");
/** The date of the first registration of the vehicle with the respective public authorities. */
const dateVehicleFirstRegistered = ns$1("dateVehicleFirstRegistered");
/** A <a href="https://en.wikipedia.org/wiki/Dateline">dateline</a> is a brief piece of text included in news articles that describes where and when the story was written or filed though the date is often omitted. Sometimes only a placename is provided.<br/><br/>

 Structured representations of dateline-related information can also be expressed more explicitly using <a class="localLink" href="http://schema.org/locationCreated">locationCreated</a> (which represents where a work was created e.g. where a news report was written).  For location depicted or described in the content, use <a class="localLink" href="http://schema.org/contentLocation">contentLocation</a>.<br/><br/>

 Dateline summaries are oriented more towards human readers than towards automated processing, and can vary substantially. Some examples: "BEIRUT, Lebanon, June 2.", "Paris, France", "December 19, 2017 11:43AM Reporting from Washington", "Beijing/Moscow", "QUEZON CITY, Philippines". */
const dateline = ns$1("dateline");
/** The day of the week for which these opening hours are valid. */
const dayOfWeek = ns$1("dayOfWeek");
/** Date of death. */
const deathDate = ns$1("deathDate");
/** The place where the person died. */
const deathPlace = ns$1("deathPlace");
/** The default value of the input.  For properties that expect a literal, the default is a literal value, for properties that expect an object, it's an ID reference to one of the current values. */
const defaultValue = ns$1("defaultValue");
/** Destination address. */
const deliveryAddress = ns$1("deliveryAddress");
/** The typical delay between the receipt of the order and the goods either leaving the warehouse or being prepared for pickup, in case the delivery method is on site pickup. */
const deliveryLeadTime = ns$1("deliveryLeadTime");
/** A sub property of instrument. The method of delivery. */
const deliveryMethod = ns$1("deliveryMethod");
/** New entry added as the package passes through each leg of its journey (from shipment to final delivery). */
const deliveryStatus = ns$1("deliveryStatus");
/** A relationship between an organization and a department of that organization, also described as an organization (allowing different urls, logos, opening hours). For example: a store with a pharmacy, or a bakery with a cafe. */
const department = ns$1("department");
/** The airport where the flight originates. */
const departureAirport = ns$1("departureAirport");
/** The stop or station from which the bus departs. */
const departureBusStop = ns$1("departureBusStop");
/** Identifier of the flight's departure gate. */
const departureGate = ns$1("departureGate");
/** The platform from which the train departs. */
const departurePlatform = ns$1("departurePlatform");
/** The station from which the train departs. */
const departureStation = ns$1("departureStation");
/** Identifier of the flight's departure terminal. */
const departureTerminal = ns$1("departureTerminal");
/** The expected departure time. */
const departureTime = ns$1("departureTime");
/** Prerequisites needed to fulfill steps in article. */
const dependencies = ns$1("dependencies");
/** The depth of the item. */
const depth = ns$1("depth");
/** Device required to run the application. Used in cases where a specific make/model is required to run the application. */
const device = ns$1("device");
/** A director of e.g. tv, radio, movie, video games etc. content. Directors can be associated with individual items or with a series, episode, clip. */
const directors = ns$1("directors");
/** A sub property of description. A short description of the item used to disambiguate from other, similar items. Information from other properties (in particular, name) may be necessary for the description to be useful for disambiguation. */
const disambiguatingDescription = ns$1("disambiguatingDescription");
/** Any discount applied (to an Order). */
const discount = ns$1("discount");
/** Code used to redeem a discount. */
const discountCode = ns$1("discountCode");
/** The currency of the discount.<br/><br/>

 Use standard formats: <a href="http://en.wikipedia.org/wiki/ISO_4217">ISO 4217 currency format</a> e.g. "USD"; <a href="https://en.wikipedia.org/wiki/List_of_cryptocurrencies">Ticker symbol</a> for cryptocurrencies e.g. "BTC"; well known names for <a href="https://en.wikipedia.org/wiki/Local_exchange_trading_system">Local Exchange Tradings Systems</a> (LETS) and other currency types e.g. "Ithaca HOUR". */
const discountCurrency = ns$1("discountCurrency");
/** Specifies the CreativeWork associated with the UserComment. */
const discusses = ns$1("discusses");
/** A link to the page containing the comments of the CreativeWork. */
const discussionUrl = ns$1("discussionUrl");
/** The date that this organization was dissolved. */
const dissolutionDate = ns$1("dissolutionDate");
/** The distance travelled, e.g. exercising or travelling. */
const distance = ns$1("distance");
/** A downloadable form of this dataset, at a specific location, in a specific format. */
const distribution = ns$1("distribution");
/** The time admission will commence. */
const doorTime = ns$1("doorTime");
/** If the file can be downloaded, URL to download the binary. */
const downloadUrl = ns$1("downloadUrl");
/** The number of downvotes this question, answer or comment has received from the community. */
const downvoteCount = ns$1("downvoteCount");
/** The drive wheel configuration, i.e. which roadwheels will receive torque from the vehicle's engine via the drivetrain. */
const driveWheelConfiguration = ns$1("driveWheelConfiguration");
/** Where a rental car can be dropped off. */
const dropoffLocation = ns$1("dropoffLocation");
/** When a rental car can be dropped off. */
const dropoffTime = ns$1("dropoffTime");
/** The Dun &amp; Bradstreet DUNS number for identifying an organization or business person. */
const duns = ns$1("duns");
/** The duration of the warranty promise. Common unitCode values are ANN for year, MON for months, or DAY for days. */
const durationOfWarranty = ns$1("durationOfWarranty");
/** A media object representing the circumstances while performing this direction. */
const duringMedia = ns$1("duringMedia");
/** Specifies the Person who edited the CreativeWork. */
const editor = ns$1("editor");
/** An alignment to an established educational framework. */
const educationalAlignment = ns$1("educationalAlignment");
/** A description of the qualification, award, certificate, diploma or other educational credential awarded as a consequence of successful completion of this course or program. */
const educationalCredentialAwarded = ns$1("educationalCredentialAwarded");
/** The framework to which the resource being described is aligned. */
const educationalFramework = ns$1("educationalFramework");
/** An educationalRole of an EducationalAudience. */
const educationalRole = ns$1("educationalRole");
/** The purpose of a work in the context of education; for example, 'assignment', 'group work'. */
const educationalUse = ns$1("educationalUse");
/** The elevation of a location (<a href="https://en.wikipedia.org/wiki/World_Geodetic_System">WGS 84</a>). Values may be of the form 'NUMBER UNIT<em>OF</em>MEASUREMENT' (e.g., '1,000 m', '3,200 ft') while numbers alone should be assumed to be a value in meters. */
const elevation = ns$1("elevation");
/** The type(s) of customers for which the given offer is valid. */
const eligibleCustomerType = ns$1("eligibleCustomerType");
/** The duration for which the given offer is valid. */
const eligibleDuration = ns$1("eligibleDuration");
/** The interval and unit of measurement of ordering quantities for which the offer or price specification is valid. This allows e.g. specifying that a certain freight charge is valid only for a certain quantity. */
const eligibleQuantity = ns$1("eligibleQuantity");
/** The ISO 3166-1 (ISO 3166-1 alpha-2) or ISO 3166-2 code, the place, or the GeoShape for the geo-political region(s) for which the offer or delivery charge specification is valid.<br/><br/>

 See also <a class="localLink" href="http://schema.org/ineligibleRegion">ineligibleRegion</a>. */
const eligibleRegion = ns$1("eligibleRegion");
/** The transaction volume, in a monetary unit, for which the offer or price specification is valid, e.g. for indicating a minimal purchasing volume, to express free shipping above a certain order volume, or to limit the acceptance of credit cards to purchases to a certain minimal amount. */
const eligibleTransactionVolume = ns$1("eligibleTransactionVolume");
/** Email address. */
const email = ns$1("email");
/** A URL pointing to a player for a specific video. In general, this is the information in the <code>src</code> element of an <code>embed</code> tag and should not be the same as the content of the <code>loc</code> tag. */
const embedUrl = ns$1("embedUrl");
/** People working for this organization. */
const employees = ns$1("employees");
/** Type of employment (e.g. full-time, part-time, contract, temporary, seasonal, internship). */
const employmentType = ns$1("employmentType");
/** The supported encoding type(s) for an EntryPoint request. */
const encodingType = ns$1("encodingType");
/** A media object that encodes this CreativeWork. */
const encodings = ns$1("encodings");
/** The end date and time of the item (in <a href="http://en.wikipedia.org/wiki/ISO_8601">ISO 8601 date format</a>). */
const endDate = ns$1("endDate");
/** The endTime of something. For a reserved event or service (e.g. FoodEstablishmentReservation), the time that it is expected to end. For actions that span a period of time, when the action was performed. e.g. John wrote a book from January to <em>December</em>. For media, including audio and video, it's the time offset of the end of a clip within a larger file.<br/><br/>

 Note that Event uses startDate/endDate instead of startTime/endTime, even when describing dates with times. This situation may be clarified in future revisions. */
const endTime = ns$1("endTime");
/** A sub property of participant. The person/organization being supported. */
const endorsee = ns$1("endorsee");
/** A sub property of location. The entertainment business where the action occurred. */
const entertainmentBusiness = ns$1("entertainmentBusiness");
/** Position of the episode within an ordered group of episodes. */
const episodeNumber = ns$1("episodeNumber");
/** An episode of a TV/radio series or season. */
const episodes = ns$1("episodes");
/** This ordering relation for qualitative values indicates that the subject is equal to the object. */
const equal = ns$1("equal");
/** For failed actions, more information on the cause of the failure. */
const error = ns$1("error");
/** The estimated cost of the supply or supplies consumed when performing instructions. */
const estimatedCost = ns$1("estimatedCost");
/** The estimated time the flight will take. */
const estimatedFlightDuration = ns$1("estimatedFlightDuration");
/** An estimated salary for a job posting or occupation, based on a variety of variables including, but not limited to industry, job title, and location. Estimated salaries  are often computed by outside organizations rather than the hiring organization, who may not have committed to the estimated value. */
const estimatedSalary = ns$1("estimatedSalary");
/** An eventStatus of an event represents its status; particularly useful when an event is cancelled or rescheduled. */
const eventStatus = ns$1("eventStatus");
/** Upcoming or past events associated with this place or organization. */
const events = ns$1("events");
/** exif data for this object. */
const exifData = ns$1("exifData");
/** The earliest date the package may arrive. */
const expectedArrivalFrom = ns$1("expectedArrivalFrom");
/** The latest date the package may arrive. */
const expectedArrivalUntil = ns$1("expectedArrivalUntil");
/** An Offer which must be accepted before the user can perform the Action. For example, the user may need to buy a movie before being able to watch it. */
const expectsAcceptanceOf = ns$1("expectsAcceptanceOf");
/** Description of skills and experience needed for the position or Occupation. */
const experienceRequirements = ns$1("experienceRequirements");
/** Date the content expires and is no longer useful or available. For example a <a class="localLink" href="http://schema.org/VideoObject">VideoObject</a> or <a class="localLink" href="http://schema.org/NewsArticle">NewsArticle</a> whose availability or relevance is time-limited, or a <a class="localLink" href="http://schema.org/ClaimReview">ClaimReview</a> fact check whose publisher wants to indicate that it may no longer be relevant (or helpful to highlight) after some date. */
const expires = ns$1("expires");
/** Family name. In the U.S., the last name of an Person. This can be used along with givenName instead of the name property. */
const familyName = ns$1("familyName");
/** The number of grams of fat. */
const fatContent = ns$1("fatContent");
/** The fax number. */
const faxNumber = ns$1("faxNumber");
/** Features or modules provided by this application (and possibly required by other applications). */
const featureList = ns$1("featureList");
/** Description of fees, commissions, and other terms applied either to a class of financial product, or by a financial service organization. */
const feesAndCommissionsSpecification = ns$1("feesAndCommissionsSpecification");
/** The number of grams of fiber. */
const fiberContent = ns$1("fiberContent");
/** Media type, typically MIME format (see <a href="http://www.iana.org/assignments/media-types/media-types.xhtml">IANA site</a>) of the content e.g. application/zip of a SoftwareApplication binary. In cases where a CreativeWork has several media type representations, 'encoding' can be used to indicate each MediaObject alongside particular fileFormat information. Unregistered or niche file formats can be indicated instead via the most appropriate URL, e.g. defining Web page or a Wikipedia entry. */
const fileFormat = ns$1("fileFormat");
/** Size of the application / package (e.g. 18MB). In the absence of a unit (MB, KB etc.), KB will be assumed. */
const fileSize = ns$1("fileSize");
/** The date and place the work was first performed. */
const firstPerformance = ns$1("firstPerformance");
/** The distance of the flight. */
const flightDistance = ns$1("flightDistance");
/** The unique identifier for a flight including the airline IATA code. For example, if describing United flight 110, where the IATA code for United is 'UA', the flightNumber is 'UA110'. */
const flightNumber = ns$1("flightNumber");
/** The size of the accommodation, e.g. in square meter or squarefoot.
 Typical unit code(s): MTK for square meter, FTK for square foot, or YDK for square yard */
const floorSize = ns$1("floorSize");
/** A sub property of object. The person or organization being followed. */
const followee = ns$1("followee");
/** The most generic uni-directional social relation. */
const follows = ns$1("follows");
/** A sub property of location. The specific food establishment where the action occurred. */
const foodEstablishment = ns$1("foodEstablishment");
/** A sub property of location. The specific food event where the action occurred. */
const foodEvent = ns$1("foodEvent");
/** A person who founded this organization. */
const founders = ns$1("founders");
/** The date that this organization was founded. */
const foundingDate = ns$1("foundingDate");
/** The place where the Organization was founded. */
const foundingLocation = ns$1("foundingLocation");
/** A flag to signal that the item, event, or place is accessible for free. */
const free = ns$1("free");
/** A sub property of location. The original location of the object or the agent before the action. */
const fromLocation = ns$1("fromLocation");
/** The amount of fuel consumed for traveling a particular distance or temporal duration with the given vehicle (e.g. liters per 100 km).<br/><br/>

 <ul>
 <li>Note 1: There are unfortunately no standard unit codes for liters per 100 km.  Use <a class="localLink" href="http://schema.org/unitText">unitText</a> to indicate the unit of measurement, e.g. L/100 km.</li>
 <li>Note 2: There are two ways of indicating the fuel consumption, <a class="localLink" href="http://schema.org/fuelConsumption">fuelConsumption</a> (e.g. 8 liters per 100 km) and <a class="localLink" href="http://schema.org/fuelEfficiency">fuelEfficiency</a> (e.g. 30 miles per gallon). They are reciprocal.</li>
 <li>Note 3: Often, the absolute value is useful only when related to driving speed ("at 80 km/h") or usage pattern ("city traffic"). You can use <a class="localLink" href="http://schema.org/valueReference">valueReference</a> to link the value for the fuel consumption to another value.</li>
 </ul>
 */
const fuelConsumption = ns$1("fuelConsumption");
/** The distance traveled per unit of fuel used; most commonly miles per gallon (mpg) or kilometers per liter (km/L).<br/><br/>

 <ul>
 <li>Note 1: There are unfortunately no standard unit codes for miles per gallon or kilometers per liter. Use <a class="localLink" href="http://schema.org/unitText">unitText</a> to indicate the unit of measurement, e.g. mpg or km/L.</li>
 <li>Note 2: There are two ways of indicating the fuel consumption, <a class="localLink" href="http://schema.org/fuelConsumption">fuelConsumption</a> (e.g. 8 liters per 100 km) and <a class="localLink" href="http://schema.org/fuelEfficiency">fuelEfficiency</a> (e.g. 30 miles per gallon). They are reciprocal.</li>
 <li>Note 3: Often, the absolute value is useful only when related to driving speed ("at 80 km/h") or usage pattern ("city traffic"). You can use <a class="localLink" href="http://schema.org/valueReference">valueReference</a> to link the value for the fuel economy to another value.</li>
 </ul>
 */
const fuelEfficiency = ns$1("fuelEfficiency");
/** The type of fuel suitable for the engine or engines of the vehicle. If the vehicle has only one engine, this property can be attached directly to the vehicle. */
const fuelType = ns$1("fuelType");
/** A person or organization that supports (sponsors) something through some kind of financial contribution. */
const funder = ns$1("funder");
/** An item is an object within the game world that can be collected by a player or, occasionally, a non-player character. */
const gameItem = ns$1("gameItem");
/** Real or fictional location of the game (or part of game). */
const gameLocation = ns$1("gameLocation");
/** The electronic systems used to play <a href="http://en.wikipedia.org/wiki/Category:Video_game_platforms">video games</a>. */
const gamePlatform = ns$1("gamePlatform");
/** Links to tips, tactics, etc. */
const gameTip = ns$1("gameTip");
/** Gender of the person. While http://schema.org/Male and http://schema.org/Female may be used, text strings are also acceptable for people who do not identify as a binary gender. */
const gender = ns$1("gender");
/** Genre of the creative work, broadcast channel or group. */
const genre = ns$1("genre");
/** The geo coordinates of the place. */
const geo = ns$1("geo");
/** Represents a relationship between two geometries (or the places they represent), relating a containing geometry to a contained geometry. "a contains b iff no points of b lie in the exterior of a, and at least one point of the interior of b lies in the interior of a". As defined in <a href="https://en.wikipedia.org/wiki/DE-9IM">DE-9IM</a>. */
const geoContains = ns$1("geoContains");
/** Represents a relationship between two geometries (or the places they represent), relating a geometry to another that covers it. As defined in <a href="https://en.wikipedia.org/wiki/DE-9IM">DE-9IM</a>. */
const geoCoveredBy = ns$1("geoCoveredBy");
/** Represents a relationship between two geometries (or the places they represent), relating a covering geometry to a covered geometry. "Every point of b is a point of (the interior or boundary of) a". As defined in <a href="https://en.wikipedia.org/wiki/DE-9IM">DE-9IM</a>. */
const geoCovers = ns$1("geoCovers");
/** Represents a relationship between two geometries (or the places they represent), relating a geometry to another that crosses it: "a crosses b: they have some but not all interior points in common, and the dimension of the intersection is less than that of at least one of them". As defined in <a href="https://en.wikipedia.org/wiki/DE-9IM">DE-9IM</a>. */
const geoCrosses = ns$1("geoCrosses");
/** Represents spatial relations in which two geometries (or the places they represent) are topologically disjoint: they have no point in common. They form a set of disconnected geometries." (a symmetric relationship, as defined in <a href="https://en.wikipedia.org/wiki/DE-9IM">DE-9IM</a>) */
const geoDisjoint = ns$1("geoDisjoint");
/** Represents spatial relations in which two geometries (or the places they represent) are topologically equal, as defined in <a href="https://en.wikipedia.org/wiki/DE-9IM">DE-9IM</a>. "Two geometries are topologically equal if their interiors intersect and no part of the interior or boundary of one geometry intersects the exterior of the other" (a symmetric relationship) */
const geoEquals = ns$1("geoEquals");
/** Represents spatial relations in which two geometries (or the places they represent) have at least one point in common. As defined in <a href="https://en.wikipedia.org/wiki/DE-9IM">DE-9IM</a>. */
const geoIntersects = ns$1("geoIntersects");
/** Indicates the GeoCoordinates at the centre of a GeoShape e.g. GeoCircle. */
const geoMidpoint = ns$1("geoMidpoint");
/** Represents a relationship between two geometries (or the places they represent), relating a geometry to another that geospatially overlaps it, i.e. they have some but not all points in common. As defined in <a href="https://en.wikipedia.org/wiki/DE-9IM">DE-9IM</a>. */
const geoOverlaps = ns$1("geoOverlaps");
/** Indicates the approximate radius of a GeoCircle (metres unless indicated otherwise via Distance notation). */
const geoRadius = ns$1("geoRadius");
/** Represents spatial relations in which two geometries (or the places they represent) touch: they have at least one boundary point in common, but no interior points." (a symmetric relationship, as defined in <a href="https://en.wikipedia.org/wiki/DE-9IM">DE-9IM</a> ) */
const geoTouches = ns$1("geoTouches");
/** Represents a relationship between two geometries (or the places they represent), relating a geometry to one that contains it, i.e. it is inside (i.e. within) its interior. As defined in <a href="https://en.wikipedia.org/wiki/DE-9IM">DE-9IM</a>. */
const geoWithin = ns$1("geoWithin");
/** The geographic area associated with the audience. */
const geographicArea = ns$1("geographicArea");
/** Given name. In the U.S., the first name of a Person. This can be used along with familyName instead of the name property. */
const givenName = ns$1("givenName");
/** The <a href="http://www.gs1.org/gln">Global Location Number</a> (GLN, sometimes also referred to as International Location Number or ILN) of the respective organization, person, or place. The GLN is a 13-digit number used to identify parties and physical locations. */
const globalLocationNumber = ns$1("globalLocationNumber");
/** The person, organization, contact point, or audience that has been granted this permission. */
const grantee = ns$1("grantee");
/** This ordering relation for qualitative values indicates that the subject is greater than the object. */
const greater = ns$1("greater");
/** This ordering relation for qualitative values indicates that the subject is greater than or equal to the object. */
const greaterOrEqual = ns$1("greaterOrEqual");
/** The GTIN-12 code of the product, or the product to which the offer refers. The GTIN-12 is the 12-digit GS1 Identification Key composed of a U.P.C. Company Prefix, Item Reference, and Check Digit used to identify trade items. See <a href="http://www.gs1.org/barcodes/technical/idkeys/gtin">GS1 GTIN Summary</a> for more details. */
const gtin12 = ns$1("gtin12");
/** The GTIN-13 code of the product, or the product to which the offer refers. This is equivalent to 13-digit ISBN codes and EAN UCC-13. Former 12-digit UPC codes can be converted into a GTIN-13 code by simply adding a preceeding zero. See <a href="http://www.gs1.org/barcodes/technical/idkeys/gtin">GS1 GTIN Summary</a> for more details. */
const gtin13 = ns$1("gtin13");
/** The GTIN-14 code of the product, or the product to which the offer refers. See <a href="http://www.gs1.org/barcodes/technical/idkeys/gtin">GS1 GTIN Summary</a> for more details. */
const gtin14 = ns$1("gtin14");
/** The <a href="http://apps.gs1.org/GDD/glossary/Pages/GTIN-8.aspx">GTIN-8</a> code of the product, or the product to which the offer refers. This code is also known as EAN/UCC-8 or 8-digit EAN. See <a href="http://www.gs1.org/barcodes/technical/idkeys/gtin">GS1 GTIN Summary</a> for more details. */
const gtin8 = ns$1("gtin8");
/** An offering of the course at a specific time and place or through specific media or mode of study or to a specific section of students. */
const hasCourseInstance = ns$1("hasCourseInstance");
/** Method used for delivery or shipping. */
const hasDeliveryMethod = ns$1("hasDeliveryMethod");
/** A permission related to the access to this document (e.g. permission to read or write an electronic document). For a public document, specify a grantee with an Audience with audienceType equal to "public". */
const hasDigitalDocumentPermission = ns$1("hasDigitalDocumentPermission");
/** A food or drink item contained in a menu or menu section. */
const hasMenuItem = ns$1("hasMenuItem");
/** A subgrouping of the menu (by dishes, course, serving time period, etc.). */
const hasMenuSection = ns$1("hasMenuSection");
/** The Person's occupation. For past professions, use Role for expressing dates. */
const hasOccupation = ns$1("hasOccupation");
/** Indicates an OfferCatalog listing for this Organization, Person, or Service. */
const hasOfferCatalog = ns$1("hasOfferCatalog");
/** Points-of-Sales operated by the organization or person. */
const hasPOS = ns$1("hasPOS");
/** Headline of the article. */
const headline = ns$1("headline");
/** The height of the item. */
const height = ns$1("height");
/** The highest price of all offers available.<br/><br/>

 Usage guidelines:<br/><br/>

 <ul>
 <li>Use values from 0123456789 (Unicode 'DIGIT ZERO' (U+0030) to 'DIGIT NINE' (U+0039)) rather than superficially similiar Unicode symbols.</li>
 <li>Use '.' (Unicode 'FULL STOP' (U+002E)) rather than ',' to indicate a decimal point. Avoid using these symbols as a readability separator.</li>
 </ul>
 */
const highPrice = ns$1("highPrice");
/** Organization offering the job position. */
const hiringOrganization = ns$1("hiringOrganization");
/** A contact location for a person's residence. */
const homeLocation = ns$1("homeLocation");
/** The home team in a sports event. */
const homeTeam = ns$1("homeTeam");
/** An honorific prefix preceding a Person's name such as Dr/Mrs/Mr. */
const honorificPrefix = ns$1("honorificPrefix");
/** An honorific suffix preceding a Person's name such as M.D. /PhD/MSCSW. */
const honorificSuffix = ns$1("honorificSuffix");
/** The organization (airline, travelers' club, etc.) the membership is made with. */
const hostingOrganization = ns$1("hostingOrganization");
/** The hours during which this service or contact is available. */
const hoursAvailable = ns$1("hoursAvailable");
/** An HTTP method that specifies the appropriate HTTP method for a request to an HTTP EntryPoint. Values are capitalized strings as used in HTTP. */
const httpMethod = ns$1("httpMethod");
/** IATA identifier for an airline or airport. */
const iataCode = ns$1("iataCode");
/** ICAO identifier for an airport. */
const icaoCode = ns$1("icaoCode");
/** The illustrator of the book. */
const illustrator = ns$1("illustrator");
/** The album to which this recording belongs. */
const inAlbum = ns$1("inAlbum");
/** The CableOrSatelliteService offering the channel. */
const inBroadcastLineup = ns$1("inBroadcastLineup");
/** The playlist to which this recording belongs. */
const inPlaylist = ns$1("inPlaylist");
/** Description of bonus and commission compensation aspects of the job. */
const incentives = ns$1("incentives");
/** Smaller compositions included in this work (e.g. a movement in a symphony). */
const includedComposition = ns$1("includedComposition");
/** A data catalog which contains this dataset (this property was previously 'catalog', preferred name is now 'includedInDataCatalog'). */
const includedDataCatalog = ns$1("includedDataCatalog");
/** This links to a node or nodes indicating the exact quantity of the products included in the offer. */
const includesObject = ns$1("includesObject");
/** The industry associated with the job position. */
const industry = ns$1("industry");
/** The ISO 3166-1 (ISO 3166-1 alpha-2) or ISO 3166-2 code, the place, or the GeoShape for the geo-political region(s) for which the offer or delivery charge specification is not valid, e.g. a region where the transaction is not allowed.<br/><br/>

 See also <a class="localLink" href="http://schema.org/eligibleRegion">eligibleRegion</a>. */
const ineligibleRegion = ns$1("ineligibleRegion");
/** A single ingredient used in the recipe, e.g. sugar, flour or garlic. */
const ingredients = ns$1("ingredients");
/** URL at which the app may be installed, if different from the URL of the item. */
const installUrl = ns$1("installUrl");
/** A person assigned to instruct or provide instructional assistance for the <a class="localLink" href="http://schema.org/CourseInstance">CourseInstance</a>. */
const instructor = ns$1("instructor");
/** This property is deprecated, alongside the UserInteraction types on which it depended. */
const interactionCount = ns$1("interactionCount");
/** The WebSite or SoftwareApplication where the interactions took place. */
const interactionService = ns$1("interactionService");
/** The Action representing the type of interaction. For up votes, +1s, etc. use <a class="localLink" href="http://schema.org/LikeAction">LikeAction</a>. For down votes use <a class="localLink" href="http://schema.org/DislikeAction">DislikeAction</a>. Otherwise, use the most specific Action. */
const interactionType = ns$1("interactionType");
/** The predominant mode of learning supported by the learning resource. Acceptable values are 'active', 'expositive', or 'mixed'. */
const interactivityType = ns$1("interactivityType");
/** The interest rate, charged or paid, applicable to the financial product. Note: This is different from the calculated annualPercentageRate. */
const interestRate = ns$1("interestRate");
/** The current approximate inventory level for the item or items. */
const inventoryLevel = ns$1("inventoryLevel");
/** A pointer to another product (or multiple products) for which this product is an accessory or spare part. */
const isAccessoryOrSparePartFor = ns$1("isAccessoryOrSparePartFor");
/** A resource that was used in the creation of this resource. This term can be repeated for multiple sources. For example, http://example.com/great-multiplication-intro.html. */
const isBasedOnUrl = ns$1("isBasedOnUrl");
/** A pointer to another product (or multiple products) for which this product is a consumable. */
const isConsumableFor = ns$1("isConsumableFor");
/** Indicates whether this content is family friendly. */
const isFamilyFriendly = ns$1("isFamilyFriendly");
/** Was the offer accepted as a gift for someone other than the buyer. */
const isGift = ns$1("isGift");
/** True is the broadcast is of a live event. */
const isLiveBroadcast = ns$1("isLiveBroadcast");
/** A pointer to another, somehow related product (or multiple products). */
const isRelatedTo = ns$1("isRelatedTo");
/** A pointer to another, functionally similar product (or multiple products). */
const isSimilarTo = ns$1("isSimilarTo");
/** A pointer to a base product from which this product is a variant. It is safe to infer that the variant inherits all product features from the base model, unless defined locally. This is not transitive. */
const isVariantOf = ns$1("isVariantOf");
/** The ISBN of the book. */
const isbn = ns$1("isbn");
/** The International Standard of Industrial Classification of All Economic Activities (ISIC), Revision 4 code for a particular organization, business person, or place. */
const isicV4 = ns$1("isicV4");
/** The International Standard Recording Code for the recording. */
const isrcCode = ns$1("isrcCode");
/** The International Standard Serial Number (ISSN) that identifies this serial publication. You can repeat this property to identify different formats of, or the linking ISSN (ISSN-L) for, this serial publication. */
const issn = ns$1("issn");
/** Identifies the issue of publication; for example, "iii" or "2". */
const issueNumber = ns$1("issueNumber");
/** The organization issuing the ticket or permit. */
const issuedBy = ns$1("issuedBy");
/** The service through with the permit was granted. */
const issuedThrough = ns$1("issuedThrough");
/** The International Standard Musical Work Code for the composition. */
const iswcCode = ns$1("iswcCode");
/** An entity represented by an entry in a list or data feed (e.g. an 'artist' in a list of 'artists')’. */
const item = ns$1("item");
/** A predefined value from OfferItemCondition or a textual description of the condition of the product or service, or the products or services included in the offer. */
const itemCondition = ns$1("itemCondition");
/** For itemListElement values, you can use simple strings (e.g. "Peter", "Paul", "Mary"), existing entities, or use ListItem.<br/><br/>

 Text values are best if the elements in the list are plain strings. Existing entities are best for a simple, unordered list of existing things in your data. ListItem is used with ordered lists when you want to provide additional context about the element in that list or when the same item might be in different places in different lists.<br/><br/>

 Note: The order of elements in your mark-up is not sufficient for indicating the order or elements.  Use ListItem with a 'position' property in such cases. */
const itemListElement = ns$1("itemListElement");
/** Type of ordering (e.g. Ascending, Descending, Unordered). */
const itemListOrder = ns$1("itemListOrder");
/** The item being offered. */
const itemOffered = ns$1("itemOffered");
/** The item that is being reviewed/rated. */
const itemReviewed = ns$1("itemReviewed");
/** Item(s) being shipped. */
const itemShipped = ns$1("itemShipped");
/** A (typically single) geographic location associated with the job position. */
const jobLocation = ns$1("jobLocation");
/** Keywords or tags used to describe this content. Multiple entries in a keywords list are typically delimited by commas. */
const keywords = ns$1("keywords");
/** A textual description of known damages, both repaired and unrepaired. */
const knownVehicleDamages = ns$1("knownVehicleDamages");
/** The most generic bi-directional social/work relation. */
const knows = ns$1("knows");
/** A sub property of participant. The owner of the real estate property. */
const landlord = ns$1("landlord");
/** A sub property of instrument. The language used on this action. */
const language = ns$1("language");
/** Date on which the content on this web page was last reviewed for accuracy and/or completeness. */
const lastReviewed = ns$1("lastReviewed");
/** The latitude of a location. For example <code>37.42242</code> (<a href="https://en.wikipedia.org/wiki/World_Geodetic_System">WGS 84</a>). */
const latitude = ns$1("latitude");
/** The predominant type or kind characterizing the learning resource. For example, 'presentation', 'handout'. */
const learningResourceType = ns$1("learningResourceType");
/** The official name of the organization, e.g. the registered company name. */
const legalName = ns$1("legalName");
/** An organization identifier that uniquely identifies a legal entity as defined in ISO 17442. */
const leiCode = ns$1("leiCode");
/** A sub property of participant. The person that lends the object being borrowed. */
const lender = ns$1("lender");
/** This ordering relation for qualitative values indicates that the subject is lesser than the object. */
const lesser = ns$1("lesser");
/** This ordering relation for qualitative values indicates that the subject is lesser than or equal to the object. */
const lesserOrEqual = ns$1("lesserOrEqual");
/** A license document that applies to this content, typically indicated by URL. */
const license = ns$1("license");
/** A line is a point-to-point path consisting of two or more points. A line is expressed as a series of two or more point objects separated by space. */
const line = ns$1("line");
/** An update to the LiveBlog. */
const liveBlogUpdate = ns$1("liveBlogUpdate");
/** The duration of the loan or credit agreement. */
const loanTerm = ns$1("loanTerm");
/** The location where the CreativeWork was created, which may not be the same as the location depicted in the CreativeWork. */
const locationCreated = ns$1("locationCreated");
/** A full description of the lodging unit. */
const lodgingUnitDescription = ns$1("lodgingUnitDescription");
/** Textual description of the unit type (including suite vs. room, size of bed, etc.). */
const lodgingUnitType = ns$1("lodgingUnitType");
/** An associated logo. */
const logo = ns$1("logo");
/** The longitude of a location. For example <code>-122.08585</code> (<a href="https://en.wikipedia.org/wiki/World_Geodetic_System">WGS 84</a>). */
const longitude = ns$1("longitude");
/** A sub property of participant. The loser of the action. */
const loser = ns$1("loser");
/** The lowest price of all offers available.<br/><br/>

 Usage guidelines:<br/><br/>

 <ul>
 <li>Use values from 0123456789 (Unicode 'DIGIT ZERO' (U+0030) to 'DIGIT NINE' (U+0039)) rather than superficially similiar Unicode symbols.</li>
 <li>Use '.' (Unicode 'FULL STOP' (U+002E)) rather than ',' to indicate a decimal point. Avoid using these symbols as a readability separator.</li>
 </ul>
 */
const lowPrice = ns$1("lowPrice");
/** The person who wrote the words. */
const lyricist = ns$1("lyricist");
/** The words in the song. */
const lyrics = ns$1("lyrics");
/** Indicates if this web page element is the main subject of the page. */
const mainContentOfPage = ns$1("mainContentOfPage");
/** The manufacturer of the product. */
const manufacturer = ns$1("manufacturer");
/** A URL to a map of the place. */
const map = ns$1("map");
/** Indicates the kind of Map, from the MapCategoryType Enumeration. */
const mapType = ns$1("mapType");
/** A URL to a map of the place. */
const maps = ns$1("maps");
/** The highest price if the price is a range. */
const maxPrice = ns$1("maxPrice");
/** The upper value of some characteristic or property. */
const maxValue = ns$1("maxValue");
/** The total number of individuals that may attend an event or venue. */
const maximumAttendeeCapacity = ns$1("maximumAttendeeCapacity");
/** Description of the meals that will be provided or available for purchase. */
const mealService = ns$1("mealService");
/** The median value. */
const median = ns$1("median");
/** A member of this organization. */
const members = ns$1("members");
/** A unique identifier for the membership. */
const membershipNumber = ns$1("membershipNumber");
/** Minimum memory requirements. */
const memoryRequirements = ns$1("memoryRequirements");
/** Indicates that the CreativeWork contains a reference to, but is not necessarily about a concept. */
const mentions = ns$1("mentions");
/** Either the actual menu as a structured representation, as text, or a URL of the menu. */
const menu = ns$1("menu");
/** Additional menu item(s) such as a side dish of salad or side order of fries that can be added to this menu item. Additionally it can be a menu section containing allowed add-on menu items for this menu item. */
const menuAddOn = ns$1("menuAddOn");
/** 'merchant' is an out-dated term for 'seller'. */
const merchant = ns$1("merchant");
/** A CreativeWork attached to the message. */
const messageAttachment = ns$1("messageAttachment");
/** The total distance travelled by the particular vehicle since its initial production, as read from its odometer.<br/><br/>

 Typical unit code(s): KMT for kilometers, SMI for statute miles */
const mileageFromOdometer = ns$1("mileageFromOdometer");
/** The lowest price if the price is a range. */
const minPrice = ns$1("minPrice");
/** The lower value of some characteristic or property. */
const minValue = ns$1("minValue");
/** The minimum payment required at this time. */
const minimumPaymentDue = ns$1("minimumPaymentDue");
/** The model of the product. Use with the URL of a ProductModel or a textual representation of the model identifier. The URL of the ProductModel can be from an external source. It is recommended to additionally provide strong product identifiers via the gtin8/gtin13/gtin14 and mpn properties. */
const model = ns$1("model");
/** The date and time the reservation was modified. */
const modifiedTime = ns$1("modifiedTime");
/** The Manufacturer Part Number (MPN) of the product, or the product to which the offer refers. */
const mpn = ns$1("mpn");
/** Whether multiple values are allowed for the property.  Default is false. */
const multipleValues = ns$1("multipleValues");
/** An arrangement derived from the composition. */
const musicArrangement = ns$1("musicArrangement");
/** The composer of the soundtrack. */
const musicBy = ns$1("musicBy");
/** The type of composition (e.g. overture, sonata, symphony, etc.). */
const musicCompositionForm = ns$1("musicCompositionForm");
/** A member of a music group&#x2014;for example, John, Paul, George, or Ringo. */
const musicGroupMember = ns$1("musicGroupMember");
/** Format of this release (the type of recording media used, ie. compact disc, digital media, LP, etc.). */
const musicReleaseFormat = ns$1("musicReleaseFormat");
/** The key, mode, or scale this composition uses. */
const musicalKey = ns$1("musicalKey");
/** The North American Industry Classification System (NAICS) code for a particular organization or business person. */
const naics = ns$1("naics");
/** The name of the item. */
const name = ns$1("name");
/** A position played, performed or filled by a person or organization, as part of an organization. For example, an athlete in a SportsTeam might play in the position named 'Quarterback'. */
const namedPosition = ns$1("namedPosition");
/** Nationality of the person. */
const nationality = ns$1("nationality");
/** The total financial value of the person as calculated by subtracting assets from liabilities. */
const netWorth = ns$1("netWorth");
/** A link to the ListItem that follows the current one. */
const nextItem = ns$1("nextItem");
/** This ordering relation for qualitative values indicates that the subject is not equal to the object. */
const nonEqual = ns$1("nonEqual");
/** The number of adults staying in the unit. */
const numAdults = ns$1("numAdults");
/** The number of children staying in the unit. */
const numChildren = ns$1("numChildren");
/** The number of tracks in this album or playlist. */
const numTracks = ns$1("numTracks");
/** The number or type of airbags in the vehicle. */
const numberOfAirbags = ns$1("numberOfAirbags");
/** The number of axles.<br/><br/>

 Typical unit code(s): C62 */
const numberOfAxles = ns$1("numberOfAxles");
/** The quantity of the given bed type available in the HotelRoom, Suite, House, or Apartment. */
const numberOfBeds = ns$1("numberOfBeds");
/** The number of doors.<br/><br/>

 Typical unit code(s): C62 */
const numberOfDoors = ns$1("numberOfDoors");
/** The number of employees in an organization e.g. business. */
const numberOfEmployees = ns$1("numberOfEmployees");
/** The number of episodes in this season or series. */
const numberOfEpisodes = ns$1("numberOfEpisodes");
/** The total number of forward gears available for the transmission system of the vehicle.<br/><br/>

 Typical unit code(s): C62 */
const numberOfForwardGears = ns$1("numberOfForwardGears");
/** The number of items in an ItemList. Note that some descriptions might not fully describe all items in a list (e.g., multi-page pagination); in such cases, the numberOfItems would be for the entire list. */
const numberOfItems = ns$1("numberOfItems");
/** The number of pages in the book. */
const numberOfPages = ns$1("numberOfPages");
/** Indicate how many people can play this game (minimum, maximum, or range). */
const numberOfPlayers = ns$1("numberOfPlayers");
/** The number of owners of the vehicle, including the current one.<br/><br/>

 Typical unit code(s): C62 */
const numberOfPreviousOwners = ns$1("numberOfPreviousOwners");
/** The number of rooms (excluding bathrooms and closets) of the accommodation or lodging business.
 Typical unit code(s): ROM for room or C62 for no unit. The type of room can be put in the unitText property of the QuantitativeValue. */
const numberOfRooms = ns$1("numberOfRooms");
/** The number of seasons in this series. */
const numberOfSeasons = ns$1("numberOfSeasons");
/** A number associated with a role in an organization, for example, the number on an athlete's jersey. */
const numberedPosition = ns$1("numberedPosition");
/** Nutrition information about the recipe or menu item. */
const nutrition = ns$1("nutrition");
/** The allowed total occupancy for the accommodation in persons (including infants etc). For individual accommodations, this is not necessarily the legal maximum but defines the permitted usage as per the contractual agreement (e.g. a double room used by a single person).
 Typical unit code(s): C62 for person */
const occupancy = ns$1("occupancy");
/** The region/country for which this occupational description is appropriate. Note that educational requirements and qualifications can vary between jurisdictions. */
const occupationLocation = ns$1("occupationLocation");
/** A category describing the job, preferably using a term from a taxonomy such as <a href="http://www.onetcenter.org/taxonomy.html">BLS O*NET-SOC</a>, <a href="https://www.ilo.org/public/english/bureau/stat/isco/isco08/">ISCO-08</a> or similar, with the property repeated for each applicable value. Ideally the taxonomy should be identified, and both the textual label and formal code for the category should be provided.<br/><br/>

 Note: for historical reasons, any textual label and formal code provided as a literal may be assumed to be from O*NET-SOC. */
const occupationalCategory = ns$1("occupationalCategory");
/** The number of offers for the product. */
const offerCount = ns$1("offerCount");
/** An offer to provide this item&#x2014;for example, an offer to sell a product, rent the DVD of a movie, perform a service, or give away tickets to an event. */
const offers = ns$1("offers");
/** The general opening hours for a business. Opening hours can be specified as a weekly time range, starting with days, then times per day. Multiple days can be listed with commas ',' separating each day. Day or time ranges are specified using a hyphen '-'.<br/><br/>

 <ul>
 <li>Days are specified using the following two-letter combinations: <code>Mo</code>, <code>Tu</code>, <code>We</code>, <code>Th</code>, <code>Fr</code>, <code>Sa</code>, <code>Su</code>.</li>
 <li>Times are specified using 24:00 time. For example, 3pm is specified as <code>15:00</code>. </li>
 <li>Here is an example: <code>&lt;time itemprop="openingHours" datetime=&quot;Tu,Th 16:00-20:00&quot;&gt;Tuesdays and Thursdays 4-8pm&lt;/time&gt;</code>.</li>
 <li>If a business is open 7 days a week, then it can be specified as <code>&lt;time itemprop=&quot;openingHours&quot; datetime=&quot;Mo-Su&quot;&gt;Monday through Sunday, all day&lt;/time&gt;</code>.</li>
 </ul>
 */
const openingHours = ns$1("openingHours");
/** The opening hours of a certain place. */
const openingHoursSpecification = ns$1("openingHoursSpecification");
/** The opening hour of the place or service on the given day(s) of the week. */
const opens = ns$1("opens");
/** Operating systems supported (Windows 7, OSX 10.6, Android 1.6). */
const operatingSystem = ns$1("operatingSystem");
/** A sub property of participant. The opponent on this action. */
const opponent = ns$1("opponent");
/** A sub property of object. The options subject to this action. */
const option = ns$1("option");
/** Date order was placed. */
const orderDate = ns$1("orderDate");
/** The delivery of the parcel related to this order or order item. */
const orderDelivery = ns$1("orderDelivery");
/** The identifier of the order item. */
const orderItemNumber = ns$1("orderItemNumber");
/** The current status of the order item. */
const orderItemStatus = ns$1("orderItemStatus");
/** The identifier of the transaction. */
const orderNumber = ns$1("orderNumber");
/** The number of the item ordered. If the property is not set, assume the quantity is one. */
const orderQuantity = ns$1("orderQuantity");
/** The current status of the order. */
const orderStatus = ns$1("orderStatus");
/** The item ordered. */
const orderedItem = ns$1("orderedItem");
/** An organizer of an Event. */
const organizer = ns$1("organizer");
/** Shipper's address. */
const originAddress = ns$1("originAddress");
/** The date and time of obtaining the product. */
const ownedFrom = ns$1("ownedFrom");
/** The date and time of giving up ownership on the product. */
const ownedThrough = ns$1("ownedThrough");
/** Products owned by the organization or person. */
const owns = ns$1("owns");
/** The page on which the work ends; for example "138" or "xvi". */
const pageEnd = ns$1("pageEnd");
/** The page on which the work starts; for example "135" or "xiii". */
const pageStart = ns$1("pageStart");
/** Any description of pages that is not separated into pageStart and pageEnd; for example, "1-6, 9, 55" or "10-12, 46-49". */
const pagination = ns$1("pagination");
/** The parent of a question, answer or item in general. */
const parentItem = ns$1("parentItem");
/** A broadcast service to which the broadcast service may belong to such as regional variations of a national channel. */
const parentService = ns$1("parentService");
/** A parents of the person. */
const parents = ns$1("parents");
/** The episode to which this clip belongs. */
const partOfEpisode = ns$1("partOfEpisode");
/** The order is being paid as part of the referenced Invoice. */
const partOfInvoice = ns$1("partOfInvoice");
/** The overall order the items in this delivery were included in. */
const partOfOrder = ns$1("partOfOrder");
/** The season to which this episode belongs. */
const partOfSeason = ns$1("partOfSeason");
/** The TV series to which this episode or season belongs. */
const partOfTVSeries = ns$1("partOfTVSeries");
/** Number of people the reservation should accommodate. */
const partySize = ns$1("partySize");
/** The priority status assigned to a passenger for security or boarding (e.g. FastTrack or Priority). */
const passengerPriorityStatus = ns$1("passengerPriorityStatus");
/** The passenger's sequence number as assigned by the airline. */
const passengerSequenceNumber = ns$1("passengerSequenceNumber");
/** Cash, Credit Card, Cryptocurrency, Local Exchange Tradings System, etc. */
const paymentAccepted = ns$1("paymentAccepted");
/** The date that payment is due. */
const paymentDue = ns$1("paymentDue");
/** The name of the credit card or other method of payment for the order. */
const paymentMethod = ns$1("paymentMethod");
/** An identifier for the method of payment used (e.g. the last 4 digits of the credit card). */
const paymentMethodId = ns$1("paymentMethodId");
/** The status of payment; whether the invoice has been paid or not. */
const paymentStatus = ns$1("paymentStatus");
/** The URL for sending a payment. */
const paymentUrl = ns$1("paymentUrl");
/** The 10th percentile value. */
const percentile10 = ns$1("percentile10");
/** The 25th percentile value. */
const percentile25 = ns$1("percentile25");
/** The 75th percentile value. */
const percentile75 = ns$1("percentile75");
/** The 90th percentile value. */
const percentile90 = ns$1("percentile90");
/** Event that this person is a performer or participant in. */
const performerIn = ns$1("performerIn");
/** The main performer or performers of the event&#x2014;for example, a presenter, musician, or actor. */
const performers = ns$1("performers");
/** The type of permission granted the person, organization, or audience. */
const permissionType = ns$1("permissionType");
/** Permission(s) required to run the app (for example, a mobile app may require full internet access or may run only on wifi). */
const permissions = ns$1("permissions");
/** The target audience for this permit. */
const permitAudience = ns$1("permitAudience");
/** Indications regarding the permitted usage of the accommodation. */
const permittedUsage = ns$1("permittedUsage");
/** Indicates whether pets are allowed to enter the accommodation or lodging business. More detailed information can be put in a text value. */
const petsAllowed = ns$1("petsAllowed");
/** Photographs of this place. */
const photos = ns$1("photos");
/** Where a taxi will pick up a passenger or a rental car can be picked up. */
const pickupLocation = ns$1("pickupLocation");
/** When a taxi will pickup a passenger or a rental car can be picked up. */
const pickupTime = ns$1("pickupTime");
/** Indicates whether this game is multi-player, co-op or single-player.  The game can be marked as multi-player, co-op and single-player at the same time. */
const playMode = ns$1("playMode");
/** Player type required&#x2014;for example, Flash or Silverlight. */
const playerType = ns$1("playerType");
/** Number of players on the server. */
const playersOnline = ns$1("playersOnline");
/** A polygon is the area enclosed by a point-to-point path for which the starting and ending points are the same. A polygon is expressed as a series of four or more space delimited points where the first and final points are identical. */
const polygon = ns$1("polygon");
/** The post office box number for PO box addresses. */
const postOfficeBoxNumber = ns$1("postOfficeBoxNumber");
/** The postal code. For example, 94043. */
const postalCode = ns$1("postalCode");
/** Indicates a potential Action, which describes an idealized action in which this thing would play an 'object' role. */
const potentialAction = ns$1("potentialAction");
/** A pointer from a previous, often discontinued variant of the product to its newer variant. */
const predecessorOf = ns$1("predecessorOf");
/** The length of time it takes to prepare the items to be used in instructions or a direction, in <a href="http://en.wikipedia.org/wiki/ISO_8601">ISO 8601 duration format</a>. */
const prepTime = ns$1("prepTime");
/** A link to the ListItem that preceeds the current one. */
const previousItem = ns$1("previousItem");
/** Used in conjunction with eventStatus for rescheduled or cancelled events. This property contains the previously scheduled start date. For rescheduled events, the startDate property should be used for the newly scheduled start date. In the (rare) case of an event that has been postponed and rescheduled multiple times, this field may be repeated. */
const previousStartDate = ns$1("previousStartDate");
/** The offer price of a product, or of a price component when attached to PriceSpecification and its subtypes.<br/><br/>

 Usage guidelines:<br/><br/>

 <ul>
 <li>Use the <a class="localLink" href="http://schema.org/priceCurrency">priceCurrency</a> property (with standard formats: <a href="http://en.wikipedia.org/wiki/ISO_4217">ISO 4217 currency format</a> e.g. "USD"; <a href="https://en.wikipedia.org/wiki/List_of_cryptocurrencies">Ticker symbol</a> for cryptocurrencies e.g. "BTC"; well known names for <a href="https://en.wikipedia.org/wiki/Local_exchange_trading_system">Local Exchange Tradings Systems</a> (LETS) and other currency types e.g. "Ithaca HOUR") instead of including <a href="http://en.wikipedia.org/wiki/Dollar_sign#Currencies_that_use_the_dollar_or_peso_sign">ambiguous symbols</a> such as '$' in the value.</li>
 <li>Use '.' (Unicode 'FULL STOP' (U+002E)) rather than ',' to indicate a decimal point. Avoid using these symbols as a readability separator.</li>
 <li>Note that both <a href="http://www.w3.org/TR/xhtml-rdfa-primer/#using-the-content-attribute">RDFa</a> and Microdata syntax allow the use of a "content=" attribute for publishing simple machine-readable values alongside more human-friendly formatting.</li>
 <li>Use values from 0123456789 (Unicode 'DIGIT ZERO' (U+0030) to 'DIGIT NINE' (U+0039)) rather than superficially similiar Unicode symbols.</li>
 </ul>
 */
const price = ns$1("price");
/** This property links to all <a class="localLink" href="http://schema.org/UnitPriceSpecification">UnitPriceSpecification</a> nodes that apply in parallel for the <a class="localLink" href="http://schema.org/CompoundPriceSpecification">CompoundPriceSpecification</a> node. */
const priceComponent = ns$1("priceComponent");
/** The currency of the price, or a price component when attached to <a class="localLink" href="http://schema.org/PriceSpecification">PriceSpecification</a> and its subtypes.<br/><br/>

 Use standard formats: <a href="http://en.wikipedia.org/wiki/ISO_4217">ISO 4217 currency format</a> e.g. "USD"; <a href="https://en.wikipedia.org/wiki/List_of_cryptocurrencies">Ticker symbol</a> for cryptocurrencies e.g. "BTC"; well known names for <a href="https://en.wikipedia.org/wiki/Local_exchange_trading_system">Local Exchange Tradings Systems</a> (LETS) and other currency types e.g. "Ithaca HOUR". */
const priceCurrency = ns$1("priceCurrency");
/** The price range of the business, for example <code>$$$</code>. */
const priceRange = ns$1("priceRange");
/** One or more detailed price specifications, indicating the unit price and delivery or payment charges. */
const priceSpecification = ns$1("priceSpecification");
/** A short text or acronym indicating multiple price specifications for the same offer, e.g. SRP for the suggested retail price or INVOICE for the invoice price, mostly used in the car industry. */
const priceType = ns$1("priceType");
/** The date after which the price is no longer available. */
const priceValidUntil = ns$1("priceValidUntil");
/** Indicates the main image on the page. */
const primaryImageOfPage = ns$1("primaryImageOfPage");
/** The number of the column in which the NewsArticle appears in the print edition. */
const printColumn = ns$1("printColumn");
/** The edition of the print product in which the NewsArticle appears. */
const printEdition = ns$1("printEdition");
/** If this NewsArticle appears in print, this field indicates the name of the page on which the article is found. Please note that this field is intended for the exact page name (e.g. A5, B18). */
const printPage = ns$1("printPage");
/** If this NewsArticle appears in print, this field indicates the print section in which the article appeared. */
const printSection = ns$1("printSection");
/** Estimated processing time for the service using this channel. */
const processingTime = ns$1("processingTime");
/** Processor architecture required to run the application (e.g. IA64). */
const processorRequirements = ns$1("processorRequirements");
/** The person or organization who produced the work (e.g. music album, movie, tv/radio series etc.). */
const producer = ns$1("producer");
/** The tangible thing generated by the service, e.g. a passport, permit, etc. */
const produces = ns$1("produces");
/** The product identifier, such as ISBN. For example: <code>meta itemprop="productID" content="isbn:123-456-789"</code>. */
const productID = ns$1("productID");
/** The product or service this support contact point is related to (such as product support for a particular product line). This can be a specific product or product line (e.g. "iPhone") or a general category of products or services (e.g. "smartphones"). */
const productSupported = ns$1("productSupported");
/** The production company or studio responsible for the item e.g. series, video game, episode etc. */
const productionCompany = ns$1("productionCompany");
/** The date of production of the item, e.g. vehicle. */
const productionDate = ns$1("productionDate");
/** Proficiency needed for this content; expected values: 'Beginner', 'Expert'. */
const proficiencyLevel = ns$1("proficiencyLevel");
/** Any membership in a frequent flyer, hotel loyalty program, etc. being applied to the reservation. */
const programMembershipUsed = ns$1("programMembershipUsed");
/** The program providing the membership. */
const programName = ns$1("programName");
/** The computer programming language. */
const programmingLanguage = ns$1("programmingLanguage");
/** Indicates whether API is managed or unmanaged. */
const programmingModel = ns$1("programmingModel");
/** A commonly used identifier for the characteristic represented by the property, e.g. a manufacturer or a standard code for a property. propertyID can be
 (1) a prefixed string, mainly meant to be used with standards for product properties; (2) a site-specific, non-prefixed string (e.g. the primary key of the property or the vendor-specific id of the property), or (3)
 a URL indicating the type of the property, either pointing to an external vocabulary, or a Web resource that describes the property (e.g. a glossary entry).
 Standards bodies should promote a standard prefix for the identifiers of properties from their standards. */
const propertyID = ns$1("propertyID");
/** The number of grams of protein. */
const proteinContent = ns$1("proteinContent");
/** Indicates the mobility of a provided service (e.g. 'static', 'dynamic'). */
const providerMobility = ns$1("providerMobility");
/** The service provided by this channel. */
const providesService = ns$1("providesService");
/** A flag to signal that the <a class="localLink" href="http://schema.org/Place">Place</a> is open to public visitors.  If this property is omitted there is no assumed default boolean value */
const publicAccess = ns$1("publicAccess");
/** A publication event associated with the item. */
const publication = ns$1("publication");
/** A broadcast service associated with the publication event. */
const publishedOn = ns$1("publishedOn");
/** The publisher of the creative work. */
const publisher = ns$1("publisher");
/** The publishingPrinciples property indicates (typically via <a class="localLink" href="http://schema.org/URL">URL</a>) a document describing the editorial principles of an <a class="localLink" href="http://schema.org/Organization">Organization</a> (or individual e.g. a <a class="localLink" href="http://schema.org/Person">Person</a> writing a blog) that relate to their activities as a publisher, e.g. ethics or diversity policies. When applied to a <a class="localLink" href="http://schema.org/CreativeWork">CreativeWork</a> (e.g. <a class="localLink" href="http://schema.org/NewsArticle">NewsArticle</a>) the principles are those of the party primarily responsible for the creation of the <a class="localLink" href="http://schema.org/CreativeWork">CreativeWork</a>.<br/><br/>

 While such policies are most typically expressed in natural language, sometimes related information (e.g. indicating a <a class="localLink" href="http://schema.org/funder">funder</a>) can be expressed using schema.org terminology. */
const publishingPrinciples = ns$1("publishingPrinciples");
/** The date the item e.g. vehicle was purchased by the current owner. */
const purchaseDate = ns$1("purchaseDate");
/** A sub property of instrument. The query used on this action. */
const query = ns$1("query");
/** The task that a player-controlled character, or group of characters may complete in order to gain a reward. */
const quest = ns$1("quest");
/** A sub property of object. A question. */
const question = ns$1("question");
/** The count of total number of ratings. */
const ratingCount = ns$1("ratingCount");
/** The rating for the content.<br/><br/>

 Usage guidelines:<br/><br/>

 <ul>
 <li>Use values from 0123456789 (Unicode 'DIGIT ZERO' (U+0030) to 'DIGIT NINE' (U+0039)) rather than superficially similiar Unicode symbols.</li>
 <li>Use '.' (Unicode 'FULL STOP' (U+002E)) rather than ',' to indicate a decimal point. Avoid using these symbols as a readability separator.</li>
 </ul>
 */
const ratingValue = ns$1("ratingValue");
/** Whether or not a property is mutable.  Default is false. Specifying this for a property that also has a value makes it act similar to a "hidden" input in an HTML form. */
const readonlyValue = ns$1("readonlyValue");
/** A sub property of participant. The real estate agent involved in the action. */
const realEstateAgent = ns$1("realEstateAgent");
/** A sub property of instrument. The recipe/instructions used to perform the action. */
const recipe = ns$1("recipe");
/** The category of the recipe—for example, appetizer, entree, etc. */
const recipeCategory = ns$1("recipeCategory");
/** The cuisine of the recipe (for example, French or Ethiopian). */
const recipeCuisine = ns$1("recipeCuisine");
/** A step in making the recipe, in the form of a single item (document, video, etc.) or an ordered list with HowToStep and/or HowToSection items. */
const recipeInstructions = ns$1("recipeInstructions");
/** The quantity produced by the recipe (for example, number of people served, number of servings, etc). */
const recipeYield = ns$1("recipeYield");
/** The label that issued the release. */
const recordLabel = ns$1("recordLabel");
/** The reference quantity for which a certain price applies, e.g. 1 EUR per 4 kWh of electricity. This property is a replacement for unitOfMeasurement for the advanced cases where the price does not relate to a standard unit. */
const referenceQuantity = ns$1("referenceQuantity");
/** The Order(s) related to this Invoice. One or more Orders may be combined into a single Invoice. */
const referencesOrder = ns$1("referencesOrder");
/** The regions where the media is allowed. If not specified, then it's assumed to be allowed everywhere. Specify the countries in <a href="http://en.wikipedia.org/wiki/ISO_3166">ISO 3166 format</a>. */
const regionsAllowed = ns$1("regionsAllowed");
/** A link related to this web page, for example to other related web pages. */
const relatedLink = ns$1("relatedLink");
/** The most generic familial relation. */
const relatedTo = ns$1("relatedTo");
/** The release date of a product or product model. This can be used to distinguish the exact variant of a product. */
const releaseDate = ns$1("releaseDate");
/** Description of what changed in this version. */
const releaseNotes = ns$1("releaseNotes");
/** The place and time the release was issued, expressed as a PublicationEvent. */
const releasedEvent = ns$1("releasedEvent");
/** The Occupation for the JobPosting. */
const relevantOccupation = ns$1("relevantOccupation");
/** The number of attendee places for an event that remain unallocated. */
const remainingAttendeeCapacity = ns$1("remainingAttendeeCapacity");
/** A sub property of object. The object that is being replaced. */
const replacee = ns$1("replacee");
/** A sub property of object. The object that replaces. */
const replacer = ns$1("replacer");
/** The URL at which a reply may be posted to the specified UserComment. */
const replyToUrl = ns$1("replyToUrl");
/** The number or other unique designator assigned to a Report by the publishing organization. */
const reportNumber = ns$1("reportNumber");
/** Indicates whether this image is representative of the content of the page. */
const representativeOfPage = ns$1("representativeOfPage");
/** Assets required to secure loan or credit repayments. It may take form of third party pledge, goods, financial instruments (cash, securities, etc.) */
const requiredCollateral = ns$1("requiredCollateral");
/** Audiences defined by a person's gender. */
const requiredGender = ns$1("requiredGender");
/** Audiences defined by a person's maximum age. */
const requiredMaxAge = ns$1("requiredMaxAge");
/** Audiences defined by a person's minimum age. */
const requiredMinAge = ns$1("requiredMinAge");
/** The required quantity of the item(s). */
const requiredQuantity = ns$1("requiredQuantity");
/** Component dependency requirements for application. This includes runtime environments and shared libraries that are not included in the application distribution package, but required to run the application (Examples: DirectX, Java or .NET runtime). */
const requirements = ns$1("requirements");
/** Indicates if use of the media require a subscription  (either paid or free). Allowed values are <code>true</code> or <code>false</code> (note that an earlier version had 'yes', 'no'). */
const requiresSubscription = ns$1("requiresSubscription");
/** The thing -- flight, event, restaurant,etc. being reserved. */
const reservationFor = ns$1("reservationFor");
/** A unique identifier for the reservation. */
const reservationId = ns$1("reservationId");
/** The current status of the reservation. */
const reservationStatus = ns$1("reservationStatus");
/** A ticket associated with the reservation. */
const reservedTicket = ns$1("reservedTicket");
/** Responsibilities associated with this role or Occupation. */
const responsibilities = ns$1("responsibilities");
/** A sub property of result. The Comment created or sent as a result of this action. */
const resultComment = ns$1("resultComment");
/** A sub property of result. The review that resulted in the performing of the action. */
const resultReview = ns$1("resultReview");
/** This Review or Rating is relevant to this part or facet of the itemReviewed. */
const reviewAspect = ns$1("reviewAspect");
/** The actual body of the review. */
const reviewBody = ns$1("reviewBody");
/** The count of total number of reviews. */
const reviewCount = ns$1("reviewCount");
/** The rating given in this review. Note that reviews can themselves be rated. The <code>reviewRating</code> applies to rating given by the review. The <a class="localLink" href="http://schema.org/aggregateRating">aggregateRating</a> property applies to the review itself, as a creative work. */
const reviewRating = ns$1("reviewRating");
/** People or organizations that have reviewed the content on this web page for accuracy and/or completeness. */
const reviewedBy = ns$1("reviewedBy");
/** Review of the item. */
const reviews = ns$1("reviews");
/** The response (yes, no, maybe) to the RSVP. */
const rsvpResponse = ns$1("rsvpResponse");
/** Runtime platform or script interpreter dependencies (Example - Java v1, Python2.3, .Net Framework 3.0). */
const runtime = ns$1("runtime");
/** The currency (coded using <a href="http://en.wikipedia.org/wiki/ISO_4217">ISO 4217</a> ) used for the main salary information in this job posting or for this employee. */
const salaryCurrency = ns$1("salaryCurrency");
/** URL of a reference Web page that unambiguously indicates the item's identity. E.g. the URL of the item's Wikipedia page, Wikidata entry, or official website. */
const sameAs = ns$1("sameAs");
/** What type of code sample: full (compile ready) solution, code snippet, inline code, scripts, template. */
const sampleType = ns$1("sampleType");
/** The number of grams of saturated fat. */
const saturatedFatContent = ns$1("saturatedFatContent");
/** The date the invoice is scheduled to be paid. */
const scheduledPaymentDate = ns$1("scheduledPaymentDate");
/** The time the object is scheduled to. */
const scheduledTime = ns$1("scheduledTime");
/** Indicates (by URL or string) a particular version of a schema used in some CreativeWork. For example, a document could declare a schemaVersion using an URL such as http://schema.org/version/2.0/ if precise indication of schema version was required by some application. */
const schemaVersion = ns$1("schemaVersion");
/** The number of screens in the movie theater. */
const screenCount = ns$1("screenCount");
/** A link to a screenshot image of the app. */
const screenshot = ns$1("screenshot");
/** Position of the season within an ordered group of seasons. */
const seasonNumber = ns$1("seasonNumber");
/** A season in a media series. */
const seasons = ns$1("seasons");
/** The location of the reserved seat (e.g., 27). */
const seatNumber = ns$1("seatNumber");
/** The row location of the reserved seat (e.g., B). */
const seatRow = ns$1("seatRow");
/** The section location of the reserved seat (e.g. Orchestra). */
const seatSection = ns$1("seatSection");
/** The type/class of the seat. */
const seatingType = ns$1("seatingType");
/** The type of security screening the passenger is subject to. */
const securityScreening = ns$1("securityScreening");
/** A pointer to products or services sought by the organization or person (demand). */
const seeks = ns$1("seeks");
/** A sub property of participant. The participant who is at the sending end of the action. */
const sender = ns$1("sender");
/** Status of a game server. */
const serverStatus = ns$1("serverStatus");
/** The cuisine of the restaurant. */
const servesCuisine = ns$1("servesCuisine");
/** The audience eligible for this service. */
const serviceAudience = ns$1("serviceAudience");
/** The location (e.g. civic structure, local business, etc.) where a person can go to access the service. */
const serviceLocation = ns$1("serviceLocation");
/** The operating organization, if different from the provider.  This enables the representation of services that are provided by an organization, but operated by another organization like a subcontractor. */
const serviceOperator = ns$1("serviceOperator");
/** The phone number to use to access the service. */
const servicePhone = ns$1("servicePhone");
/** The address for accessing the service by mail. */
const servicePostalAddress = ns$1("servicePostalAddress");
/** The number to access the service by text message. */
const serviceSmsNumber = ns$1("serviceSmsNumber");
/** The type of service being offered, e.g. veterans' benefits, emergency relief, etc. */
const serviceType = ns$1("serviceType");
/** The website to access the service. */
const serviceUrl = ns$1("serviceUrl");
/** The serving size, in terms of the number of volume or mass. */
const servingSize = ns$1("servingSize");
/** A CreativeWork such as an image, video, or audio clip shared as part of this posting. */
const sharedContent = ns$1("sharedContent");
/** A sibling of the person. */
const siblings = ns$1("siblings");
/** The most significant URLs on the page. Typically, these are the non-navigation links that are clicked on the most. */
const significantLinks = ns$1("significantLinks");
/** Skills required to fulfill this role or in this Occupation. */
const skills = ns$1("skills");
/** The Stock Keeping Unit (SKU), i.e. a merchant-specific identifier for a product or service, or the product to which the offer refers. */
const sku = ns$1("sku");
/** A slogan or motto associated with the item. */
const slogan = ns$1("slogan");
/** Indicates whether it is allowed to smoke in the place, e.g. in the restaurant, hotel or hotel room. */
const smokingAllowed = ns$1("smokingAllowed");
/** The number of milligrams of sodium. */
const sodiumContent = ns$1("sodiumContent");
/** Additional content for a software application. */
const softwareAddOn = ns$1("softwareAddOn");
/** Software application help. */
const softwareHelp = ns$1("softwareHelp");
/** Version of the software instance. */
const softwareVersion = ns$1("softwareVersion");
/** The Organization on whose behalf the creator was working. */
const sourceOrganization = ns$1("sourceOrganization");
/** The "spatial" property can be used in cases when more specific properties
 (e.g. <a class="localLink" href="http://schema.org/locationCreated">locationCreated</a>, <a class="localLink" href="http://schema.org/spatialCoverage">spatialCoverage</a>, <a class="localLink" href="http://schema.org/contentLocation">contentLocation</a>) are not known to be appropriate. */
const spatial = ns$1("spatial");
/** The spatialCoverage of a CreativeWork indicates the place(s) which are the focus of the content. It is a subproperty of
 contentLocation intended primarily for more technical and detailed materials. For example with a Dataset, it indicates
 areas that the dataset describes: a dataset of New York weather would have spatialCoverage which was the place: the state of New York. */
const spatialCoverage = ns$1("spatialCoverage");
/** Indicates sections of a Web page that are particularly 'speakable' in the sense of being highlighted as being especially appropriate for text-to-speech conversion. Other sections of a page may also be usefully spoken in particular circumstances; the 'speakable' property serves to indicate the parts most likely to be generally useful for speech.<br/><br/>

 The <em>speakable</em> property can be repeated an arbitrary number of times, with three kinds of possible 'content-locator' values:<br/><br/>

 1.) <em>id-value</em> URL references - uses <em>id-value</em> of an element in the page being annotated. The simplest use of <em>speakable</em> has (potentially relative) URL values, referencing identified sections of the document concerned.<br/><br/>

 2.) CSS Selectors - addresses content in the annotated page, eg. via class attribute. Use the <a class="localLink" href="http://schema.org/cssSelector">cssSelector</a> property.<br/><br/>

 3.)  XPaths - addresses content via XPaths (assuming an XML view of the content). Use the <a class="localLink" href="http://schema.org/xpath">xpath</a> property.<br/><br/>

 For more sophisticated markup of speakable sections beyond simple ID references, either CSS selectors or XPath expressions to pick out document section(s) as speakable. For this
 we define a supporting type, <a class="localLink" href="http://schema.org/SpeakableSpecification">SpeakableSpecification</a>  which is defined to be a possible value of the <em>speakable</em> property. */
const speakable = ns$1("speakable");
/** Any special commitments associated with this job posting. Valid entries include VeteranCommit, MilitarySpouseCommit, etc. */
const specialCommitments = ns$1("specialCommitments");
/** The special opening hours of a certain place.<br/><br/>

 Use this to explicitly override general opening hours brought in scope by <a class="localLink" href="http://schema.org/openingHoursSpecification">openingHoursSpecification</a> or <a class="localLink" href="http://schema.org/openingHours">openingHours</a>. */
const specialOpeningHoursSpecification = ns$1("specialOpeningHoursSpecification");
/** One of the domain specialities to which this web page's content applies. */
const specialty = ns$1("specialty");
/** A type of sport (e.g. Baseball). */
const sport = ns$1("sport");
/** A sub property of location. The sports activity location where this action occurred. */
const sportsActivityLocation = ns$1("sportsActivityLocation");
/** A sub property of location. The sports event where this action occurred. */
const sportsEvent = ns$1("sportsEvent");
/** A sub property of participant. The sports team that participated on this action. */
const sportsTeam = ns$1("sportsTeam");
/** The person's spouse. */
const spouse = ns$1("spouse");
/** An official rating for a lodging business or food establishment, e.g. from national associations or standards bodies. Use the author property to indicate the rating organization, e.g. as an Organization with name such as (e.g. HOTREC, DEHOGA, WHR, or Hotelstars). */
const starRating = ns$1("starRating");
/** The start date and time of the item (in <a href="http://en.wikipedia.org/wiki/ISO_8601">ISO 8601 date format</a>). */
const startDate = ns$1("startDate");
/** The startTime of something. For a reserved event or service (e.g. FoodEstablishmentReservation), the time that it is expected to start. For actions that span a period of time, when the action was performed. e.g. John wrote a book from <em>January</em> to December. For media, including audio and video, it's the time offset of the start of a clip within a larger file.<br/><br/>

 Note that Event uses startDate/endDate instead of startTime/endTime, even when describing dates with times. This situation may be clarified in future revisions. */
const startTime = ns$1("startTime");
/** The position of the steering wheel or similar device (mostly for cars). */
const steeringPosition = ns$1("steeringPosition");
/** The stepValue attribute indicates the granularity that is expected (and required) of the value in a PropertyValueSpecification. */
const stepValue = ns$1("stepValue");
/** A single step item (as HowToStep, text, document, video, etc.) or a HowToSection (originally misnamed 'steps'; 'step' is preferred). */
const steps = ns$1("steps");
/** Storage requirements (free space required). */
const storageRequirements = ns$1("storageRequirements");
/** The street address. For example, 1600 Amphitheatre Pkwy. */
const streetAddress = ns$1("streetAddress");
/** Events that are a part of this event. For example, a conference event includes many presentations, each subEvents of the conference. */
const subEvents = ns$1("subEvents");
/** The individual reservations included in the package. Typically a repeated property. */
const subReservation = ns$1("subReservation");
/** Languages in which subtitles/captions are available, in <a href="http://tools.ietf.org/html/bcp47">IETF BCP 47 standard format</a>. */
const subtitleLanguage = ns$1("subtitleLanguage");
/** A pointer from a newer variant of a product  to its previous, often discontinued predecessor. */
const successorOf = ns$1("successorOf");
/** The number of grams of sugar. */
const sugarContent = ns$1("sugarContent");
/** The gender of the person or audience. */
const suggestedGender = ns$1("suggestedGender");
/** Maximal age recommended for viewing content. */
const suggestedMaxAge = ns$1("suggestedMaxAge");
/** Minimal age recommended for viewing content. */
const suggestedMinAge = ns$1("suggestedMinAge");
/** Indicates a dietary restriction or guideline for which this recipe or menu item is suitable, e.g. diabetic, halal etc. */
const suitableForDiet = ns$1("suitableForDiet");
/** Supporting data for a SoftwareApplication. */
const supportingData = ns$1("supportingData");
/** A material used as a surface in some artwork, e.g. Canvas, Paper, Wood, Board, etc. */
const surface = ns$1("surface");
/** Indicates a target EntryPoint for an Action. */
const target = ns$1("target");
/** The description of a node in an established educational framework. */
const targetDescription = ns$1("targetDescription");
/** The name of a node in an established educational framework. */
const targetName = ns$1("targetName");
/** Type of app development: phone, Metro style, desktop, XBox, etc. */
const targetPlatform = ns$1("targetPlatform");
/** Target Operating System / Product to which the code applies.  If applies to several versions, just the product name can be used. */
const targetProduct = ns$1("targetProduct");
/** The URL of a node in an established educational framework. */
const targetUrl = ns$1("targetUrl");
/** The Tax / Fiscal ID of the organization or person, e.g. the TIN in the US or the CIF/NIF in Spain. */
const taxID = ns$1("taxID");
/** The telephone number. */
const telephone = ns$1("telephone");
/** The "temporal" property can be used in cases where more specific properties
 (e.g. <a class="localLink" href="http://schema.org/temporalCoverage">temporalCoverage</a>, <a class="localLink" href="http://schema.org/dateCreated">dateCreated</a>, <a class="localLink" href="http://schema.org/dateModified">dateModified</a>, <a class="localLink" href="http://schema.org/datePublished">datePublished</a>) are not known to be appropriate. */
const temporal = ns$1("temporal");
/** The textual content of this CreativeWork. */
const text = ns$1("text");
/** Thumbnail image for an image or video. */
const thumbnail = ns$1("thumbnail");
/** A thumbnail image relevant to the Thing. */
const thumbnailUrl = ns$1("thumbnailUrl");
/** The exchange traded instrument associated with a Corporation object. The tickerSymbol is expressed as an exchange and an instrument name separated by a space character. For the exchange component of the tickerSymbol attribute, we recommend using the controlled vocabulary of Market Identifier Codes (MIC) specified in ISO15022. */
const tickerSymbol = ns$1("tickerSymbol");
/** The unique identifier for the ticket. */
const ticketNumber = ns$1("ticketNumber");
/** Reference to an asset (e.g., Barcode, QR code image or PDF) usable for entrance. */
const ticketToken = ns$1("ticketToken");
/** The seat associated with the ticket. */
const ticketedSeat = ns$1("ticketedSeat");
/** Approximate or typical time it takes to work with or through this learning resource for the typical intended target audience, e.g. 'PT30M', 'PT1H25M'. */
const timeRequired = ns$1("timeRequired");
/** The title of the job. */
const title = ns$1("title");
/** A sub property of location. The final location of the object or the agent after the action. */
const toLocation = ns$1("toLocation");
/** A sub property of recipient. The recipient who was directly sent the message. */
const toRecipient = ns$1("toRecipient");
/** A sub property of instrument. An object used (but not consumed) when performing instructions or a direction. */
const tool = ns$1("tool");
/** The total amount due. */
const totalPaymentDue = ns$1("totalPaymentDue");
/** The total price for the reservation or ticket, including applicable taxes, shipping, etc.<br/><br/>

 Usage guidelines:<br/><br/>

 <ul>
 <li>Use values from 0123456789 (Unicode 'DIGIT ZERO' (U+0030) to 'DIGIT NINE' (U+0039)) rather than superficially similiar Unicode symbols.</li>
 <li>Use '.' (Unicode 'FULL STOP' (U+002E)) rather than ',' to indicate a decimal point. Avoid using these symbols as a readability separator.</li>
 </ul>
 */
const totalPrice = ns$1("totalPrice");
/** The total time required to perform instructions or a direction (including time to prepare the supplies), in <a href="http://en.wikipedia.org/wiki/ISO_8601">ISO 8601 duration format</a>. */
const totalTime = ns$1("totalTime");
/** Attraction suitable for type(s) of tourist. eg. Children, visitors from a particular country, etc. */
const touristType = ns$1("touristType");
/** Shipper tracking number. */
const trackingNumber = ns$1("trackingNumber");
/** Tracking url for the parcel delivery. */
const trackingUrl = ns$1("trackingUrl");
/** A music recording (track)&#x2014;usually a single song. */
const tracks = ns$1("tracks");
/** The trailer of a movie or tv/radio series, season, episode, etc. */
const trailer = ns$1("trailer");
/** The name of the train (e.g. The Orient Express). */
const trainName = ns$1("trainName");
/** The unique identifier for the train. */
const trainNumber = ns$1("trainNumber");
/** The number of grams of trans fat. */
const transFatContent = ns$1("transFatContent");
/** If this MediaObject is an AudioObject or VideoObject, the transcript of that object. */
const transcript = ns$1("transcript");
/** Organization or person who adapts a creative work to different languages, regional differences and technical requirements of a target market, or that translates during some event. */
const translator = ns$1("translator");
/** The type of bed to which the BedDetail refers, i.e. the type of bed available in the quantity indicated by quantity. */
const typeOfBed = ns$1("typeOfBed");
/** The product that this structured value is referring to. */
const typeOfGood = ns$1("typeOfGood");
/** The typical expected age range, e.g. '7-9', '11-'. */
const typicalAgeRange = ns$1("typicalAgeRange");
/** The person or organization the reservation or ticket is for. */
const underName = ns$1("underName");
/** The unit of measurement given using the UN/CEFACT Common Code (3 characters) or a URL. Other codes than the UN/CEFACT Common Code may be used with a prefix followed by a colon. */
const unitCode = ns$1("unitCode");
/** A string or text indicating the unit of measurement. Useful if you cannot provide a standard unit code for
 <a href='unitCode'>unitCode</a>. */
const unitText = ns$1("unitText");
/** The number of grams of unsaturated fat. */
const unsaturatedFatContent = ns$1("unsaturatedFatContent");
/** Date when this media object was uploaded to this site. */
const uploadDate = ns$1("uploadDate");
/** The number of upvotes this question, answer or comment has received from the community. */
const upvoteCount = ns$1("upvoteCount");
/** URL of the item. */
const url = ns$1("url");
/** An url template (RFC6570) that will be used to construct the target of the execution of the action. */
const urlTemplate = ns$1("urlTemplate");
/** The number of interactions for the CreativeWork using the WebSite or SoftwareApplication. */
const userInteractionCount = ns$1("userInteractionCount");
/** The duration of validity of a permit or similar thing. */
const validFor = ns$1("validFor");
/** The date when the item becomes valid. */
const validFrom = ns$1("validFrom");
/** The geographic area where a permit or similar thing is valid. */
const validIn = ns$1("validIn");
/** The date after when the item is not valid. For example the end of an offer, salary period, or a period of opening hours. */
const validThrough = ns$1("validThrough");
/** The date when the item is no longer valid. */
const validUntil = ns$1("validUntil");
/** The value of the quantitative value or property value node.<br/><br/>

 <ul>
 <li>For <a class="localLink" href="http://schema.org/QuantitativeValue">QuantitativeValue</a> and <a class="localLink" href="http://schema.org/MonetaryAmount">MonetaryAmount</a>, the recommended type for values is 'Number'.</li>
 <li>For <a class="localLink" href="http://schema.org/PropertyValue">PropertyValue</a>, it can be 'Text;', 'Number', 'Boolean', or 'StructuredValue'.</li>
 <li>Use values from 0123456789 (Unicode 'DIGIT ZERO' (U+0030) to 'DIGIT NINE' (U+0039)) rather than superficially similiar Unicode symbols.</li>
 <li>Use '.' (Unicode 'FULL STOP' (U+002E)) rather than ',' to indicate a decimal point. Avoid using these symbols as a readability separator.</li>
 </ul>
 */
const value$1 = ns$1("value");
/** Specifies whether the applicable value-added tax (VAT) is included in the price specification or not. */
const valueAddedTaxIncluded = ns$1("valueAddedTaxIncluded");
/** Specifies the allowed range for number of characters in a literal value. */
const valueMaxLength = ns$1("valueMaxLength");
/** Specifies the minimum allowed range for number of characters in a literal value. */
const valueMinLength = ns$1("valueMinLength");
/** Indicates the name of the PropertyValueSpecification to be used in URL templates and form encoding in a manner analogous to HTML's input@name. */
const valueName = ns$1("valueName");
/** Specifies a regular expression for testing literal values according to the HTML spec. */
const valuePattern = ns$1("valuePattern");
/** A pointer to a secondary value that provides additional information on the original value, e.g. a reference temperature. */
const valueReference = ns$1("valueReference");
/** Whether the property must be filled in to complete the action.  Default is false. */
const valueRequired = ns$1("valueRequired");
/** The Value-added Tax ID of the organization or person. */
const vatID = ns$1("vatID");
/** A short text indicating the configuration of the vehicle, e.g. '5dr hatchback ST 2.5 MT 225 hp' or 'limited edition'. */
const vehicleConfiguration = ns$1("vehicleConfiguration");
/** Information about the engine or engines of the vehicle. */
const vehicleEngine = ns$1("vehicleEngine");
/** The Vehicle Identification Number (VIN) is a unique serial number used by the automotive industry to identify individual motor vehicles. */
const vehicleIdentificationNumber = ns$1("vehicleIdentificationNumber");
/** The color or color combination of the interior of the vehicle. */
const vehicleInteriorColor = ns$1("vehicleInteriorColor");
/** The type or material of the interior of the vehicle (e.g. synthetic fabric, leather, wood, etc.). While most interior types are characterized by the material used, an interior type can also be based on vehicle usage or target audience. */
const vehicleInteriorType = ns$1("vehicleInteriorType");
/** The release date of a vehicle model (often used to differentiate versions of the same make and model). */
const vehicleModelDate = ns$1("vehicleModelDate");
/** The number of passengers that can be seated in the vehicle, both in terms of the physical space available, and in terms of limitations set by law.<br/><br/>

 Typical unit code(s): C62 for persons. */
const vehicleSeatingCapacity = ns$1("vehicleSeatingCapacity");
/** The type of component used for transmitting the power from a rotating power source to the wheels or other relevant component(s) ("gearbox" for cars). */
const vehicleTransmission = ns$1("vehicleTransmission");
/** 'vendor' is an earlier term for 'seller'. */
const vendor = ns$1("vendor");
/** The version of the CreativeWork embodied by a specified resource. */
const version = ns$1("version");
/** An embedded video object. */
const video = ns$1("video");
/** The type of screening or video broadcast used (e.g. IMAX, 3D, SD, HD, etc.). */
const videoFormat = ns$1("videoFormat");
/** The frame size of the video. */
const videoFrameSize = ns$1("videoFrameSize");
/** The quality of the video. */
const videoQuality = ns$1("videoQuality");
/** Identifies the volume of publication or multi-part work; for example, "iii" or "2". */
const volumeNumber = ns$1("volumeNumber");
/** The warranty promise(s) included in the offer. */
const warrantyPromise = ns$1("warrantyPromise");
/** The scope of the warranty promise. */
const warrantyScope = ns$1("warrantyScope");
/** The time when a passenger can check into the flight online. */
const webCheckinTime = ns$1("webCheckinTime");
/** The weight of the product or person. */
const weight = ns$1("weight");
/** The width of the item. */
const width = ns$1("width");
/** A sub property of participant. The winner of the action. */
const winner = ns$1("winner");
/** The number of words in the text of the Article. */
const wordCount = ns$1("wordCount");
/** The typical working hours for this job (e.g. 1st shift, night shift, 8am-5pm). */
const workHours = ns$1("workHours");
/** A contact location for a person's place of work. */
const workLocation = ns$1("workLocation");
/** A work performed in some event, for example a play performed in a TheaterEvent. */
const workPerformed = ns$1("workPerformed");
/** The movie presented during this event. */
const workPresented = ns$1("workPresented");
/** Organizations that the person works for. */
const worksFor = ns$1("worksFor");
/** The lowest value allowed in this rating system. If worstRating is omitted, 1 is assumed. */
const worstRating = ns$1("worstRating");
/** An XPath, e.g. of a <a class="localLink" href="http://schema.org/SpeakableSpecification">SpeakableSpecification</a> or <a class="localLink" href="http://schema.org/WebPageElement">WebPageElement</a>. In the latter case, multiple matches within a page can constitute a single conceptual "Web page element". */
const xpath = ns$1("xpath");
/** The size of the business in annual revenue. */
const yearlyRevenue = ns$1("yearlyRevenue");
/** The age of the business. */
const yearsInOperation = ns$1("yearsInOperation");
/** An application that can complete the request. */
const actionApplication = ns$1("actionApplication");
/** A sub property of object. The options subject to this action. */
const actionOption = ns$1("actionOption");
/** An actor, e.g. in tv, radio, movie, video games etc., or in an event. Actors can be associated with individual items or with a series, episode, clip. */
const actor = ns$1("actor");
/** A music album. */
const album = ns$1("album");
/** A release of this album. */
const albumRelease = ns$1("albumRelease");
/** Alumni of an organization. */
const alumni = ns$1("alumni");
/** An organization that the person is an alumni of. */
const alumniOf = ns$1("alumniOf");
/** The supporting materials for the artwork, e.g. Canvas, Paper, Wood, Board, etc. */
const artworkSurface = ns$1("artworkSurface");
/** A person or organization attending the event. */
const attendee = ns$1("attendee");
/** An intended audience, i.e. a group for whom something was created. */
const audience = ns$1("audience");
/** Device required to run the application. Used in cases where a specific make/model is required to run the application. */
const availableOnDevice = ns$1("availableOnDevice");
/** An award won by or for this item. */
const award = ns$1("award");
/** A posting that is part of this blog. */
const blogPost = ns$1("blogPost");
/** An entity that arranges for an exchange between a buyer and a seller.  In most cases a broker never acquires or releases ownership of a product or service involved in an exchange.  If it is not clear whether an entity is a broker, seller, or buyer, the latter two terms are preferred. */
const broker = ns$1("broker");
/** What type of code sample: full (compile ready) solution, code snippet, inline code, scripts, template. */
const codeSampleType = ns$1("codeSampleType");
/** A colleague of the person. */
const colleague = ns$1("colleague");
/** A contact point for a person or organization. */
const contactPoint = ns$1("contactPoint");
/** The basic containment relation between a place and another that it contains. */
const containsPlace = ns$1("containsPlace");
/** A season that is part of the media series. */
const containsSeason = ns$1("containsSeason");
/** The location depicted or described in the content. For example, the location in a photograph or painting. */
const contentLocation = ns$1("contentLocation");
/** A dataset contained in this catalog. */
const dataset = ns$1("dataset");
/** A description of the item. */
const description = ns$1("description");
/** A director of e.g. tv, radio, movie, video gaming etc. content, or of an event. Directors can be associated with individual items or with a series, episode, clip. */
const director = ns$1("director");
/** The duration of the item (movie, audio recording, event, etc.) in <a href="http://en.wikipedia.org/wiki/ISO_8601">ISO 8601 date format</a>. */
const duration = ns$1("duration");
/** Someone working for this organization. */
const employee = ns$1("employee");
/** The CreativeWork encoded by this media object. */
const encodesCreativeWork = ns$1("encodesCreativeWork");
/** Media type typically expressed using a MIME format (see <a href="http://www.iana.org/assignments/media-types/media-types.xhtml">IANA site</a> and <a href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types">MDN reference</a>) e.g. application/zip for a SoftwareApplication binary, audio/mpeg for .mp3 etc.).<br/><br/>

 In cases where a <a class="localLink" href="http://schema.org/CreativeWork">CreativeWork</a> has several media type representations, <a class="localLink" href="http://schema.org/encoding">encoding</a> can be used to indicate each <a class="localLink" href="http://schema.org/MediaObject">MediaObject</a> alongside particular <a class="localLink" href="http://schema.org/encodingFormat">encodingFormat</a> information.<br/><br/>

 Unregistered or niche encoding and file formats can be indicated instead via the most appropriate URL, e.g. defining Web page or a Wikipedia/Wikidata entry. */
const encodingFormat = ns$1("encodingFormat");
/** An episode of a tv, radio or game media within a series or season. */
const episode = ns$1("episode");
/** Upcoming or past event associated with this place, organization, or action. */
const event = ns$1("event");
/** A creative work that this work is an example/instance/realization/derivation of. */
const exampleOfWork = ns$1("exampleOfWork");
/** Library file name e.g., mscorlib.dll, system.web.dll. */
const executableLibraryName = ns$1("executableLibraryName");
/** A sub property of location. The course where this action was taken. */
const exerciseCourse = ns$1("exerciseCourse");
/** A person who founded this organization. */
const founder = ns$1("founder");
/** Video game which is played on this server. */
const game = ns$1("game");
/** The server on which  it is possible to play the game. */
const gameServer = ns$1("gameServer");
/** A broadcast channel of a broadcast service. */
const hasBroadcastChannel = ns$1("hasBroadcastChannel");
/** Either the actual menu as a structured representation, as text, or a URL of the menu. */
const hasMenu = ns$1("hasMenu");
/** The language of the content or performance or used in an action. Please use one of the language codes from the <a href="http://tools.ietf.org/html/bcp47">IETF BCP 47 standard</a>. See also <a class="localLink" href="http://schema.org/availableLanguage">availableLanguage</a>. */
const inLanguage = ns$1("inLanguage");
/** Description of bonus and commission compensation aspects of the job. */
const incentiveCompensation = ns$1("incentiveCompensation");
/** The number of interactions for the CreativeWork using the WebSite or SoftwareApplication. The most specific child type of InteractionCounter should be used. */
const interactionStatistic = ns$1("interactionStatistic");
/** A flag to signal that the item, event, or place is accessible for free. */
const isAccessibleForFree = ns$1("isAccessibleForFree");
/** A resource from which this work is derived or from which it is a modification or adaption. */
const isBasedOn = ns$1("isBasedOn");
/** Description of benefits associated with the job. */
const jobBenefits = ns$1("jobBenefits");
/** Indicates the primary entity described in some page or other CreativeWork. */
const mainEntity = ns$1("mainEntity");
/** Indicates a page (or other CreativeWork) for which this thing is the main entity being described. See <a href="/docs/datamodel.html#mainEntityBackground">background notes</a> for details. */
const mainEntityOfPage = ns$1("mainEntityOfPage");
/** A pointer to products or services offered by the organization or person. */
const makesOffer = ns$1("makesOffer");
/** A pointer to the organization or person making the offer. */
const offeredBy = ns$1("offeredBy");
/** A parent of this person. */
const parent = ns$1("parent");
/** The series to which this episode or season belongs. */
const partOfSeries = ns$1("partOfSeries");
/** The date that payment is due. */
const paymentDueDate = ns$1("paymentDueDate");
/** The length of time it takes to perform instructions or a direction (not including time to prepare the supplies), in <a href="http://en.wikipedia.org/wiki/ISO_8601">ISO 8601 duration format</a>. */
const performTime = ns$1("performTime");
/** A performer at the event&#x2014;for example, a presenter, musician, musical group or actor. */
const performer = ns$1("performer");
/** A photograph of this place. */
const photo = ns$1("photo");
/** The service provider, service operator, or service performer; the goods producer. Another party (a seller) may offer those services or goods on behalf of the provider. A provider may also serve as the seller. */
const provider = ns$1("provider");
/** The BroadcastService offered on this channel. */
const providesBroadcastService = ns$1("providesBroadcastService");
/** A single ingredient used in the recipe, e.g. sugar, flour or garlic. */
const recipeIngredient = ns$1("recipeIngredient");
/** An audio recording of the work. */
const recordedAs = ns$1("recordedAs");
/** The Event where the CreativeWork was recorded. The CreativeWork may capture all or part of the event. */
const recordedAt = ns$1("recordedAt");
/** The CreativeWork that captured all or part of this Event. */
const recordedIn = ns$1("recordedIn");
/** The composition this track is a recording of. */
const recordingOf = ns$1("recordingOf");
/** The album this is a release of. */
const releaseOf = ns$1("releaseOf");
/** A review of the item. */
const review = ns$1("review");
/** A role played, performed or filled by a person or organization. For example, the team of creators for a comic book might fill the roles named 'inker', 'penciller', and 'letterer'; or an athlete in a SportsTeam might play in the position named 'Quarterback'. */
const roleName = ns$1("roleName");
/** Runtime platform or script interpreter dependencies (Example - Java v1, Python2.3, .Net Framework 3.0). */
const runtimePlatform = ns$1("runtimePlatform");
/** A season in a media series. */
const season = ns$1("season");
/** The serial number or any alphanumeric identifier of a particular product. When attached to an offer, it is a shortcut for the serial number of the product included in the offer. */
const serialNumber = ns$1("serialNumber");
/** The geographic area where the service is provided. */
const serviceArea = ns$1("serviceArea");
/** The tangible thing generated by the service, e.g. a passport, permit, etc. */
const serviceOutput = ns$1("serviceOutput");
/** A sibling of the person. */
const sibling = ns$1("sibling");
/** One of the more significant URLs on the page. Typically, these are the non-navigation links that are clicked on the most. */
const significantLink = ns$1("significantLink");
/** Component dependency requirements for application. This includes runtime environments and shared libraries that are not included in the application distribution package, but required to run the application (Examples: DirectX, Java or .NET runtime). */
const softwareRequirements = ns$1("softwareRequirements");
/** A person or organization that supports a thing through a pledge, promise, or financial contribution. e.g. a sponsor of a Medical Study or a corporate sponsor of an event. */
const sponsor = ns$1("sponsor");
/** A relationship between two organizations where the first includes the second, e.g., as a subsidiary. See also: the more specific 'department' property. */
const subOrganization = ns$1("subOrganization");
/** A CreativeWork or Event about this Thing. */
const subjectOf = ns$1("subjectOf");
/** An answer (possibly one of several, possibly incorrect) to a Question, e.g. on a Question/Answer site. */
const suggestedAnswer = ns$1("suggestedAnswer");
/** An event that this event is a part of. For example, a collection of individual music performances might each have a music festival as their superEvent. */
const superEvent = ns$1("superEvent");
/** A sub property of object. The collection target of the action. */
const targetCollection = ns$1("targetCollection");
/** The temporalCoverage of a CreativeWork indicates the period that the content applies to, i.e. that it describes, either as a DateTime or as a textual string indicating a time period in <a href="https://en.wikipedia.org/wiki/ISO_8601#Time_intervals">ISO 8601 time interval format</a>. In
 the case of a Dataset it will typically indicate the relevant time period in a precise notation (e.g. for a 2011 census dataset, the year 2011 would be written "2011/2012"). Other forms of content e.g. ScholarlyArticle, Book, TVSeries or TVEpisode may indicate their temporalCoverage in broader terms - textually or via well-known URL.
 Written works such as books may sometimes have precise temporal coverage too, e.g. a work set in 1939 - 1945 can be indicated in ISO 8601 interval format format via "1939/1945".<br/><br/>

 Open-ended date ranges can be written with ".." in place of the end date. For example, "2015-11/.." indicates a range beginning in November 2015 and with no specified final date. This is tentative and might be updated in future when ISO 8601 is officially updated. */
const temporalCoverage = ns$1("temporalCoverage");
/** A music recording (track)&#x2014;usually a single song. If an ItemList is given, the list should contain items of type MusicRecording. */
const track = ns$1("track");
/** The warranty promise(s) included in the offer. */
const warranty = ns$1("warranty");
/** Example/instance/realization/derivation of the concept of this creative work. eg. The paperback edition, first edition, or eBook. */
const workExample = ns$1("workExample");
/** The quantity that results by performing instructions. For example, a paper airplane, 10 personalized candles. */
const schemayield = ns$1("yield");
/** The subject matter of the content. */
const about = ns$1("about");
/** A competitor in a sports event. */
const competitor = ns$1("competitor");
/** The basic containment relation between a place and one that contains it. */
const containedInPlace = ns$1("containedInPlace");
/** A media object that encodes this CreativeWork. This property is a synonym for associatedMedia. */
const encoding = ns$1("encoding");
/** A URL to a map of the place. */
const hasMap = ns$1("hasMap");
/** An image of the item. This can be a <a class="localLink" href="http://schema.org/URL">URL</a> or a fully described <a class="localLink" href="http://schema.org/ImageObject">ImageObject</a>. */
const image = ns$1("image");
/** A material that something is made from, e.g. leather, wool, cotton, paper. */
const material = ns$1("material");
/** An Organization (or ProgramMembership) to which this Person or Organization belongs. */
const memberOf = ns$1("memberOf");
/** The larger organization that this organization is a <a class="localLink" href="http://schema.org/subOrganization">subOrganization</a> of, if any. */
const parentOrganization = ns$1("parentOrganization");
/** The result produced in the action. e.g. John wrote <em>a book</em>. */
const result = ns$1("result");
/** An entity which offers (sells / leases / lends / loans) the services / goods.  A seller may also be a provider. */
const seller = ns$1("seller");
/** A single step item (as HowToStep, text, document, video, etc.) or a HowToSection. */
const step = ns$1("step");
/** An Event that is part of this event. For example, a conference event includes many presentations, each of which is a subEvent of the conference. */
const subEvent = ns$1("subEvent");
/** A sub-property of instrument. A supply consumed when performing instructions or a direction. */
const supply = ns$1("supply");
/** A work featured in some event, e.g. exhibited in an ExhibitionEvent.
 Specific subproperties are available for workPerformed (e.g. a play), or a workPresented (a Movie at a ScreeningEvent). */
const workFeatured = ns$1("workFeatured");
/** The geographic area where a service or offered item is provided. */
const areaServed = ns$1("areaServed");
/** A data catalog which contains this dataset. */
const includedInDataCatalog = ns$1("includedInDataCatalog");
/** A member of an Organization or a ProgramMembership. Organizations can be members of organizations; ProgramMembership is typically for individuals. */
const member = ns$1("member");
/** A sub property of participant. The participant who is at the receiving end of the action. */
const recipient = ns$1("recipient");
/** Indicates an item or CreativeWork that is part of this item, or CreativeWork (in some sense). */
const hasPart = ns$1("hasPart");
/** Indicates an item or CreativeWork that this item, or CreativeWork (in some sense), is part of. */
const isPartOf = ns$1("isPartOf");
/** The position of an item in a series or sequence of items. */
const position = ns$1("position");
/** The object that helped the agent perform the action. e.g. John wrote a book with <em>a pen</em>. */
const instrument = ns$1("instrument");
/** The object upon which the action is carried out, whose state is kept intact or changed. Also known as the semantic roles patient, affected or undergoer (which change their state) or theme (which doesn't). e.g. John read <em>a book</em>. */
const object$1 = ns$1("object");
/** The location of for example where the event is happening, an organization is located, or where an action takes place. */
const location = ns$1("location");
/** Other co-agents that participated in the action indirectly. e.g. John wrote a book with <em>Steve</em>. */
const participant = ns$1("participant");
/** The identifier property represents any kind of identifier for any kind of <a class="localLink" href="http://schema.org/Thing">Thing</a>, such as ISBNs, GTIN codes, UUIDs etc. Schema.org provides dedicated properties for representing many of these, either as textual strings or as URL (URI) links. See <a href="/docs/datamodel.html#identifierBg">background notes</a> for more details. */
const identifier = ns$1("identifier");

var schema = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ns: ns$1,
  AMRadioChannel: AMRadioChannel,
  APIReference: APIReference,
  AboutPage: AboutPage,
  AcceptAction: AcceptAction,
  Accommodation: Accommodation,
  AccountingService: AccountingService,
  AchieveAction: AchieveAction,
  Action: Action,
  ActionAccessSpecification: ActionAccessSpecification,
  ActionStatusType: ActionStatusType,
  ActivateAction: ActivateAction,
  AddAction: AddAction,
  AdministrativeArea: AdministrativeArea,
  AdultEntertainment: AdultEntertainment,
  AggregateOffer: AggregateOffer,
  AggregateRating: AggregateRating,
  AgreeAction: AgreeAction,
  Airline: Airline,
  Airport: Airport,
  AlignmentObject: AlignmentObject,
  AllocateAction: AllocateAction,
  AmusementPark: AmusementPark,
  AnimalShelter: AnimalShelter,
  Answer: Answer,
  Apartment: Apartment,
  ApartmentComplex: ApartmentComplex,
  AppendAction: AppendAction,
  ApplyAction: ApplyAction,
  Aquarium: Aquarium,
  ArriveAction: ArriveAction,
  ArtGallery: ArtGallery,
  Article: Article,
  AskAction: AskAction,
  AssessAction: AssessAction,
  AssignAction: AssignAction,
  Attorney: Attorney,
  Audience: Audience,
  AudioObject: AudioObject,
  AuthorizeAction: AuthorizeAction,
  AutoBodyShop: AutoBodyShop,
  AutoDealer: AutoDealer,
  AutoPartsStore: AutoPartsStore,
  AutoRental: AutoRental,
  AutoRepair: AutoRepair,
  AutoWash: AutoWash,
  AutomatedTeller: AutomatedTeller,
  AutomotiveBusiness: AutomotiveBusiness,
  Bakery: Bakery,
  BankAccount: BankAccount,
  BankOrCreditUnion: BankOrCreditUnion,
  BarOrPub: BarOrPub,
  Barcode: Barcode,
  Beach: Beach,
  BeautySalon: BeautySalon,
  BedAndBreakfast: BedAndBreakfast,
  BedDetails: BedDetails,
  BedType: BedType,
  BefriendAction: BefriendAction,
  BikeStore: BikeStore,
  Blog: Blog,
  BlogPosting: BlogPosting,
  BoardingPolicyType: BoardingPolicyType,
  BodyOfWater: BodyOfWater,
  Book: Book,
  BookFormatType: BookFormatType,
  BookSeries: BookSeries,
  BookStore: BookStore,
  BookmarkAction: BookmarkAction,
  Boolean: Boolean$1,
  BorrowAction: BorrowAction,
  BowlingAlley: BowlingAlley,
  Brand: Brand,
  BreadcrumbList: BreadcrumbList,
  Brewery: Brewery,
  Bridge: Bridge,
  BroadcastChannel: BroadcastChannel,
  BroadcastEvent: BroadcastEvent,
  BroadcastFrequencySpecification: BroadcastFrequencySpecification,
  BroadcastService: BroadcastService,
  BuddhistTemple: BuddhistTemple,
  BusReservation: BusReservation,
  BusStation: BusStation,
  BusStop: BusStop,
  BusTrip: BusTrip,
  BusinessAudience: BusinessAudience,
  BusinessEntityType: BusinessEntityType,
  BusinessEvent: BusinessEvent,
  BusinessFunction: BusinessFunction,
  BuyAction: BuyAction,
  CableOrSatelliteService: CableOrSatelliteService,
  CafeOrCoffeeShop: CafeOrCoffeeShop,
  Campground: Campground,
  CampingPitch: CampingPitch,
  Canal: Canal,
  CancelAction: CancelAction,
  Car: Car,
  Casino: Casino,
  CatholicChurch: CatholicChurch,
  Cemetery: Cemetery,
  CheckAction: CheckAction,
  CheckInAction: CheckInAction,
  CheckOutAction: CheckOutAction,
  CheckoutPage: CheckoutPage,
  ChildCare: ChildCare,
  ChildrensEvent: ChildrensEvent,
  ChooseAction: ChooseAction,
  Church: Church,
  City: City,
  CityHall: CityHall,
  CivicStructure: CivicStructure,
  ClaimReview: ClaimReview,
  Clip: Clip,
  ClothingStore: ClothingStore,
  Code: Code,
  CollectionPage: CollectionPage,
  CollegeOrUniversity: CollegeOrUniversity,
  ComedyClub: ComedyClub,
  ComedyEvent: ComedyEvent,
  Comment: Comment,
  CommentAction: CommentAction,
  CommunicateAction: CommunicateAction,
  CompoundPriceSpecification: CompoundPriceSpecification,
  ComputerLanguage: ComputerLanguage,
  ComputerStore: ComputerStore,
  ConfirmAction: ConfirmAction,
  ConsumeAction: ConsumeAction,
  ContactPage: ContactPage,
  ContactPoint: ContactPoint,
  ContactPointOption: ContactPointOption,
  Continent: Continent,
  ControlAction: ControlAction,
  ConvenienceStore: ConvenienceStore,
  Conversation: Conversation,
  CookAction: CookAction,
  Corporation: Corporation,
  Country: Country,
  Course: Course,
  CourseInstance: CourseInstance,
  Courthouse: Courthouse,
  CreateAction: CreateAction,
  CreativeWork: CreativeWork,
  CreativeWorkSeason: CreativeWorkSeason,
  CreativeWorkSeries: CreativeWorkSeries,
  CreditCard: CreditCard,
  Crematorium: Crematorium,
  CurrencyConversionService: CurrencyConversionService,
  DanceEvent: DanceEvent,
  DanceGroup: DanceGroup,
  DataCatalog: DataCatalog,
  DataDownload: DataDownload,
  DataFeed: DataFeed,
  DataFeedItem: DataFeedItem,
  DataType: DataType,
  Dataset: Dataset,
  Date: Date$1,
  DateTime: DateTime,
  DatedMoneySpecification: DatedMoneySpecification,
  DayOfWeek: DayOfWeek,
  DaySpa: DaySpa,
  DeactivateAction: DeactivateAction,
  DefenceEstablishment: DefenceEstablishment,
  DeleteAction: DeleteAction,
  DeliveryChargeSpecification: DeliveryChargeSpecification,
  DeliveryEvent: DeliveryEvent,
  DeliveryMethod: DeliveryMethod,
  Demand: Demand,
  Dentist: Dentist,
  DepartAction: DepartAction,
  DepartmentStore: DepartmentStore,
  DepositAccount: DepositAccount,
  DigitalDocument: DigitalDocument,
  DigitalDocumentPermission: DigitalDocumentPermission,
  DigitalDocumentPermissionType: DigitalDocumentPermissionType,
  DisagreeAction: DisagreeAction,
  DiscoverAction: DiscoverAction,
  DiscussionForumPosting: DiscussionForumPosting,
  DislikeAction: DislikeAction,
  Distance: Distance,
  Distillery: Distillery,
  DonateAction: DonateAction,
  DownloadAction: DownloadAction,
  DrawAction: DrawAction,
  DrinkAction: DrinkAction,
  DriveWheelConfigurationValue: DriveWheelConfigurationValue,
  DryCleaningOrLaundry: DryCleaningOrLaundry,
  Duration: Duration,
  EatAction: EatAction,
  EducationEvent: EducationEvent,
  EducationalAudience: EducationalAudience,
  EducationalOrganization: EducationalOrganization,
  Electrician: Electrician,
  ElectronicsStore: ElectronicsStore,
  ElementarySchool: ElementarySchool,
  EmailMessage: EmailMessage,
  Embassy: Embassy,
  EmergencyService: EmergencyService,
  EmployeeRole: EmployeeRole,
  EmployerAggregateRating: EmployerAggregateRating,
  EmploymentAgency: EmploymentAgency,
  EndorseAction: EndorseAction,
  EndorsementRating: EndorsementRating,
  Energy: Energy,
  EngineSpecification: EngineSpecification,
  EntertainmentBusiness: EntertainmentBusiness,
  EntryPoint: EntryPoint,
  Enumeration: Enumeration,
  Episode: Episode,
  Event: Event,
  EventReservation: EventReservation,
  EventStatusType: EventStatusType,
  EventVenue: EventVenue,
  ExerciseAction: ExerciseAction,
  ExerciseGym: ExerciseGym,
  ExhibitionEvent: ExhibitionEvent,
  FAQPage: FAQPage,
  FMRadioChannel: FMRadioChannel,
  FastFoodRestaurant: FastFoodRestaurant,
  Festival: Festival,
  FilmAction: FilmAction,
  FinancialProduct: FinancialProduct,
  FinancialService: FinancialService,
  FindAction: FindAction,
  FireStation: FireStation,
  Flight: Flight,
  FlightReservation: FlightReservation,
  Float: Float,
  Florist: Florist,
  FollowAction: FollowAction,
  FoodEstablishment: FoodEstablishment,
  FoodEstablishmentReservation: FoodEstablishmentReservation,
  FoodEvent: FoodEvent,
  FoodService: FoodService,
  FurnitureStore: FurnitureStore,
  Game: Game,
  GamePlayMode: GamePlayMode,
  GameServer: GameServer,
  GameServerStatus: GameServerStatus,
  GardenStore: GardenStore,
  GasStation: GasStation,
  GatedResidenceCommunity: GatedResidenceCommunity,
  GenderType: GenderType,
  GeneralContractor: GeneralContractor,
  GeoCircle: GeoCircle,
  GeoCoordinates: GeoCoordinates,
  GeoShape: GeoShape,
  GiveAction: GiveAction,
  GolfCourse: GolfCourse,
  GovernmentBuilding: GovernmentBuilding,
  GovernmentOffice: GovernmentOffice,
  GovernmentOrganization: GovernmentOrganization,
  GovernmentPermit: GovernmentPermit,
  GovernmentService: GovernmentService,
  GroceryStore: GroceryStore,
  HVACBusiness: HVACBusiness,
  HairSalon: HairSalon,
  HardwareStore: HardwareStore,
  HealthAndBeautyBusiness: HealthAndBeautyBusiness,
  HealthClub: HealthClub,
  HighSchool: HighSchool,
  HinduTemple: HinduTemple,
  HobbyShop: HobbyShop,
  HomeAndConstructionBusiness: HomeAndConstructionBusiness,
  HomeGoodsStore: HomeGoodsStore,
  Hospital: Hospital,
  Hostel: Hostel,
  Hotel: Hotel,
  HotelRoom: HotelRoom,
  House: House,
  HousePainter: HousePainter,
  HowTo: HowTo,
  HowToDirection: HowToDirection,
  HowToItem: HowToItem,
  HowToSection: HowToSection,
  HowToStep: HowToStep,
  HowToSupply: HowToSupply,
  HowToTip: HowToTip,
  HowToTool: HowToTool,
  IceCreamShop: IceCreamShop,
  IgnoreAction: IgnoreAction,
  ImageGallery: ImageGallery,
  ImageObject: ImageObject,
  IndividualProduct: IndividualProduct,
  InformAction: InformAction,
  InsertAction: InsertAction,
  InstallAction: InstallAction,
  InsuranceAgency: InsuranceAgency,
  Intangible: Intangible,
  Integer: Integer,
  InteractAction: InteractAction,
  InteractionCounter: InteractionCounter,
  InternetCafe: InternetCafe,
  InvestmentOrDeposit: InvestmentOrDeposit,
  InviteAction: InviteAction,
  Invoice: Invoice,
  ItemAvailability: ItemAvailability,
  ItemList: ItemList,
  ItemListOrderType: ItemListOrderType,
  ItemPage: ItemPage,
  JewelryStore: JewelryStore,
  JobPosting: JobPosting,
  JoinAction: JoinAction,
  LakeBodyOfWater: LakeBodyOfWater,
  Landform: Landform,
  LandmarksOrHistoricalBuildings: LandmarksOrHistoricalBuildings,
  Language: Language,
  LeaveAction: LeaveAction,
  LegalService: LegalService,
  LegislativeBuilding: LegislativeBuilding,
  LendAction: LendAction,
  Library: Library,
  LikeAction: LikeAction,
  LiquorStore: LiquorStore,
  ListItem: ListItem,
  ListenAction: ListenAction,
  LiteraryEvent: LiteraryEvent,
  LiveBlogPosting: LiveBlogPosting,
  LoanOrCredit: LoanOrCredit,
  LocalBusiness: LocalBusiness,
  LocationFeatureSpecification: LocationFeatureSpecification,
  LockerDelivery: LockerDelivery,
  Locksmith: Locksmith,
  LodgingBusiness: LodgingBusiness,
  LodgingReservation: LodgingReservation,
  LoseAction: LoseAction,
  Map: Map$1,
  MapCategoryType: MapCategoryType,
  MarryAction: MarryAction,
  Mass: Mass,
  MediaObject: MediaObject,
  MediaSubscription: MediaSubscription,
  MedicalOrganization: MedicalOrganization,
  MeetingRoom: MeetingRoom,
  MensClothingStore: MensClothingStore,
  Menu: Menu,
  MenuItem: MenuItem,
  MenuSection: MenuSection,
  Message: Message,
  MiddleSchool: MiddleSchool,
  MobileApplication: MobileApplication,
  MobilePhoneStore: MobilePhoneStore,
  MonetaryAmount: MonetaryAmount,
  MonetaryAmountDistribution: MonetaryAmountDistribution,
  Mosque: Mosque,
  Motel: Motel,
  MotorcycleDealer: MotorcycleDealer,
  MotorcycleRepair: MotorcycleRepair,
  Mountain: Mountain,
  MoveAction: MoveAction,
  Movie: Movie,
  MovieClip: MovieClip,
  MovieRentalStore: MovieRentalStore,
  MovieSeries: MovieSeries,
  MovieTheater: MovieTheater,
  MovingCompany: MovingCompany,
  Museum: Museum,
  MusicAlbum: MusicAlbum,
  MusicAlbumProductionType: MusicAlbumProductionType,
  MusicAlbumReleaseType: MusicAlbumReleaseType,
  MusicComposition: MusicComposition,
  MusicEvent: MusicEvent,
  MusicGroup: MusicGroup,
  MusicPlaylist: MusicPlaylist,
  MusicRecording: MusicRecording,
  MusicRelease: MusicRelease,
  MusicReleaseFormatType: MusicReleaseFormatType,
  MusicStore: MusicStore,
  MusicVenue: MusicVenue,
  MusicVideoObject: MusicVideoObject,
  NGO: NGO,
  NailSalon: NailSalon,
  NewsArticle: NewsArticle,
  NightClub: NightClub,
  Notary: Notary,
  NoteDigitalDocument: NoteDigitalDocument,
  Number: Number$1,
  NutritionInformation: NutritionInformation,
  Occupation: Occupation,
  OceanBodyOfWater: OceanBodyOfWater,
  Offer: Offer,
  OfferCatalog: OfferCatalog,
  OfferItemCondition: OfferItemCondition,
  OfficeEquipmentStore: OfficeEquipmentStore,
  OnDemandEvent: OnDemandEvent,
  OpeningHoursSpecification: OpeningHoursSpecification,
  Order: Order,
  OrderAction: OrderAction,
  OrderItem: OrderItem,
  OrderStatus: OrderStatus,
  Organization: Organization,
  OrganizationRole: OrganizationRole,
  OrganizeAction: OrganizeAction,
  OutletStore: OutletStore,
  OwnershipInfo: OwnershipInfo,
  PaintAction: PaintAction,
  Painting: Painting,
  ParcelDelivery: ParcelDelivery,
  ParcelService: ParcelService,
  ParentAudience: ParentAudience,
  Park: Park,
  ParkingFacility: ParkingFacility,
  PawnShop: PawnShop,
  PayAction: PayAction,
  PaymentCard: PaymentCard,
  PaymentChargeSpecification: PaymentChargeSpecification,
  PaymentMethod: PaymentMethod,
  PaymentService: PaymentService,
  PaymentStatusType: PaymentStatusType,
  PeopleAudience: PeopleAudience,
  PerformAction: PerformAction,
  PerformanceRole: PerformanceRole,
  PerformingArtsTheater: PerformingArtsTheater,
  PerformingGroup: PerformingGroup,
  Periodical: Periodical,
  Permit: Permit,
  Person: Person,
  PetStore: PetStore,
  Pharmacy: Pharmacy,
  Photograph: Photograph,
  PhotographAction: PhotographAction,
  Physician: Physician,
  Place: Place,
  PlaceOfWorship: PlaceOfWorship,
  PlanAction: PlanAction,
  PlayAction: PlayAction,
  Playground: Playground,
  Plumber: Plumber,
  PoliceStation: PoliceStation,
  Pond: Pond,
  PostOffice: PostOffice,
  PostalAddress: PostalAddress,
  PreOrderAction: PreOrderAction,
  PrependAction: PrependAction,
  Preschool: Preschool,
  PresentationDigitalDocument: PresentationDigitalDocument,
  PriceSpecification: PriceSpecification,
  Product: Product,
  ProductModel: ProductModel,
  ProfessionalService: ProfessionalService,
  ProfilePage: ProfilePage,
  ProgramMembership: ProgramMembership,
  PropertyValue: PropertyValue,
  PropertyValueSpecification: PropertyValueSpecification,
  PublicSwimmingPool: PublicSwimmingPool,
  PublicationEvent: PublicationEvent,
  PublicationIssue: PublicationIssue,
  PublicationVolume: PublicationVolume,
  QAPage: QAPage,
  QualitativeValue: QualitativeValue,
  QuantitativeValue: QuantitativeValue,
  QuantitativeValueDistribution: QuantitativeValueDistribution,
  Quantity: Quantity,
  Question: Question,
  QuoteAction: QuoteAction,
  RVPark: RVPark,
  RadioChannel: RadioChannel,
  RadioClip: RadioClip,
  RadioEpisode: RadioEpisode,
  RadioSeason: RadioSeason,
  RadioSeries: RadioSeries,
  RadioStation: RadioStation,
  Rating: Rating,
  ReactAction: ReactAction,
  ReadAction: ReadAction,
  RealEstateAgent: RealEstateAgent,
  ReceiveAction: ReceiveAction,
  Recipe: Recipe,
  RecyclingCenter: RecyclingCenter,
  RegisterAction: RegisterAction,
  RejectAction: RejectAction,
  RentAction: RentAction,
  RentalCarReservation: RentalCarReservation,
  ReplaceAction: ReplaceAction,
  ReplyAction: ReplyAction,
  Report: Report,
  Reservation: Reservation,
  ReservationPackage: ReservationPackage,
  ReservationStatusType: ReservationStatusType,
  ReserveAction: ReserveAction,
  Reservoir: Reservoir,
  Residence: Residence,
  Resort: Resort,
  Restaurant: Restaurant,
  RestrictedDiet: RestrictedDiet,
  ResumeAction: ResumeAction,
  ReturnAction: ReturnAction,
  Review: Review,
  ReviewAction: ReviewAction,
  RiverBodyOfWater: RiverBodyOfWater,
  Role: Role,
  RoofingContractor: RoofingContractor,
  Room: Room,
  RsvpAction: RsvpAction,
  RsvpResponseType: RsvpResponseType,
  SaleEvent: SaleEvent,
  ScheduleAction: ScheduleAction,
  ScholarlyArticle: ScholarlyArticle,
  School: School,
  ScreeningEvent: ScreeningEvent,
  Sculpture: Sculpture,
  SeaBodyOfWater: SeaBodyOfWater,
  SearchAction: SearchAction,
  SearchResultsPage: SearchResultsPage,
  Season: Season,
  Seat: Seat,
  SelfStorage: SelfStorage,
  SellAction: SellAction,
  SendAction: SendAction,
  Series: Series,
  Service: Service,
  ServiceChannel: ServiceChannel,
  ShareAction: ShareAction,
  ShoeStore: ShoeStore,
  ShoppingCenter: ShoppingCenter,
  SingleFamilyResidence: SingleFamilyResidence,
  SiteNavigationElement: SiteNavigationElement,
  SkiResort: SkiResort,
  SocialEvent: SocialEvent,
  SocialMediaPosting: SocialMediaPosting,
  SoftwareApplication: SoftwareApplication,
  SoftwareSourceCode: SoftwareSourceCode,
  SomeProducts: SomeProducts,
  SpeakableSpecification: SpeakableSpecification,
  Specialty: Specialty,
  SportingGoodsStore: SportingGoodsStore,
  SportsActivityLocation: SportsActivityLocation,
  SportsClub: SportsClub,
  SportsEvent: SportsEvent,
  SportsOrganization: SportsOrganization,
  SportsTeam: SportsTeam,
  SpreadsheetDigitalDocument: SpreadsheetDigitalDocument,
  StadiumOrArena: StadiumOrArena,
  State: State,
  SteeringPositionValue: SteeringPositionValue,
  Store: Store,
  StructuredValue: StructuredValue,
  SubscribeAction: SubscribeAction,
  SubwayStation: SubwayStation,
  Suite: Suite,
  SuspendAction: SuspendAction,
  Synagogue: Synagogue,
  TVClip: TVClip,
  TVEpisode: TVEpisode,
  TVSeason: TVSeason,
  TVSeries: TVSeries,
  Table: Table,
  TakeAction: TakeAction,
  TattooParlor: TattooParlor,
  Taxi: Taxi,
  TaxiReservation: TaxiReservation,
  TaxiService: TaxiService,
  TaxiStand: TaxiStand,
  TechArticle: TechArticle,
  TelevisionChannel: TelevisionChannel,
  TelevisionStation: TelevisionStation,
  TennisComplex: TennisComplex,
  Text: Text,
  TextDigitalDocument: TextDigitalDocument,
  TheaterEvent: TheaterEvent,
  TheaterGroup: TheaterGroup,
  Thing: Thing,
  Ticket: Ticket,
  TieAction: TieAction,
  Time: Time,
  TipAction: TipAction,
  TireShop: TireShop,
  TouristAttraction: TouristAttraction,
  TouristInformationCenter: TouristInformationCenter,
  ToyStore: ToyStore,
  TrackAction: TrackAction,
  TradeAction: TradeAction,
  TrainReservation: TrainReservation,
  TrainStation: TrainStation,
  TrainTrip: TrainTrip,
  TransferAction: TransferAction,
  TravelAction: TravelAction,
  TravelAgency: TravelAgency,
  Trip: Trip,
  TypeAndQuantityNode: TypeAndQuantityNode,
  URL: URL$1,
  UnRegisterAction: UnRegisterAction,
  UnitPriceSpecification: UnitPriceSpecification,
  UpdateAction: UpdateAction,
  UseAction: UseAction,
  UserBlocks: UserBlocks,
  UserCheckins: UserCheckins,
  UserComments: UserComments,
  UserDownloads: UserDownloads,
  UserInteraction: UserInteraction,
  UserLikes: UserLikes,
  UserPageVisits: UserPageVisits,
  UserPlays: UserPlays,
  UserPlusOnes: UserPlusOnes,
  UserTweets: UserTweets,
  Vehicle: Vehicle,
  VideoGallery: VideoGallery,
  VideoGame: VideoGame,
  VideoGameClip: VideoGameClip,
  VideoGameSeries: VideoGameSeries,
  VideoObject: VideoObject,
  ViewAction: ViewAction,
  VisualArtsEvent: VisualArtsEvent,
  VisualArtwork: VisualArtwork,
  Volcano: Volcano,
  VoteAction: VoteAction,
  WPAdBlock: WPAdBlock,
  WPFooter: WPFooter,
  WPHeader: WPHeader,
  WPSideBar: WPSideBar,
  WantAction: WantAction,
  WarrantyPromise: WarrantyPromise,
  WarrantyScope: WarrantyScope,
  WatchAction: WatchAction,
  Waterfall: Waterfall,
  WearAction: WearAction,
  WebApplication: WebApplication,
  WebPage: WebPage,
  WebPageElement: WebPageElement,
  WebSite: WebSite,
  WholesaleStore: WholesaleStore,
  WinAction: WinAction,
  Winery: Winery,
  WorkersUnion: WorkersUnion,
  WriteAction: WriteAction,
  Zoo: Zoo,
  ActiveActionStatus: ActiveActionStatus,
  AlbumRelease: AlbumRelease,
  AllWheelDriveConfiguration: AllWheelDriveConfiguration,
  AudiobookFormat: AudiobookFormat,
  BroadcastRelease: BroadcastRelease,
  CDFormat: CDFormat,
  CassetteFormat: CassetteFormat,
  CoOp: CoOp,
  CommentPermission: CommentPermission,
  CompilationAlbum: CompilationAlbum,
  CompletedActionStatus: CompletedActionStatus,
  DJMixAlbum: DJMixAlbum,
  DVDFormat: DVDFormat,
  DamagedCondition: DamagedCondition,
  DemoAlbum: DemoAlbum,
  DiabeticDiet: DiabeticDiet,
  DigitalAudioTapeFormat: DigitalAudioTapeFormat,
  DigitalFormat: DigitalFormat,
  Discontinued: Discontinued,
  EBook: EBook,
  EPRelease: EPRelease,
  EventCancelled: EventCancelled,
  EventPostponed: EventPostponed,
  EventRescheduled: EventRescheduled,
  EventScheduled: EventScheduled,
  FailedActionStatus: FailedActionStatus,
  False: False,
  Female: Female,
  FourWheelDriveConfiguration: FourWheelDriveConfiguration,
  Friday: Friday,
  FrontWheelDriveConfiguration: FrontWheelDriveConfiguration,
  GlutenFreeDiet: GlutenFreeDiet,
  GroupBoardingPolicy: GroupBoardingPolicy,
  HalalDiet: HalalDiet,
  Hardcover: Hardcover,
  HearingImpairedSupported: HearingImpairedSupported,
  HinduDiet: HinduDiet,
  InStock: InStock,
  InStoreOnly: InStoreOnly,
  ItemListOrderAscending: ItemListOrderAscending,
  ItemListOrderDescending: ItemListOrderDescending,
  ItemListUnordered: ItemListUnordered,
  KosherDiet: KosherDiet,
  LaserDiscFormat: LaserDiscFormat,
  LeftHandDriving: LeftHandDriving,
  LimitedAvailability: LimitedAvailability,
  LiveAlbum: LiveAlbum,
  LowCalorieDiet: LowCalorieDiet,
  LowFatDiet: LowFatDiet,
  LowLactoseDiet: LowLactoseDiet,
  LowSaltDiet: LowSaltDiet,
  Male: Male,
  MixtapeAlbum: MixtapeAlbum,
  Monday: Monday,
  MultiPlayer: MultiPlayer,
  NewCondition: NewCondition,
  OfflinePermanently: OfflinePermanently,
  OfflineTemporarily: OfflineTemporarily,
  OnSitePickup: OnSitePickup,
  Online: Online,
  OnlineFull: OnlineFull,
  OnlineOnly: OnlineOnly,
  OrderCancelled: OrderCancelled,
  OrderDelivered: OrderDelivered,
  OrderInTransit: OrderInTransit,
  OrderPaymentDue: OrderPaymentDue,
  OrderPickupAvailable: OrderPickupAvailable,
  OrderProblem: OrderProblem,
  OrderProcessing: OrderProcessing,
  OrderReturned: OrderReturned,
  OutOfStock: OutOfStock,
  Paperback: Paperback,
  ParkingMap: ParkingMap,
  PaymentAutomaticallyApplied: PaymentAutomaticallyApplied,
  PaymentComplete: PaymentComplete,
  PaymentDeclined: PaymentDeclined,
  PaymentDue: PaymentDue,
  PaymentPastDue: PaymentPastDue,
  PotentialActionStatus: PotentialActionStatus,
  PreOrder: PreOrder,
  PreSale: PreSale,
  PublicHolidays: PublicHolidays,
  ReadPermission: ReadPermission,
  RearWheelDriveConfiguration: RearWheelDriveConfiguration,
  RefurbishedCondition: RefurbishedCondition,
  RemixAlbum: RemixAlbum,
  Researcher: Researcher,
  ReservationCancelled: ReservationCancelled,
  ReservationConfirmed: ReservationConfirmed,
  ReservationHold: ReservationHold,
  ReservationPending: ReservationPending,
  RightHandDriving: RightHandDriving,
  RsvpResponseMaybe: RsvpResponseMaybe,
  RsvpResponseNo: RsvpResponseNo,
  RsvpResponseYes: RsvpResponseYes,
  Saturday: Saturday,
  SeatingMap: SeatingMap,
  SinglePlayer: SinglePlayer,
  SingleRelease: SingleRelease,
  SoldOut: SoldOut,
  SoundtrackAlbum: SoundtrackAlbum,
  SpokenWordAlbum: SpokenWordAlbum,
  StudioAlbum: StudioAlbum,
  Sunday: Sunday,
  Thursday: Thursday,
  TollFree: TollFree,
  TransitMap: TransitMap,
  True: True,
  Tuesday: Tuesday,
  UsedCondition: UsedCondition,
  VeganDiet: VeganDiet,
  VegetarianDiet: VegetarianDiet,
  VenueMap: VenueMap,
  VinylFormat: VinylFormat,
  Wednesday: Wednesday,
  WritePermission: WritePermission,
  ZoneBoardingPolicy: ZoneBoardingPolicy,
  acceptedAnswer: acceptedAnswer,
  acceptedOffer: acceptedOffer,
  acceptedPaymentMethod: acceptedPaymentMethod,
  acceptsReservations: acceptsReservations,
  accessCode: accessCode,
  accessMode: accessMode,
  accessModeSufficient: accessModeSufficient,
  accessibilityAPI: accessibilityAPI,
  accessibilityControl: accessibilityControl,
  accessibilityFeature: accessibilityFeature,
  accessibilityHazard: accessibilityHazard,
  accessibilitySummary: accessibilitySummary,
  accountId: accountId,
  accountablePerson: accountablePerson,
  acquiredFrom: acquiredFrom,
  actionAccessibilityRequirement: actionAccessibilityRequirement,
  actionPlatform: actionPlatform,
  actionStatus: actionStatus,
  actors: actors,
  addOn: addOn,
  additionalName: additionalName,
  additionalNumberOfGuests: additionalNumberOfGuests,
  additionalProperty: additionalProperty,
  additionalType: additionalType,
  address: address,
  addressCountry: addressCountry,
  addressLocality: addressLocality,
  addressRegion: addressRegion,
  advanceBookingRequirement: advanceBookingRequirement,
  affiliation: affiliation,
  afterMedia: afterMedia,
  agent: agent,
  aggregateRating: aggregateRating,
  aircraft: aircraft,
  albumProductionType: albumProductionType,
  albumReleaseType: albumReleaseType,
  albums: albums,
  alignmentType: alignmentType,
  alternateName: alternateName,
  alternativeHeadline: alternativeHeadline,
  amenityFeature: amenityFeature,
  amount: amount,
  amountOfThisGood: amountOfThisGood,
  annualPercentageRate: annualPercentageRate,
  answerCount: answerCount,
  application: application,
  applicationCategory: applicationCategory,
  applicationSubCategory: applicationSubCategory,
  applicationSuite: applicationSuite,
  appliesToDeliveryMethod: appliesToDeliveryMethod,
  appliesToPaymentMethod: appliesToPaymentMethod,
  area: area,
  arrivalAirport: arrivalAirport,
  arrivalBusStop: arrivalBusStop,
  arrivalGate: arrivalGate,
  arrivalPlatform: arrivalPlatform,
  arrivalStation: arrivalStation,
  arrivalTerminal: arrivalTerminal,
  arrivalTime: arrivalTime,
  artEdition: artEdition,
  artMedium: artMedium,
  artform: artform,
  articleBody: articleBody,
  articleSection: articleSection,
  assembly: assembly,
  assemblyVersion: assemblyVersion,
  associatedArticle: associatedArticle,
  associatedMedia: associatedMedia,
  athlete: athlete,
  attendees: attendees,
  audienceType: audienceType,
  audio: audio,
  authenticator: authenticator,
  author: author,
  availability: availability,
  availabilityEnds: availabilityEnds,
  availabilityStarts: availabilityStarts,
  availableAtOrFrom: availableAtOrFrom,
  availableChannel: availableChannel,
  availableDeliveryMethod: availableDeliveryMethod,
  availableFrom: availableFrom,
  availableLanguage: availableLanguage,
  availableThrough: availableThrough,
  awards: awards,
  awayTeam: awayTeam,
  baseSalary: baseSalary,
  bccRecipient: bccRecipient,
  bed: bed,
  beforeMedia: beforeMedia,
  benefits: benefits,
  bestRating: bestRating,
  billingAddress: billingAddress,
  billingIncrement: billingIncrement,
  billingPeriod: billingPeriod,
  birthDate: birthDate,
  birthPlace: birthPlace,
  bitrate: bitrate,
  blogPosts: blogPosts,
  boardingGroup: boardingGroup,
  boardingPolicy: boardingPolicy,
  bookEdition: bookEdition,
  bookFormat: bookFormat,
  bookingAgent: bookingAgent,
  bookingTime: bookingTime,
  borrower: borrower,
  box: box,
  branchCode: branchCode,
  branchOf: branchOf,
  brand: brand,
  breadcrumb: breadcrumb,
  broadcastAffiliateOf: broadcastAffiliateOf,
  broadcastChannelId: broadcastChannelId,
  broadcastDisplayName: broadcastDisplayName,
  broadcastFrequency: broadcastFrequency,
  broadcastFrequencyValue: broadcastFrequencyValue,
  broadcastOfEvent: broadcastOfEvent,
  broadcastServiceTier: broadcastServiceTier,
  broadcastTimezone: broadcastTimezone,
  broadcaster: broadcaster,
  browserRequirements: browserRequirements,
  busName: busName,
  busNumber: busNumber,
  businessFunction: businessFunction,
  buyer: buyer,
  byArtist: byArtist,
  calories: calories,
  candidate: candidate,
  caption: caption,
  carbohydrateContent: carbohydrateContent,
  cargoVolume: cargoVolume,
  carrier: carrier,
  carrierRequirements: carrierRequirements,
  catalog: catalog,
  catalogNumber: catalogNumber,
  category: category,
  ccRecipient: ccRecipient,
  character: character,
  characterAttribute: characterAttribute,
  characterName: characterName,
  cheatCode: cheatCode,
  checkinTime: checkinTime,
  checkoutTime: checkoutTime,
  childMaxAge: childMaxAge,
  childMinAge: childMinAge,
  children: children,
  cholesterolContent: cholesterolContent,
  circle: circle,
  citation: citation,
  claimReviewed: claimReviewed,
  clipNumber: clipNumber,
  closes: closes,
  coach: coach,
  codeRepository: codeRepository,
  colleagues: colleagues,
  collection: collection,
  color: color,
  comment: comment,
  commentCount: commentCount,
  commentText: commentText,
  commentTime: commentTime,
  composer: composer,
  confirmationNumber: confirmationNumber,
  contactOption: contactOption,
  contactPoints: contactPoints,
  contactType: contactType,
  containedIn: containedIn,
  contentRating: contentRating,
  contentSize: contentSize,
  contentType: contentType,
  contentUrl: contentUrl,
  contributor: contributor,
  cookTime: cookTime,
  cookingMethod: cookingMethod,
  copyrightHolder: copyrightHolder,
  copyrightYear: copyrightYear,
  countriesNotSupported: countriesNotSupported,
  countriesSupported: countriesSupported,
  countryOfOrigin: countryOfOrigin,
  course: course,
  courseCode: courseCode,
  courseMode: courseMode,
  coursePrerequisites: coursePrerequisites,
  coverageEndTime: coverageEndTime,
  coverageStartTime: coverageStartTime,
  creator: creator,
  creditedTo: creditedTo,
  cssSelector: cssSelector,
  currenciesAccepted: currenciesAccepted,
  currency: currency,
  customer: customer,
  dataFeedElement: dataFeedElement,
  datasetTimeInterval: datasetTimeInterval,
  dateCreated: dateCreated,
  dateDeleted: dateDeleted,
  dateIssued: dateIssued,
  dateModified: dateModified,
  datePosted: datePosted,
  datePublished: datePublished,
  dateRead: dateRead,
  dateReceived: dateReceived,
  dateSent: dateSent,
  dateVehicleFirstRegistered: dateVehicleFirstRegistered,
  dateline: dateline,
  dayOfWeek: dayOfWeek,
  deathDate: deathDate,
  deathPlace: deathPlace,
  defaultValue: defaultValue,
  deliveryAddress: deliveryAddress,
  deliveryLeadTime: deliveryLeadTime,
  deliveryMethod: deliveryMethod,
  deliveryStatus: deliveryStatus,
  department: department,
  departureAirport: departureAirport,
  departureBusStop: departureBusStop,
  departureGate: departureGate,
  departurePlatform: departurePlatform,
  departureStation: departureStation,
  departureTerminal: departureTerminal,
  departureTime: departureTime,
  dependencies: dependencies,
  depth: depth,
  device: device,
  directors: directors,
  disambiguatingDescription: disambiguatingDescription,
  discount: discount,
  discountCode: discountCode,
  discountCurrency: discountCurrency,
  discusses: discusses,
  discussionUrl: discussionUrl,
  dissolutionDate: dissolutionDate,
  distance: distance,
  distribution: distribution,
  doorTime: doorTime,
  downloadUrl: downloadUrl,
  downvoteCount: downvoteCount,
  driveWheelConfiguration: driveWheelConfiguration,
  dropoffLocation: dropoffLocation,
  dropoffTime: dropoffTime,
  duns: duns,
  durationOfWarranty: durationOfWarranty,
  duringMedia: duringMedia,
  editor: editor,
  educationalAlignment: educationalAlignment,
  educationalCredentialAwarded: educationalCredentialAwarded,
  educationalFramework: educationalFramework,
  educationalRole: educationalRole,
  educationalUse: educationalUse,
  elevation: elevation,
  eligibleCustomerType: eligibleCustomerType,
  eligibleDuration: eligibleDuration,
  eligibleQuantity: eligibleQuantity,
  eligibleRegion: eligibleRegion,
  eligibleTransactionVolume: eligibleTransactionVolume,
  email: email,
  embedUrl: embedUrl,
  employees: employees,
  employmentType: employmentType,
  encodingType: encodingType,
  encodings: encodings,
  endDate: endDate,
  endTime: endTime,
  endorsee: endorsee,
  entertainmentBusiness: entertainmentBusiness,
  episodeNumber: episodeNumber,
  episodes: episodes,
  equal: equal,
  error: error,
  estimatedCost: estimatedCost,
  estimatedFlightDuration: estimatedFlightDuration,
  estimatedSalary: estimatedSalary,
  eventStatus: eventStatus,
  events: events,
  exifData: exifData,
  expectedArrivalFrom: expectedArrivalFrom,
  expectedArrivalUntil: expectedArrivalUntil,
  expectsAcceptanceOf: expectsAcceptanceOf,
  experienceRequirements: experienceRequirements,
  expires: expires,
  familyName: familyName,
  fatContent: fatContent,
  faxNumber: faxNumber,
  featureList: featureList,
  feesAndCommissionsSpecification: feesAndCommissionsSpecification,
  fiberContent: fiberContent,
  fileFormat: fileFormat,
  fileSize: fileSize,
  firstPerformance: firstPerformance,
  flightDistance: flightDistance,
  flightNumber: flightNumber,
  floorSize: floorSize,
  followee: followee,
  follows: follows,
  foodEstablishment: foodEstablishment,
  foodEvent: foodEvent,
  founders: founders,
  foundingDate: foundingDate,
  foundingLocation: foundingLocation,
  free: free,
  fromLocation: fromLocation,
  fuelConsumption: fuelConsumption,
  fuelEfficiency: fuelEfficiency,
  fuelType: fuelType,
  funder: funder,
  gameItem: gameItem,
  gameLocation: gameLocation,
  gamePlatform: gamePlatform,
  gameTip: gameTip,
  gender: gender,
  genre: genre,
  geo: geo,
  geoContains: geoContains,
  geoCoveredBy: geoCoveredBy,
  geoCovers: geoCovers,
  geoCrosses: geoCrosses,
  geoDisjoint: geoDisjoint,
  geoEquals: geoEquals,
  geoIntersects: geoIntersects,
  geoMidpoint: geoMidpoint,
  geoOverlaps: geoOverlaps,
  geoRadius: geoRadius,
  geoTouches: geoTouches,
  geoWithin: geoWithin,
  geographicArea: geographicArea,
  givenName: givenName,
  globalLocationNumber: globalLocationNumber,
  grantee: grantee,
  greater: greater,
  greaterOrEqual: greaterOrEqual,
  gtin12: gtin12,
  gtin13: gtin13,
  gtin14: gtin14,
  gtin8: gtin8,
  hasCourseInstance: hasCourseInstance,
  hasDeliveryMethod: hasDeliveryMethod,
  hasDigitalDocumentPermission: hasDigitalDocumentPermission,
  hasMenuItem: hasMenuItem,
  hasMenuSection: hasMenuSection,
  hasOccupation: hasOccupation,
  hasOfferCatalog: hasOfferCatalog,
  hasPOS: hasPOS,
  headline: headline,
  height: height,
  highPrice: highPrice,
  hiringOrganization: hiringOrganization,
  homeLocation: homeLocation,
  homeTeam: homeTeam,
  honorificPrefix: honorificPrefix,
  honorificSuffix: honorificSuffix,
  hostingOrganization: hostingOrganization,
  hoursAvailable: hoursAvailable,
  httpMethod: httpMethod,
  iataCode: iataCode,
  icaoCode: icaoCode,
  illustrator: illustrator,
  inAlbum: inAlbum,
  inBroadcastLineup: inBroadcastLineup,
  inPlaylist: inPlaylist,
  incentives: incentives,
  includedComposition: includedComposition,
  includedDataCatalog: includedDataCatalog,
  includesObject: includesObject,
  industry: industry,
  ineligibleRegion: ineligibleRegion,
  ingredients: ingredients,
  installUrl: installUrl,
  instructor: instructor,
  interactionCount: interactionCount,
  interactionService: interactionService,
  interactionType: interactionType,
  interactivityType: interactivityType,
  interestRate: interestRate,
  inventoryLevel: inventoryLevel,
  isAccessoryOrSparePartFor: isAccessoryOrSparePartFor,
  isBasedOnUrl: isBasedOnUrl,
  isConsumableFor: isConsumableFor,
  isFamilyFriendly: isFamilyFriendly,
  isGift: isGift,
  isLiveBroadcast: isLiveBroadcast,
  isRelatedTo: isRelatedTo,
  isSimilarTo: isSimilarTo,
  isVariantOf: isVariantOf,
  isbn: isbn,
  isicV4: isicV4,
  isrcCode: isrcCode,
  issn: issn,
  issueNumber: issueNumber,
  issuedBy: issuedBy,
  issuedThrough: issuedThrough,
  iswcCode: iswcCode,
  item: item,
  itemCondition: itemCondition,
  itemListElement: itemListElement,
  itemListOrder: itemListOrder,
  itemOffered: itemOffered,
  itemReviewed: itemReviewed,
  itemShipped: itemShipped,
  jobLocation: jobLocation,
  keywords: keywords,
  knownVehicleDamages: knownVehicleDamages,
  knows: knows,
  landlord: landlord,
  language: language,
  lastReviewed: lastReviewed,
  latitude: latitude,
  learningResourceType: learningResourceType,
  legalName: legalName,
  leiCode: leiCode,
  lender: lender,
  lesser: lesser,
  lesserOrEqual: lesserOrEqual,
  license: license,
  line: line,
  liveBlogUpdate: liveBlogUpdate,
  loanTerm: loanTerm,
  locationCreated: locationCreated,
  lodgingUnitDescription: lodgingUnitDescription,
  lodgingUnitType: lodgingUnitType,
  logo: logo,
  longitude: longitude,
  loser: loser,
  lowPrice: lowPrice,
  lyricist: lyricist,
  lyrics: lyrics,
  mainContentOfPage: mainContentOfPage,
  manufacturer: manufacturer,
  map: map,
  mapType: mapType,
  maps: maps,
  maxPrice: maxPrice,
  maxValue: maxValue,
  maximumAttendeeCapacity: maximumAttendeeCapacity,
  mealService: mealService,
  median: median,
  members: members,
  membershipNumber: membershipNumber,
  memoryRequirements: memoryRequirements,
  mentions: mentions,
  menu: menu,
  menuAddOn: menuAddOn,
  merchant: merchant,
  messageAttachment: messageAttachment,
  mileageFromOdometer: mileageFromOdometer,
  minPrice: minPrice,
  minValue: minValue,
  minimumPaymentDue: minimumPaymentDue,
  model: model,
  modifiedTime: modifiedTime,
  mpn: mpn,
  multipleValues: multipleValues,
  musicArrangement: musicArrangement,
  musicBy: musicBy,
  musicCompositionForm: musicCompositionForm,
  musicGroupMember: musicGroupMember,
  musicReleaseFormat: musicReleaseFormat,
  musicalKey: musicalKey,
  naics: naics,
  name: name,
  namedPosition: namedPosition,
  nationality: nationality,
  netWorth: netWorth,
  nextItem: nextItem,
  nonEqual: nonEqual,
  numAdults: numAdults,
  numChildren: numChildren,
  numTracks: numTracks,
  numberOfAirbags: numberOfAirbags,
  numberOfAxles: numberOfAxles,
  numberOfBeds: numberOfBeds,
  numberOfDoors: numberOfDoors,
  numberOfEmployees: numberOfEmployees,
  numberOfEpisodes: numberOfEpisodes,
  numberOfForwardGears: numberOfForwardGears,
  numberOfItems: numberOfItems,
  numberOfPages: numberOfPages,
  numberOfPlayers: numberOfPlayers,
  numberOfPreviousOwners: numberOfPreviousOwners,
  numberOfRooms: numberOfRooms,
  numberOfSeasons: numberOfSeasons,
  numberedPosition: numberedPosition,
  nutrition: nutrition,
  occupancy: occupancy,
  occupationLocation: occupationLocation,
  occupationalCategory: occupationalCategory,
  offerCount: offerCount,
  offers: offers,
  openingHours: openingHours,
  openingHoursSpecification: openingHoursSpecification,
  opens: opens,
  operatingSystem: operatingSystem,
  opponent: opponent,
  option: option,
  orderDate: orderDate,
  orderDelivery: orderDelivery,
  orderItemNumber: orderItemNumber,
  orderItemStatus: orderItemStatus,
  orderNumber: orderNumber,
  orderQuantity: orderQuantity,
  orderStatus: orderStatus,
  orderedItem: orderedItem,
  organizer: organizer,
  originAddress: originAddress,
  ownedFrom: ownedFrom,
  ownedThrough: ownedThrough,
  owns: owns,
  pageEnd: pageEnd,
  pageStart: pageStart,
  pagination: pagination,
  parentItem: parentItem,
  parentService: parentService,
  parents: parents,
  partOfEpisode: partOfEpisode,
  partOfInvoice: partOfInvoice,
  partOfOrder: partOfOrder,
  partOfSeason: partOfSeason,
  partOfTVSeries: partOfTVSeries,
  partySize: partySize,
  passengerPriorityStatus: passengerPriorityStatus,
  passengerSequenceNumber: passengerSequenceNumber,
  paymentAccepted: paymentAccepted,
  paymentDue: paymentDue,
  paymentMethod: paymentMethod,
  paymentMethodId: paymentMethodId,
  paymentStatus: paymentStatus,
  paymentUrl: paymentUrl,
  percentile10: percentile10,
  percentile25: percentile25,
  percentile75: percentile75,
  percentile90: percentile90,
  performerIn: performerIn,
  performers: performers,
  permissionType: permissionType,
  permissions: permissions,
  permitAudience: permitAudience,
  permittedUsage: permittedUsage,
  petsAllowed: petsAllowed,
  photos: photos,
  pickupLocation: pickupLocation,
  pickupTime: pickupTime,
  playMode: playMode,
  playerType: playerType,
  playersOnline: playersOnline,
  polygon: polygon,
  postOfficeBoxNumber: postOfficeBoxNumber,
  postalCode: postalCode,
  potentialAction: potentialAction,
  predecessorOf: predecessorOf,
  prepTime: prepTime,
  previousItem: previousItem,
  previousStartDate: previousStartDate,
  price: price,
  priceComponent: priceComponent,
  priceCurrency: priceCurrency,
  priceRange: priceRange,
  priceSpecification: priceSpecification,
  priceType: priceType,
  priceValidUntil: priceValidUntil,
  primaryImageOfPage: primaryImageOfPage,
  printColumn: printColumn,
  printEdition: printEdition,
  printPage: printPage,
  printSection: printSection,
  processingTime: processingTime,
  processorRequirements: processorRequirements,
  producer: producer,
  produces: produces,
  productID: productID,
  productSupported: productSupported,
  productionCompany: productionCompany,
  productionDate: productionDate,
  proficiencyLevel: proficiencyLevel,
  programMembershipUsed: programMembershipUsed,
  programName: programName,
  programmingLanguage: programmingLanguage,
  programmingModel: programmingModel,
  propertyID: propertyID,
  proteinContent: proteinContent,
  providerMobility: providerMobility,
  providesService: providesService,
  publicAccess: publicAccess,
  publication: publication,
  publishedOn: publishedOn,
  publisher: publisher,
  publishingPrinciples: publishingPrinciples,
  purchaseDate: purchaseDate,
  query: query,
  quest: quest,
  question: question,
  ratingCount: ratingCount,
  ratingValue: ratingValue,
  readonlyValue: readonlyValue,
  realEstateAgent: realEstateAgent,
  recipe: recipe,
  recipeCategory: recipeCategory,
  recipeCuisine: recipeCuisine,
  recipeInstructions: recipeInstructions,
  recipeYield: recipeYield,
  recordLabel: recordLabel,
  referenceQuantity: referenceQuantity,
  referencesOrder: referencesOrder,
  regionsAllowed: regionsAllowed,
  relatedLink: relatedLink,
  relatedTo: relatedTo,
  releaseDate: releaseDate,
  releaseNotes: releaseNotes,
  releasedEvent: releasedEvent,
  relevantOccupation: relevantOccupation,
  remainingAttendeeCapacity: remainingAttendeeCapacity,
  replacee: replacee,
  replacer: replacer,
  replyToUrl: replyToUrl,
  reportNumber: reportNumber,
  representativeOfPage: representativeOfPage,
  requiredCollateral: requiredCollateral,
  requiredGender: requiredGender,
  requiredMaxAge: requiredMaxAge,
  requiredMinAge: requiredMinAge,
  requiredQuantity: requiredQuantity,
  requirements: requirements,
  requiresSubscription: requiresSubscription,
  reservationFor: reservationFor,
  reservationId: reservationId,
  reservationStatus: reservationStatus,
  reservedTicket: reservedTicket,
  responsibilities: responsibilities,
  resultComment: resultComment,
  resultReview: resultReview,
  reviewAspect: reviewAspect,
  reviewBody: reviewBody,
  reviewCount: reviewCount,
  reviewRating: reviewRating,
  reviewedBy: reviewedBy,
  reviews: reviews,
  rsvpResponse: rsvpResponse,
  runtime: runtime,
  salaryCurrency: salaryCurrency,
  sameAs: sameAs,
  sampleType: sampleType,
  saturatedFatContent: saturatedFatContent,
  scheduledPaymentDate: scheduledPaymentDate,
  scheduledTime: scheduledTime,
  schemaVersion: schemaVersion,
  screenCount: screenCount,
  screenshot: screenshot,
  seasonNumber: seasonNumber,
  seasons: seasons,
  seatNumber: seatNumber,
  seatRow: seatRow,
  seatSection: seatSection,
  seatingType: seatingType,
  securityScreening: securityScreening,
  seeks: seeks,
  sender: sender,
  serverStatus: serverStatus,
  servesCuisine: servesCuisine,
  serviceAudience: serviceAudience,
  serviceLocation: serviceLocation,
  serviceOperator: serviceOperator,
  servicePhone: servicePhone,
  servicePostalAddress: servicePostalAddress,
  serviceSmsNumber: serviceSmsNumber,
  serviceType: serviceType,
  serviceUrl: serviceUrl,
  servingSize: servingSize,
  sharedContent: sharedContent,
  siblings: siblings,
  significantLinks: significantLinks,
  skills: skills,
  sku: sku,
  slogan: slogan,
  smokingAllowed: smokingAllowed,
  sodiumContent: sodiumContent,
  softwareAddOn: softwareAddOn,
  softwareHelp: softwareHelp,
  softwareVersion: softwareVersion,
  sourceOrganization: sourceOrganization,
  spatial: spatial,
  spatialCoverage: spatialCoverage,
  speakable: speakable,
  specialCommitments: specialCommitments,
  specialOpeningHoursSpecification: specialOpeningHoursSpecification,
  specialty: specialty,
  sport: sport,
  sportsActivityLocation: sportsActivityLocation,
  sportsEvent: sportsEvent,
  sportsTeam: sportsTeam,
  spouse: spouse,
  starRating: starRating,
  startDate: startDate,
  startTime: startTime,
  steeringPosition: steeringPosition,
  stepValue: stepValue,
  steps: steps,
  storageRequirements: storageRequirements,
  streetAddress: streetAddress,
  subEvents: subEvents,
  subReservation: subReservation,
  subtitleLanguage: subtitleLanguage,
  successorOf: successorOf,
  sugarContent: sugarContent,
  suggestedGender: suggestedGender,
  suggestedMaxAge: suggestedMaxAge,
  suggestedMinAge: suggestedMinAge,
  suitableForDiet: suitableForDiet,
  supportingData: supportingData,
  surface: surface,
  target: target,
  targetDescription: targetDescription,
  targetName: targetName,
  targetPlatform: targetPlatform,
  targetProduct: targetProduct,
  targetUrl: targetUrl,
  taxID: taxID,
  telephone: telephone,
  temporal: temporal,
  text: text,
  thumbnail: thumbnail,
  thumbnailUrl: thumbnailUrl,
  tickerSymbol: tickerSymbol,
  ticketNumber: ticketNumber,
  ticketToken: ticketToken,
  ticketedSeat: ticketedSeat,
  timeRequired: timeRequired,
  title: title,
  toLocation: toLocation,
  toRecipient: toRecipient,
  tool: tool,
  totalPaymentDue: totalPaymentDue,
  totalPrice: totalPrice,
  totalTime: totalTime,
  touristType: touristType,
  trackingNumber: trackingNumber,
  trackingUrl: trackingUrl,
  tracks: tracks,
  trailer: trailer,
  trainName: trainName,
  trainNumber: trainNumber,
  transFatContent: transFatContent,
  transcript: transcript,
  translator: translator,
  typeOfBed: typeOfBed,
  typeOfGood: typeOfGood,
  typicalAgeRange: typicalAgeRange,
  underName: underName,
  unitCode: unitCode,
  unitText: unitText,
  unsaturatedFatContent: unsaturatedFatContent,
  uploadDate: uploadDate,
  upvoteCount: upvoteCount,
  url: url,
  urlTemplate: urlTemplate,
  userInteractionCount: userInteractionCount,
  validFor: validFor,
  validFrom: validFrom,
  validIn: validIn,
  validThrough: validThrough,
  validUntil: validUntil,
  value: value$1,
  valueAddedTaxIncluded: valueAddedTaxIncluded,
  valueMaxLength: valueMaxLength,
  valueMinLength: valueMinLength,
  valueName: valueName,
  valuePattern: valuePattern,
  valueReference: valueReference,
  valueRequired: valueRequired,
  vatID: vatID,
  vehicleConfiguration: vehicleConfiguration,
  vehicleEngine: vehicleEngine,
  vehicleIdentificationNumber: vehicleIdentificationNumber,
  vehicleInteriorColor: vehicleInteriorColor,
  vehicleInteriorType: vehicleInteriorType,
  vehicleModelDate: vehicleModelDate,
  vehicleSeatingCapacity: vehicleSeatingCapacity,
  vehicleTransmission: vehicleTransmission,
  vendor: vendor,
  version: version,
  video: video,
  videoFormat: videoFormat,
  videoFrameSize: videoFrameSize,
  videoQuality: videoQuality,
  volumeNumber: volumeNumber,
  warrantyPromise: warrantyPromise,
  warrantyScope: warrantyScope,
  webCheckinTime: webCheckinTime,
  weight: weight,
  width: width,
  winner: winner,
  wordCount: wordCount,
  workHours: workHours,
  workLocation: workLocation,
  workPerformed: workPerformed,
  workPresented: workPresented,
  worksFor: worksFor,
  worstRating: worstRating,
  xpath: xpath,
  yearlyRevenue: yearlyRevenue,
  yearsInOperation: yearsInOperation,
  actionApplication: actionApplication,
  actionOption: actionOption,
  actor: actor,
  album: album,
  albumRelease: albumRelease,
  alumni: alumni,
  alumniOf: alumniOf,
  artworkSurface: artworkSurface,
  attendee: attendee,
  audience: audience,
  availableOnDevice: availableOnDevice,
  award: award,
  blogPost: blogPost,
  broker: broker,
  codeSampleType: codeSampleType,
  colleague: colleague,
  contactPoint: contactPoint,
  containsPlace: containsPlace,
  containsSeason: containsSeason,
  contentLocation: contentLocation,
  dataset: dataset,
  description: description,
  director: director,
  duration: duration,
  employee: employee,
  encodesCreativeWork: encodesCreativeWork,
  encodingFormat: encodingFormat,
  episode: episode,
  event: event,
  exampleOfWork: exampleOfWork,
  executableLibraryName: executableLibraryName,
  exerciseCourse: exerciseCourse,
  founder: founder,
  game: game,
  gameServer: gameServer,
  hasBroadcastChannel: hasBroadcastChannel,
  hasMenu: hasMenu,
  inLanguage: inLanguage,
  incentiveCompensation: incentiveCompensation,
  interactionStatistic: interactionStatistic,
  isAccessibleForFree: isAccessibleForFree,
  isBasedOn: isBasedOn,
  jobBenefits: jobBenefits,
  mainEntity: mainEntity,
  mainEntityOfPage: mainEntityOfPage,
  makesOffer: makesOffer,
  offeredBy: offeredBy,
  parent: parent,
  partOfSeries: partOfSeries,
  paymentDueDate: paymentDueDate,
  performTime: performTime,
  performer: performer,
  photo: photo,
  provider: provider,
  providesBroadcastService: providesBroadcastService,
  recipeIngredient: recipeIngredient,
  recordedAs: recordedAs,
  recordedAt: recordedAt,
  recordedIn: recordedIn,
  recordingOf: recordingOf,
  releaseOf: releaseOf,
  review: review,
  roleName: roleName,
  runtimePlatform: runtimePlatform,
  season: season,
  serialNumber: serialNumber,
  serviceArea: serviceArea,
  serviceOutput: serviceOutput,
  sibling: sibling,
  significantLink: significantLink,
  softwareRequirements: softwareRequirements,
  sponsor: sponsor,
  subOrganization: subOrganization,
  subjectOf: subjectOf,
  suggestedAnswer: suggestedAnswer,
  superEvent: superEvent,
  targetCollection: targetCollection,
  temporalCoverage: temporalCoverage,
  track: track,
  warranty: warranty,
  workExample: workExample,
  schemayield: schemayield,
  about: about,
  competitor: competitor,
  containedInPlace: containedInPlace,
  encoding: encoding,
  hasMap: hasMap,
  image: image,
  material: material,
  memberOf: memberOf,
  parentOrganization: parentOrganization,
  result: result,
  seller: seller,
  step: step,
  subEvent: subEvent,
  supply: supply,
  workFeatured: workFeatured,
  areaServed: areaServed,
  includedInDataCatalog: includedInDataCatalog,
  member: member,
  recipient: recipient,
  hasPart: hasPart,
  isPartOf: isPartOf,
  position: position,
  instrument: instrument,
  object: object$1,
  location: location,
  participant: participant,
  identifier: identifier
});

/** Function to create arbitrary terms within the 'RDF-Schema' ontology */
const ns$2 = createNS("http://www.w3.org/2000/01/rdf-schema#");
/** The class resource, everything. */
const Resource = ns$2("Resource");
/** The class of classes. */
const Class = ns$2("Class");
/** The class of literal values, eg. textual strings and integers. */
const Literal = ns$2("Literal");
/** The class of RDF containers. */
const Container = ns$2("Container");
/** The class of container membership properties, rdf:_1, rdf:_2, ...,
                    all of which are sub-properties of 'member'. */
const ContainerMembershipProperty = ns$2("ContainerMembershipProperty");
/** The class of RDF datatypes. */
const Datatype = ns$2("Datatype");
/** The subject is a subclass of a class. */
const subClassOf = ns$2("subClassOf");
/** The subject is a subproperty of a property. */
const subPropertyOf = ns$2("subPropertyOf");
/** A description of the subject resource. */
const comment$1 = ns$2("comment");
/** A human-readable name for the subject. */
const label = ns$2("label");
/** A domain of the subject property. */
const domain = ns$2("domain");
/** A range of the subject property. */
const range = ns$2("range");
/** Further information about the subject resource. */
const seeAlso = ns$2("seeAlso");
/** The defininition of the subject resource. */
const isDefinedBy = ns$2("isDefinedBy");
/** A member of the subject resource. */
const member$1 = ns$2("member");
var rdfs = {
    /** Function to create arbitrary terms within the 'RDF-Schema' ontology */
    ns: ns$2,
    /** The class resource, everything. */
    Resource,
    /** The class of classes. */
    Class,
    /** The class of literal values, eg. textual strings and integers. */
    Literal,
    /** The class of RDF containers. */
    Container,
    /** The class of container membership properties, rdf:_1, rdf:_2, ...,
                        all of which are sub-properties of 'member'. */
    ContainerMembershipProperty,
    /** The class of RDF datatypes. */
    Datatype,
    /** The subject is a subclass of a class. */
    subClassOf,
    /** The subject is a subproperty of a property. */
    subPropertyOf,
    /** A description of the subject resource. */
    comment: comment$1,
    /** A human-readable name for the subject. */
    label,
    /** A domain of the subject property. */
    domain,
    /** A range of the subject property. */
    range,
    /** Further information about the subject resource. */
    seeAlso,
    /** The defininition of the subject resource. */
    isDefinedBy,
    /** A member of the subject resource. */
    member: member$1
};

/** Function to create arbitrary terms within the 'ActivityStreams 2.0' ontology */
const ns$3 = createNS("http://www.w3.org/ns/activitystreams#");
/** Actor accepts the Object */
const Accept = ns$3("Accept");
/** An Object representing some form of Action that has been taken */
const Activity = ns$3("Activity");
const Block = ns$3("Block");
/** An Activity that has no direct object */
const IntransitiveActivity = ns$3("IntransitiveActivity");
/** To Add an Object or Link to Something */
const Add = ns$3("Add");
/** Actor announces the object to the target */
const Announce = ns$3("Announce");
/** Represents a software application of any sort */
const Application = ns$3("Application");
/** To Arrive Somewhere (can be used, for instance, to indicate that a particular entity is currently located somewhere, e.g. a "check-in") */
const Arrive = ns$3("Arrive");
/** A written work. Typically several paragraphs long. For example, a blog post or a news article. */
const Article$1 = ns$3("Article");
/** An audio file */
const Audio = ns$3("Audio");
/** An ordered or unordered collection of Objects or Links */
const Collection = ns$3("Collection");
/** A subset of items from a Collection */
const CollectionPage$1 = ns$3("CollectionPage");
/** An ordered subset of items from an OrderedCollection */
const OrderedCollectionPage = ns$3("OrderedCollectionPage");
/** Represents a Social Graph relationship between two Individuals (indicated by the 'a' and 'b' properties) */
const Relationship = ns$3("Relationship");
/** To Create Something */
const Create = ns$3("Create");
/** To Delete Something */
const Delete = ns$3("Delete");
/** The actor dislikes the object */
const Dislike = ns$3("Dislike");
/** Represents a digital document/file of any sort */
const Document = ns$3("Document");
/** An Event of any kind */
const Event$1 = ns$3("Event");
/** To flag something (e.g. flag as inappropriate, flag as spam, etc) */
const Flag = ns$3("Flag");
/** To Express Interest in Something */
const Follow = ns$3("Follow");
/** A Group of any kind. */
const Group = ns$3("Group");
/** Actor is ignoring the Object */
const Ignore = ns$3("Ignore");
/** An Image file */
const Image = ns$3("Image");
/** To invite someone or something to something */
const Invite = ns$3("Invite");
/** To Join Something */
const Join = ns$3("Join");
/** To Leave Something */
const Leave = ns$3("Leave");
/** To Like Something */
const Like = ns$3("Like");
/** The actor viewed the object */
const View = ns$3("View");
/** The actor listened to the object */
const Listen = ns$3("Listen");
/** The actor read the object */
const Read = ns$3("Read");
/** The actor is moving the object. The target specifies where the object is moving to. The origin specifies where the object is moving from. */
const Move = ns$3("Move");
/** The actor is traveling to the target. The origin specifies where the actor is traveling from. */
const Travel = ns$3("Travel");
/** Represents a qualified reference to another resource. Patterned after the RFC5988 Web Linking Model */
const Link = ns$3("Link");
/** A specialized Link that represents an @mention */
const Mention = ns$3("Mention");
/** A Short note, typically less than a single paragraph. A "tweet" is an example, or a "status update" */
const Note = ns$3("Note");
const Object$1 = ns$3("Object");
/** To Offer something to someone or something */
const Offer$1 = ns$3("Offer");
/** A variation of Collection in which items are strictly ordered */
const OrderedCollection = ns$3("OrderedCollection");
/** A rdf:List variant for Objects and Links */
const OrderedItems = ns$3("OrderedItems");
/** A Web Page */
const Page = ns$3("Page");
/** A Person */
const Person$1 = ns$3("Person");
/** An Organization */
const Organization$1 = ns$3("Organization");
/** A Profile Document */
const Profile = ns$3("Profile");
/** A physical or logical location */
const Place$1 = ns$3("Place");
/** A question of any sort. */
const Question$1 = ns$3("Question");
/** Actor rejects the Object */
const Reject = ns$3("Reject");
/** To Remove Something */
const Remove = ns$3("Remove");
/** A service provided by some entity */
const Service$1 = ns$3("Service");
/** Actor tentatively accepts the Object */
const TentativeAccept = ns$3("TentativeAccept");
/** Actor tentatively rejects the object */
const TentativeReject = ns$3("TentativeReject");
/** A placeholder for a deleted object */
const Tombstone = ns$3("Tombstone");
/** To Undo Something. This would typically be used to indicate that a previous Activity has been undone. */
const Undo = ns$3("Undo");
/** To Update/Modify Something */
const Update = ns$3("Update");
/** A Video document of any kind. */
const Video = ns$3("Video");
/** Subproperty of as:attributedTo that identifies the primary actor */
const actor$1 = ns$3("actor");
/** Identifies an entity to which an object is attributed */
const attributedTo = ns$3("attributedTo");
const attachment = ns$3("attachment");
const attachments = ns$3("attachments");
/** Identifies the author of an object. Deprecated. Use as:attributedTo instead */
const author$1 = ns$3("author");
const bcc = ns$3("bcc");
const bto = ns$3("bto");
const cc = ns$3("cc");
/** Specifies the context within which an object exists or an activity was performed */
const context = ns$3("context");
const current = ns$3("current");
const first$1 = ns$3("first");
const generator = ns$3("generator");
const icon = ns$3("icon");
const image$1 = ns$3("image");
const inReplyTo = ns$3("inReplyTo");
const items = ns$3("items");
const last = ns$3("last");
const location$1 = ns$3("location");
const next = ns$3("next");
const object$2 = ns$3("object");
/** Describes a possible exclusive answer or option for a question. */
const oneOf = ns$3("oneOf");
/** Describes a possible inclusive answer or option for a question. */
const anyOf = ns$3("anyOf");
const prev = ns$3("prev");
const preview = ns$3("preview");
const provider$1 = ns$3("provider");
const replies = ns$3("replies");
const result$1 = ns$3("result");
const audience$1 = ns$3("audience");
const partOf = ns$3("partOf");
const tag = ns$3("tag");
const tags = ns$3("tags");
const target$1 = ns$3("target");
/** For certain activities, specifies the entity from which the action is directed. */
const origin = ns$3("origin");
/** Indentifies an object used (or to be used) to complete an activity */
const instrument$1 = ns$3("instrument");
const to = ns$3("to");
/** Specifies a link to a specific representation of the Object */
const url$1 = ns$3("url");
/** On a Relationship object, identifies the subject. e.g. when saying "John is connected to Sally", 'subject' refers to 'John' */
const subject$1 = ns$3("subject");
/** On a Relationship object, describes the type of relationship */
const relationship = ns$3("relationship");
/** On a Profile object, describes the object described by the profile */
const describes = ns$3("describes");
/** On a Tombstone object, describes the former type of the deleted object */
const formerType = ns$3("formerType");
/** Specifies the accuracy around the point established by the longitude and latitude */
const accuracy = ns$3("accuracy");
/** The altitude of a place */
const altitude = ns$3("altitude");
/** The content of the object. */
const content = ns$3("content");
const name$1 = ns$3("name");
const downstreamDuplicates = ns$3("downstreamDuplicates");
/** The duration of the object */
const duration$1 = ns$3("duration");
/** The ending time of the object */
const endTime$1 = ns$3("endTime");
/** The display height expressed as device independent pixels */
const height$1 = ns$3("height");
/** The target URI of the Link */
const href = ns$3("href");
/** A hint about the language of the referenced resource */
const hreflang = ns$3("hreflang");
const id = ns$3("id");
/** The latitude */
const latitude$1 = ns$3("latitude");
/** The longitude */
const longitude$1 = ns$3("longitude");
/** The MIME Media Type */
const mediaType = ns$3("mediaType");
const objectType = ns$3("objectType");
/** Specifies the date and time the object was published */
const published = ns$3("published");
/** Specifies a radius around the point established by the longitude and latitude */
const radius = ns$3("radius");
/** A numeric rating (>= 0.0, <= 5.0) for the object */
const rating = ns$3("rating");
/** The RFC 5988 or HTML5 Link Relation associated with the Link */
const rel = ns$3("rel");
/** In a strictly ordered logical collection, specifies the index position of the first item in the items list */
const startIndex = ns$3("startIndex");
/** The starting time of the object */
const startTime$1 = ns$3("startTime");
/** A short summary of the object */
const summary = ns$3("summary");
/** The total number of items in a logical collection */
const totalItems = ns$3("totalItems");
/** Identifies the unit of measurement used by the radius, altitude and accuracy properties. The value can be expressed either as one of a set of predefined units or as a well-known common URI that identifies units. */
const units = ns$3("units");
/** Specifies when the object was last updated */
const updated = ns$3("updated");
const upstreamDuplicates = ns$3("upstreamDuplicates");
const verb = ns$3("verb");
/** Specifies the preferred display width of the content, expressed in terms of device independent pixels. */
const width$1 = ns$3("width");
/** Specifies the date and time the object was deleted */
const deleted = ns$3("deleted");
var as = {
    /** Function to create arbitrary terms within the 'ActivityStreams 2.0' ontology */
    ns: ns$3,
    /** Actor accepts the Object */
    Accept,
    /** An Object representing some form of Action that has been taken */
    Activity,
    Block,
    /** An Activity that has no direct object */
    IntransitiveActivity,
    /** To Add an Object or Link to Something */
    Add,
    /** Actor announces the object to the target */
    Announce,
    /** Represents a software application of any sort */
    Application,
    /** To Arrive Somewhere (can be used, for instance, to indicate that a particular entity is currently located somewhere, e.g. a "check-in") */
    Arrive,
    /** A written work. Typically several paragraphs long. For example, a blog post or a news article. */
    Article: Article$1,
    /** An audio file */
    Audio,
    /** An ordered or unordered collection of Objects or Links */
    Collection,
    /** A subset of items from a Collection */
    CollectionPage: CollectionPage$1,
    /** An ordered subset of items from an OrderedCollection */
    OrderedCollectionPage,
    /** Represents a Social Graph relationship between two Individuals (indicated by the 'a' and 'b' properties) */
    Relationship,
    /** To Create Something */
    Create,
    /** To Delete Something */
    Delete,
    /** The actor dislikes the object */
    Dislike,
    /** Represents a digital document/file of any sort */
    Document,
    /** An Event of any kind */
    Event: Event$1,
    /** To flag something (e.g. flag as inappropriate, flag as spam, etc) */
    Flag,
    /** To Express Interest in Something */
    Follow,
    /** A Group of any kind. */
    Group,
    /** Actor is ignoring the Object */
    Ignore,
    /** An Image file */
    Image,
    /** To invite someone or something to something */
    Invite,
    /** To Join Something */
    Join,
    /** To Leave Something */
    Leave,
    /** To Like Something */
    Like,
    /** The actor viewed the object */
    View,
    /** The actor listened to the object */
    Listen,
    /** The actor read the object */
    Read,
    /** The actor is moving the object. The target specifies where the object is moving to. The origin specifies where the object is moving from. */
    Move,
    /** The actor is traveling to the target. The origin specifies where the actor is traveling from. */
    Travel,
    /** Represents a qualified reference to another resource. Patterned after the RFC5988 Web Linking Model */
    Link,
    /** A specialized Link that represents an @mention */
    Mention,
    /** A Short note, typically less than a single paragraph. A "tweet" is an example, or a "status update" */
    Note,
    Object: Object$1,
    /** To Offer something to someone or something */
    Offer: Offer$1,
    /** A variation of Collection in which items are strictly ordered */
    OrderedCollection,
    /** A rdf:List variant for Objects and Links */
    OrderedItems,
    /** A Web Page */
    Page,
    /** A Person */
    Person: Person$1,
    /** An Organization */
    Organization: Organization$1,
    /** A Profile Document */
    Profile,
    /** A physical or logical location */
    Place: Place$1,
    /** A question of any sort. */
    Question: Question$1,
    /** Actor rejects the Object */
    Reject,
    /** To Remove Something */
    Remove,
    /** A service provided by some entity */
    Service: Service$1,
    /** Actor tentatively accepts the Object */
    TentativeAccept,
    /** Actor tentatively rejects the object */
    TentativeReject,
    /** A placeholder for a deleted object */
    Tombstone,
    /** To Undo Something. This would typically be used to indicate that a previous Activity has been undone. */
    Undo,
    /** To Update/Modify Something */
    Update,
    /** A Video document of any kind. */
    Video,
    /** Subproperty of as:attributedTo that identifies the primary actor */
    actor: actor$1,
    /** Identifies an entity to which an object is attributed */
    attributedTo,
    attachment,
    attachments,
    /** Identifies the author of an object. Deprecated. Use as:attributedTo instead */
    author: author$1,
    bcc,
    bto,
    cc,
    /** Specifies the context within which an object exists or an activity was performed */
    context,
    current,
    first: first$1,
    generator,
    icon,
    image: image$1,
    inReplyTo,
    items,
    last,
    location: location$1,
    next,
    object: object$2,
    /** Describes a possible exclusive answer or option for a question. */
    oneOf,
    /** Describes a possible inclusive answer or option for a question. */
    anyOf,
    prev,
    preview,
    provider: provider$1,
    replies,
    result: result$1,
    audience: audience$1,
    partOf,
    tag,
    tags,
    target: target$1,
    /** For certain activities, specifies the entity from which the action is directed. */
    origin,
    /** Indentifies an object used (or to be used) to complete an activity */
    instrument: instrument$1,
    to,
    /** Specifies a link to a specific representation of the Object */
    url: url$1,
    /** On a Relationship object, identifies the subject. e.g. when saying "John is connected to Sally", 'subject' refers to 'John' */
    subject: subject$1,
    /** On a Relationship object, describes the type of relationship */
    relationship,
    /** On a Profile object, describes the object described by the profile */
    describes,
    /** On a Tombstone object, describes the former type of the deleted object */
    formerType,
    /** Specifies the accuracy around the point established by the longitude and latitude */
    accuracy,
    /** The altitude of a place */
    altitude,
    /** The content of the object. */
    content,
    name: name$1,
    downstreamDuplicates,
    /** The duration of the object */
    duration: duration$1,
    /** The ending time of the object */
    endTime: endTime$1,
    /** The display height expressed as device independent pixels */
    height: height$1,
    /** The target URI of the Link */
    href,
    /** A hint about the language of the referenced resource */
    hreflang,
    id,
    /** The latitude */
    latitude: latitude$1,
    /** The longitude */
    longitude: longitude$1,
    /** The MIME Media Type */
    mediaType,
    objectType,
    /** Specifies the date and time the object was published */
    published,
    /** Specifies a radius around the point established by the longitude and latitude */
    radius,
    /** A numeric rating (>= 0.0, <= 5.0) for the object */
    rating,
    /** The RFC 5988 or HTML5 Link Relation associated with the Link */
    rel,
    /** In a strictly ordered logical collection, specifies the index position of the first item in the items list */
    startIndex,
    /** The starting time of the object */
    startTime: startTime$1,
    /** A short summary of the object */
    summary,
    /** The total number of items in a logical collection */
    totalItems,
    /** Identifies the unit of measurement used by the radius, altitude and accuracy properties. The value can be expressed either as one of a set of predefined units or as a well-known common URI that identifies units. */
    units,
    /** Specifies when the object was last updated */
    updated,
    upstreamDuplicates,
    verb,
    /** Specifies the preferred display width of the content, expressed in terms of device independent pixels. */
    width: width$1,
    /** Specifies the date and time the object was deleted */
    deleted
};

/** Function to create arbitrary terms within the 'W3C Shapes Constraint Language (SHACL) Vocabulary' ontology */
const ns$4 = createNS("http://www.w3.org/ns/shacl#");
/** A shape is a collection of constraints that may be targeted for certain nodes. */
const Shape = ns$4("Shape");
/** A node shape is a shape that specifies constraint that need to be met with respect to focus nodes. */
const NodeShape = ns$4("NodeShape");
/** A property shape is a shape that specifies constraints on the values of a focus node for a given property or path. */
const PropertyShape = ns$4("PropertyShape");
/** The class of all node kinds, including sh:BlankNode, sh:IRI, sh:Literal or the combinations of these: sh:BlankNodeOrIRI, sh:BlankNodeOrLiteral, sh:IRIOrLiteral. */
const NodeKind = ns$4("NodeKind");
/** The class of SHACL validation reports. */
const ValidationReport = ns$4("ValidationReport");
/** The base class of validation results, typically not instantiated directly. */
const AbstractResult = ns$4("AbstractResult");
/** The class of validation results. */
const ValidationResult = ns$4("ValidationResult");
/** The class of validation result severity levels, including violation and warning levels. */
const Severity = ns$4("Severity");
/** Superclass of components that can take parameters, especially functions and constraint components. */
const Parameterizable = ns$4("Parameterizable");
/** The class of parameter declarations, consisting of a path predicate and (possibly) information about allowed value type, cardinality and other characteristics. */
const Parameter = ns$4("Parameter");
/** The class of constraint components. */
const ConstraintComponent = ns$4("ConstraintComponent");
/** The class of validators, which provide instructions on how to process a constraint definition. This class serves as base class for the SPARQL-based validators and other possible implementations. */
const Validator = ns$4("Validator");
/** The class of validators based on SPARQL ASK queries. The queries are evaluated for each value node and are supposed to return true if the given node conforms. */
const SPARQLAskValidator = ns$4("SPARQLAskValidator");
/** The class of validators based on SPARQL SELECT queries. The queries are evaluated for each focus node and are supposed to produce bindings for all focus nodes that do not conform. */
const SPARQLSelectValidator = ns$4("SPARQLSelectValidator");
/** The class of resources that encapsulate a SPARQL query. */
const SPARQLExecutable = ns$4("SPARQLExecutable");
/** The class of SPARQL executables that are based on an ASK query. */
const SPARQLAskExecutable = ns$4("SPARQLAskExecutable");
/** The class of SPARQL executables that are based on a CONSTRUCT query. */
const SPARQLConstructExecutable = ns$4("SPARQLConstructExecutable");
/** The class of SPARQL executables based on a SELECT query. */
const SPARQLSelectExecutable = ns$4("SPARQLSelectExecutable");
/** The class of SPARQL executables based on a SPARQL UPDATE. */
const SPARQLUpdateExecutable = ns$4("SPARQLUpdateExecutable");
/** The class of prefix declarations, consisting of pairs of a prefix with a namespace. */
const PrefixDeclaration = ns$4("PrefixDeclaration");
/** The class of constraints based on SPARQL SELECT queries. */
const SPARQLConstraint = ns$4("SPARQLConstraint");
/** Instances of this class represent groups of property shapes that belong together. */
const PropertyGroup = ns$4("PropertyGroup");
/** The base class of targets such as those based on SPARQL queries. */
const Target = ns$4("Target");
/** The (meta) class for parameterizable targets.	Instances of this are instantiated as values of the sh:target property. */
const TargetType = ns$4("TargetType");
/** The class of targets that are based on SPARQL queries. */
const SPARQLTarget = ns$4("SPARQLTarget");
/** The (meta) class for parameterizable targets that are based on SPARQL queries. */
const SPARQLTargetType = ns$4("SPARQLTargetType");
/** The class of SHACL functions. */
const Function = ns$4("Function");
/** A function backed by a SPARQL query - either ASK or SELECT. */
const SPARQLFunction = ns$4("SPARQLFunction");
/** A class of result annotations, which define the rules to derive the values of a given annotation property as extra values for a validation result. */
const ResultAnnotation = ns$4("ResultAnnotation");
/** The class of SHACL rules. Never instantiated directly. */
const Rule = ns$4("Rule");
const TripleRule = ns$4("TripleRule");
/** The class of SHACL rules based on SPARQL CONSTRUCT queries. */
const SPARQLRule = ns$4("SPARQLRule");
/** Abstract base class of resources that declare an executable JavaScript. */
const JSExecutable = ns$4("JSExecutable");
/** The class of targets that are based on JavaScript functions. */
const JSTarget = ns$4("JSTarget");
/** The (meta) class for parameterizable targets that are based on JavaScript functions. */
const JSTargetType = ns$4("JSTargetType");
/** The class of constraints backed by a JavaScript function. */
const JSConstraint = ns$4("JSConstraint");
/** The class of SHACL functions that execute a JavaScript function when called. */
const JSFunction = ns$4("JSFunction");
/** Represents a JavaScript library, typically identified by one or more URLs of files to include. */
const JSLibrary = ns$4("JSLibrary");
/** The class of SHACL rules expressed using JavaScript. */
const JSRule = ns$4("JSRule");
/** A SHACL validator based on JavaScript. This can be used to declare SHACL constraint components that perform JavaScript-based validation when used. */
const JSValidator = ns$4("JSValidator");
/** If set to true then all nodes conform to this. */
const deactivated = ns$4("deactivated");
/** Links a shape to a class, indicating that all instances of the class must conform to the shape. */
const targetClass = ns$4("targetClass");
/** Links a shape to individual nodes, indicating that these nodes must conform to the shape. */
const targetNode = ns$4("targetNode");
/** Links a shape to a property, indicating that all all objects of triples that have the given property as their predicate must conform to the shape. */
const targetObjectsOf = ns$4("targetObjectsOf");
/** Links a shape to a property, indicating that all subjects of triples that have the given property as their predicate must conform to the shape. */
const targetSubjectsOf = ns$4("targetSubjectsOf");
/** A human-readable message (possibly with placeholders for variables) explaining the cause of the result. */
const message = ns$4("message");
/** Defines the severity that validation results produced by a shape must have. Defaults to sh:Violation. */
const severity = ns$4("severity");
/** True if the validation did not produce any validation results, and false otherwise. */
const conforms = ns$4("conforms");
/** The validation results contained in a validation report. */
const result$2 = ns$4("result");
/** If true then the validation engine was certain that the shapes graph has passed all SHACL syntax requirements during the validation process. */
const shapesGraphWellFormed = ns$4("shapesGraphWellFormed");
/** Links a result with other results that provide more details, for example to describe violations against nested shapes. */
const detail = ns$4("detail");
/** The focus node that was validated when the result was produced. */
const focusNode = ns$4("focusNode");
/** Human-readable messages explaining the cause of the result. */
const resultMessage = ns$4("resultMessage");
/** The path of a validation result, based on the path of the validated property shape. */
const resultPath = ns$4("resultPath");
/** The severity of the result, e.g. warning. */
const resultSeverity = ns$4("resultSeverity");
/** The constraint that was validated when the result was produced. */
const sourceConstraint = ns$4("sourceConstraint");
/** The shape that is was validated when the result was produced. */
const sourceShape = ns$4("sourceShape");
/** The constraint component that is the source of the result. */
const sourceConstraintComponent = ns$4("sourceConstraintComponent");
/** An RDF node that has caused the result. */
const value$2 = ns$4("value");
/** Shapes graphs that should be used when validating this data graph. */
const shapesGraph = ns$4("shapesGraph");
/** Suggested shapes graphs for this ontology. The values of this property may be used in the absence of specific sh:shapesGraph statements. */
const suggestedShapesGraph = ns$4("suggestedShapesGraph");
/** An entailment regime that indicates what kind of inferencing is required by a shapes graph. */
const entailment = ns$4("entailment");
/** Specifies the property path of a property shape. */
const path = ns$4("path");
/** The (single) value of this property represents an inverse path (object to subject). */
const inversePath = ns$4("inversePath");
/** The (single) value of this property must be a list of path elements, representing the elements of alternative paths. */
const alternativePath = ns$4("alternativePath");
/** The (single) value of this property represents a path that is matched zero or more times. */
const zeroOrMorePath = ns$4("zeroOrMorePath");
/** The (single) value of this property represents a path that is matched one or more times. */
const oneOrMorePath = ns$4("oneOrMorePath");
/** The (single) value of this property represents a path that is matched zero or one times. */
const zeroOrOnePath = ns$4("zeroOrOnePath");
/** The parameters of a function or constraint component. */
const parameter = ns$4("parameter");
/** Outlines how human-readable labels of instances of the associated Parameterizable shall be produced. The values can contain {?paramName} as placeholders for the actual values of the given parameter. */
const labelTemplate = ns$4("labelTemplate");
/** Indicates whether a parameter is optional. */
const optional = ns$4("optional");
/** The validator(s) used to evaluate constraints of either node or property shapes. */
const validator = ns$4("validator");
/** The validator(s) used to evaluate a constraint in the context of a node shape. */
const nodeValidator = ns$4("nodeValidator");
/** The validator(s) used to evaluate a constraint in the context of a property shape. */
const propertyValidator = ns$4("propertyValidator");
/** RDF list of shapes to validate the value nodes against. */
const and = ns$4("and");
/** The type that all value nodes must have. */
const shaclclass = ns$4("class");
/** If set to true then the shape is closed. */
const closed = ns$4("closed");
/** An optional RDF list of properties that are also permitted in addition to those explicitly enumerated via sh:property/sh:path. */
const ignoredProperties = ns$4("ignoredProperties");
/** Specifies an RDF datatype that all value nodes must have. */
const datatype = ns$4("datatype");
/** Specifies a property where the set of values must be disjoint with the value nodes. */
const disjoint = ns$4("disjoint");
/** Specifies a property that must have the same values as the value nodes. */
const equals$1 = ns$4("equals");
/** Specifies a value that must be among the value nodes. */
const hasValue = ns$4("hasValue");
/** Specifies a list of allowed values so that each value node must be among the members of the given list. */
const shaclin = ns$4("in");
/** Specifies a list of language tags that all value nodes must have. */
const languageIn = ns$4("languageIn");
/** Specifies a property that must have smaller values than the value nodes. */
const lessThan = ns$4("lessThan");
/** Specifies a property that must have smaller or equal values than the value nodes. */
const lessThanOrEquals = ns$4("lessThanOrEquals");
/** Specifies the maximum number of values in the set of value nodes. */
const maxCount = ns$4("maxCount");
/** Specifies the maximum exclusive value of each value node. */
const maxExclusive = ns$4("maxExclusive");
/** Specifies the maximum inclusive value of each value node. */
const maxInclusive = ns$4("maxInclusive");
/** Specifies the maximum string length of each value node. */
const maxLength = ns$4("maxLength");
/** Specifies the minimum number of values in the set of value nodes. */
const minCount = ns$4("minCount");
/** Specifies the minimum exclusive value of each value node. */
const minExclusive = ns$4("minExclusive");
/** Specifies the minimum inclusive value of each value node. */
const minInclusive = ns$4("minInclusive");
/** Specifies the minimum string length of each value node. */
const minLength = ns$4("minLength");
/** Specifies the node shape that all value nodes must conform to. */
const node = ns$4("node");
/** Specifies the node kind (e.g. IRI or literal) each value node. */
const nodeKind = ns$4("nodeKind");
/** Specifies a shape that the value nodes must not conform to. */
const not = ns$4("not");
/** Specifies a list of shapes so that the value nodes must conform to at least one of the shapes. */
const or = ns$4("or");
/** An optional flag to be used with regular expression pattern matching. */
const flags = ns$4("flags");
/** Specifies a regular expression pattern that the string representations of the value nodes must match. */
const pattern = ns$4("pattern");
/** Links a shape to its property shapes. */
const property = ns$4("property");
/** The maximum number of value nodes that can conform to the shape. */
const qualifiedMaxCount = ns$4("qualifiedMaxCount");
/** The minimum number of value nodes that must conform to the shape. */
const qualifiedMinCount = ns$4("qualifiedMinCount");
/** The shape that a specified number of values must conform to. */
const qualifiedValueShape = ns$4("qualifiedValueShape");
/** Can be used to mark the qualified value shape to be disjoint with its sibling shapes. */
const qualifiedValueShapesDisjoint = ns$4("qualifiedValueShapesDisjoint");
/** Specifies whether all node values must have a unique (or no) language tag. */
const uniqueLang = ns$4("uniqueLang");
/** Specifies a list of shapes so that the value nodes must conform to exactly one of the shapes. */
const xone = ns$4("xone");
/** The SPARQL ASK query to execute. */
const ask = ns$4("ask");
/** The SPARQL CONSTRUCT query to execute. */
const construct = ns$4("construct");
/** The SPARQL SELECT query to execute. */
const select = ns$4("select");
/** The SPARQL UPDATE to execute. */
const update = ns$4("update");
/** The prefixes that shall be applied before parsing the associated SPARQL query. */
const prefixes = ns$4("prefixes");
/** Links a resource with its namespace prefix declarations. */
const declare = ns$4("declare");
/** The prefix of a prefix declaration. */
const prefix = ns$4("prefix");
/** The namespace associated with a prefix in a prefix declaration. */
const namespace = ns$4("namespace");
/** Links a shape with SPARQL constraints. */
const sparql = ns$4("sparql");
/** A default value for a property, for example for user interface tools to pre-populate input fields. */
const defaultValue$1 = ns$4("defaultValue");
/** Human-readable descriptions for the property in the context of the surrounding shape. */
const description$1 = ns$4("description");
/** Can be used to link to a property group to indicate that a property shape belongs to a group of related property shapes. */
const group = ns$4("group");
/** Human-readable labels for the property in the context of the surrounding shape. */
const name$2 = ns$4("name");
/** Specifies the relative order of this compared to its siblings. For example use 0 for the first, 1 for the second. */
const order = ns$4("order");
/** Links a shape to a target specified by an extension language, for example instances of sh:SPARQLTarget. */
const target$2 = ns$4("target");
/** The expected type of values returned by the associated function. */
const returnType = ns$4("returnType");
/** Links a SPARQL validator with zero or more sh:ResultAnnotation instances, defining how to derive additional result properties based on the variables of the SELECT query. */
const resultAnnotation = ns$4("resultAnnotation");
/** The annotation property that shall be set. */
const annotationProperty = ns$4("annotationProperty");
/** The (default) values of the annotation property. */
const annotationValue = ns$4("annotationValue");
/** The name of the SPARQL variable from the SELECT clause that shall be used for the values. */
const annotationVarName = ns$4("annotationVarName");
/** The shape that all input nodes of the expression need to conform to. */
const filterShape = ns$4("filterShape");
/** The node expression producing the input nodes of a filter shape expression. */
const nodes = ns$4("nodes");
/** A list of node expressions that shall be intersected. */
const intersection = ns$4("intersection");
/** A list of node expressions that shall be used together. */
const union = ns$4("union");
/** The node expression that must return true for the value nodes. */
const expression = ns$4("expression");
/** The rules linked to a shape. */
const rule = ns$4("rule");
/** The shapes that the focus nodes need to conform to before a rule is executed on them. */
const condition = ns$4("condition");
/** An expression producing the resources that shall be inferred as subjects. */
const subject$2 = ns$4("subject");
/** An expression producing the properties that shall be inferred as predicates. */
const predicate$1 = ns$4("predicate");
/** An expression producing the nodes that shall be inferred as objects. */
const object$3 = ns$4("object");
/** Constraints expressed in JavaScript. */
const js = ns$4("js");
/** The name of the JavaScript function to execute. */
const jsFunctionName = ns$4("jsFunctionName");
/** Declares which JavaScript libraries are needed to execute this. */
const jsLibrary = ns$4("jsLibrary");
/** Declares the URLs of a JavaScript library. This should be the absolute URL of a JavaScript file. Implementations may redirect those to local files. */
const jsLibraryURL = ns$4("jsLibraryURL");
/** The node kind of all blank nodes. */
const BlankNode = ns$4("BlankNode");
/** The node kind of all blank nodes or IRIs. */
const BlankNodeOrIRI = ns$4("BlankNodeOrIRI");
/** The node kind of all blank nodes or literals. */
const BlankNodeOrLiteral = ns$4("BlankNodeOrLiteral");
/** The node kind of all IRIs. */
const IRI = ns$4("IRI");
/** The node kind of all IRIs or literals. */
const IRIOrLiteral = ns$4("IRIOrLiteral");
/** The node kind of all literals. */
const Literal$1 = ns$4("Literal");
/** The severity for an informational validation result. */
const Info = ns$4("Info");
/** The severity for a violation validation result. */
const Violation = ns$4("Violation");
/** The severity for a warning validation result. */
const Warning = ns$4("Warning");
/** A constraint component that can be used to test whether a value node conforms to all members of a provided list of shapes. */
const AndConstraintComponent = ns$4("AndConstraintComponent");
const AndConstraintComponent_and = ns$4("AndConstraintComponent-and");
/** A constraint component that can be used to verify that each value node is an instance of a given type. */
const ClassConstraintComponent = ns$4("ClassConstraintComponent");
const ClassConstraintComponent_class = ns$4("ClassConstraintComponent-class");
/** A constraint component that can be used to indicate that focus nodes must only have values for those properties that have been explicitly enumerated via sh:property/sh:path. */
const ClosedConstraintComponent = ns$4("ClosedConstraintComponent");
const ClosedConstraintComponent_closed = ns$4("ClosedConstraintComponent-closed");
const ClosedConstraintComponent_ignoredProperties = ns$4("ClosedConstraintComponent-ignoredProperties");
/** A constraint component that can be used to restrict the datatype of all value nodes. */
const DatatypeConstraintComponent = ns$4("DatatypeConstraintComponent");
const DatatypeConstraintComponent_datatype = ns$4("DatatypeConstraintComponent-datatype");
/** A constraint component that can be used to verify that the set of value nodes is disjoint with the the set of nodes that have the focus node as subject and the value of a given property as predicate. */
const DisjointConstraintComponent = ns$4("DisjointConstraintComponent");
const DisjointConstraintComponent_disjoint = ns$4("DisjointConstraintComponent-disjoint");
/** A constraint component that can be used to verify that the set of value nodes is equal to the set of nodes that have the focus node as subject and the value of a given property as predicate. */
const EqualsConstraintComponent = ns$4("EqualsConstraintComponent");
const EqualsConstraintComponent_equals = ns$4("EqualsConstraintComponent-equals");
/** A constraint component that can be used to verify that one of the value nodes is a given RDF node. */
const HasValueConstraintComponent = ns$4("HasValueConstraintComponent");
const HasValueConstraintComponent_hasValue = ns$4("HasValueConstraintComponent-hasValue");
/** A constraint component that can be used to exclusively enumerate the permitted value nodes. */
const InConstraintComponent = ns$4("InConstraintComponent");
const InConstraintComponent_in = ns$4("InConstraintComponent-in");
/** A constraint component that can be used to enumerate language tags that all value nodes must have. */
const LanguageInConstraintComponent = ns$4("LanguageInConstraintComponent");
const LanguageInConstraintComponent_languageIn = ns$4("LanguageInConstraintComponent-languageIn");
/** A constraint component that can be used to verify that each value node is smaller than all the nodes that have the focus node as subject and the value of a given property as predicate. */
const LessThanConstraintComponent = ns$4("LessThanConstraintComponent");
const LessThanConstraintComponent_lessThan = ns$4("LessThanConstraintComponent-lessThan");
/** A constraint component that can be used to verify that every value node is smaller than all the nodes that have the focus node as subject and the value of a given property as predicate. */
const LessThanOrEqualsConstraintComponent = ns$4("LessThanOrEqualsConstraintComponent");
const LessThanOrEqualsConstraintComponent_lessThanOrEquals = ns$4("LessThanOrEqualsConstraintComponent-lessThanOrEquals");
/** A constraint component that can be used to restrict the maximum number of value nodes. */
const MaxCountConstraintComponent = ns$4("MaxCountConstraintComponent");
const MaxCountConstraintComponent_maxCount = ns$4("MaxCountConstraintComponent-maxCount");
/** A constraint component that can be used to restrict the range of value nodes with a maximum exclusive value. */
const MaxExclusiveConstraintComponent = ns$4("MaxExclusiveConstraintComponent");
const MaxExclusiveConstraintComponent_maxExclusive = ns$4("MaxExclusiveConstraintComponent-maxExclusive");
/** A constraint component that can be used to restrict the range of value nodes with a maximum inclusive value. */
const MaxInclusiveConstraintComponent = ns$4("MaxInclusiveConstraintComponent");
const MaxInclusiveConstraintComponent_maxInclusive = ns$4("MaxInclusiveConstraintComponent-maxInclusive");
/** A constraint component that can be used to restrict the maximum string length of value nodes. */
const MaxLengthConstraintComponent = ns$4("MaxLengthConstraintComponent");
const MaxLengthConstraintComponent_maxLength = ns$4("MaxLengthConstraintComponent-maxLength");
/** A constraint component that can be used to restrict the minimum number of value nodes. */
const MinCountConstraintComponent = ns$4("MinCountConstraintComponent");
const MinCountConstraintComponent_minCount = ns$4("MinCountConstraintComponent-minCount");
/** A constraint component that can be used to restrict the range of value nodes with a minimum exclusive value. */
const MinExclusiveConstraintComponent = ns$4("MinExclusiveConstraintComponent");
const MinExclusiveConstraintComponent_minExclusive = ns$4("MinExclusiveConstraintComponent-minExclusive");
/** A constraint component that can be used to restrict the range of value nodes with a minimum inclusive value. */
const MinInclusiveConstraintComponent = ns$4("MinInclusiveConstraintComponent");
const MinInclusiveConstraintComponent_minInclusive = ns$4("MinInclusiveConstraintComponent-minInclusive");
/** A constraint component that can be used to restrict the minimum string length of value nodes. */
const MinLengthConstraintComponent = ns$4("MinLengthConstraintComponent");
const MinLengthConstraintComponent_minLength = ns$4("MinLengthConstraintComponent-minLength");
/** A constraint component that can be used to verify that all value nodes conform to the given node shape. */
const NodeConstraintComponent = ns$4("NodeConstraintComponent");
const NodeConstraintComponent_node = ns$4("NodeConstraintComponent-node");
/** A constraint component that can be used to restrict the RDF node kind of each value node. */
const NodeKindConstraintComponent = ns$4("NodeKindConstraintComponent");
const NodeKindConstraintComponent_nodeKind = ns$4("NodeKindConstraintComponent-nodeKind");
/** A constraint component that can be used to verify that value nodes do not conform to a given shape. */
const NotConstraintComponent = ns$4("NotConstraintComponent");
const NotConstraintComponent_not = ns$4("NotConstraintComponent-not");
/** A constraint component that can be used to restrict the value nodes so that they conform to at least one out of several provided shapes. */
const OrConstraintComponent = ns$4("OrConstraintComponent");
const OrConstraintComponent_or = ns$4("OrConstraintComponent-or");
/** A constraint component that can be used to verify that every value node matches a given regular expression. */
const PatternConstraintComponent = ns$4("PatternConstraintComponent");
const PatternConstraintComponent_pattern = ns$4("PatternConstraintComponent-pattern");
const PatternConstraintComponent_flags = ns$4("PatternConstraintComponent-flags");
/** A constraint component that can be used to verify that all value nodes conform to the given property shape. */
const PropertyConstraintComponent = ns$4("PropertyConstraintComponent");
const PropertyConstraintComponent_property = ns$4("PropertyConstraintComponent-property");
/** A constraint component that can be used to verify that a specified maximum number of value nodes conforms to a given shape. */
const QualifiedMaxCountConstraintComponent = ns$4("QualifiedMaxCountConstraintComponent");
const QualifiedMaxCountConstraintComponent_qualifiedMaxCount = ns$4("QualifiedMaxCountConstraintComponent-qualifiedMaxCount");
const QualifiedMaxCountConstraintComponent_qualifiedValueShape = ns$4("QualifiedMaxCountConstraintComponent-qualifiedValueShape");
const QualifiedMaxCountConstraintComponent_qualifiedValueShapesDisjoint = ns$4("QualifiedMaxCountConstraintComponent-qualifiedValueShapesDisjoint");
/** A constraint component that can be used to verify that a specified minimum number of value nodes conforms to a given shape. */
const QualifiedMinCountConstraintComponent = ns$4("QualifiedMinCountConstraintComponent");
const QualifiedMinCountConstraintComponent_qualifiedMinCount = ns$4("QualifiedMinCountConstraintComponent-qualifiedMinCount");
const QualifiedMinCountConstraintComponent_qualifiedValueShape = ns$4("QualifiedMinCountConstraintComponent-qualifiedValueShape");
const QualifiedMinCountConstraintComponent_qualifiedValueShapesDisjoint = ns$4("QualifiedMinCountConstraintComponent-qualifiedValueShapesDisjoint");
/** A constraint component that can be used to specify that no pair of value nodes may use the same language tag. */
const UniqueLangConstraintComponent = ns$4("UniqueLangConstraintComponent");
const UniqueLangConstraintComponent_uniqueLang = ns$4("UniqueLangConstraintComponent-uniqueLang");
/** A constraint component that can be used to restrict the value nodes so that they conform to exactly one out of several provided shapes. */
const XoneConstraintComponent = ns$4("XoneConstraintComponent");
const XoneConstraintComponent_xone = ns$4("XoneConstraintComponent-xone");
/** A constraint component that can be used to define constraints based on SPARQL queries. */
const SPARQLConstraintComponent = ns$4("SPARQLConstraintComponent");
const SPARQLConstraintComponent_sparql = ns$4("SPARQLConstraintComponent-sparql");
/** A node expression that represents the current focus node. */
const shaclthis = ns$4("this");
/** A constraint component that can be used to verify that a given node expression produces true for all value nodes. */
const ExpressionConstraintComponent = ns$4("ExpressionConstraintComponent");
const ExpressionConstraintComponent_expression = ns$4("ExpressionConstraintComponent-expression");
/** A constraint component with the parameter sh:js linking to a sh:JSConstraint containing a sh:script. */
const JSConstraintComponent = ns$4("JSConstraintComponent");
const JSConstraint_js = ns$4("JSConstraint-js");
var shacl = {
    /** Function to create arbitrary terms within the 'W3C Shapes Constraint Language (SHACL) Vocabulary' ontology */
    ns: ns$4,
    /** A shape is a collection of constraints that may be targeted for certain nodes. */
    Shape,
    /** A node shape is a shape that specifies constraint that need to be met with respect to focus nodes. */
    NodeShape,
    /** A property shape is a shape that specifies constraints on the values of a focus node for a given property or path. */
    PropertyShape,
    /** The class of all node kinds, including sh:BlankNode, sh:IRI, sh:Literal or the combinations of these: sh:BlankNodeOrIRI, sh:BlankNodeOrLiteral, sh:IRIOrLiteral. */
    NodeKind,
    /** The class of SHACL validation reports. */
    ValidationReport,
    /** The base class of validation results, typically not instantiated directly. */
    AbstractResult,
    /** The class of validation results. */
    ValidationResult,
    /** The class of validation result severity levels, including violation and warning levels. */
    Severity,
    /** Superclass of components that can take parameters, especially functions and constraint components. */
    Parameterizable,
    /** The class of parameter declarations, consisting of a path predicate and (possibly) information about allowed value type, cardinality and other characteristics. */
    Parameter,
    /** The class of constraint components. */
    ConstraintComponent,
    /** The class of validators, which provide instructions on how to process a constraint definition. This class serves as base class for the SPARQL-based validators and other possible implementations. */
    Validator,
    /** The class of validators based on SPARQL ASK queries. The queries are evaluated for each value node and are supposed to return true if the given node conforms. */
    SPARQLAskValidator,
    /** The class of validators based on SPARQL SELECT queries. The queries are evaluated for each focus node and are supposed to produce bindings for all focus nodes that do not conform. */
    SPARQLSelectValidator,
    /** The class of resources that encapsulate a SPARQL query. */
    SPARQLExecutable,
    /** The class of SPARQL executables that are based on an ASK query. */
    SPARQLAskExecutable,
    /** The class of SPARQL executables that are based on a CONSTRUCT query. */
    SPARQLConstructExecutable,
    /** The class of SPARQL executables based on a SELECT query. */
    SPARQLSelectExecutable,
    /** The class of SPARQL executables based on a SPARQL UPDATE. */
    SPARQLUpdateExecutable,
    /** The class of prefix declarations, consisting of pairs of a prefix with a namespace. */
    PrefixDeclaration,
    /** The class of constraints based on SPARQL SELECT queries. */
    SPARQLConstraint,
    /** Instances of this class represent groups of property shapes that belong together. */
    PropertyGroup,
    /** The base class of targets such as those based on SPARQL queries. */
    Target,
    /** The (meta) class for parameterizable targets.	Instances of this are instantiated as values of the sh:target property. */
    TargetType,
    /** The class of targets that are based on SPARQL queries. */
    SPARQLTarget,
    /** The (meta) class for parameterizable targets that are based on SPARQL queries. */
    SPARQLTargetType,
    /** The class of SHACL functions. */
    Function,
    /** A function backed by a SPARQL query - either ASK or SELECT. */
    SPARQLFunction,
    /** A class of result annotations, which define the rules to derive the values of a given annotation property as extra values for a validation result. */
    ResultAnnotation,
    /** The class of SHACL rules. Never instantiated directly. */
    Rule,
    TripleRule,
    /** The class of SHACL rules based on SPARQL CONSTRUCT queries. */
    SPARQLRule,
    /** Abstract base class of resources that declare an executable JavaScript. */
    JSExecutable,
    /** The class of targets that are based on JavaScript functions. */
    JSTarget,
    /** The (meta) class for parameterizable targets that are based on JavaScript functions. */
    JSTargetType,
    /** The class of constraints backed by a JavaScript function. */
    JSConstraint,
    /** The class of SHACL functions that execute a JavaScript function when called. */
    JSFunction,
    /** Represents a JavaScript library, typically identified by one or more URLs of files to include. */
    JSLibrary,
    /** The class of SHACL rules expressed using JavaScript. */
    JSRule,
    /** A SHACL validator based on JavaScript. This can be used to declare SHACL constraint components that perform JavaScript-based validation when used. */
    JSValidator,
    /** If set to true then all nodes conform to this. */
    deactivated,
    /** Links a shape to a class, indicating that all instances of the class must conform to the shape. */
    targetClass,
    /** Links a shape to individual nodes, indicating that these nodes must conform to the shape. */
    targetNode,
    /** Links a shape to a property, indicating that all all objects of triples that have the given property as their predicate must conform to the shape. */
    targetObjectsOf,
    /** Links a shape to a property, indicating that all subjects of triples that have the given property as their predicate must conform to the shape. */
    targetSubjectsOf,
    /** A human-readable message (possibly with placeholders for variables) explaining the cause of the result. */
    message,
    /** Defines the severity that validation results produced by a shape must have. Defaults to sh:Violation. */
    severity,
    /** True if the validation did not produce any validation results, and false otherwise. */
    conforms,
    /** The validation results contained in a validation report. */
    result: result$2,
    /** If true then the validation engine was certain that the shapes graph has passed all SHACL syntax requirements during the validation process. */
    shapesGraphWellFormed,
    /** Links a result with other results that provide more details, for example to describe violations against nested shapes. */
    detail,
    /** The focus node that was validated when the result was produced. */
    focusNode,
    /** Human-readable messages explaining the cause of the result. */
    resultMessage,
    /** The path of a validation result, based on the path of the validated property shape. */
    resultPath,
    /** The severity of the result, e.g. warning. */
    resultSeverity,
    /** The constraint that was validated when the result was produced. */
    sourceConstraint,
    /** The shape that is was validated when the result was produced. */
    sourceShape,
    /** The constraint component that is the source of the result. */
    sourceConstraintComponent,
    /** An RDF node that has caused the result. */
    value: value$2,
    /** Shapes graphs that should be used when validating this data graph. */
    shapesGraph,
    /** Suggested shapes graphs for this ontology. The values of this property may be used in the absence of specific sh:shapesGraph statements. */
    suggestedShapesGraph,
    /** An entailment regime that indicates what kind of inferencing is required by a shapes graph. */
    entailment,
    /** Specifies the property path of a property shape. */
    path,
    /** The (single) value of this property represents an inverse path (object to subject). */
    inversePath,
    /** The (single) value of this property must be a list of path elements, representing the elements of alternative paths. */
    alternativePath,
    /** The (single) value of this property represents a path that is matched zero or more times. */
    zeroOrMorePath,
    /** The (single) value of this property represents a path that is matched one or more times. */
    oneOrMorePath,
    /** The (single) value of this property represents a path that is matched zero or one times. */
    zeroOrOnePath,
    /** The parameters of a function or constraint component. */
    parameter,
    /** Outlines how human-readable labels of instances of the associated Parameterizable shall be produced. The values can contain {?paramName} as placeholders for the actual values of the given parameter. */
    labelTemplate,
    /** Indicates whether a parameter is optional. */
    optional,
    /** The validator(s) used to evaluate constraints of either node or property shapes. */
    validator,
    /** The validator(s) used to evaluate a constraint in the context of a node shape. */
    nodeValidator,
    /** The validator(s) used to evaluate a constraint in the context of a property shape. */
    propertyValidator,
    /** RDF list of shapes to validate the value nodes against. */
    and,
    /** The type that all value nodes must have. */
    class: shaclclass,
    /** If set to true then the shape is closed. */
    closed,
    /** An optional RDF list of properties that are also permitted in addition to those explicitly enumerated via sh:property/sh:path. */
    ignoredProperties,
    /** Specifies an RDF datatype that all value nodes must have. */
    datatype,
    /** Specifies a property where the set of values must be disjoint with the value nodes. */
    disjoint,
    /** Specifies a property that must have the same values as the value nodes. */
    equals: equals$1,
    /** Specifies a value that must be among the value nodes. */
    hasValue,
    /** Specifies a list of allowed values so that each value node must be among the members of the given list. */
    in: shaclin,
    /** Specifies a list of language tags that all value nodes must have. */
    languageIn,
    /** Specifies a property that must have smaller values than the value nodes. */
    lessThan,
    /** Specifies a property that must have smaller or equal values than the value nodes. */
    lessThanOrEquals,
    /** Specifies the maximum number of values in the set of value nodes. */
    maxCount,
    /** Specifies the maximum exclusive value of each value node. */
    maxExclusive,
    /** Specifies the maximum inclusive value of each value node. */
    maxInclusive,
    /** Specifies the maximum string length of each value node. */
    maxLength,
    /** Specifies the minimum number of values in the set of value nodes. */
    minCount,
    /** Specifies the minimum exclusive value of each value node. */
    minExclusive,
    /** Specifies the minimum inclusive value of each value node. */
    minInclusive,
    /** Specifies the minimum string length of each value node. */
    minLength,
    /** Specifies the node shape that all value nodes must conform to. */
    node,
    /** Specifies the node kind (e.g. IRI or literal) each value node. */
    nodeKind,
    /** Specifies a shape that the value nodes must not conform to. */
    not,
    /** Specifies a list of shapes so that the value nodes must conform to at least one of the shapes. */
    or,
    /** An optional flag to be used with regular expression pattern matching. */
    flags,
    /** Specifies a regular expression pattern that the string representations of the value nodes must match. */
    pattern,
    /** Links a shape to its property shapes. */
    property,
    /** The maximum number of value nodes that can conform to the shape. */
    qualifiedMaxCount,
    /** The minimum number of value nodes that must conform to the shape. */
    qualifiedMinCount,
    /** The shape that a specified number of values must conform to. */
    qualifiedValueShape,
    /** Can be used to mark the qualified value shape to be disjoint with its sibling shapes. */
    qualifiedValueShapesDisjoint,
    /** Specifies whether all node values must have a unique (or no) language tag. */
    uniqueLang,
    /** Specifies a list of shapes so that the value nodes must conform to exactly one of the shapes. */
    xone,
    /** The SPARQL ASK query to execute. */
    ask,
    /** The SPARQL CONSTRUCT query to execute. */
    construct,
    /** The SPARQL SELECT query to execute. */
    select,
    /** The SPARQL UPDATE to execute. */
    update,
    /** The prefixes that shall be applied before parsing the associated SPARQL query. */
    prefixes,
    /** Links a resource with its namespace prefix declarations. */
    declare,
    /** The prefix of a prefix declaration. */
    prefix,
    /** The namespace associated with a prefix in a prefix declaration. */
    namespace,
    /** Links a shape with SPARQL constraints. */
    sparql,
    /** A default value for a property, for example for user interface tools to pre-populate input fields. */
    defaultValue: defaultValue$1,
    /** Human-readable descriptions for the property in the context of the surrounding shape. */
    description: description$1,
    /** Can be used to link to a property group to indicate that a property shape belongs to a group of related property shapes. */
    group,
    /** Human-readable labels for the property in the context of the surrounding shape. */
    name: name$2,
    /** Specifies the relative order of this compared to its siblings. For example use 0 for the first, 1 for the second. */
    order,
    /** Links a shape to a target specified by an extension language, for example instances of sh:SPARQLTarget. */
    target: target$2,
    /** The expected type of values returned by the associated function. */
    returnType,
    /** Links a SPARQL validator with zero or more sh:ResultAnnotation instances, defining how to derive additional result properties based on the variables of the SELECT query. */
    resultAnnotation,
    /** The annotation property that shall be set. */
    annotationProperty,
    /** The (default) values of the annotation property. */
    annotationValue,
    /** The name of the SPARQL variable from the SELECT clause that shall be used for the values. */
    annotationVarName,
    /** The shape that all input nodes of the expression need to conform to. */
    filterShape,
    /** The node expression producing the input nodes of a filter shape expression. */
    nodes,
    /** A list of node expressions that shall be intersected. */
    intersection,
    /** A list of node expressions that shall be used together. */
    union,
    /** The node expression that must return true for the value nodes. */
    expression,
    /** The rules linked to a shape. */
    rule,
    /** The shapes that the focus nodes need to conform to before a rule is executed on them. */
    condition,
    /** An expression producing the resources that shall be inferred as subjects. */
    subject: subject$2,
    /** An expression producing the properties that shall be inferred as predicates. */
    predicate: predicate$1,
    /** An expression producing the nodes that shall be inferred as objects. */
    object: object$3,
    /** Constraints expressed in JavaScript. */
    js,
    /** The name of the JavaScript function to execute. */
    jsFunctionName,
    /** Declares which JavaScript libraries are needed to execute this. */
    jsLibrary,
    /** Declares the URLs of a JavaScript library. This should be the absolute URL of a JavaScript file. Implementations may redirect those to local files. */
    jsLibraryURL,
    /** The node kind of all blank nodes. */
    BlankNode,
    /** The node kind of all blank nodes or IRIs. */
    BlankNodeOrIRI,
    /** The node kind of all blank nodes or literals. */
    BlankNodeOrLiteral,
    /** The node kind of all IRIs. */
    IRI,
    /** The node kind of all IRIs or literals. */
    IRIOrLiteral,
    /** The node kind of all literals. */
    Literal: Literal$1,
    /** The severity for an informational validation result. */
    Info,
    /** The severity for a violation validation result. */
    Violation,
    /** The severity for a warning validation result. */
    Warning,
    /** A constraint component that can be used to test whether a value node conforms to all members of a provided list of shapes. */
    AndConstraintComponent,
    AndConstraintComponent_and: AndConstraintComponent_and,
    ["AndConstraintComponent-and"]: AndConstraintComponent_and,
    /** A constraint component that can be used to verify that each value node is an instance of a given type. */
    ClassConstraintComponent,
    ClassConstraintComponent_class: ClassConstraintComponent_class,
    ["ClassConstraintComponent-class"]: ClassConstraintComponent_class,
    /** A constraint component that can be used to indicate that focus nodes must only have values for those properties that have been explicitly enumerated via sh:property/sh:path. */
    ClosedConstraintComponent,
    ClosedConstraintComponent_closed: ClosedConstraintComponent_closed,
    ["ClosedConstraintComponent-closed"]: ClosedConstraintComponent_closed,
    ClosedConstraintComponent_ignoredProperties: ClosedConstraintComponent_ignoredProperties,
    ["ClosedConstraintComponent-ignoredProperties"]: ClosedConstraintComponent_ignoredProperties,
    /** A constraint component that can be used to restrict the datatype of all value nodes. */
    DatatypeConstraintComponent,
    DatatypeConstraintComponent_datatype: DatatypeConstraintComponent_datatype,
    ["DatatypeConstraintComponent-datatype"]: DatatypeConstraintComponent_datatype,
    /** A constraint component that can be used to verify that the set of value nodes is disjoint with the the set of nodes that have the focus node as subject and the value of a given property as predicate. */
    DisjointConstraintComponent,
    DisjointConstraintComponent_disjoint: DisjointConstraintComponent_disjoint,
    ["DisjointConstraintComponent-disjoint"]: DisjointConstraintComponent_disjoint,
    /** A constraint component that can be used to verify that the set of value nodes is equal to the set of nodes that have the focus node as subject and the value of a given property as predicate. */
    EqualsConstraintComponent,
    EqualsConstraintComponent_equals: EqualsConstraintComponent_equals,
    ["EqualsConstraintComponent-equals"]: EqualsConstraintComponent_equals,
    /** A constraint component that can be used to verify that one of the value nodes is a given RDF node. */
    HasValueConstraintComponent,
    HasValueConstraintComponent_hasValue: HasValueConstraintComponent_hasValue,
    ["HasValueConstraintComponent-hasValue"]: HasValueConstraintComponent_hasValue,
    /** A constraint component that can be used to exclusively enumerate the permitted value nodes. */
    InConstraintComponent,
    InConstraintComponent_in: InConstraintComponent_in,
    ["InConstraintComponent-in"]: InConstraintComponent_in,
    /** A constraint component that can be used to enumerate language tags that all value nodes must have. */
    LanguageInConstraintComponent,
    LanguageInConstraintComponent_languageIn: LanguageInConstraintComponent_languageIn,
    ["LanguageInConstraintComponent-languageIn"]: LanguageInConstraintComponent_languageIn,
    /** A constraint component that can be used to verify that each value node is smaller than all the nodes that have the focus node as subject and the value of a given property as predicate. */
    LessThanConstraintComponent,
    LessThanConstraintComponent_lessThan: LessThanConstraintComponent_lessThan,
    ["LessThanConstraintComponent-lessThan"]: LessThanConstraintComponent_lessThan,
    /** A constraint component that can be used to verify that every value node is smaller than all the nodes that have the focus node as subject and the value of a given property as predicate. */
    LessThanOrEqualsConstraintComponent,
    LessThanOrEqualsConstraintComponent_lessThanOrEquals: LessThanOrEqualsConstraintComponent_lessThanOrEquals,
    ["LessThanOrEqualsConstraintComponent-lessThanOrEquals"]: LessThanOrEqualsConstraintComponent_lessThanOrEquals,
    /** A constraint component that can be used to restrict the maximum number of value nodes. */
    MaxCountConstraintComponent,
    MaxCountConstraintComponent_maxCount: MaxCountConstraintComponent_maxCount,
    ["MaxCountConstraintComponent-maxCount"]: MaxCountConstraintComponent_maxCount,
    /** A constraint component that can be used to restrict the range of value nodes with a maximum exclusive value. */
    MaxExclusiveConstraintComponent,
    MaxExclusiveConstraintComponent_maxExclusive: MaxExclusiveConstraintComponent_maxExclusive,
    ["MaxExclusiveConstraintComponent-maxExclusive"]: MaxExclusiveConstraintComponent_maxExclusive,
    /** A constraint component that can be used to restrict the range of value nodes with a maximum inclusive value. */
    MaxInclusiveConstraintComponent,
    MaxInclusiveConstraintComponent_maxInclusive: MaxInclusiveConstraintComponent_maxInclusive,
    ["MaxInclusiveConstraintComponent-maxInclusive"]: MaxInclusiveConstraintComponent_maxInclusive,
    /** A constraint component that can be used to restrict the maximum string length of value nodes. */
    MaxLengthConstraintComponent,
    MaxLengthConstraintComponent_maxLength: MaxLengthConstraintComponent_maxLength,
    ["MaxLengthConstraintComponent-maxLength"]: MaxLengthConstraintComponent_maxLength,
    /** A constraint component that can be used to restrict the minimum number of value nodes. */
    MinCountConstraintComponent,
    MinCountConstraintComponent_minCount: MinCountConstraintComponent_minCount,
    ["MinCountConstraintComponent-minCount"]: MinCountConstraintComponent_minCount,
    /** A constraint component that can be used to restrict the range of value nodes with a minimum exclusive value. */
    MinExclusiveConstraintComponent,
    MinExclusiveConstraintComponent_minExclusive: MinExclusiveConstraintComponent_minExclusive,
    ["MinExclusiveConstraintComponent-minExclusive"]: MinExclusiveConstraintComponent_minExclusive,
    /** A constraint component that can be used to restrict the range of value nodes with a minimum inclusive value. */
    MinInclusiveConstraintComponent,
    MinInclusiveConstraintComponent_minInclusive: MinInclusiveConstraintComponent_minInclusive,
    ["MinInclusiveConstraintComponent-minInclusive"]: MinInclusiveConstraintComponent_minInclusive,
    /** A constraint component that can be used to restrict the minimum string length of value nodes. */
    MinLengthConstraintComponent,
    MinLengthConstraintComponent_minLength: MinLengthConstraintComponent_minLength,
    ["MinLengthConstraintComponent-minLength"]: MinLengthConstraintComponent_minLength,
    /** A constraint component that can be used to verify that all value nodes conform to the given node shape. */
    NodeConstraintComponent,
    NodeConstraintComponent_node: NodeConstraintComponent_node,
    ["NodeConstraintComponent-node"]: NodeConstraintComponent_node,
    /** A constraint component that can be used to restrict the RDF node kind of each value node. */
    NodeKindConstraintComponent,
    NodeKindConstraintComponent_nodeKind: NodeKindConstraintComponent_nodeKind,
    ["NodeKindConstraintComponent-nodeKind"]: NodeKindConstraintComponent_nodeKind,
    /** A constraint component that can be used to verify that value nodes do not conform to a given shape. */
    NotConstraintComponent,
    NotConstraintComponent_not: NotConstraintComponent_not,
    ["NotConstraintComponent-not"]: NotConstraintComponent_not,
    /** A constraint component that can be used to restrict the value nodes so that they conform to at least one out of several provided shapes. */
    OrConstraintComponent,
    OrConstraintComponent_or: OrConstraintComponent_or,
    ["OrConstraintComponent-or"]: OrConstraintComponent_or,
    /** A constraint component that can be used to verify that every value node matches a given regular expression. */
    PatternConstraintComponent,
    PatternConstraintComponent_pattern: PatternConstraintComponent_pattern,
    ["PatternConstraintComponent-pattern"]: PatternConstraintComponent_pattern,
    PatternConstraintComponent_flags: PatternConstraintComponent_flags,
    ["PatternConstraintComponent-flags"]: PatternConstraintComponent_flags,
    /** A constraint component that can be used to verify that all value nodes conform to the given property shape. */
    PropertyConstraintComponent,
    PropertyConstraintComponent_property: PropertyConstraintComponent_property,
    ["PropertyConstraintComponent-property"]: PropertyConstraintComponent_property,
    /** A constraint component that can be used to verify that a specified maximum number of value nodes conforms to a given shape. */
    QualifiedMaxCountConstraintComponent,
    QualifiedMaxCountConstraintComponent_qualifiedMaxCount: QualifiedMaxCountConstraintComponent_qualifiedMaxCount,
    ["QualifiedMaxCountConstraintComponent-qualifiedMaxCount"]: QualifiedMaxCountConstraintComponent_qualifiedMaxCount,
    QualifiedMaxCountConstraintComponent_qualifiedValueShape: QualifiedMaxCountConstraintComponent_qualifiedValueShape,
    ["QualifiedMaxCountConstraintComponent-qualifiedValueShape"]: QualifiedMaxCountConstraintComponent_qualifiedValueShape,
    QualifiedMaxCountConstraintComponent_qualifiedValueShapesDisjoint: QualifiedMaxCountConstraintComponent_qualifiedValueShapesDisjoint,
    ["QualifiedMaxCountConstraintComponent-qualifiedValueShapesDisjoint"]: QualifiedMaxCountConstraintComponent_qualifiedValueShapesDisjoint,
    /** A constraint component that can be used to verify that a specified minimum number of value nodes conforms to a given shape. */
    QualifiedMinCountConstraintComponent,
    QualifiedMinCountConstraintComponent_qualifiedMinCount: QualifiedMinCountConstraintComponent_qualifiedMinCount,
    ["QualifiedMinCountConstraintComponent-qualifiedMinCount"]: QualifiedMinCountConstraintComponent_qualifiedMinCount,
    QualifiedMinCountConstraintComponent_qualifiedValueShape: QualifiedMinCountConstraintComponent_qualifiedValueShape,
    ["QualifiedMinCountConstraintComponent-qualifiedValueShape"]: QualifiedMinCountConstraintComponent_qualifiedValueShape,
    QualifiedMinCountConstraintComponent_qualifiedValueShapesDisjoint: QualifiedMinCountConstraintComponent_qualifiedValueShapesDisjoint,
    ["QualifiedMinCountConstraintComponent-qualifiedValueShapesDisjoint"]: QualifiedMinCountConstraintComponent_qualifiedValueShapesDisjoint,
    /** A constraint component that can be used to specify that no pair of value nodes may use the same language tag. */
    UniqueLangConstraintComponent,
    UniqueLangConstraintComponent_uniqueLang: UniqueLangConstraintComponent_uniqueLang,
    ["UniqueLangConstraintComponent-uniqueLang"]: UniqueLangConstraintComponent_uniqueLang,
    /** A constraint component that can be used to restrict the value nodes so that they conform to exactly one out of several provided shapes. */
    XoneConstraintComponent,
    XoneConstraintComponent_xone: XoneConstraintComponent_xone,
    ["XoneConstraintComponent-xone"]: XoneConstraintComponent_xone,
    /** A constraint component that can be used to define constraints based on SPARQL queries. */
    SPARQLConstraintComponent,
    SPARQLConstraintComponent_sparql: SPARQLConstraintComponent_sparql,
    ["SPARQLConstraintComponent-sparql"]: SPARQLConstraintComponent_sparql,
    /** A node expression that represents the current focus node. */
    this: shaclthis,
    /** A constraint component that can be used to verify that a given node expression produces true for all value nodes. */
    ExpressionConstraintComponent,
    ExpressionConstraintComponent_expression: ExpressionConstraintComponent_expression,
    ["ExpressionConstraintComponent-expression"]: ExpressionConstraintComponent_expression,
    /** A constraint component with the parameter sh:js linking to a sh:JSConstraint containing a sh:script. */
    JSConstraintComponent,
    JSConstraint_js: JSConstraint_js,
    ["JSConstraint-js"]: JSConstraint_js
};

const F_NTRIPLES = "application/n-triples";
const F_NQUADS = "application/n-quads";
const F_TURTLE = "text/turtle";
const F_N3 = "text/n3";
const F_PLAIN = "text/plain";
const F_JSON = "application/json";
const F_JSONLD = "application/ld+json";
const F_RDF_XML = "application/rdf+xml";
const F_NTRIPLES_DEP = "text/ntriples";
const F_TURTLE_DEP = "application/x-turtle";
const NON_CONTENT_EXTS = ["php", "asp", "aspx", "cgi", "jsp"];
/** @deprecated Use the @ontologies/<ns> packages directly, these provide better typing and documentation */
const defaultNS = Object.freeze({
    argu: createNS("https://argu.co/ns/core#"),
    as: as.ns,
    bibo: createNS("http://purl.org/ontology/bibo/"),
    cc: createNS("http://creativecommons.org/ns#"),
    dbo: createNS("http://dbpedia.org/ontology/"),
    dbp: createNS("http://dbpedia.org/property/"),
    dbpedia: createNS("http://dbpedia.org/resource/"),
    dcat: createNS("http://www.w3.org/ns/dcat#"),
    dctype: createNS("http://purl.org/dc/dcmitype/"),
    ex: createNS("http://example.com/ns#"),
    example: createNS("http://www.example.com/"),
    fhir: createNS("http://hl7.org/fhir/"),
    fhir3: createNS("http://hl7.org/fhir/STU3"),
    foaf: createNS("http://xmlns.com/foaf/0.1/"),
    geo: createNS("http://www.w3.org/2003/01/geo/wgs84_pos#"),
    http: createNS("http://www.w3.org/2011/http#"),
    http07: createNS("http://www.w3.org/2007/ont/http#"),
    httph: createNS("http://www.w3.org/2007/ont/httph#"),
    hydra: createNS("http://www.w3.org/ns/hydra/core#"),
    ianalr: createNS("http://www.iana.org/assignments/link-relations/"),
    ld: createNS("http://purl.org/linked-delta/"),
    link: createNS("http://www.w3.org/2007/ont/link#"),
    ll: createNS("http://purl.org/link-lib/"),
    owl: createNS("http://www.w3.org/2002/07/owl#"),
    p: createNS("http://www.wikidata.org/prop/"),
    prov: createNS("http://www.w3.org/ns/prov#"),
    qb: createNS("http://purl.org/linked-data/cube#"),
    rdf: rdf.ns,
    rdfs: rdfs.ns,
    schema: schema.ns,
    sh: shacl.ns,
    shacl: shacl.ns,
    skos: createNS("http://www.w3.org/2004/02/skos/core#"),
    wd: createNS("http://www.wikidata.org/entity/"),
    wdata: createNS("https://www.wikidata.org/wiki/Special:EntityData/"),
    wdref: createNS("http://www.wikidata.org/reference/"),
    wds: createNS("http://www.wikidata.org/entity/statement/"),
    wdt: createNS("http://www.wikidata.org/prop/direct/"),
    wdv: createNS("http://www.wikidata.org/value/"),
    xmlns: createNS("http://www.w3.org/2000/xmlns/"),
});
const DEFAULT_TOPOLOGY = defaultNS.ll("defaultTopology");
/** Constant used to determine that a class is used to render a type rather than a property. */
const RENDER_CLASS_NAME = defaultNS.ll("typeRenderClass");
const MAIN_NODE_DEFAULT_IRI = defaultNS.ll("targetResource");
// tslint:disable-next-line ban-types
const NON_DATA_OBJECTS_CTORS = [
    Array,
    ArrayBuffer,
    Boolean,
    DataView,
    Date,
    Error,
    EvalError,
    Float32Array,
    Float64Array,
    Int16Array,
    Int32Array,
    Int8Array,
    Intl.Collator,
    Intl.DateTimeFormat,
    Intl.NumberFormat,
    Map,
    Number,
    Promise,
    (typeof Proxy !== "undefined" ? Proxy : undefined),
    RangeError,
    ReferenceError,
    RegExp,
    Set,
].filter(Boolean);
const MSG_BAD_REQUEST = "Request failed with bad status code";
const MSG_INCORRECT_TARGET = "Collections or Literals can't be the target";
const MSG_URL_UNDEFINED = "No url given with action.";
const MSG_URL_UNRESOLVABLE = "Can't execute action with non-named-node url.";
const MSG_OBJECT_NOT_IRI = "Action object property must be an IRI.";

const MSG_TYPE_ERR = "Non-optimized NamedNode instance given. Please memoize your namespace correctly.";
/** Constant used to determine that a component is used to render a type rather than a property. */
const RENDER_CLASS_NAME$1 = defaultNS.ll("typeRenderClass");
function convertToCacheKey(types, props, topology) {
    return `${types.join()}[${props.join()}][${topology}]`;
}
/**
 * Handles registration and querying for view components.
 */
class ComponentStore {
    constructor(schema) {
        this.lookupCache = {};
        /**
         * Lookup map ordered with the following hierarchy;
         * [propertyType][resourceType][topology]
         */
        this.mapping = {};
        this.schema = schema;
        this.mapping[rdfFactory.id(RENDER_CLASS_NAME$1)] = {};
    }
    /**
     * Generate registration description objects for later registration use.
     * @see LinkedRenderStore#registerAll
     */
    static registerRenderer(component, types, properties, topologies) {
        if (typeof component === "undefined") {
            throw new Error(`Undefined component was given for (${types}, ${properties}, ${topologies}).`);
        }
        const registrations = [];
        for (let t = 0; t < types.length; t++) {
            if (types[t] === undefined) {
                throw new TypeError(MSG_TYPE_ERR);
            }
            for (let p = 0; p < properties.length; p++) {
                if (properties[p] === undefined) {
                    throw new TypeError(MSG_TYPE_ERR);
                }
                for (let top = 0; top < topologies.length; top++) {
                    if (topologies[top] === undefined) {
                        throw new TypeError(MSG_TYPE_ERR);
                    }
                    registrations.push({
                        component,
                        property: properties[p],
                        topology: topologies[top],
                        type: types[t],
                    });
                }
            }
        }
        return registrations;
    }
    /**
     * TODO: remove defaultType - Basically a bug. We default the type if no matches were found, rather than using
     *   inheritance to associate unknown types the RDF way (using rdfs:Resource).
     */
    getRenderComponent(types, predicates, topology, defaultType) {
        const oTypes = this.schema.expand(types);
        const key = convertToCacheKey(oTypes, predicates, topology);
        const cached = this.getComponentFromCache(key);
        if (cached !== undefined) {
            return cached;
        }
        for (let i = 0; i < oTypes.length; i++) {
            const exact = this.lookup(predicates[0], oTypes[i], topology);
            if (exact !== undefined) {
                return this.addComponentToCache(exact, key);
            }
        }
        const possibleComponents = this.possibleComponents(predicates, topology);
        if (possibleComponents.length === 0) {
            if (topology === rdfFactory.id(DEFAULT_TOPOLOGY)) {
                return undefined;
            }
            const foundComponent = this.getRenderComponent(oTypes, predicates, rdfFactory.id(DEFAULT_TOPOLOGY), defaultType);
            if (!foundComponent) {
                return undefined;
            }
            return this.addComponentToCache(foundComponent, key);
        }
        for (let i = 0; i < predicates.length; i++) {
            const bestComponent = this.bestComponent(possibleComponents, oTypes);
            const component = bestComponent && this.lookup(predicates[i], bestComponent, topology);
            if (component) {
                return this.addComponentToCache(component, key);
            }
        }
        for (let i = 0; i < predicates.length; i++) {
            const component = this.lookup(predicates[i], defaultType, topology);
            if (component) {
                return this.addComponentToCache(component, key);
            }
        }
        return undefined;
    }
    /**
     * Register a renderer for a type/property.
     * @param component The component to return for the rendering of the object.
     * @param type The type's SomeNode of the object which the {component} can render.
     * @param [property] The property's SomeNode if the {component} is a subject renderer.
     * @param [topology] An alternate topology this {component} should render.
     */
    registerRenderer(component, type, property = rdfFactory.id(RENDER_CLASS_NAME$1), topology = rdfFactory.id(DEFAULT_TOPOLOGY)) {
        if (!property || !type) {
            return;
        }
        this.store(component, property, type, topology);
        this.lookupCache = {};
    }
    /**
     * Find a component from a cache.
     * @param predicate The index of the property (or {RENDER_CLASS_NAME})
     * @param obj The index of either the resource type or resource IRI
     * @param topology The index of the topology
     * @param cache The cache to look into (defaults to the mapping)
     * @returns The appropriate component if any
     */
    lookup(predicate, obj, topology, cache = this.mapping) {
        const predMap = cache[predicate];
        if (!predMap || !predMap[obj]) {
            return undefined;
        }
        return predMap[obj][topology];
    }
    /** Store a component to a cache. */
    store(component, predicate, obj, topology, cache = this.mapping) {
        if (typeof cache[predicate] === "undefined") {
            cache[predicate] = {};
        }
        if (typeof cache[predicate][obj] === "undefined") {
            cache[predicate][obj] = {};
        }
        cache[predicate][obj][topology] = component;
    }
    /**
     * Adds a renderer to {this.lookupCache}
     * @param component The render component.
     * @param key The memoization key.
     * @returns The renderer passed with {component}
     */
    addComponentToCache(component, key) {
        this.lookupCache[key] = component;
        return this.lookupCache[key];
    }
    /**
     * Expands the given types and returns the best component to render it with.
     * @param components The set of components to choose from.
     * @param [types] The types to expand on.
     * @returns The best match for the given components and types.
     */
    bestComponent(components, types) {
        if (types.length > 0) {
            const direct = this.schema.sort(types).find((c) => components.indexOf(c) >= 0);
            if (direct) {
                return direct;
            }
        }
        const chain = this.schema.expand(types || []);
        return components.find((c) => chain.indexOf(c) > 0);
    }
    /**
     * Resolves a renderer from the {lookupCache}.
     * @param key The key to look up.
     * @returns If saved the render component, otherwise undefined.
     */
    getComponentFromCache(key) {
        return this.lookupCache[key];
    }
    possibleComponents(predicates, topology) {
        const classes = [rdfFactory.id(rdfs.Resource)];
        for (let i = 0; i < predicates.length; i++) {
            const predicate = predicates[i];
            if (typeof this.mapping[predicate] !== "undefined") {
                const types = Object.values(this.mapping[predicate]);
                for (let j = 0; j < types.length; j++) {
                    const compType = this.lookup(predicate, j, topology);
                    if (compType !== undefined) {
                        classes.push(j);
                    }
                }
            }
        }
        return classes;
    }
}

/**
 * Tries to schedule updates async if possible.
 */
class ProcessBroadcast {
    constructor(opts) {
        this.hasIdleCallback = "requestIdleCallback" in window;
        this.hasRequestAnimationFrame = "requestAnimationFrame" in window;
        this.resolve = () => undefined;
        this.timeout = opts.timeout;
        this.regUpdateTime = Date.now();
        this.bulkSubscriptions = opts.bulkSubscriptions;
        this.changedSubjects = opts.changedSubjects;
        this.subjectSubscriptions = opts.subjectSubscriptions;
        this.work = Object.freeze(opts.work);
        this.queue = this.queue.bind(this);
    }
    done() {
        return this.work.length === 0
            || this.subjectSubscriptions.length === 0 && this.bulkSubscriptions.length === 0;
    }
    run() {
        if (this.timeout === 0) {
            this.queue();
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.resolve = resolve;
            this.queue();
        });
    }
    /**
     * Calls the subscriber callback function {reg} with the correct arguments according to its
     * registration settings.
     */
    callSubscriber(reg) {
        if (reg.markedForDelete) {
            return;
        }
        reg.callback(reg.onlySubjects ? this.changedSubjects : this.work, this.regUpdateTime);
    }
    process() {
        if (this.bulkSubscriptions.length > 0) {
            this.callSubscriber(this.bulkSubscriptions.pop());
        }
        else if (this.subjectSubscriptions.length > 0) {
            this.callSubscriber(this.subjectSubscriptions.pop());
        }
    }
    queue(idleCallback) {
        if (this.timeout !== 0 && this.hasIdleCallback) {
            while (typeof idleCallback === "object"
                && (!this.done() && (idleCallback.timeRemaining() > 0 || idleCallback.didTimeout))) {
                this.process();
            }
            if (this.done()) {
                return this.resolve();
            }
            window.requestIdleCallback(this.queue, { timeout: this.timeout });
        }
        else if (this.timeout !== 0 && this.hasRequestAnimationFrame) {
            this.process();
            while (typeof idleCallback === "number" && (performance.now() - idleCallback) < 33) {
                this.process();
            }
            if (this.done()) {
                return this.resolve();
            }
            window.requestAnimationFrame(this.queue);
        }
        else {
            while (!this.done()) {
                this.process();
            }
            this.resolve();
        }
    }
}

/** Function to create arbitrary terms within the 'XML Schema Datatypes in RDF and OWL' ontology */
const ns$5 = createNS("http://www.w3.org/2001/XMLSchema#");
const string = ns$5("string");
const xsdboolean = ns$5("boolean");
const decimal = ns$5("decimal");
const xsdfloat = ns$5("float");
const xsddouble = ns$5("double");
const duration$2 = ns$5("duration");
const dateTime = ns$5("dateTime");
const time = ns$5("time");
const date = ns$5("date");
const gYearMonth = ns$5("gYearMonth");
const gYear = ns$5("gYear");
const gMonthDay = ns$5("gMonthDay");
const gDay = ns$5("gDay");
const gMonth = ns$5("gMonth");
const hexBinary = ns$5("hexBinary");
const base64Binary = ns$5("base64Binary");
const anyURI = ns$5("anyURI");
const normalizedString = ns$5("normalizedString");
const token = ns$5("token");
const language$1 = ns$5("language");
const NMTOKEN = ns$5("NMTOKEN");
const Name = ns$5("Name");
const NCName = ns$5("NCName");
const integer = ns$5("integer");
const nonPositiveInteger = ns$5("nonPositiveInteger");
const negativeInteger = ns$5("negativeInteger");
const xsdlong = ns$5("long");
const xsdint = ns$5("int");
const xsdshort = ns$5("short");
const xsdbyte = ns$5("byte");
const nonNegativeInteger = ns$5("nonNegativeInteger");
const unsignedLong = ns$5("unsignedLong");
const unsignedInt = ns$5("unsignedInt");
const unsignedShort = ns$5("unsignedShort");
const unsignedByte = ns$5("unsignedByte");
const positiveInteger = ns$5("positiveInteger");
const yearMonthDuration = ns$5("yearMonthDuration");
const dayTimeDuration = ns$5("dayTimeDuration");
const dateTimeStamp = ns$5("dateTimeStamp");
var xsd = {
    /** Function to create arbitrary terms within the 'XML Schema Datatypes in RDF and OWL' ontology */
    ns: ns$5,
    string,
    boolean: xsdboolean,
    decimal,
    float: xsdfloat,
    double: xsddouble,
    duration: duration$2,
    dateTime,
    time,
    date,
    gYearMonth,
    gYear,
    gMonthDay,
    gDay,
    gMonth,
    hexBinary,
    base64Binary,
    anyURI,
    normalizedString,
    token,
    language: language$1,
    NMTOKEN,
    Name,
    NCName,
    integer,
    nonPositiveInteger,
    negativeInteger,
    long: xsdlong,
    int: xsdint,
    short: xsdshort,
    byte: xsdbyte,
    nonNegativeInteger,
    unsignedLong,
    unsignedInt,
    unsignedShort,
    unsignedByte,
    positiveInteger,
    yearMonthDuration,
    dayTimeDuration,
    dateTimeStamp
};

const convertedIndexedFormula = IndexedFormula;
const graph = ((features, opts) => new convertedIndexedFormula(features, opts || { rdfFactory }));
const convertedFetcher = Fetcher;
const convertedSerializer = Serializer;
/**
 * NamedNode
 */
const siteImpl = NamedNode.prototype.site;
const site = (node) => rdfFactory.namedNode(siteImpl.call(node).value);
const uri = uri$1;
const convertedParse = parse;

var rdflib = /*#__PURE__*/Object.freeze({
  __proto__: null,
  graph: graph,
  IndexedFormula: convertedIndexedFormula,
  RDFFetcher: convertedFetcher,
  RDFSerializer: convertedSerializer,
  RDFparse: convertedParse,
  site: site,
  URI: uri
});

/* global chrome */
const memberPrefix = rdf.ns("_").value;
/**
 * Filters {obj} to only include statements where the subject equals {predicate}.
 * @param obj The statements to filter.
 * @param predicate The subject to filter for.
 * @return A possibly empty filtered array of statements.
 */
function allRDFPropertyStatements(obj, predicate) {
    if (typeof obj === "undefined") {
        return [];
    }
    if (rdfFactory.equals(predicate, rdfs.member)) {
        return obj.filter((s) => rdfFactory.equals(s.predicate, rdfs.member)
            || s.predicate.value.startsWith(memberPrefix));
    }
    return obj.filter((s) => rdfFactory.equals(s.predicate, predicate));
}
/**
 * Filters {obj} on subject {predicate} returning the resulting statements' objects.
 * @see allRDFPropertyStatements
 */
function allRDFValues(obj, predicate) {
    const props = allRDFPropertyStatements(obj, predicate);
    if (props.length === 0) {
        return [];
    }
    return props.map((s) => s.object);
}
/**
 * Resolve {predicate} to any value, if any. If present, additional values are ignored.
 */
function anyRDFValue(obj, predicate) {
    if (!Array.isArray(obj)) {
        return undefined;
    }
    const match = rdfFactory.equals(predicate, rdfs.member)
        ? obj.find((s) => s.predicate.value.startsWith(memberPrefix))
        : obj.find((s) => rdfFactory.equals(s.predicate, predicate));
    if (typeof match === "undefined") {
        return undefined;
    }
    return match.object;
}
function getPropBestLang(rawProp, langPrefs) {
    if (!Array.isArray(rawProp)) {
        return rawProp.object;
    }
    if (rawProp.length === 1) {
        return rawProp[0].object;
    }
    for (let i = 0; i < langPrefs.length; i++) {
        const pIndex = rawProp.findIndex((p) => "language" in p.object
            && p.object.language === langPrefs[i]);
        if (pIndex >= 0) {
            return rawProp[pIndex].object;
        }
    }
    return rawProp[0].object;
}
function getPropBestLangRaw(statements, langPrefs) {
    if (!Array.isArray(statements)) {
        return statements;
    }
    if (statements.length === 1) {
        return statements[0];
    }
    for (let i = 0; i < langPrefs.length; i++) {
        const pIndex = statements.findIndex((s) => "language" in s.object
            && s.object.language === langPrefs[i]);
        if (pIndex >= 0) {
            return statements[pIndex];
        }
    }
    return statements[0];
}
function getTermBestLang(rawTerm, langPrefs) {
    if (!Array.isArray(rawTerm)) {
        return rawTerm;
    }
    if (rawTerm.length === 1) {
        return rawTerm[0];
    }
    for (let i = 0; i < langPrefs.length; i++) {
        const pIndex = rawTerm.findIndex((p) => "language" in p && p.language === langPrefs[i]);
        if (pIndex >= 0) {
            return rawTerm[pIndex];
        }
    }
    return rawTerm[0];
}
/**
 * Checks if the origin of {href} matches current origin from {window.location}
 * @returns `true` if matches, `false` otherwise.
 */
function isDifferentOrigin(href) {
    if (typeof href !== "string" && href.termType === "BlankNode") {
        return false;
    }
    const origin = typeof href !== "string" ? href.value : href;
    return !origin.startsWith(self.location.origin + "/");
}
function normalizeType(type) {
    return Array.isArray(type) ? type : [type];
}

/**
 * Fix rdflib issue where multiline strings are serialized in nquads.
 * @see https://github.com/linkeddata/rdflib.js/pull/282
 * @monkey
 */
function patchRDFLibSerializer(serializer, fallback) {
    const old = serializer.stringToN3;
    serializer.stringToN3 = function stringToN3(str, flags) {
        let flagsWithFallback = flags;
        if (!flags) {
            flagsWithFallback = fallback;
        }
        return old(str, flagsWithFallback);
    };
}
/**
 * Patch rdflib with memoized versions of terms by overriding certain object methods.
 * For browsers that don't support Proxy.
 */
function patchRDFLibStoreWithOverrides(graph, changeBufferTarget) {
    // Don't try this at home, kids!
    graph.statements.push = (...elems) => {
        let elem;
        for (let i = 0, len = elems.length; i < len; i++) {
            elem = elems[i];
            changeBufferTarget.changeBuffer[changeBufferTarget.changeBufferCount] = elem;
            changeBufferTarget.changeBufferCount++;
        }
        return Array.prototype.push.call(graph.statements, ...elems);
    };
    graph.statements.splice = (index, len) => {
        const rem = Array.prototype.splice.call(graph.statements, index, len);
        changeBufferTarget.changeBuffer.push(...rem);
        changeBufferTarget.changeBufferCount += len;
        return rem;
    };
    return graph;
}

/**
 * Extracts the content type from a request.
 * The content-type value has precedence if it contains a known type.
 * Otherwise it returns the extension if present, or the content-type without the encoding.
 *
 * @summary Extracts the content type from a request.
 */
function getContentType(res) {
    const contentTypeRaw = getHeader(res, "Content-Type");
    if (contentTypeRaw === undefined || contentTypeRaw === null) {
        return "";
    }
    const contentType = contentTypeRaw.split(";")[0];
    const url = getURL(res);
    const urlMatch = url && new URL(url).href.match(/\.([a-zA-Z0-9]{1,8})($|\?|#)/);
    const ext = urlMatch ? urlMatch[1] : "";
    if (contentType) {
        const matched = contentTypeByMimeString(contentType, ext);
        if (matched) {
            return matched;
        }
    }
    if (ext && !NON_CONTENT_EXTS.includes(ext)) {
        return contentTypeByExtention(ext);
    }
    return contentTypeRaw.split(";")[0];
}
async function getJSON(res) {
    if (res instanceof Response) {
        return res.json();
    }
    else if (res instanceof XMLHttpRequest) {
        return JSON.parse(res.responseText);
    }
    return JSON.parse(res.body);
}
function getHeader(res, header) {
    if (res instanceof Response) {
        return res.headers.get(header);
    }
    else if (typeof XMLHttpRequest !== "undefined" && res instanceof XMLHttpRequest) {
        return res.getResponseHeader(header) || null;
    }
    else if (res && res.headers) {
        const headerValue = res.headers[header];
        return headerValue || null;
    }
    return null;
}
function getURL(res) {
    if (typeof XMLHttpRequest !== "undefined" && res instanceof XMLHttpRequest) {
        return res.responseURL;
    }
    else if ("requestedURI" in res) {
        return res.requestedURI;
    }
    return res.url;
}
function contentTypeByExtention(ext) {
    if (["ttl"].includes(ext)) {
        return F_TURTLE;
    }
    else if (["ntriples", "nt"].includes(ext)) {
        return F_NTRIPLES;
    }
    else if (["jsonld"].includes(ext)) {
        return F_JSONLD;
    }
    else if (["n3"].includes(ext)) {
        return F_N3;
    }
    return ext;
}
function contentTypeByMimeString(contentType, ext) {
    if (contentType.includes(F_NTRIPLES) || contentType.includes(F_NTRIPLES_DEP)) {
        return F_NTRIPLES;
    }
    else if (contentType.includes(F_PLAIN) && ["ntriples", "nt"].indexOf(ext) >= 0) {
        return F_NTRIPLES;
    }
    else if (contentType.includes(F_TURTLE) || contentType.includes(F_TURTLE_DEP)) {
        return F_TURTLE;
    }
    else if (contentType.includes(F_N3)) {
        return F_N3;
    }
    else if (contentType.includes(F_JSONLD)) {
        return F_JSONLD;
    }
    else if (contentType.includes(F_RDF_XML)) {
        return F_RDF_XML;
    }
    return undefined;
}

class ProcessorError extends Error {
    constructor(msg, response) {
        super(msg);
        this.response = response;
    }
}

class RequestInitGenerator {
    constructor(opts = {
        credentials: "include",
        csrfFieldName: "csrf-token",
        mode: "same-origin",
        xRequestedWith: "XMLHttpRequest",
    }) {
        this.baseHeaders = opts.headers || {};
        this.csrfFieldName = opts.csrfFieldName;
        this.credentials = opts.credentials;
        this.mode = opts.mode;
        this.xRequestedWith = opts.xRequestedWith;
    }
    authenticityHeader(options = {}) {
        return Object.assign({}, options, {
            "X-CSRF-Token": this.getAuthenticityToken(),
            "X-Requested-With": this.xRequestedWith,
        });
    }
    generate(method = "GET", accept = "text/turtle", body) {
        const isFormEncoded = body instanceof URLSearchParams;
        const headers = this.getHeaders(accept);
        if (isFormEncoded) {
            headers["Content-Type"] = "application/x-www-form-urlencoded; charset=UTF-8";
        }
        return {
            body: isFormEncoded ? body.toString() : body,
            credentials: this.credentials,
            headers,
            method: method.toUpperCase(),
            mode: this.mode,
        };
    }
    getHeaders(accept) {
        const acceptHeader = Object.assign({}, this.baseHeaders, { Accept: accept });
        if (this.credentials === "include" || this.credentials === "same-origin") {
            return this.authenticityHeader(acceptHeader);
        }
        return acceptHeader;
    }
    getMetaContent(name) {
        const header = document.head && document.head.querySelector(`meta[name="${name}"]`);
        return header && header.getAttribute("content");
    }
    getAuthenticityToken() {
        return this.getMetaContent(this.csrfFieldName) || "";
    }
}

const SAFE_METHODS = ["GET", "HEAD", "OPTIONS", "CONNECT", "TRACE"];
const FETCHER_CALLBACKS = ["done", "fail", "refresh", "request", "retract"];
async function handleStatus(res) {
    if (res.status === NOT_FOUND) {
        throw {
            message: `404: '${getURL(res)}' could not be found`,
            res,
        };
    }
    else if (res.status >= BAD_REQUEST && res.status < INTERNAL_SERVER_ERROR) {
        if ((getHeader(res, "Content-Type") || "").includes("json")) {
            const body = await getJSON(res);
            throw {
                message: "errors" in body ? body.errors && body.errors[0] && body.errors[0].message : "",
                res,
            };
        }
        throw {
            message: `404: '${getURL(res)}' could not be found`,
            res,
        };
    }
    else if (res.status >= INTERNAL_SERVER_ERROR) {
        return Promise.reject({
            message: "Internal server error",
            res,
        });
    }
    return res;
}
/**
 * Pushes in-place value {v} onto an array under key {k} of Map {map}.
 * @param map The reference to the Map to add the data to.
 * @param k The key on {map}. An array is initialized when it doesn't yet exists.
 * @param v The value to push on the array under {k}.
 */
function pushToMap(map, k, v) {
    if (typeof map[k] === "undefined") {
        map[k] = [];
    }
    map[k].push(v);
}
/**
 * Saves response metadata into a graph.
 * @param iri The original iri that was fetched.
 * @param res The (fetch) response object from the request.
 * @returns A graph with metadata about the response.
 */
function processResponse(iri, res) {
    const rawURL = getURL(res);
    const origin = typeof iri === "string"
        ? rdfFactory.namedNode(new URL(rawURL).origin)
        : site(iri);
    if (rawURL && iri !== rawURL) {
        return [
            rdfFactory.quad(typeof iri === "string" ? rdfFactory.namedNode(iri) : iri, rdfFactory.namedNode("http://www.w3.org/2002/07/owl#sameAs"), rdfFactory.namedNode(rawURL), origin),
        ];
    }
    return [];
}
const emptyRequest = Object.freeze({
    lastRequested: null,
    requested: false,
    status: null,
    timesRequested: 0,
});
/**
 * The client (User Agent) has closed the connection, e.g. due to CORS or going offline.
 */
const failedRequest = () => Object.freeze({
    lastRequested: new Date(),
    requested: true,
    status: 499,
    timesRequested: 0,
});
const timedOutRequest = (totalRequested) => Object.freeze({
    lastRequested: new Date(),
    requested: true,
    status: 408,
    timesRequested: totalRequested,
});
const queuedDeltaStatus = (totalRequested) => Object.freeze({
    lastRequested: new Date(),
    requested: true,
    status: NON_AUTHORITATIVE_INFORMATION,
    timesRequested: totalRequested,
});
class DataProcessor {
    constructor(opts) {
        this.timeout = 30000;
        this.deltas = [];
        this.accept = opts.accept || {
            default: "",
        };
        this.bulkEndpoint = `${typeof window !== "undefined" ? window.location.origin : ""}/link-lib/bulk`;
        this._dispatch = opts.dispatch;
        this.invalidationMap = new Map();
        this.requestInitGenerator = opts.requestInitGenerator || new RequestInitGenerator();
        this.mapping = opts.mapping || {};
        this.report = opts.report;
        this.requestMap = new Map();
        this.statusMap = {};
        this.store = opts.store;
        this.fetch = opts.fetch || (typeof fetch !== "undefined"
            ? fetch.bind(window)
            : (_, __) => Promise.reject());
        if (opts.fetcher) {
            this.fetcher = opts.fetcher;
            if (!opts.fetch) {
                this.fetch = (url, fetchOpts) => {
                    const iri = this.store.rdfFactory.namedNode(typeof url === "string" ? url : url.url);
                    return this.fetcher.load(iri, fetchOpts);
                };
            }
        }
        if (opts.transformers) {
            opts.transformers.forEach((t) => this.registerTransformer(t.transformer, t.mediaType, t.acceptValue));
        }
        this.execExecHeader = this.execExecHeader.bind(this);
        this.processExecAction = this.processExecAction.bind(this);
        this.feedResponse = this.feedResponse.bind(this);
    }
    get fetcher() {
        if (typeof this._fetcher === "undefined") {
            this._fetcher = new convertedFetcher(this.store.getInternalStore(), {
                fetch: typeof fetch !== "undefined"
                    ? fetch
                    : typeof window !== "undefined" && window.fetch.bind(window) || undefined,
                timeout: this.timeout,
            });
            FETCHER_CALLBACKS.forEach((hook) => {
                const hookIRI = defaultNS.ll(`data/rdflib/${hook}`);
                this._fetcher.addCallback(hook, this.invalidate.bind(this));
                this._fetcher.addCallback(hook, (iri, _err) => {
                    this.dispatch(hookIRI, [typeof iri === "string" ? rdfFactory.namedNode(iri) : iri, _err]);
                    return true;
                });
            });
        }
        return this._fetcher;
    }
    set fetcher(v) {
        this._fetcher = v;
    }
    get dispatch() {
        if (typeof this._dispatch === "undefined") {
            throw new Error("Invariant: cannot call `dispatch` before initialization is complete");
        }
        return this._dispatch;
    }
    set dispatch(value) {
        this._dispatch = value;
    }
    async execActionByIRI(subject, dataTuple) {
        const [graph, blobs = []] = dataTuple;
        await (this.store.statementsFor(subject).length > 0
            ? Promise.resolve([])
            : this.getEntity(subject));
        const object = this.store.getResourceProperty(subject, schema.object);
        if (!object || object.termType !== "BlankNode" && object.termType !== "NamedNode") {
            throw new ProcessorError(MSG_OBJECT_NOT_IRI);
        }
        const target = this.store.getResourceProperty(subject, schema.target);
        if (!target || target.termType === "Collection" || target.termType === TermType.Literal) {
            throw new ProcessorError(MSG_INCORRECT_TARGET);
        }
        const urls = this.store.getResourceProperty(target, schema.url);
        const url = Array.isArray(urls) ? urls[0] : urls;
        if (!url) {
            throw new ProcessorError(MSG_URL_UNDEFINED);
        }
        if (url.termType !== "NamedNode") {
            throw new ProcessorError(MSG_URL_UNRESOLVABLE);
        }
        const targetMethod = this.store.getResourceProperty(target, schema.httpMethod);
        const method = typeof targetMethod !== "undefined" ? targetMethod.toString() : "GET";
        const opts = this.requestInitGenerator.generate(method, this.acceptForHost(url));
        if (opts.headers instanceof Headers) {
            opts.headers.set("Request-Referrer", subject.value);
        }
        else if (opts.headers && !Array.isArray(opts.headers)) {
            opts.headers["Request-Referrer"] = subject.value;
        }
        if (!SAFE_METHODS.includes(method) && graph && graph !== null && graph.length > 0) {
            if (opts.headers instanceof Headers) {
                opts.headers.delete("Content-Type");
            }
            else if (opts.headers && !Array.isArray(opts.headers)) {
                delete opts.headers["Content-Type"];
            }
            const data = new FormData();
            const rdfSerialization = this.serialize(graph.statements);
            data.append(defaultNS.ll("graph").toString(), new Blob([rdfSerialization], { type: F_NTRIPLES }));
            for (let i = 0; i < blobs.length; i++) {
                data.append(blobs[i][0].toString(), blobs[i][1]);
            }
            opts.body = data;
        }
        const resp = await this.fetch(url.value, opts).then(this.processExecAction);
        if (resp.status > BAD_REQUEST) {
            // TODO: process responses with a correct content-type.
            throw new ProcessorError(MSG_BAD_REQUEST, resp);
        }
        const statements = await this.feedResponse(resp, true);
        const location = getHeader(resp, "Location");
        const fqLocation = location && uri.join(location, window.location.origin);
        const iri = fqLocation && rdfFactory.namedNode(fqLocation) || null;
        return {
            data: statements,
            iri,
        };
    }
    fetchableURLFromIRI(iri) {
        return rdfFactory.namedNode(iri.value.split("#").shift());
    }
    // public async fetchResource(iri: NamedNode | string, opts?: RDFFetchOpts): Promise<ResponseAndFallbacks> {
    //     const accept = this.acceptForHost(iri);
    //     if (accept) {
    //         this.fetcher.mediatypes = {[accept]: {q: 1.0}};
    //     }
    //
    //     const options = this.requestInitGenerator.generate("GET", accept);
    //
    //     let res;
    //     try {
    //         res = await this.fetcher.load(iri, options);
    //     } catch (e) {
    //         if (typeof e.response !== "undefined") {
    //             res =  e.response;
    //         } else {
    //             throw e;
    //         }
    //     }
    //
    //     return this.processExecAction(res);
    // }
    flush() {
        const deltas = this.deltas;
        this.deltas = [];
        for (let i = 0; i < deltas.length; i++) {
            try {
                this.processDelta(deltas[i]);
            }
            catch (e) {
                this.report(e);
            }
        }
        return [];
    }
    getEntities(resources) {
        const reload = [];
        const body = new URLSearchParams();
        for (let i = 0; i < resources.length; i++) {
            const resource = resources[i];
            if (resource[1] && resource[1].reload) {
                reload.push(resource[0]);
            }
            body.append("resource[]", encodeURIComponent(resource[0].value));
        }
        const opts = this.requestInitGenerator.generate("POST", this.acceptForHost(this.bulkEndpoint), body);
        return this.fetch(this.bulkEndpoint, opts)
            .then(this.feedResponse)
            .catch((err) => {
            const status = rdfFactory.literal(err instanceof Error ? 499 : err.status, xsd.integer);
            const delta = resources
                .map(([s]) => [s, defaultNS.http("statusCode"), status, defaultNS.ll("meta")]);
            return this.processDelta(delta);
        });
    }
    /**
     * @see LinkedDataAPI#getEntity
     * @param iri The SomeNode of the entity
     * @param opts The options for fetch-/processing the resource.
     * @param opts The options for fetch-/processing the resource.
     * @return A promise with the resulting entity
     */
    async getEntity(iri, opts) {
        const url = new URL(iri.value);
        url.hash = "";
        const requestIRI = rdfFactory.namedNode(url.toString());
        const existing = this.requestMap.get(requestIRI.id);
        if (existing) {
            return existing;
        }
        let preExistingData = [];
        if (opts && opts.clearPreviousData) {
            preExistingData = this.store.statementsFor(iri);
            preExistingData = preExistingData.concat(this.store.statementsFor(requestIRI));
        }
        try {
            const reqOpts = this.requestInitGenerator.generate("GET", this.acceptForHost(requestIRI));
            const req = this.fetch(requestIRI.value, reqOpts)
                .then(this.feedResponse)
                .catch((err) => {
                const status = rdfFactory.literal(err instanceof Error ? 499 : err.status, xsd.integer);
                const delta = [
                    [requestIRI, defaultNS.http("statusCode"), status, defaultNS.ll("meta")],
                ];
                return this.processDelta(delta);
            });
            this.requestMap.set(rdfFactory.id(requestIRI), req);
            return await req;
        }
        catch (e) {
            if (typeof e.res === "undefined") {
                throw e;
            }
            this.store.removeStatements(preExistingData);
            const responseQuads = processResponse(iri, e.res);
            this.store.addStatements(responseQuads);
            return responseQuads;
        }
        finally {
            this.requestMap.delete(rdfFactory.id(requestIRI));
        }
    }
    /**
     * @see LinkedDataAPI#getStatus for documentation
     */
    getStatus(iri) {
        var _a, _b;
        const irl = this.fetchableURLFromIRI(iri);
        const existing = this.statusMap[rdfFactory.id(irl)];
        if (existing) {
            return existing;
        }
        const fetcherStatus = (_a = this.fetcher) === null || _a === void 0 ? void 0 : _a.requested[irl.value];
        if (fetcherStatus === undefined) {
            if (irl.value in ((_b = this.fetcher) === null || _b === void 0 ? void 0 : _b.requested)) {
                return this.memoizeStatus(irl, failedRequest());
            }
            return this.memoizeStatus(irl, emptyRequest);
        }
        const requests = this.store.match(null, defaultNS.link("requestedURI"), rdfFactory.literal(irl.value));
        const totalRequested = requests.length;
        if (requests.length === 0) {
            return this.memoizeStatus(irl, emptyRequest);
        }
        if (fetcherStatus === true) {
            return this.memoizeStatus(irl, {
                lastRequested: new Date(),
                lastResponseHeaders: null,
                requested: true,
                status: 202,
                timesRequested: totalRequested,
            });
        }
        if (fetcherStatus === "timeout") {
            return this.memoizeStatus(irl, timedOutRequest(totalRequested));
        }
        const requestIRI = requests.pop().subject;
        const requestObj = anyRDFValue(this.store.statementsFor(requestIRI), defaultNS.link("response"));
        if (!requestObj) {
            return this.memoizeStatus(irl, emptyRequest);
        }
        const requestObjData = this.store.statementsFor(requestObj);
        // RDFLib has different behaviour across browsers and code-paths, so we must check for multiple properties.
        const requestStatus = anyRDFValue(requestObjData, defaultNS.http("status"))
            || anyRDFValue(requestObjData, defaultNS.http07("status"))
            || anyRDFValue(requestObjData, defaultNS.httph("status"));
        const requestDate = anyRDFValue(requestObjData, defaultNS.httph("date"));
        if (!requestStatus) {
            if (fetcherStatus === "done") {
                return this.memoizeStatus(irl, timedOutRequest(totalRequested));
            }
            return this.memoizeStatus(irl, emptyRequest);
        }
        return this.memoizeStatus(irl, {
            lastRequested: requestDate ? new Date(requestDate.value) : new Date(0),
            lastResponseHeaders: requestObj,
            requested: true,
            status: Number.parseInt(requestStatus.value, 10),
            timesRequested: totalRequested,
        });
    }
    invalidate(iri, _err) {
        const id = rdfFactory.id(typeof iri === "string" ? rdfFactory.namedNode(iri) : iri);
        this.invalidationMap.set(id);
        // TODO: Don't just remove, but rather mark it as invalidated so it's history isn't lost.
        this.statusMap[id] = undefined;
        return true;
    }
    isInvalid(iri) {
        return this.invalidationMap.has(rdfFactory.id(iri));
    }
    processExternalResponse(response) {
        return handleStatus(response)
            .then(this.feedResponse);
    }
    processDelta(delta) {
        let s;
        for (let i = 0, len = delta.length; i < len; i++) {
            s = delta[i];
            const subj = s ? s[0] : undefined;
            const currentStatus = subj && this.statusMap[rdfFactory.id(subj)];
            if (subj && currentStatus && currentStatus.status === 203) {
                this.statusMap[rdfFactory.id(subj)] = undefined;
            }
            if (!s || s[3] !== defaultNS.ll("meta")) {
                continue;
            }
            if (rdfFactory.equals(s[1], defaultNS.http("statusCode"))) {
                this.removeInvalidation(subj);
                this.setStatus(subj, Number.parseInt(s[2].value, 10));
            }
            else if (rdfFactory.equals(s[1], defaultNS.httph("Exec-Action"))) {
                this.execExecHeader(s[2].value);
            }
        }
        return [];
    }
    /** Register a transformer so it can be used to interact with API's. */
    registerTransformer(transformer, mediaType, acceptValue) {
        const mediaTypes = Array.isArray(mediaType) ? mediaType : [mediaType];
        mediaTypes.forEach((type) => {
            pushToMap(this.mapping, type, transformer);
            this.accept.default = [this.accept.default, [type, acceptValue].join(";")].join();
        });
    }
    save(iri, opts = {}) {
        var _a, _b, _c;
        if (isBlankNode(iri) && !((_a = opts) === null || _a === void 0 ? void 0 : _a.url)) {
            throw new Error("Can't resolve");
        }
        const target = isBlankNode(iri) ? opts.url : (((_b = opts) === null || _b === void 0 ? void 0 : _b.url) || iri);
        const targetData = opts.data || ((_c = opts) === null || _c === void 0 ? void 0 : _c.useDefaultGraph)
            ? this.store.match(null, null, null, iri)
            : this.store.match(iri, null, null, this.store.rdfFactory.defaultGraph());
        const options = this.requestInitGenerator.generate(opts.method || "PUT", this.acceptForHost(target), this.serialize(targetData));
        return this.fetch(target.value, options)
            .then(() => Promise.resolve());
    }
    queueDelta(delta, subjects) {
        this.deltas.push(delta);
        const status = queuedDeltaStatus(1);
        for (const s of subjects) {
            if (!this.statusMap[s]) {
                this.statusMap[s] = status;
            }
        }
    }
    setAcceptForHost(origin, acceptValue) {
        this.accept[new URL(origin).origin] = acceptValue;
    }
    acceptForHost(iri) {
        return this.accept[new URL(typeof iri === "string" ? iri : iri.value).origin]
            || this.accept.default;
    }
    execExecHeader(actionsHeader) {
        if (actionsHeader) {
            const actions = actionsHeader.split(", ");
            for (let i = 0; i < actions.length; i++) {
                this.dispatch(rdfFactory.namedNode(actions[i]), undefined);
            }
        }
    }
    feedResponse(res, expedite = false) {
        if (res.status >= INTERNAL_SERVER_ERROR) {
            return Promise.reject(res);
        }
        const format = getContentType(res);
        const formatProcessors = this.mapping[format];
        const processor = formatProcessors && formatProcessors[0];
        if (processor === undefined) {
            return Promise.resolve([]);
        }
        res.expedite = expedite;
        return processor(res);
    }
    memoizeStatus(iri, s) {
        this.statusMap[rdfFactory.id(iri)] = s;
        return s;
    }
    processExecAction(res) {
        const actionsHeader = getHeader(res, "Exec-Action");
        this.execExecHeader(actionsHeader);
        return Promise.resolve(res);
    }
    removeInvalidation(subject) {
        this.invalidationMap.delete(rdfFactory.id(subject));
    }
    serialize(data) {
        const s = new convertedSerializer(new convertedIndexedFormula());
        patchRDFLibSerializer(s, "deinprstux");
        s.setFlags("deinprstux");
        return s.statementsToNTriples(data);
    }
    setStatus(iri, status) {
        const url = this.fetchableURLFromIRI(iri);
        const prevStatus = this.statusMap[rdfFactory.id(url)];
        this.store.touch(url);
        this.store.touch(iri);
        this.memoizeStatus(url, {
            lastRequested: new Date(),
            lastResponseHeaders: null,
            requested: true,
            status,
            timesRequested: prevStatus ? prevStatus.timesRequested + 1 : 1,
        });
    }
}

const CI_MATCH_PREFIX = 0;
const CI_MATCH_SUFFIX = 1;
/**
 * Expands a property if it's in short-form while preserving long-form.
 * Note: The vocabulary needs to be present in the store prefix library
 * @param prop The short- or long-form property
 * @param namespaces Object of namespaces by their abbreviation.
 * @returns The (expanded) property
 */
function expandProperty(prop, namespaces = defaultNS) {
    if (!prop) {
        return prop;
    }
    if (typeof prop !== "string"
        && Object.prototype.hasOwnProperty.call(prop, "termType")
        && prop.termType === "NamedNode") {
        return rdfFactory.namedNode(prop.value);
    }
    if (typeof prop === "object") {
        if (prop.termType === "NamedNode") {
            return rdfFactory.namedNode(prop.value);
        }
        return undefined;
    }
    if (prop.indexOf("/") >= 1) {
        if (prop.startsWith("<") && prop.endsWith(">")) {
            return rdfFactory.namedNode(prop.slice(1, -1));
        }
        return rdfFactory.namedNode(prop);
    }
    const matches = prop.split(":");
    const constructor = namespaces[matches[CI_MATCH_PREFIX]];
    return constructor && constructor(matches[CI_MATCH_SUFFIX]);
}

const BASE = 36;
const DEC_CUTOFF = 2;
const IRI_LEN = 20;
function isPlainObject(o) {
    return typeof o === "object"
        && o !== null
        && !NON_DATA_OBJECTS_CTORS.find((c) => typeof o.prototype !== "undefined" && o instanceof c)
        && !Object.prototype.hasOwnProperty.call(o, "termType");
}
function isIterable(o) {
    return Array.isArray(o) || o instanceof Set;
}
function uploadIRI() {
    return defaultNS.ll(`blobs/a${Math.random().toString(BASE).substr(DEC_CUTOFF, IRI_LEN)}`);
}
/**
 * Converts an array to an RDF list-shaped {DataObject} for serialization.
 */
function list(arr) {
    // @ts-ignore
    return arr.reduceRight((acc, next) => ({
        [rdf.first.toString()]: next,
        [rdf.rest.toString()]: acc,
    }), rdf.nil);
}
/**
 * Converts an array to an RDF sequence-shaped {DataObject} for serialization.
 */
function seq(arr, id) {
    const base = { [rdf.type.toString()]: rdf.Seq };
    if (id) {
        base["@id"] = id;
    }
    return arr.reduce((acc, next, n) => Object.assign(acc, { [rdf.ns(`_${n}`).toString()]: next }), base);
}
/** @private */
function processObject(subject, predicate, datum, graph) {
    let blobs = [];
    if (isIterable(datum)) {
        for (const subResource of datum) {
            if (isPlainObject(subResource)) {
                const id = subResource["@id"] || rdfFactory.blankNode();
                blobs = blobs.concat(processDataObject(id, subResource, graph));
                graph.add(subject, predicate, id);
            }
            else {
                blobs = blobs.concat(processObject(subject, predicate, subResource, graph));
            }
        }
    }
    else if (typeof datum === "string"
        || typeof datum === "number"
        || typeof datum === "boolean"
        || datum instanceof Date) {
        graph.add(subject, predicate, rdfFactory.literal(datum));
    }
    else if (datum instanceof File) {
        const f = uploadIRI();
        const file = rdfFactory.quad(subject, predicate, f);
        blobs.push([f, datum]);
        graph.add(file);
    }
    else if (isPlainObject(datum)) {
        const id = datum["@id"] || rdfFactory.blankNode();
        blobs = blobs.concat(processDataObject(id, datum, graph));
        graph.add(subject, predicate, id);
    }
    else if (datum && datum.termType === "NamedNode") {
        graph.add(subject, predicate, rdfFactory.namedNode(datum.value));
    }
    else if (datum && datum.termType === "Literal") {
        graph.add(subject, predicate, rdfFactory.literal(datum.value, datum.language || rdfFactory.namedNode(datum.datatype.value)));
    }
    else if (datum !== null && datum !== undefined) {
        graph.add(subject, predicate, rdfFactory.literal(datum));
    }
    return blobs;
}
function processDataObject(subject, data, graph) {
    let blobs = [];
    const keys = Object.keys(data);
    for (let i = 0; i < keys.length; i++) {
        if (keys[i] === "@id") {
            continue;
        }
        const predicate = expandProperty(keys[i], defaultNS);
        const datum = data[keys[i]];
        if (predicate === undefined) {
            throw new Error(`Unknown predicate ${keys[i]} given (for subject '${subject}').`);
        }
        blobs = blobs.concat(processObject(subject, predicate, datum, graph));
    }
    return blobs;
}
function dataToGraphTuple(data) {
    const g = graph();
    const blobs = processDataObject(MAIN_NODE_DEFAULT_IRI, data, g);
    return [g, blobs];
}
/**
 * Convert a DataObject into a graph. Useful for writing test data in semi-plain JS objects
 * @param iriOrData The data object or an iri for the top-level object.
 * @param data The data object if an IRI was passed.
 * @param graph A graph to write the statements into.
 */
function toGraph(iriOrData, data, graph$1) {
    const passedIRI = iriOrData.termType === TermType.BlankNode || iriOrData.termType === TermType.NamedNode;
    if (passedIRI && !data) {
        throw new TypeError("Only an IRI was passed to `toObject`, a valid data object has to be the second argument");
    }
    const embeddedIRI = (passedIRI ? data : iriOrData)["@id"];
    let iri;
    if (embeddedIRI) {
        if (typeof embeddedIRI !== "string") {
            throw new TypeError("Embedded IRI (`@id`) value must be of type string");
        }
        iri = rdfFactory.namedNode(embeddedIRI);
    }
    else {
        iri = passedIRI ? iriOrData : rdfFactory.blankNode();
    }
    const dataObj = passedIRI ? data : iriOrData;
    const g = graph$1 || graph();
    const blobs = processDataObject(iri, dataObj, g);
    return [iri, g, blobs];
}

const matchSingle = (graphIRI) => {
    if (graphIRI) {
        const value = graphIRI.value;
        return (graph) => rdfFactory.equals(graph, graphIRI) || graph.value.startsWith(value);
    }
    else {
        return (graph) => graph === undefined;
    }
};
const isInGraph = (graphIRIS) => {
    if (graphIRIS.length === 0) {
        return matchSingle(graphIRIS[0]);
    }
    const matchers = graphIRIS.map((iri) => matchSingle(iri));
    return (graph) => matchers.some((matcher) => matcher(graph));
};
const deltaProcessor = (addGraphIRIS, replaceGraphIRIS, removeGraphIRIS, purgeGraphIRIS, sliceGraphIRIS) => {
    const isAdd = isInGraph(addGraphIRIS);
    const isReplace = isInGraph(replaceGraphIRIS);
    const isRemove = isInGraph(removeGraphIRIS);
    const isPurge = isInGraph(purgeGraphIRIS);
    const isSlice = isInGraph(sliceGraphIRIS);
    return (store) => (delta) => {
        const addable = [];
        const replaceable = [];
        const removable = [];
        let quad;
        for (let i = 0, len = delta.length; i < len; i++) {
            quad = delta[i];
            if (!quad) {
                continue;
            }
            if (isAdd(quad[3])) {
                const g = new URL(quad[3].value).searchParams.get("graph");
                if (g) {
                    addable.push([quad[0], quad[1], quad[2], rdfFactory.namedNode(g)]);
                }
                else {
                    addable.push(quad);
                }
            }
            else if (isReplace(quad[3])) {
                const g = new URL(quad[3].value).searchParams.get("graph");
                if (g) {
                    replaceable.push([quad[0], quad[1], quad[2], rdfFactory.namedNode(g)]);
                }
                else {
                    replaceable.push(quad);
                }
            }
            else if (isRemove(quad[3])) {
                const g = new URL(quad[3].value).searchParams.get("graph");
                if (g) {
                    const matches = store.match(quad[0], quad[1], null, rdfFactory.namedNode(g));
                    removable.push(...matches);
                }
                else {
                    const matches = store.match(quad[0], quad[1], null, null);
                    removable.push(...matches);
                }
            }
            else if (isPurge(quad[3])) {
                const g = new URL(quad[3].value).searchParams.get("graph");
                if (g) {
                    const matches = store.match(quad[0], null, null, rdfFactory.namedNode(g));
                    removable.push(...matches);
                }
                else {
                    const matches = store.match(quad[0], null, null, null);
                    removable.push(...matches);
                }
            }
            else if (isSlice(quad[3])) {
                const g = new URL(quad[3].value).searchParams.get("graph");
                removable.push(...store.match(quad[QuadPosition.subject], quad[QuadPosition.predicate], quad[QuadPosition.object], g ? rdfFactory.namedNode(g) : null));
            }
        }
        return [
            addable,
            replaceable,
            removable,
        ];
    };
};

const EMPTY_ST_ARR = Object.freeze([]);
/**
 * Provides a clean consistent interface to stored (RDF) data.
 */
class RDFStore {
    constructor({ deltaProcessorOpts, innerStore } = {}) {
        var _a, _b, _c, _d, _e;
        this.changeBuffer = new Array(100);
        this.changeBufferCount = 0;
        /**
         * Record of the last time a resource was flushed.
         *
         * @note Not to be confused with the last change in the store, which might be later than the flush time.
         */
        this.changeTimestamps = [];
        this.typeCache = [];
        this.deltas = [];
        this.langPrefs = Array.from(typeof navigator !== "undefined"
            ? (navigator.languages || [navigator.language])
            : ["en"]);
        this.store = graph();
        this.processDelta = this.processDelta.bind(this);
        const g = innerStore || graph();
        this.store = patchRDFLibStoreWithOverrides(g, this);
        this.store.newPropertyAction(rdf.type, this.processTypeStatement.bind(this));
        const defaults = {
            addGraphIRIS: [defaultNS.ll("add"), defaultNS.ld("add")],
            purgeGraphIRIS: [defaultNS.ll("purge"), defaultNS.ld("purge")],
            removeGraphIRIS: [defaultNS.ll("remove"), defaultNS.ld("remove")],
            replaceGraphIRIS: [
                undefined,
                defaultNS.ll("replace"),
                defaultNS.ld("replace"),
                this.store.rdfFactory.defaultGraph(),
            ],
            sliceGraphIRIS: [defaultNS.ll("slice"), defaultNS.ld("slice")],
        };
        this.deltaProcessor = deltaProcessor(((_a = deltaProcessorOpts) === null || _a === void 0 ? void 0 : _a.addGraphIRIS) || defaults.addGraphIRIS, ((_b = deltaProcessorOpts) === null || _b === void 0 ? void 0 : _b.replaceGraphIRIS) || defaults.replaceGraphIRIS, ((_c = deltaProcessorOpts) === null || _c === void 0 ? void 0 : _c.removeGraphIRIS) || defaults.removeGraphIRIS, ((_d = deltaProcessorOpts) === null || _d === void 0 ? void 0 : _d.purgeGraphIRIS) || defaults.purgeGraphIRIS, ((_e = deltaProcessorOpts) === null || _e === void 0 ? void 0 : _e.sliceGraphIRIS) || defaults.sliceGraphIRIS)(this.store);
    }
    get rdfFactory() {
        return this.store.rdfFactory;
    }
    set rdfFactory(value) {
        throw this.store.rdfFactory = value;
    }
    /**
     * Add statements to the store.
     * @param data Data to parse and add to the store.
     */
    addStatements(data) {
        if (!Array.isArray(data)) {
            throw new TypeError("An array of statements must be passed to addStatements");
        }
        for (let i = 0, len = data.length; i < len; i++) {
            this.store.add(data[i].subject, data[i].predicate, data[i].object, data[i].graph);
        }
    }
    addQuads(data) {
        const statements = new Array(data.length);
        for (let i = 0, len = data.length; i < len; i++) {
            statements[i] = this.store.add(data[i][0], data[i][1], data[i][2], data[i][3]);
        }
        return statements;
    }
    any(subj, pred, obj, why) {
        return this.store.any(subj, pred, obj, why);
    }
    anyStatementMatching(subj, pred, obj, why) {
        return this.store.anyStatementMatching(subj, pred, obj, why);
    }
    anyValue(subj, pred, obj, why) {
        return this.store.anyValue(subj, pred, obj, why);
    }
    canon(term) {
        return this.store.canon(term);
    }
    defaultGraph() {
        return this.store.rdfFactory.defaultGraph();
    }
    /**
     * Flushes the change buffer to the return value.
     * @return Statements held in memory since the last flush.
     */
    flush() {
        const deltas = this.deltas;
        this.deltas = [];
        for (let i = 0; i < deltas.length; i++) {
            this.processDelta(deltas[i]);
        }
        if (this.changeBufferCount === 0) {
            return EMPTY_ST_ARR;
        }
        const processingBuffer = this.changeBuffer;
        this.changeBuffer = new Array(100);
        this.changeBufferCount = 0;
        const changeStamp = Date.now();
        processingBuffer
            .filter((s) => {
            this.changeTimestamps[rdfFactory.id(s.subject)] = changeStamp;
            return rdfFactory.equals(s.predicate, rdf.type);
        })
            .map((s) => this.processTypeStatement(undefined, s.subject, s.predicate, undefined, undefined));
        return processingBuffer;
    }
    /** @private */
    getInternalStore() {
        return this.store;
    }
    match(subj, pred, obj, why) {
        return this.store.match(subj, pred, obj, why) || [];
    }
    processDelta(delta) {
        const [addables, replacables, removables,] = this.deltaProcessor(delta);
        this.removeStatements(removables);
        return this.replaceMatches(replacables).concat(this.addQuads(addables));
    }
    removeResource(subject) {
        this.touch(subject);
        this.typeCache[rdfFactory.id(subject)] = [];
        this.removeStatements(this.statementsFor(subject));
    }
    removeStatements(statements) {
        this.store.remove(statements.slice());
    }
    /**
     * Removes an array of statements and inserts another.
     * Note: Be sure that the replacement contains the same subjects as the original to let the
     *  broadcast work correctly.
     * @access private This is in conflict with the typescript declaration due to the development of some experimental
     *                  features, but this method shouldn't be used nevertheless.
     * @param original The statements to remove from the store.
     * @param replacement The statements to add to the store.
     */
    replaceStatements(original, replacement) {
        const uniqueStatements = new Array(replacement.length).filter(Boolean);
        for (let i = 0; i < replacement.length; i++) {
            const cond = original.some(({ subject, predicate }) => rdfFactory.equals(subject, replacement[i].subject)
                && rdfFactory.equals(predicate, replacement[i].predicate));
            if (!cond) {
                uniqueStatements.push(replacement[i]);
            }
        }
        this.removeStatements(original);
        // Remove statements not in the old object. Useful for replacing data loosely related to the main resource.
        for (let i = 0; i < uniqueStatements.length; i++) {
            this.store.removeMatches(uniqueStatements[i].subject, uniqueStatements[i].predicate);
        }
        return this.addStatements(replacement);
    }
    replaceMatches(statements) {
        for (let i = 0; i < statements.length; i++) {
            this.removeStatements(this.match(statements[i][0], statements[i][1], undefined, undefined));
        }
        return this.addQuads(statements);
    }
    getResourcePropertyRaw(subject, property) {
        const props = this.statementsFor(subject);
        if (Array.isArray(property)) {
            for (let i = 0; i < property.length; i++) {
                const values = allRDFPropertyStatements(props, property[i]);
                if (values.length > 0) {
                    return values;
                }
            }
            return EMPTY_ST_ARR;
        }
        return allRDFPropertyStatements(props, property);
    }
    getResourceProperties(subject, property) {
        if (property === rdf.type) {
            return (this.typeCache[rdfFactory.id(subject)] || []);
        }
        return this
            .getResourcePropertyRaw(subject, property)
            .map((s) => s.object);
    }
    getResourceProperty(subject, property) {
        if (!Array.isArray(property) && rdfFactory.equals(property, rdf.type)) {
            const entry = this.typeCache[rdfFactory.id(subject)];
            return entry ? entry[0] : undefined;
        }
        const rawProp = this.getResourcePropertyRaw(subject, property);
        if (rawProp.length === 0) {
            return undefined;
        }
        return getPropBestLang(rawProp, this.langPrefs);
    }
    queueDelta(delta) {
        this.deltas.push(delta);
    }
    /**
     * Searches the store for all the statements on {iri} (so not all statements relating to {iri}).
     * @param subject The identifier of the resource.
     */
    statementsFor(subject) {
        const canon = rdfFactory.id(this.store.canon(subject));
        return typeof this.store.subjectIndex[canon] !== "undefined"
            ? this.store.subjectIndex[canon]
            : EMPTY_ST_ARR;
    }
    touch(iri) {
        this.changeTimestamps[rdfFactory.id(iri)] = Date.now();
        this.changeBuffer.push(rdfFactory.quad(iri, defaultNS.ll("nop"), defaultNS.ll("nop")));
        this.changeBufferCount++;
    }
    workAvailable() {
        return this.deltas.length + this.changeBufferCount;
    }
    /**
     * Builds a cache of types per resource. Can be omitted when compiled against a well known service.
     */
    processTypeStatement(_formula, subj, _pred, obj, _why) {
        const subjId = rdfFactory.id(subj);
        if (!Array.isArray(this.typeCache[subjId])) {
            this.typeCache[subjId] = [obj];
            return false;
        }
        this.typeCache[subjId] = this.statementsFor(subj)
            .filter((s) => rdfFactory.equals(s.predicate, rdf.type))
            .map((s) => s.object);
        if (obj) {
            this.typeCache[subjId].push(obj);
        }
        return false;
    }
}

/** Function to create arbitrary terms within the 'The OWL 2 Schema vocabulary (OWL 2)' ontology */
const ns$6 = createNS("http://www.w3.org/2002/07/owl#");
/** The class of collections of pairwise different individuals. */
const AllDifferent = ns$6("AllDifferent");
/** The class of collections of pairwise disjoint classes. */
const AllDisjointClasses = ns$6("AllDisjointClasses");
/** The class of collections of pairwise disjoint properties. */
const AllDisjointProperties = ns$6("AllDisjointProperties");
/** The class of annotated annotations for which the RDF serialization consists of an annotated subject, predicate and object. */
const Annotation = ns$6("Annotation");
/** The class of annotation properties. */
const AnnotationProperty = ns$6("AnnotationProperty");
/** The class of asymmetric properties. */
const AsymmetricProperty = ns$6("AsymmetricProperty");
/** The class of annotated axioms for which the RDF serialization consists of an annotated subject, predicate and object. */
const Axiom = ns$6("Axiom");
/** The class of OWL classes. */
const Class$1 = ns$6("Class");
/** The class of OWL data ranges, which are special kinds of datatypes. Note: The use of the IRI owl:DataRange has been deprecated as of OWL 2. The IRI rdfs:Datatype SHOULD be used instead. */
const DataRange = ns$6("DataRange");
/** The class of data properties. */
const DatatypeProperty = ns$6("DatatypeProperty");
/** The class of deprecated classes. */
const DeprecatedClass = ns$6("DeprecatedClass");
/** The class of deprecated properties. */
const DeprecatedProperty = ns$6("DeprecatedProperty");
/** The class of functional properties. */
const FunctionalProperty = ns$6("FunctionalProperty");
/** The class of inverse-functional properties. */
const InverseFunctionalProperty = ns$6("InverseFunctionalProperty");
/** The class of irreflexive properties. */
const IrreflexiveProperty = ns$6("IrreflexiveProperty");
/** The class of named individuals. */
const NamedIndividual = ns$6("NamedIndividual");
/** The class of negative property assertions. */
const NegativePropertyAssertion = ns$6("NegativePropertyAssertion");
/** This is the empty class. */
const Nothing = ns$6("Nothing");
/** The class of object properties. */
const ObjectProperty = ns$6("ObjectProperty");
/** The class of ontologies. */
const Ontology = ns$6("Ontology");
/** The class of ontology properties. */
const OntologyProperty = ns$6("OntologyProperty");
/** The class of reflexive properties. */
const ReflexiveProperty = ns$6("ReflexiveProperty");
/** The class of property restrictions. */
const Restriction = ns$6("Restriction");
/** The class of symmetric properties. */
const SymmetricProperty = ns$6("SymmetricProperty");
/** The class of transitive properties. */
const TransitiveProperty = ns$6("TransitiveProperty");
/** The class of OWL individuals. */
const Thing$1 = ns$6("Thing");
/** The property that determines the class that a universal property restriction refers to. */
const allValuesFrom = ns$6("allValuesFrom");
/** The property that determines the predicate of an annotated axiom or annotated annotation. */
const annotatedProperty = ns$6("annotatedProperty");
/** The property that determines the subject of an annotated axiom or annotated annotation. */
const annotatedSource = ns$6("annotatedSource");
/** The property that determines the object of an annotated axiom or annotated annotation. */
const annotatedTarget = ns$6("annotatedTarget");
/** The property that determines the predicate of a negative property assertion. */
const assertionProperty = ns$6("assertionProperty");
/** The data property that does not relate any individual to any data value. */
const bottomDataProperty = ns$6("bottomDataProperty");
/** The object property that does not relate any two individuals. */
const bottomObjectProperty = ns$6("bottomObjectProperty");
/** The property that determines the cardinality of an exact cardinality restriction. */
const cardinality = ns$6("cardinality");
/** The property that determines that a given class is the complement of another class. */
const complementOf = ns$6("complementOf");
/** The property that determines that a given data range is the complement of another data range with respect to the data domain. */
const datatypeComplementOf = ns$6("datatypeComplementOf");
/** The property that determines that two given individuals are different. */
const differentFrom = ns$6("differentFrom");
/** The property that determines that a given class is equivalent to the disjoint union of a collection of other classes. */
const disjointUnionOf = ns$6("disjointUnionOf");
/** The property that determines that two given classes are disjoint. */
const disjointWith = ns$6("disjointWith");
/** The property that determines the collection of pairwise different individuals in a owl:AllDifferent axiom. */
const distinctMembers = ns$6("distinctMembers");
/** The property that determines that two given classes are equivalent, and that is used to specify datatype definitions. */
const equivalentClass = ns$6("equivalentClass");
/** The property that determines that two given properties are equivalent. */
const equivalentProperty = ns$6("equivalentProperty");
/** The property that determines the collection of properties that jointly build a key. */
const hasKey = ns$6("hasKey");
/** The property that determines the property that a self restriction refers to. */
const hasSelf = ns$6("hasSelf");
/** The property that determines the individual that a has-value restriction refers to. */
const hasValue$1 = ns$6("hasValue");
/** The property that determines the collection of classes or data ranges that build an intersection. */
const intersectionOf = ns$6("intersectionOf");
/** The property that determines that two given properties are inverse. */
const inverseOf = ns$6("inverseOf");
/** The property that determines the cardinality of a maximum cardinality restriction. */
const maxCardinality = ns$6("maxCardinality");
/** The property that determines the cardinality of a maximum qualified cardinality restriction. */
const maxQualifiedCardinality = ns$6("maxQualifiedCardinality");
/** The property that determines the collection of members in either a owl:AllDifferent, owl:AllDisjointClasses or owl:AllDisjointProperties axiom. */
const members$1 = ns$6("members");
/** The property that determines the cardinality of a minimum cardinality restriction. */
const minCardinality = ns$6("minCardinality");
/** The property that determines the cardinality of a minimum qualified cardinality restriction. */
const minQualifiedCardinality = ns$6("minQualifiedCardinality");
/** The property that determines the class that a qualified object cardinality restriction refers to. */
const onClass = ns$6("onClass");
/** The property that determines the data range that a qualified data cardinality restriction refers to. */
const onDataRange = ns$6("onDataRange");
/** The property that determines the datatype that a datatype restriction refers to. */
const onDatatype = ns$6("onDatatype");
/** The property that determines the collection of individuals or data values that build an enumeration. */
const oneOf$1 = ns$6("oneOf");
/** The property that determines the n-tuple of properties that a property restriction on an n-ary data range refers to. */
const onProperties = ns$6("onProperties");
/** The property that determines the property that a property restriction refers to. */
const onProperty = ns$6("onProperty");
/** The property that determines the n-tuple of properties that build a sub property chain of a given property. */
const propertyChainAxiom = ns$6("propertyChainAxiom");
/** The property that determines that two given properties are disjoint. */
const propertyDisjointWith = ns$6("propertyDisjointWith");
/** The property that determines the cardinality of an exact qualified cardinality restriction. */
const qualifiedCardinality = ns$6("qualifiedCardinality");
/** The property that determines that two given individuals are equal. */
const sameAs$1 = ns$6("sameAs");
/** The property that determines the class that an existential property restriction refers to. */
const someValuesFrom = ns$6("someValuesFrom");
/** The property that determines the subject of a negative property assertion. */
const sourceIndividual = ns$6("sourceIndividual");
/** The property that determines the object of a negative object property assertion. */
const targetIndividual = ns$6("targetIndividual");
/** The property that determines the value of a negative data property assertion. */
const targetValue = ns$6("targetValue");
/** The data property that relates every individual to every data value. */
const topDataProperty = ns$6("topDataProperty");
/** The object property that relates every two individuals. */
const topObjectProperty = ns$6("topObjectProperty");
/** The property that determines the collection of classes or data ranges that build a union. */
const unionOf = ns$6("unionOf");
/** The property that determines the collection of facet-value pairs that define a datatype restriction. */
const withRestrictions = ns$6("withRestrictions");
/** The annotation property that indicates that a given ontology is backward compatible with another ontology. */
const backwardCompatibleWith = ns$6("backwardCompatibleWith");
/** The annotation property that indicates that a given entity has been deprecated. */
const deprecated = ns$6("deprecated");
/** The property that is used for importing other ontologies into a given ontology. */
const imports = ns$6("imports");
/** The annotation property that indicates that a given ontology is incompatible with another ontology. */
const incompatibleWith = ns$6("incompatibleWith");
/** The annotation property that indicates the predecessor ontology of a given ontology. */
const priorVersion = ns$6("priorVersion");
/** The annotation property that provides version information for an ontology or another OWL construct. */
const versionInfo = ns$6("versionInfo");
/** The property that identifies the version IRI of an ontology. */
const versionIRI = ns$6("versionIRI");
var owl = {
    /** Function to create arbitrary terms within the 'The OWL 2 Schema vocabulary (OWL 2)' ontology */
    ns: ns$6,
    /** The class of collections of pairwise different individuals. */
    AllDifferent,
    /** The class of collections of pairwise disjoint classes. */
    AllDisjointClasses,
    /** The class of collections of pairwise disjoint properties. */
    AllDisjointProperties,
    /** The class of annotated annotations for which the RDF serialization consists of an annotated subject, predicate and object. */
    Annotation,
    /** The class of annotation properties. */
    AnnotationProperty,
    /** The class of asymmetric properties. */
    AsymmetricProperty,
    /** The class of annotated axioms for which the RDF serialization consists of an annotated subject, predicate and object. */
    Axiom,
    /** The class of OWL classes. */
    Class: Class$1,
    /** The class of OWL data ranges, which are special kinds of datatypes. Note: The use of the IRI owl:DataRange has been deprecated as of OWL 2. The IRI rdfs:Datatype SHOULD be used instead. */
    DataRange,
    /** The class of data properties. */
    DatatypeProperty,
    /** The class of deprecated classes. */
    DeprecatedClass,
    /** The class of deprecated properties. */
    DeprecatedProperty,
    /** The class of functional properties. */
    FunctionalProperty,
    /** The class of inverse-functional properties. */
    InverseFunctionalProperty,
    /** The class of irreflexive properties. */
    IrreflexiveProperty,
    /** The class of named individuals. */
    NamedIndividual,
    /** The class of negative property assertions. */
    NegativePropertyAssertion,
    /** This is the empty class. */
    Nothing,
    /** The class of object properties. */
    ObjectProperty,
    /** The class of ontologies. */
    Ontology,
    /** The class of ontology properties. */
    OntologyProperty,
    /** The class of reflexive properties. */
    ReflexiveProperty,
    /** The class of property restrictions. */
    Restriction,
    /** The class of symmetric properties. */
    SymmetricProperty,
    /** The class of transitive properties. */
    TransitiveProperty,
    /** The class of OWL individuals. */
    Thing: Thing$1,
    /** The property that determines the class that a universal property restriction refers to. */
    allValuesFrom,
    /** The property that determines the predicate of an annotated axiom or annotated annotation. */
    annotatedProperty,
    /** The property that determines the subject of an annotated axiom or annotated annotation. */
    annotatedSource,
    /** The property that determines the object of an annotated axiom or annotated annotation. */
    annotatedTarget,
    /** The property that determines the predicate of a negative property assertion. */
    assertionProperty,
    /** The data property that does not relate any individual to any data value. */
    bottomDataProperty,
    /** The object property that does not relate any two individuals. */
    bottomObjectProperty,
    /** The property that determines the cardinality of an exact cardinality restriction. */
    cardinality,
    /** The property that determines that a given class is the complement of another class. */
    complementOf,
    /** The property that determines that a given data range is the complement of another data range with respect to the data domain. */
    datatypeComplementOf,
    /** The property that determines that two given individuals are different. */
    differentFrom,
    /** The property that determines that a given class is equivalent to the disjoint union of a collection of other classes. */
    disjointUnionOf,
    /** The property that determines that two given classes are disjoint. */
    disjointWith,
    /** The property that determines the collection of pairwise different individuals in a owl:AllDifferent axiom. */
    distinctMembers,
    /** The property that determines that two given classes are equivalent, and that is used to specify datatype definitions. */
    equivalentClass,
    /** The property that determines that two given properties are equivalent. */
    equivalentProperty,
    /** The property that determines the collection of properties that jointly build a key. */
    hasKey,
    /** The property that determines the property that a self restriction refers to. */
    hasSelf,
    /** The property that determines the individual that a has-value restriction refers to. */
    hasValue: hasValue$1,
    /** The property that determines the collection of classes or data ranges that build an intersection. */
    intersectionOf,
    /** The property that determines that two given properties are inverse. */
    inverseOf,
    /** The property that determines the cardinality of a maximum cardinality restriction. */
    maxCardinality,
    /** The property that determines the cardinality of a maximum qualified cardinality restriction. */
    maxQualifiedCardinality,
    /** The property that determines the collection of members in either a owl:AllDifferent, owl:AllDisjointClasses or owl:AllDisjointProperties axiom. */
    members: members$1,
    /** The property that determines the cardinality of a minimum cardinality restriction. */
    minCardinality,
    /** The property that determines the cardinality of a minimum qualified cardinality restriction. */
    minQualifiedCardinality,
    /** The property that determines the class that a qualified object cardinality restriction refers to. */
    onClass,
    /** The property that determines the data range that a qualified data cardinality restriction refers to. */
    onDataRange,
    /** The property that determines the datatype that a datatype restriction refers to. */
    onDatatype,
    /** The property that determines the collection of individuals or data values that build an enumeration. */
    oneOf: oneOf$1,
    /** The property that determines the n-tuple of properties that a property restriction on an n-ary data range refers to. */
    onProperties,
    /** The property that determines the property that a property restriction refers to. */
    onProperty,
    /** The property that determines the n-tuple of properties that build a sub property chain of a given property. */
    propertyChainAxiom,
    /** The property that determines that two given properties are disjoint. */
    propertyDisjointWith,
    /** The property that determines the cardinality of an exact qualified cardinality restriction. */
    qualifiedCardinality,
    /** The property that determines that two given individuals are equal. */
    sameAs: sameAs$1,
    /** The property that determines the class that an existential property restriction refers to. */
    someValuesFrom,
    /** The property that determines the subject of a negative property assertion. */
    sourceIndividual,
    /** The property that determines the object of a negative object property assertion. */
    targetIndividual,
    /** The property that determines the value of a negative data property assertion. */
    targetValue,
    /** The data property that relates every individual to every data value. */
    topDataProperty,
    /** The object property that relates every two individuals. */
    topObjectProperty,
    /** The property that determines the collection of classes or data ranges that build a union. */
    unionOf,
    /** The property that determines the collection of facet-value pairs that define a datatype restriction. */
    withRestrictions,
    /** The annotation property that indicates that a given ontology is backward compatible with another ontology. */
    backwardCompatibleWith,
    /** The annotation property that indicates that a given entity has been deprecated. */
    deprecated,
    /** The property that is used for importing other ontologies into a given ontology. */
    imports,
    /** The annotation property that indicates that a given ontology is incompatible with another ontology. */
    incompatibleWith,
    /** The annotation property that indicates the predecessor ontology of a given ontology. */
    priorVersion,
    /** The annotation property that provides version information for an ontology or another OWL construct. */
    versionInfo,
    /** The property that identifies the version IRI of an ontology. */
    versionIRI
};

/*
 * TODO: Basically all of them...
 */
// const nsOWLAllDifferent = NS.owl("AllDifferent");
// const nsOWLAllDisjointClasses = NS.owl("AllDisjointClasses");
// const nsOWLAllDisjointProperties = NS.owl("AllDisjointProperties");
// const nsOWLallValuesFrom = NS.owl("allValuesFrom");
// const nsOWLannotatedProperty = NS.owl("annotatedProperty");
// const nsOWLannotatedSource = NS.owl("annotatedSource");
// const nsOWLannotatedTarget = NS.owl("annotatedTarget");
// const nsOWLAnnotation = NS.owl("Annotation");
// const nsOWLAnnotationProperty = NS.owl("AnnotationProperty");
// const nsOWLassertionProperty = NS.owl("assertionProperty");
// const nsOWLAsymmetricProperty = NS.owl("AsymmetricProperty");
// const nsOWLAxiom = NS.owl("Axiom");
// const nsOWLbackwardCompatibleWith = NS.owl("backwardCompatibleWith");
// const nsOWLbottomDataProperty = NS.owl("bottomDataProperty");
// const nsOWLbottomObjectProperty = NS.owl("bottomObjectProperty");
// const nsOWLcardinality = NS.owl("cardinality");
// const nsOWLClass = NS.owl("Class");
// const nsOWLcomplementOf = NS.owl("complementOf");
// const nsOWLDataRange = NS.owl("DataRange");
// const nsOWLdatatypeComplementOf = NS.owl("datatypeComplementOf");
// const nsOWLDatatypeProperty = NS.owl("DatatypeProperty");
// const nsOWLdeprecated = NS.owl("deprecated");
// const nsOWLDeprecatedClass = NS.owl("DeprecatedClass");
// const nsOWLDeprecatedProperty = NS.owl("DeprecatedProperty");
// const nsOWLdifferentFrom = NS.owl("differentFrom");
// const nsOWLdisjointUnionOf = NS.owl("disjointUnionOf");
// const nsOWLdisjointWith = NS.owl("disjointWith");
// const nsOWLdistinctMembers = NS.owl("distinctMembers");
// const nsOWLequivalentClass = NS.owl("equivalentClass");
// const nsOWLequivalentProperty = NS.owl("equivalentProperty");
// const nsOWLFunctionalProperty = NS.owl("FunctionalProperty");
// const nsOWLhasKey = NS.owl("hasKey");
// const nsOWLhasSelf = NS.owl("hasSelf");
// const nsOWLhasValue = NS.owl("hasValue");
// const nsOWLimports = NS.owl("imports");
// const nsOWLincompatibleWith = NS.owl("incompatibleWith");
// const nsOWLintersectionOf = NS.owl("intersectionOf");
// const nsOWLInverseFunctionalProperty = NS.owl("InverseFunctionalProperty");
// const nsOWLinverseOf = NS.owl("inverseOf");
// const nsOWLIrreflexiveProperty = NS.owl("IrreflexiveProperty");
// const nsOWLmaxCardinality = NS.owl("maxCardinality");
// const nsOWLmaxQualifiedCardinality = NS.owl("maxQualifiedCardinality");
// const nsOWLmembers = NS.owl("members");
// const nsOWLminCardinality = NS.owl("minCardinality");
// const nsOWLminQualifiedCardinality = NS.owl("minQualifiedCardinality");
// const nsOWLNamedIndividual = NS.owl("NamedIndividual");
// const nsOWLNegativePropertyAssertion = NS.owl("NegativePropertyAssertion");
// const nsOWLNothing = NS.owl("Nothing");
// const nsOWLObjectProperty = NS.owl("ObjectProperty");
// const nsOWLonClass = NS.owl("onClass");
// const nsOWLonDataRange = NS.owl("onDataRange");
// const nsOWLonDatatype = NS.owl("onDatatype");
// const nsOWLoneOf = NS.owl("oneOf");
// const nsOWLonProperty = NS.owl("onProperty");
// const nsOWLonProperties = NS.owl("onProperties");
// const nsOWLOntology = NS.owl("Ontology");
// const nsOWLOntologyProperty = NS.owl("OntologyProperty");
// const nsOWLpriorVersion = NS.owl("priorVersion");
// const nsOWLpropertyChainAxiom = NS.owl("propertyChainAxiom");
// const nsOWLpropertyDisjointWith = NS.owl("propertyDisjointWith");
// const nsOWLqualifiedCardinality = NS.owl("qualifiedCardinality");
// const nsOWLReflexiveProperty = NS.owl("ReflexiveProperty");
// const nsOWLRestriction = NS.owl("Restriction");
const nsOWLsameAs = owl.sameAs;
// const nsOWLsomeValuesFrom = NS.owl("someValuesFrom");
// const nsOWLsourceIndividual = NS.owl("sourceIndividual");
// const nsOWLSymmetricProperty = NS.owl("SymmetricProperty");
// const nsOWLtargetIndividual = NS.owl("targetIndividual");
// const nsOWLtargetValue = NS.owl("targetValue");
// const nsOWLThing = NS.owl("Thing");
// const nsOWLtopDataProperty = NS.owl("topDataProperty");
// const nsOWLtopObjectProperty = NS.owl("topObjectProperty");
// const nsOWLTransitiveProperty = NS.owl("TransitiveProperty");
// const nsOWLunionOf = NS.owl("unionOf");
// const nsOWLversionInfo = NS.owl("versionInfo");
// const nsOWLversionIRI = NS.owl("versionIRI");
// const nsOWLwithRestrictions = NS.owl("withRestrictions");
const OWL = {
    axioms: [],
    processStatement(item, ctx) {
        if (rdfFactory.equals(item.predicate, nsOWLsameAs)) {
            const a = ctx.equivalenceSet.add(item.object.id);
            const b = ctx.equivalenceSet.add(item.subject.id);
            ctx.equivalenceSet.union(a, b);
            return [item];
        }
        return null;
    },
    processType(_, __) {
        return false;
    },
};

const ianaMT = createNS("http://www.w3.org/ns/iana/media-types/");
const ianaJSONResource = ianaMT(`${F_JSON}#Resource`);
const ianaJSONLDResource = ianaMT(`${F_JSONLD}#Resource`);
const ianaN3Resource = ianaMT(`${F_N3}#Resource`);
const ianaNTResource = ianaMT(`${F_NTRIPLES}#Resource`);
const ianaNQResource = ianaMT(`${F_NQUADS}#Resource`);
const ianaPlainResource = ianaMT(`${F_PLAIN}#Resource`);
const ianaRDFXMLResource = ianaMT(`${F_RDF_XML}#Resource`);
const ianaTTLResource = ianaMT(`${F_TURTLE}#Resource`);
/**
 * Implements the assumptions done by rdflib.js in various parts of their code.
 */
const RDFLIB = {
    axioms: [
        rdfFactory.quad(defaultNS.link("Document"), subClassOf, Resource),
        rdfFactory.quad(ianaJSONResource, subClassOf, Resource),
        rdfFactory.quad(ianaJSONLDResource, subClassOf, Resource),
        rdfFactory.quad(ianaN3Resource, subClassOf, Resource),
        rdfFactory.quad(ianaNTResource, subClassOf, Resource),
        rdfFactory.quad(ianaNQResource, subClassOf, Resource),
        rdfFactory.quad(ianaPlainResource, subClassOf, Resource),
        rdfFactory.quad(ianaRDFXMLResource, subClassOf, Resource),
        rdfFactory.quad(ianaTTLResource, subClassOf, Resource),
    ],
    processStatement(_, __) {
        return null;
    },
    processType(_, __) {
        return false;
    },
};

/**
 * Implements the RDF/RDFS axioms and rules.
 * @type {VocabularyProcessor}
 */
const RDFS = {
    axioms: [
        rdfFactory.quad(rdf.type, rdfs.domain, rdfs.Resource),
        rdfFactory.quad(rdfs.domain, rdfs.domain, rdf.Property),
        rdfFactory.quad(rdfs.range, rdfs.domain, rdf.Property),
        rdfFactory.quad(rdfs.subPropertyOf, rdfs.domain, rdf.Property),
        rdfFactory.quad(rdfs.subClassOf, rdfs.domain, rdfs.Class),
        rdfFactory.quad(rdf.subject, rdfs.domain, rdf.Statement),
        rdfFactory.quad(rdf.predicate, rdfs.domain, rdf.Statement),
        rdfFactory.quad(rdf.object, rdfs.domain, rdf.Statement),
        rdfFactory.quad(rdfs.member, rdfs.domain, rdfs.Resource),
        rdfFactory.quad(rdf.first, rdfs.domain, rdf.List),
        rdfFactory.quad(rdf.rest, rdfs.domain, rdf.List),
        rdfFactory.quad(rdfs.seeAlso, rdfs.domain, rdfs.Resource),
        rdfFactory.quad(rdfs.isDefinedBy, rdfs.domain, rdfs.Resource),
        rdfFactory.quad(rdfs.comment, rdfs.domain, rdfs.Resource),
        rdfFactory.quad(rdfs.label, rdfs.domain, rdfs.Resource),
        rdfFactory.quad(rdf.value, rdfs.domain, rdfs.Resource),
        rdfFactory.quad(rdf.type, rdfs.range, rdfs.Class),
        rdfFactory.quad(rdfs.domain, rdfs.range, rdfs.Class),
        rdfFactory.quad(rdfs.range, rdfs.range, rdfs.Class),
        rdfFactory.quad(rdfs.subPropertyOf, rdfs.range, rdf.Property),
        rdfFactory.quad(rdfs.subClassOf, rdfs.range, rdfs.Class),
        rdfFactory.quad(rdf.subject, rdfs.range, rdfs.Resource),
        rdfFactory.quad(rdf.predicate, rdfs.range, rdfs.Resource),
        rdfFactory.quad(rdf.object, rdfs.range, rdfs.Resource),
        rdfFactory.quad(rdfs.member, rdfs.range, rdfs.Resource),
        rdfFactory.quad(rdf.first, rdfs.range, rdfs.Resource),
        rdfFactory.quad(rdf.rest, rdfs.range, rdf.List),
        rdfFactory.quad(rdfs.seeAlso, rdfs.range, rdfs.Resource),
        rdfFactory.quad(rdfs.isDefinedBy, rdfs.range, rdfs.Resource),
        rdfFactory.quad(rdfs.comment, rdfs.range, rdfs.Literal),
        rdfFactory.quad(rdfs.label, rdfs.range, rdfs.Literal),
        rdfFactory.quad(rdf.value, rdfs.range, rdfs.Resource),
        rdfFactory.quad(rdf.Alt, rdfs.subClassOf, rdfs.Container),
        rdfFactory.quad(rdf.Bag, rdfs.subClassOf, rdfs.Container),
        rdfFactory.quad(rdf.Seq, rdfs.subClassOf, rdfs.Container),
        rdfFactory.quad(rdfs.ContainerMembershipProperty, rdfs.subClassOf, rdf.Property),
        rdfFactory.quad(rdfs.isDefinedBy, rdfs.subPropertyOf, rdfs.seeAlso),
        rdfFactory.quad(rdfs.Datatype, rdfs.subClassOf, rdfs.Class),
        rdfFactory.quad(rdfs.Resource, rdf.type, rdfs.Class),
        rdfFactory.quad(rdfs.Class, rdf.type, rdfs.Class),
    ],
    processStatement(item, ctx) {
        const result = [item];
        const domainStatements = ctx.store.statementsMatching(item.predicate, rdfs.domain);
        if (domainStatements.length > 0) {
            for (let i = 0; i < domainStatements.length; i++) {
                result.push(rdfFactory.quad(item.subject, rdf.type, domainStatements[i].object));
            }
        }
        const rangeStatements = ctx.store.statementsMatching(item.predicate, rdfs.range);
        if (rangeStatements.length > 0) { // P rdfs:range C..Cn
            for (let i = 0; i < rangeStatements.length; i++) {
                result.push(rdfFactory.quad(item.object, rdf.type, rangeStatements[i].object));
            }
        }
        if (rdfFactory.equals(rdfs.domain, item.predicate)) {
            result.push(rdfFactory.quad(item.subject, rdf.type, rdf.Property)); // P rdf:type rdf:Property
            result.push(rdfFactory.quad(item.object, rdf.type, rdfs.Class)); // C rdf:type rdfs:Class
            const dereferences = ctx.store.statementsMatching(item.subject);
            for (let i = 0; i < dereferences.length; i++) {
                result.push(rdfFactory.quad(item.subject, rdf.type, dereferences[i].object));
            }
            if (!rdfFactory.equals(item.subject, rdf.type)) {
                ctx.dataStore.getInternalStore().newPropertyAction(item.subject, (_, subj) => {
                    ctx.store.addStatements([rdfFactory.quad(subj, rdf.type, item.object)]);
                    return true;
                });
            }
        }
        else if (rdfFactory.equals(rdfs.range, item.predicate)) {
            result.push(rdfFactory.quad(item.subject, rdf.type, rdf.Property)); // P rdf:type rdf:Property
            result.push(rdfFactory.quad(item.object, rdf.type, rdfs.Class)); // C rdf:type rdfs:Class
            const dereferences = ctx.store.statementsMatching(undefined, undefined, item.subject);
            for (let i = 0; i < dereferences.length; i++) {
                result.push(rdfFactory.quad(dereferences[i].subject, rdf.type, item.object));
            }
            if (!rdfFactory.equals(item.subject, rdf.type)) {
                ctx.dataStore.getInternalStore().newPropertyAction(item.subject, (_, __, ___, obj) => {
                    ctx.store.addStatements([rdfFactory.quad(obj, rdf.type, item.object)]);
                    return true;
                });
            }
        }
        else if (rdfFactory.equals(rdfs.subClassOf, item.predicate)) { // C1 rdfs:subClassOf C2
            if (!(item.object.termType === "NamedNode" || item.object.termType === "BlankNode")) {
                throw new Error("Object of subClassOf statement must be a NamedNode");
            }
            const iSubject = rdfFactory.id(item.subject);
            const iObject = rdfFactory.id(item.object);
            if (!ctx.superMap.has(iObject)) {
                ctx.superMap.set(iObject, new Set([rdfFactory.id(rdfs.Resource)]));
            }
            let parents = ctx.superMap.get(iObject);
            if (parents === undefined) {
                parents = new Set();
                ctx.superMap.set(iObject, parents);
            }
            parents.add(iObject);
            const itemVal = ctx.superMap.get(iSubject) || new Set([iSubject]);
            parents.forEach((i) => itemVal.add(i));
            ctx.superMap.set(iSubject, itemVal);
            ctx.superMap.forEach((v, k) => {
                if (k !== iSubject && v.has(iSubject)) {
                    itemVal.forEach(v.add, v);
                }
            });
        }
        else if (rdfFactory.equals(rdfs.subPropertyOf, item.predicate)) {
            // TODO: Implement
            return result;
        }
        return result.length === 1 ? null : result;
    },
    processType(type, ctx) {
        RDFS.processStatement(rdfFactory.quad(type, rdfs.subClassOf, rdfs.Resource), ctx);
        ctx.store.add(rdfFactory.quad(type, rdf.type, rdfs.Class));
        return false;
    },
};

/**
 * DisjointSet
 * Module port from https://github.com/mljs/disjoint-set/
 */
/**
 * @class DisjointSet
 */
class DisjointSet {
    constructor() {
        this.nodes = new Map();
    }
    /**
     * Adds an element as a new set
     * @param {*} value
     * @return {DisjointSetNode} Object holding the element
     */
    add(value) {
        let node = this.nodes.get(value);
        if (!node) {
            node = new DisjointSetNode(value);
            this.nodes.set(value, node);
        }
        return node;
    }
    /**
     * Merges the sets that contain x and y
     * @param {DisjointSetNode} x
     * @param {DisjointSetNode} y
     */
    union(x, y) {
        const rootX = this.find(x);
        const rootY = this.find(y);
        if (rootX === rootY) {
            return;
        }
        if (rootX.rank < rootY.rank) {
            rootX.parent = rootY;
        }
        else if (rootX.rank > rootY.rank) {
            rootY.parent = rootX;
        }
        else {
            rootY.parent = rootX;
            rootX.rank++;
        }
    }
    /**
     * Finds and returns the root node of the set that contains node
     * @param {DisjointSetNode} node
     * @return {DisjointSetNode}
     */
    find(node) {
        let rootX = node;
        while (rootX.parent !== null) {
            rootX = rootX.parent;
        }
        let toUpdateX = node;
        while (toUpdateX.parent !== null) {
            const toUpdateParent = toUpdateX;
            toUpdateX = toUpdateX.parent;
            toUpdateParent.parent = rootX;
        }
        return rootX;
    }
    /**
     * Returns true if x and y belong to the same set
     * @param {DisjointSetNode} x
     * @param {DisjointSetNode} y
     */
    connected(x, y) {
        return this.find(x) === this.find(y);
    }
}
// tslint:disable-next-line max-classes-per-file
class DisjointSetNode {
    constructor(value) {
        this.value = value;
        this.parent = null;
        this.rank = 0;
    }
}

/**
 * Implements some RDF/OWL logic to enhance the functionality of the property lookups.
 *
 * Basically duplicates some functionality already present in {IndexedFormula} IIRC, but this API should be more
 * optimized so it can be used in real-time by low-power devices as well.
 */
class Schema extends convertedIndexedFormula {
    constructor(liveStore) {
        super();
        this.equivalenceSet = new DisjointSet();
        this.superMap = new Map();
        this.processedTypes = [];
        this.liveStore = liveStore;
        this.expansionCache = {};
        this.rdfFactory = rdfFactory;
        for (let i = 0; i < Schema.vocabularies.length; i++) {
            this.addStatements(Schema.vocabularies[i].axioms);
        }
    }
    /** Push statements onto the graph so it can be used by the render store for component determination. */
    addStatements(statements) {
        const unique = statements.filter((s) => !this.holdsStatement(s));
        const eligible = unique.filter(this.process.bind(this));
        if (eligible.length === 0) {
            return;
        }
        this.addAll(eligible);
        return this.liveStore.addStatements(eligible);
    }
    expand(types) {
        if (types.length === 1) {
            const existing = this.expansionCache[types[0]];
            this.expansionCache[types[0]] = existing
                ? existing
                : this.sort(this.mineForTypes(types));
            return this.expansionCache[types[0]];
        }
        return this.sort(this.mineForTypes(types));
    }
    getProcessingCtx() {
        return {
            dataStore: this.liveStore,
            equivalenceSet: this.equivalenceSet,
            store: this,
            superMap: this.superMap,
        };
    }
    isInstanceOf(resource, superClass) {
        return this.holdsStatement(rdfFactory.quad(rdfFactory.fromId(resource), rdf.type, rdfFactory.fromId(superClass)));
    }
    isSubclassOf(resource, superClass) {
        const resourceMap = this.superMap.get(resource);
        if (resourceMap) {
            return resourceMap.has(superClass);
        }
        return false;
    }
    /**
     * Returns the hierarchical depth of the type, or -1 if unknown.
     * @param type the type to check
     */
    superTypeDepth(type) {
        const superMap = this.superMap.get(type);
        return superMap ? superMap.size : -1;
    }
    /**
     * Expands the given lookupTypes to include all their equivalent and subclasses.
     * This is done in multiple iterations until no new types are found.
     * @param lookupTypes The types to look up. Once given, these are assumed to be classes.
     */
    mineForTypes(lookupTypes) {
        if (lookupTypes.length === 0) {
            return [rdfFactory.id(rdfs.Resource)];
        }
        const canonicalTypes = [];
        for (let i = 0; i < lookupTypes.length; i++) {
            const canon = rdfFactory.id(this.liveStore.canon(rdfFactory.fromId(lookupTypes[i])));
            if (!this.processedTypes.includes(canon)) {
                for (let j = 0; j < Schema.vocabularies.length; j++) {
                    Schema.vocabularies[j].processType(rdfFactory.fromId(canon), this.getProcessingCtx());
                }
                this.processedTypes.push(canon);
            }
            if (!canonicalTypes.includes(canon)) {
                canonicalTypes.push(canon);
            }
        }
        const allTypes = canonicalTypes
            .reduce((a, b) => {
            const superSet = this.superMap.get(b);
            if (typeof superSet === "undefined") {
                return a;
            }
            superSet.forEach((s) => {
                if (!a.includes(s)) {
                    a.push(s);
                }
            });
            return a;
        }, [...lookupTypes]);
        return this.sort(allTypes);
    }
    sort(types) {
        return types.sort((a, b) => {
            if (this.isSubclassOf(a, b)) {
                return -1;
            }
            else if (this.isSubclassOf(b, a)) {
                return 1;
            }
            const aDepth = this.superTypeDepth(a);
            const bDepth = this.superTypeDepth(b);
            if (aDepth < bDepth) {
                return 1;
            }
            else if (aDepth > bDepth) {
                return -1;
            }
            return 0;
        });
    }
    process(item) {
        for (let i = 0; i < Schema.vocabularies.length; i++) {
            const res = Schema.vocabularies[i].processStatement(item, this.getProcessingCtx());
            if (res !== null) {
                return res;
            }
        }
        return null;
    }
}
Schema.vocabularies = [OWL, RDFS, RDFLIB];

/**
 * Main entrypoint into the functionality of link-lib.
 *
 * Before using the methods for querying data and views here, search through your render library (e.g. link-redux) to
 * see if it exposes an API which covers your use-case. Skipping the render library might cause unexpected behaviour and
 * hard to solve bugs.
 */
class LinkedRenderStore {
    // tslint:disable-next-line no-object-literal-type-assertion
    constructor(opts = {}) {
        /**
         * Map of {ActionMap} which hold action dispatchers. Calling a dispatcher should execute the action, causing them to
         * be handled like any back-end sent action.
         *
         * Constructing action IRI's and dispatching them in user code was a bit hard, this object allows any middleware to
         * define their actions (within their namespace) in a code-oriented fashion. The middleware has control over how the
         * actions will be dispatched, but it should be the same as if a back-end would have executed the action (via the
         * Exec-Action header).
         */
        this.actions = {};
        /** Whenever a resource has no type, assume it to be this. */
        this.defaultType = schema.Thing;
        this.namespaces = Object.assign({}, defaultNS);
        this.store = new RDFStore();
        this.bulkFetch = false;
        this.bulkSubscriptions = [];
        this.subjectSubscriptions = {};
        if (opts.store) {
            this.store = opts.store;
        }
        this.report = opts.report || ((e) => { throw e; });
        this.api = opts.api || new DataProcessor(Object.assign(Object.assign({}, opts.apiOpts), { dispatch: opts.dispatch, report: this.report, store: this.store }));
        this.deltaProcessors = [this.api, this.store];
        if (opts.dispatch) {
            this.dispatch = opts.dispatch;
        }
        this.defaultType = opts.defaultType || schema.Thing;
        this.namespaces = opts.namespaces || Object.assign({}, defaultNS);
        this.schema = opts.schema || new Schema(this.store);
        this.mapping = opts.mapping || new ComponentStore(this.schema);
        this.resourceQueue = [];
        this.broadcast = this.broadcast.bind(this);
        this.processResourceQueue = this.processResourceQueue.bind(this);
    }
    static registerRenderer(component, type, prop = RENDER_CLASS_NAME, topology = DEFAULT_TOPOLOGY) {
        const types = normalizeType(type)
            .map((t) => rdfFactory.id(t));
        const props = normalizeType(prop)
            .map((p) => rdfFactory.id(p || RENDER_CLASS_NAME));
        const topologies = normalizeType(topology)
            .map((t) => rdfFactory.id(t || DEFAULT_TOPOLOGY));
        return ComponentStore.registerRenderer(component, types, props, topologies);
    }
    get dispatch() {
        if (typeof this._dispatch === "undefined") {
            throw new Error("Invariant: cannot call `dispatch` before initialization is complete");
        }
        return this._dispatch;
    }
    set dispatch(value) {
        this._dispatch = value;
        this.api.dispatch = value;
    }
    /**
     * Push one or more ontological items onto the graph so it can be used by the render store for component
     * determination.
     *
     * Adding information after the initial render currently conflicts with the caching and will result in inconsistent
     * results.
     *
     * Statements added here will also be added to the data store so views can access the statements.
     */
    addOntologySchematics(items) {
        this.schema.addStatements(items);
    }
    /**
     * Execute an Action by its IRI. This will result in an HTTP request being done and probably some state changes.
     * @param {module:rdflib.NamedNode} subject The resource to execute. Generally a schema:Action derivative with a
     *   schema:EntryPoint to describe the request. Currently schema:url is used over schema:urlTemplate
     *   to acquire the request URL, since template processing isn't implemented (yet).
     * @param {DataObject} data An object to send in the body when a non-safe method is used.
     * @return {Promise<LinkedActionResponse>}
     */
    execActionByIRI(subject, data) {
        const preparedData = dataToGraphTuple(data || {});
        return this
            .api
            .execActionByIRI(subject, preparedData)
            .then((res) => {
            this.broadcast(false, 100);
            return res;
        });
    }
    /**
     * Execute a resource.
     *
     * Every action will fall through the execution middleware layers.
     *
     * @see https://github.com/fletcher91/link-lib/wiki/%5BDesign-draft%5D-Actions,-data-streams,-and-middleware
     *
     * @param {NamedNode} subject The resource to execute (can be either an IRI or an URI)
     * @param {Object} args The arguments to the function defined by the subject.
     */
    async exec(subject, args) {
        return this.dispatch(subject, args);
    }
    /**
     * Convert a string value to a NamedNode if possible. Useful for looking op dynamic data like user input. Please
     * refrain from using in static code, as this will impact performance.
     */
    expandProperty(prop) {
        return expandProperty(prop, this.namespaces);
    }
    /**
     * Resolve the values at the end of the path
     * @param subject The resource to start descending on
     * @param path A list of linked predicates to descend on.
     */
    dig(subject, path) {
        if (path.length === 0) {
            return [];
        }
        const remaining = path.slice();
        const pred = remaining.shift();
        if (remaining.length === 0) {
            return this.getResourceProperties(subject, pred);
        }
        const props = this.getResourceProperties(subject, pred);
        if (props) {
            return props
                .map((term) => (term.termType === TermType.NamedNode || term.termType === TermType.BlankNode)
                && this.dig(term, remaining))
                .flat(1)
                .filter(Boolean);
        }
        return [];
    }
    /**
     * Retrieve the subjects from {subject} to find all resources which have an object at the
     * end of the {path} which matches {match}.
     * @param subject The resource to start descending on.
     * @param path A list of linked predicates to descend on.
     * @param match The value which the predicate at the end of {path} has to match for its subject to return.
     */
    findSubject(subject, path, match) {
        if (path.length === 0) {
            return [];
        }
        const remaining = path.slice();
        const pred = remaining.shift();
        const props = this.getResourceProperties(subject, pred);
        if (props && remaining.length === 0) {
            const finder = Array.isArray(match)
                ? (p) => match.some((m) => rdfFactory.equals(m, p))
                : (p) => rdfFactory.equals(match, p);
            return props.find(finder) ? [subject] : [];
        }
        else if (props) {
            return props
                .map((term) => (term.termType === TermType.NamedNode || term.termType === TermType.BlankNode)
                && this.findSubject(term, remaining, match))
                .flat(1)
                .filter(Boolean);
        }
        return [];
    }
    /**
     * Finds the best render component for a given property in respect to a topology.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @param type The type(s) of the resource to render.
     * @param predicate The predicate(s) (property(s)) to render.
     * @param [topology] The topology of the resource, if any
     * @returns The most appropriate renderer, if any.
     */
    getComponentForProperty(type = this.defaultType, predicate, topology = DEFAULT_TOPOLOGY) {
        if (type === undefined || (Array.isArray(type) && type.length === 0)) {
            return undefined;
        }
        const types = normalizeType(type).map((t) => rdfFactory.id(t));
        const predicates = normalizeType(predicate).map((p) => rdfFactory.id(p));
        return this.mapping.getRenderComponent(types, predicates, rdfFactory.id(topology), rdfFactory.id(this.defaultType));
    }
    /**
     * Finds the best render component for a type in respect to a topology.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @see LinkedRenderStore#getComponentForProperty
     * @param type The type(s) of the resource to render.
     * @param [topology] The topology of the resource, if any
     * @returns The most appropriate renderer, if any.
     */
    getComponentForType(type, topology = DEFAULT_TOPOLOGY) {
        return this.getComponentForProperty(type, RENDER_CLASS_NAME, topology);
    }
    /**
     * Efficiently queues a resource to be fetched later.
     *
     * This skips the overhead of creating a promise and allows the subsystem to retrieve multiple resource in one
     * round trip, sacrificing loading status for performance.
     * @renderlibrary This should only be used by render-libraries, not by application code.
     */
    queueEntity(iri, opts) {
        if (!(opts && opts.reload) && !this.shouldLoadResource(iri)) {
            return;
        }
        this.resourceQueue.push([iri, opts]);
        this.scheduleResourceQueue();
    }
    /**
     * Queue a linked-delta to be processed.
     *
     * Note: This should only be used by render-libraries (e.g. link-redux), not by application code.
     * @renderlibrary This should only be used by render-libraries, not by application code.
     */
    queueDelta(delta, expedite = false) {
        const quadArr = isQuad(delta[0])
            ? delta.map((s) => rdfFactory.qdrFromQuad(s))
            : delta;
        const subjects = quadArr
            .reduce((acc, [s]) => acc.includes(rdfFactory.id(s)) ? acc : acc.concat(rdfFactory.id(s)), []);
        for (const dp of this.deltaProcessors) {
            dp.queueDelta(quadArr, subjects);
        }
        return this.broadcastWithExpedite(expedite);
    }
    /**
     * Will fetch the entity with the URL {iri}. When a resource under that subject is already present, it will not
     * be fetched again.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @param iri The Node of the resource
     * @param opts The options for fetch-/processing the resource.
     * @return A promise with the resulting entity
     */
    async getEntity(iri, opts) {
        const apiOpts = {};
        let preExistingData;
        if (opts && opts.reload) {
            apiOpts.force = true;
            apiOpts.clearPreviousData = true;
            preExistingData = this.tryEntity(iri);
        }
        if (preExistingData !== undefined) {
            this.store.removeStatements(preExistingData);
        }
        await this.api.getEntity(iri, apiOpts);
        this.broadcast();
    }
    /**
     * Resolves all the properties {property} of resource {subject} to their statements.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @param {Node} subject The resource to get the properties for.
     * @param {Node | Node[]} property
     * @return {Statement[]} All the statements of {property} on {subject}, or an empty array when none are present.
     */
    getResourcePropertyRaw(subject, property) {
        return this.store.getResourcePropertyRaw(subject, property);
    }
    /**
     * Resolves all the properties {property} of resource {subject} to a value.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @typeParam TT The expected return type for the properties.
     * @param {Node} subject The resource to get the properties for.
     * @param {Node | Node[]} property
     * @return {Term[]} The resolved values of {property}, or an empty array when none are present.
     */
    getResourceProperties(subject, property) {
        return this.store.getResourceProperties(subject, property);
    }
    /**
     * Resolves the property {property} of resource {subject} to a value.
     *
     * When more than one statement on {property} is present, a random one will be chosen. See
     * {LinkedResourceContainer#getResourceProperties} to retrieve all the values.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @typeParam TT The expected return type for the property.
     * @param {Node} subject The resource to get the properties for.
     * @param {Node | Node[]} property
     * @return {Term | undefined} The resolved value of {property}, or undefined when none are present.
     */
    getResourceProperty(subject, property) {
        return this.store.getResourceProperty(subject, property);
    }
    /**
     * Retrieve the (network) status of the resource {iri}.
     *
     * Status 202 indicates that the resource has been queued for fetching (subject to change).
     */
    getStatus(iri) {
        if (iri.termType === TermType.BlankNode) {
            return emptyRequest;
        }
        if (this.resourceQueue.find(([resource]) => rdfFactory.equals(resource, iri))) {
            return {
                lastRequested: new Date(),
                lastResponseHeaders: null,
                requested: true,
                status: 202,
                timesRequested: 1,
            };
        }
        return this.api.getStatus(iri);
    }
    /**
     * Process a linked-delta onto the store.
     *
     * This should generally only be called from the middleware or the data api
     * @param delta An array of [s, p, o, g] arrays containing the delta.
     * @param expedite Will immediately process the delta rather than waiting for an idle moment, useful in conjunction
     *  with event handlers within the UI needing immediate feedback. Might cause jumpy interfaces.
     */
    processDelta(delta, expedite = false) {
        const quadArr = Object.prototype.hasOwnProperty.call(delta[0], "subject")
            ? delta.map((s) => rdfFactory.qdrFromQuad(s))
            : delta;
        const statements = this.deltaProcessors
            .reduce((acc, dp) => acc.concat(dp.processDelta(quadArr)), []);
        return this.broadcastWithExpedite(expedite)
            .then(() => statements);
    }
    /**
     * Bulk register components formatted with {LinkedRenderStore.registerRenderer}.
     * @see LinkedRenderStore.registerRenderer
     */
    registerAll(...components) {
        const registerItem = (i) => {
            this.mapping.registerRenderer(i.component, i.type, i.property, i.topology);
        };
        for (let i = 0; i < components.length; i++) {
            if (Array.isArray(components[i])) {
                for (let j = 0; j < components[i].length; j++) {
                    registerItem(components[i][j]);
                }
            }
            else {
                registerItem(components[i]);
            }
        }
    }
    /**
     * Remove a resource from the store, when views are still rendered the resource will be re-fetched.
     *
     * @unstable
     */
    removeResource(subject, expedite = false) {
        this.api.invalidate(subject);
        this.store.removeResource(subject);
        return this.broadcastWithExpedite(expedite);
    }
    /**
     * Resets the render store mappings and the schema graph.
     *
     * Note: This should only be used by render-libraries (e.g. link-redux), not by application code.
     */
    reset() {
        this.store = new RDFStore();
        this.schema = new Schema(this.store);
        this.mapping = new ComponentStore(this.schema);
    }
    /**
     * Get a render component for a rendering {property} on resource {subject}.
     *
     * @renderlibrary
     * @param {Node} subject
     * @param {NamedNode | NamedNode[]} predicate
     * @param {NamedNode} topology
     * @return {T | undefined}
     */
    resourcePropertyComponent(subject, predicate, topology) {
        return this.getComponentForProperty(this.store.getResourceProperties(subject, rdf.type), predicate, topology || DEFAULT_TOPOLOGY);
    }
    /**
     * Get a render component for {subject}.
     *
     * @renderlibrary
     * @param {Node} subject The resource to get the renderer for.
     * @param {"rdflib".NamedNode} topology The topology to take into account when picking the renderer.
     * @return {T | undefined}
     */
    resourceComponent(subject, topology) {
        return this.getComponentForProperty(this.store.getResourceProperties(subject, rdf.type), RENDER_CLASS_NAME, topology || DEFAULT_TOPOLOGY);
    }
    /**
     * Determine if it makes sense to load a resource.
     *
     * @renderlibrary
     * @unstable
     */
    shouldLoadResource(subject) {
        return (this.store.changeTimestamps[rdfFactory.id(subject)] === undefined || this.api.isInvalid(subject))
            && !this.resourceQueue.find(([i]) => rdfFactory.equals(i, subject));
    }
    /**
     * Listen for data changes by subscribing to store changes.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @param registration
     * @param registration[0] Will be called with the new statements as its argument.
     * @param registration[1] Options for the callback.
     * @param registration[1].onlySubjects Only the subjects are passed when true.
     * @return function Unsubscription function.
     */
    subscribe(registration) {
        registration.subscribedAt = Date.now();
        const subjectFilter = registration.subjectFilter;
        if (typeof subjectFilter !== "undefined" && subjectFilter.length > 0) {
            for (let i = 0, len = subjectFilter.length; i < len; i++) {
                const id = rdfFactory.id(subjectFilter[i]);
                if (!this.subjectSubscriptions[id]) {
                    this.subjectSubscriptions[id] = [];
                }
                this.subjectSubscriptions[id].push(registration);
            }
            return () => {
                registration.markedForDelete = true;
                this.markForCleanup();
            };
        }
        this.bulkSubscriptions.push(registration);
        return () => {
            this.bulkSubscriptions.splice(this.bulkSubscriptions.indexOf(registration), 1);
        };
    }
    /** @internal */
    touch(_iri, _err) {
        this.broadcast();
        return true;
    }
    /**
     * Returns an entity from the cache directly.
     * This won't cause any network requests even if the entity can't be found.
     *
     * @renderlibrary This should only be used by render-libraries, not by application code.
     * @param iri The Node of the resource.
     * @returns The object if found, or undefined.
     */
    tryEntity(iri) {
        return this.store.statementsFor(iri);
    }
    /**
     * Broadcasts buffered to all subscribers.
     * The actual broadcast might be executed asynchronously to prevent lag.
     *
     * @param buffer Controls whether processing can be delayed until enough is available.
     * @param maxTimeout Set to 0 to execute immediately.
     * Note: This should only be used by render-libraries (e.g. link-redux), not by application code.
     */
    broadcast(buffer = true, maxTimeout = 1000) {
        if (maxTimeout !== 0 && this.currentBroadcast || this.broadcastHandle) {
            return this.currentBroadcast || Promise.resolve();
        }
        if (buffer) {
            if (this.store.workAvailable() >= 2) {
                if (this.broadcastHandle) {
                    window.clearTimeout(this.broadcastHandle);
                }
                if (this.lastPostponed === undefined) {
                    this.lastPostponed = Date.now();
                    this.broadcastHandle = window.setTimeout(() => {
                        this.broadcastHandle = undefined;
                        this.broadcast(buffer, maxTimeout);
                    }, 200);
                    return this.currentBroadcast || Promise.resolve();
                }
                else if (Date.now() - this.lastPostponed <= maxTimeout) {
                    this.broadcastHandle = window.setTimeout(() => {
                        this.broadcastHandle = undefined;
                        this.broadcast(buffer, maxTimeout);
                    }, 200);
                    return this.currentBroadcast || Promise.resolve();
                }
            }
            this.lastPostponed = undefined;
            this.broadcastHandle = undefined;
        }
        if (this.store.workAvailable() === 0) {
            return Promise.resolve();
        }
        const work = this.deltaProcessors.flatMap((dp) => dp.flush());
        const subjects = work
            .flatMap((w) => [rdfFactory.id(w.subject), rdfFactory.id(w.graph)])
            .reduce((acc, w) => acc.includes(w) ? acc : acc.concat(w), []);
        const subjectRegs = subjects
            .flatMap((id) => this.subjectSubscriptions[id])
            .filter((reg) => reg
            && !reg.markedForDelete
            && (reg.subjectFilter
                ? reg.subjectFilter.some((s) => subjects.includes(rdfFactory.id(s)))
                : true));
        if (this.bulkSubscriptions.length === 0 && subjectRegs.length === 0) {
            return Promise.resolve();
        }
        return this.currentBroadcast = new ProcessBroadcast({
            bulkSubscriptions: this.bulkSubscriptions.slice(),
            changedSubjects: subjects,
            subjectSubscriptions: subjectRegs,
            timeout: maxTimeout,
            work,
        }).run()
            .then(() => {
            this.currentBroadcast = undefined;
            if (this.store.workAvailable() > 0) {
                this.broadcast();
            }
        });
    }
    broadcastWithExpedite(expedite) {
        return this.broadcast(!expedite, expedite ? 0 : 500);
    }
    markForCleanup() {
        if (this.cleanupTimer) {
            return;
        }
        this.cleanupTimer = window.setTimeout(() => {
            this.cleanupTimer = undefined;
            for (const [k, value] of Object.entries(this.subjectSubscriptions)) {
                this.subjectSubscriptions[k] = value.filter((p) => !p.markedForDelete);
            }
        }, 500);
    }
    scheduleResourceQueue() {
        if (this.resourceQueueHandle) {
            return;
        }
        if (typeof window.requestIdleCallback !== "undefined") {
            this.resourceQueueHandle = window.requestIdleCallback(this.processResourceQueue, { timeout: 100 });
        }
        else {
            this.resourceQueueHandle = window.setTimeout(this.processResourceQueue, 100);
        }
    }
    async processResourceQueue() {
        this.resourceQueueHandle = undefined;
        const queue = this.resourceQueue;
        this.resourceQueue = [];
        if (this.bulkFetch) {
            await this.api.getEntities(queue);
            this.broadcast();
        }
        else {
            for (let i = 0; i < queue.length; i++) {
                try {
                    const [iri, opts] = queue[i];
                    await this.getEntity(iri, opts);
                }
                catch (e) {
                    this.report(e);
                }
            }
        }
    }
}

/**
 * Binds various uris to link actions.
 *
 * @see {createStore}
 * @param catchActions {boolean} Set to true to catch all left-over actions to {LinkedRenderStore#execActionByIRI}.
 */
const linkMiddleware = (catchActions = true) => (lrs) => (next) => (action, args) => {
    if (action.value.startsWith(defaultNS.ll("data/rdflib/").value)) {
        return Promise.resolve(lrs.touch(args[0], args[1]));
    }
    if (catchActions) {
        return lrs.execActionByIRI(action, args);
    }
    return next(action, args);
};

function applyMiddleware(lrs, ...layers) {
    const storeBound = layers.map((middleware) => middleware(lrs));
    const finish = (a, _o) => Promise.resolve(a);
    return storeBound.reduceRight((composed, f) => f(composed), finish);
}
/**
 * Initializes a {LinkedRenderStore} with tied together middleware.
 * @param storeOpts Constructor arguments for the LRS.
 * @param middleware Main middleware, to be executed before the {linkMiddelware}.
 * @param trailingMiddleware Middleware to be placed after the {linkMiddleware}. Note: defining trailing middleware
 *  causes actions not to be executed via {LinkedRenderStore#execActionByIRI} anymore, this behaviour can be enabled
 *  manually in one of the defined middlewares if still desired.
 */
function createStore(storeOpts, middleware = [], trailingMiddleware = []) {
    const LRS = new LinkedRenderStore(storeOpts);
    LRS.dispatch = applyMiddleware(LRS, ...middleware, linkMiddleware(trailingMiddleware.length === 0), ...trailingMiddleware);
    return LRS;
}

class ComponentStoreTestProxy extends ComponentStore {
    publicLookup(predicate, obj, topology) {
        return this.lookup(predicate, obj, topology);
    }
}
const getBasicStore = (opts = {}) => {
    const report = (e) => { throw e; };
    const store = opts.store || new RDFStore();
    const processor = opts.processor || new DataProcessor(Object.assign({ report, store }, opts.apiOpts));
    const api = opts.api || processor;
    const schema = opts.schema || new Schema(store);
    const mapping = opts.mapping || new ComponentStoreTestProxy(schema);
    const conf = {
        api,
        mapping,
        report,
        schema,
        store,
    };
    const lrs = createStore(conf);
    return {
        dispatch: lrs.dispatch,
        forceBroadcast: () => lrs.broadcast(false, 0),
        lrs,
        mapping,
        processor,
        schema,
        store,
    };
};

/**
 * Processes linked-delta responses.
 */
function linkedDeltaProcessor(lrs) {
    return async function processLinkedDelta(response) {
        let data;
        if (response instanceof Response) {
            data = response.bodyUsed ? "" : await response.text();
        }
        else if (typeof XMLHttpRequest !== "undefined" && response instanceof XMLHttpRequest) {
            data = response.responseText;
        }
        else {
            data = response.body;
        }
        if (!data || data.length === 0) {
            return [];
        }
        const parser = new NQuadsParser(lrs.store.getInternalStore());
        const quads = parser.parseString(data);
        const expedite = response.hasOwnProperty("expedite") ? response.expedite : false;
        lrs.queueDelta(quads, expedite);
        // TODO: Resolve the statements in this request
        return [];
    };
}

const isRdfLibResponse = (res) => typeof res.req !== "undefined" && typeof res.req.termType !== "undefined";
/**
 * Processes a range of media types with parsers from the
 * [rdflib.js package](https://www.npmjs.com/package/rdflib).
 */
async function processRDF(response) {
    let data;
    if (isRdfLibResponse(response)) {
        data = response.responseText;
    }
    else if (response instanceof Response) {
        data = response.bodyUsed ? "" : await response.text();
    }
    else if (response instanceof XMLHttpRequest) {
        data = response.responseText;
    }
    else {
        data = response.body;
    }
    const format = getContentType(response);
    const g = graph();
    await new Promise((resolve) => {
        convertedParse(data, g, getURL(response), format, () => {
            resolve();
        });
    });
    return g.statements;
}

const transformers = {
    linkedDeltaProcessor,
    processRDF,
};

export default LinkedRenderStore;
export { ComponentStoreTestProxy, DEFAULT_TOPOLOGY, DataProcessor, LinkedRenderStore, RDFStore, RENDER_CLASS_NAME, RequestInitGenerator, Schema, allRDFPropertyStatements, allRDFValues, anyRDFValue, createStore, defaultNS, deltaProcessor, getBasicStore, getPropBestLangRaw, getTermBestLang, isDifferentOrigin, linkMiddleware, list, normalizeType, rdflib, seq, toGraph, transformers };
//# sourceMappingURL=link-lib.es6.js.map
